import PouchDB from 'pouchdb-browser';
import pouchdbFind from 'pouchdb-find';
PouchDB.plugin(pouchdbFind);

//import pouchdbDebug from 'pouchdb-debug';
import { DateTime } from 'luxon';
import { bodyFingerprint, CommonBaseMergeResult, deepClone, deepCompareEQ, deepNormalize, deepOverwriteCouchSafe, isPouchInternalKey, mergeAgainstCommonBase } from '@cf-shared/deep-helpers';
import { asciiToHex, AuthService, UserAuth } from 'services/auth.service';
import { AppEnvironment } from 'environments/environment';
import { authRoleToStrings, AuthRole } from '@cf-shared/couch-auth';
import { PouchDoc, isPouchConflict, isPouchNotFound, getTransactionDeviceId } from '@cf-shared/data-model/pouch';
import { DAY_TASK_STATUS_CACHE_ID } from '@cf-shared/data-model/day-task-status-cache';
import { browserLocalStorageOrNull, getDeviceId } from 'environments/environment-helpers';
import { pageStartedAfterPurchaseReload } from 'app/after-purchase-reload';
import { getLocalAdapterOpts, usesSqliteEngine } from './local-database-engine';
import { SQLITE_COACH_COPY_NAMES_KEY } from './local-database-engine-decisions';
import { recordCoachCopyName } from './sqlite-migration-decisions';
import { DeferredPromise, sleep, timeout } from '@cf-shared/shared-util';
import { LL } from 'i18n/i18n.service';
import { untranslatedString } from 'models/translated-string';
import { Mutex } from '@cf-shared/mutex';
import { ReadWriteLock } from '@cf-shared/read-write-lock';
import { Injectable } from '@angular/core';
import { LoggingService } from './logging.service';
import { decideTemporaryDbChange, TemporaryDbChange } from './impersonation-stop';
import { DatabaseRecoveryService } from './database-recovery.service';
import { MixpanelCopy } from './problem-kind';
import {
  makeLocalAdapterFaultReporter, makeLocalCorruptionReporter,
} from './pouchdb-adapter-capacitor-sqlite/local-corruption-reporter';
import { declareEvent, PubSubService, PubSubRegistration } from './pubsub.service';
import { AppService } from './app.service';
import { ConnectionState, OnlineStateService, connectionStateChangedEvent, FIRST_PROBE_GATE_TIMEOUT_MS } from './online-state.service';
import { pollUntilDrainedAsync } from './sign-out-guard';
import { DebugService } from './debug.service';
import { DocWatcher, DocWatcherCallback, DocWatcherOptions } from './doc-watcher';
import { DatabaseUnavailableError, DatabaseUnavailableReason, captureDatabaseCallSite, isDatabaseTornDown } from './database-unavailable-error';
import { attachReadCallSite } from './database-wrapper-read-errors';
import { localOpName, trackLocalOpAsync } from './local-op-tracker';
import { bootTimings, BootPhase } from 'app/boot-timings';
import { clearWarmResumeFlag } from './warm-resume-storage';
import { ViewBuildSerializer } from './view-build-serializer';
import {
  currentLocalStoreName,
  decideLocalStoreOrigin,
  isUserDataWrite,
  hasAutoSwitchedLocalStore,
  hasLocalStoreBeenWritten,
  markLocalStoreWritten,
  readLocalStoreOrigin,
  recordLocalStoreOrigin,
  SilentLocalStoreFacts,
} from './local-store-generation';
import { listPouchIndexedDbNamesAsync } from 'app/legacy-idb-cleanup';
import { localCoachClientDbBaseName, localSharedDbBaseName, localUserDbBaseName } from './local-user-db-name';
import { runningTimeTimer, withBootLocalStoreTimeout, withLocalStoreTimeout } from './local-store-timeout';
import { timeoutNotingFreezeAsync } from './running-clock';
import { localeTextRangeSelector, TextLocaleSegment } from './text-locale';
import {
  chooseReadDb as chooseReadDbPure,
  chooseWriteDb as chooseWriteDbPure,
  graftPassThroughDocs as graftPassThroughDocsPure,
  DbHandle,
  PassThroughDbState,
  withoutDeviceLocalDocs,
  passThroughReadMayFindLocalAhead,
  compareLocalCopyToPassThroughBody,
  PassThroughLocalCopyRelation,
  readGateAfterFailedInitialSyncRound,
} from './database-wrapper-passthrough';
import {
  readViewDesignInputsAsync,
  storedViewDesignMatchesIntended,
  swapLocalStoreAsync,
  VIEW_DESIGN_DOC_ID,
} from './database-wrapper-local-store';
import {
  decideStartLocalDbFollowup,
  collectFoundDocs,
  armSyncMutexHoldWatchdog,
  readLoadedSingletonDoc,
  selectActiveDatabases,
  mayCloseLocalStore,
} from './database-wrapper-sync-boot-policy';
import {
  recoverBulkSaveFailuresAsync,
  recoverMissingDocViaReadDbAsync,
  ReadDbRecoveryAnswer,
  recoverPutOnDeletedOrAbsentDocAsync,
  recoverDeleteOnConflictAsync,
  reportWriteDroppedIntoGracefulRecovery,
  WriteDropSite,
  WriteOutcome,
  BulkSaveResultRow,
  MISSING_ID_ROW_NAME,
  hasUsableDocId,
  captureDeleteRestorePoint,
  restoreDeletedFlags,
  bulkRowLostTheWrite,
  DeleteOutcome,
  DeleteRestorePoint,
  LocalWinnerRow,
  deleteOutcomeNeedsRestore,
  deleteOutcomesFromLocalRows,
  unknownOutcomeDocIds,
  distinctDocIds,
} from './database-wrapper-save-recovery';
import {
  resolveForcePushBody,
  didForcePushFork,
  isCaughtUp409,
  localHistoryReachesRemoteRev,
  ForcePushTrigger,
  ForcePushResult,
  PushToRemoteOutcome,
} from './database-wrapper-force-push';
import { HeldCopy, MissingBodyOutcome, resolveDocConflictsAsync } from './database-wrapper-conflict-resolve';
import { ConflictArchiveDeps } from './database-wrapper-conflict-archive';
import { describeRestoreFailure, restoreFromConflictArchiveAsync } from './database-wrapper-conflict-archive-restore';
import {
  PulledDeletionToSettle,
  PulledDeletionRepairOutcome,
  AdoptedRemoteRevisions,
  adoptRemoteLeavesAsync,
  settlePulledDeletionsAsync,
  isDocumentAliveInDbAsync,
} from './database-wrapper-pulled-deletions';
import { readSingletonThroughRecoveryAsync, SingletonDefaultReason } from './singleton-read-recovery';
import { BootSingletonRead, BootSingletonReadKind } from './boot-singleton-read';
import { SyncPhase, buildInitialSyncIdRanges, countInitialSyncDocsToPull, DocIdKeyRange } from './initial-sync-progress';
import { summarizeConflictScan, docTypeFromId } from './sync-telemetry-shaping';
import { computePrioritySyncCutoff, decideViewHealBump, decideGlobalViewRebuild, GLOBAL_VIEW_REBUILD_VERSION } from './sync-view-heal-decisions';
import { LocalRevTracker } from './local-rev-tracker';
import { LastSyncError, isRecentNetworkSyncError, classifySyncFailure, SyncFailureClass } from './sync-failure-classification';
import { PullCatchupStats, shouldRestartRealtimeListener, REALTIME_LISTENER_MAX_RESTARTS, decidePersistedUnflushedReport, decidePushStallReport, installPushStallClockResetOnHide, isLocalWriteUnackedByPush, isSyncDiagnosticEnvironmentHealthy, PUSH_STALL_MIN_MS, PUSH_STUCK_SINCE_STORAGE_KEY } from './sync-health-decisions';
import { parseRevGeneration } from './sync-rev-and-time-utils';
import { canUseRememberedMergeBase, chooseFoldBase, FoldBaseChoice, ancestryRevsOf, rememberPreviousDatabaseBody, rememberForgottenRevs, RememberedDatabaseBody, RememberedBodySource, classifySaveRevMismatch, SaveRevMismatch, decideMergeReportTier, MergeReportTier, decideCanonicalUpdateAction, CanonicalUpdateAction, SiblingRevisionPolicy, siblingPolicyForArrival, isOutOfDateCopy, isSharedContentKept, staleCopyWouldChangeContent, describeRevGap, shouldCompareCanonicalAgainstDatabaseBody, isDocumentWatchedForUnsavedContent, describeIncomingRevRelation } from './sync-revision-decisions';
import { decideConflictScanRun, CONFLICT_RESOLVE_ENUMERATION_BUDGET_MS } from './sync-conflict-resolve-decisions';
import { startSyncInternalAsync, SyncStartContext } from './database-wrapper-sync-start';
import { ensurePullCatchupInternalAsync, PullCatchupContext } from './database-wrapper-pull-catchup';
import { assureInitialSyncInternalAsync, InitialSyncContext } from './database-wrapper-initial-sync';
import { verifyPushCheckpointIntegrityInternalAsync, PushIntegrityContext } from './database-wrapper-push-integrity';
import { cancelOneShotReplicationOnError } from './one-shot-replication';
import { isTransientFetchError, errorReportFields, isPouchConflictError } from './error-patterns';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import {
  retryOnTransientErrorAsync,
  assertBulkDocsChunkSucceeded,
  isExpectedRestoreConflictId,
  isPreservedByRestoreId,
  planRestoreRepairPass,
  RestoreRepairPlan,
  RestoreVerifyRow,
  RESTORE_MAX_VERIFY_LISTINGS,
  RestoreAfterRemoteReplacedError,
  RESTORE_REMOTE_MAX_ATTEMPTS,
  RESTORE_LOCAL_DESTROY_MAX_ATTEMPTS,
  RESTORE_REMOTE_ATTACH_WAIT_MS,
  RESTORE_REMOTE_ATTACH_POLL_MS,
  RemoteAttachWaitOutcome,
  waitForRemoteAttachAsync,
} from './restore-retry';
import {
  planPurgeDeletePass,
  mergeConflictRevDeletes,
  PurgeDeleteRow,
  PurgeIncompleteError,
  PURGE_MAX_LIST_PASSES,
} from './purge-plan';
export { DocWatcher, DocWatcherCallback, DocChangeType, DocChange } from './doc-watcher';

/** the subset of PouchDB's `allDocs` response the restore actually reads */
interface RemoteAllDocsResult {
  rows: Array<{ id: string; value: { rev: string } }>;
}

/** Tag on the retry log lines that `purgeDatabaseAsync` produces. The retry helper's own default says
 * `[restore]`, which would send whoever reads a delete-my-data log looking for a restore that never ran. */
const PURGE_RETRY_LOG_PREFIX = '[purge]';

// Branded type for db__trans transaction IDs. Produced only by
// DatabaseWrapper.getTransactionId(); the brand is a compile-time
// phantom (`never`-typed) so the underlying string value is what gets
// serialized into the document — no `__transactionIdBrand` key ever
// reaches PouchDB. Prevents callers from passing arbitrary description
// strings, which would silently corrupt the forensic
// '<ISO timestamp>|<deviceId>' marker.
export type TransactionId = string & { readonly __transactionIdBrand: never };

class Singleton {
  doc: any;
  isSaving: boolean;
  needsSaving_transaction: TransactionId | null;
  /**
   * A copy of this document's body as the database last held it — set when the
   * document is first read, when a save of it succeeds, and when a version of it
   * arrives from the server.
   *
   * It is what lets a version arriving from the server be MERGED with whatever
   * this device has changed since, instead of replacing it. Without it there are
   * only two versions in hand and no way to tell a value somebody deliberately
   * wrote from a value that was merely carried along out of an older copy, which
   * is what cost a customer two training sessions' progression (#4577). See
   * `mergeAgainstCommonBase` in `shared/src/deep-helpers.ts`.
   *
   * Kept for singletons ONLY, and that bound is the whole reason it is affordable:
   * there are around a dozen of them, so this is a fixed handful of extra copies.
   * The identity map at large holds weak references to potentially thousands of
   * documents precisely so they can be collected, and a strong copy of each would
   * undo that.
   */
  lastKnownDatabaseBody?: any;
  /**
   * Where `lastKnownDatabaseBody` came from: the server (an arrival, or the read at
   * start-up) or this device's own save. Only decides how long the body is kept once
   * it moves into `previousDatabaseBodies`.
   */
  lastKnownDatabaseBodySource?: RememberedBodySource;
  /**
   * The bodies `lastKnownDatabaseBody` held before, newest first (#6404). An arriving
   * version built on ANOTHER device's save was not built on this device's latest body,
   * so merging it needs the earlier version both devices started from, and the local
   * database cannot supply it: old revision bodies are compacted away. A handful per
   * singleton, capped by `rememberPreviousDatabaseBody`. See design-docs/sync-integrity.md,
   * "The version both devices started from".
   */
  previousDatabaseBodies?: RememberedDatabaseBody<any>[];
  /**
   * The revisions of bodies that were dropped from `previousDatabaseBodies` when it was
   * full. If an arrival was built on one of them, the bodies still kept are too old to
   * merge from, and `chooseFoldBase` must not use them (#6404).
   */
  forgottenDatabaseBodyRevs?: string[];
  /**
   * #6248: start-up could not read this document, from the server or from this device, so
   * `doc` is only the empty default standing in for it. Saving it would write that default
   * over the user's real document, so `saveSingletonAsync` refuses while this is set. The
   * cold start holds the loading screen in this state and reloads, so a new wrapper (and a
   * fresh read) replaces it.
   */
  unreadAtBoot?: boolean;
}

export enum DatabaseState {
  Syncing = 'syncing',
  Ready = 'ready',
  Offline = 'offline',
}

/**
 * Which wait `shutdownAsync` is in. Reported when a sign-out teardown step gives up, so the
 * report says which wait never finished (df-5c749cefc673). `QueuedInitMutex` is set by
 * `UserDb`, which queues its shutdown behind its own start-up before it reaches the wrapper.
 */
export enum DatabaseShutdownPhase {
  NotStarted = 'not_started',
  QueuedInitMutex = 'queued_init_mutex',
  WaitingReadLock = 'waiting_read_lock',
  ClosingRemote = 'closing_remote',
  ClosingLocal = 'closing_local',
  Done = 'done',
}

/** What a gave-up sign-out report says about a database shutdown. Enums and numbers only. */
export interface DatabaseShutdownDescription {
  shutdown_phase: DatabaseShutdownPhase;
  shutdown_open_readers?: number;
}

/**
 * Cache entry for a document in the identity map.
 * Combines document reference tracking with save state management.
 */
interface DocumentCacheEntry {
  /** Weak reference to the canonical object for this document ID */
  ref: WeakRef<PouchDoc>;
  /** Expected _rev value - used to detect corruption */
  expectedRev?: string;
  /**
   * A short stand-in for the last body the DATABASE gave us for this document —
   * see `bodyFingerprint`. It is what lets the wrapper tell "a page has changed
   * this and not saved it yet" from "this is exactly what was stored", which it
   * otherwise cannot do, because pages edit the shared object in place and the
   * wrapper never sees those edits happen.
   *
   * A fingerprint rather than a copy of the body on purpose: the reference above
   * is weak so these documents can be collected when nothing is using them, and
   * a full copy each would undo that. One short string does not.
   *
   * Absent means "we have never seen the database's version of this document",
   * which is not the same as "it has not changed" — a caller that authored the
   * document, or handed in its own copy, leaves this unset, and nothing is
   * reported for a document in that state.
   */
  lastKnownDbBodyFingerprint?: string;
  /** Save state for serializing concurrent saves of the same document */
  saveState?: {
    /**
     * Whether a save is currently in progress. Read by applyPulledDocToCache, which lets
     * an arriving revision move expectedRev and leaves the merge to the conflict handler
     * while a save is running.
     */
    saving: boolean;
    /** Transaction ID the running save is about to write. Saves are not coalesced. */
    pendingTransaction: TransactionId | null;
  };
}

/**
 * What `applyPulledDocToCache` did with one document that arrived from the
 * server. Everything here is reported back rather than acted on inside, because
 * the three callers differ in what they do next.
 */
export interface PulledDocApplyResult {
  /** whether the identity map already held this document before the call */
  wasCached: boolean;
  /**
   * The revision the identity map expected for this document BEFORE the call
   * overwrote it, or undefined when it held none. Only a caller that reports on
   * the change needs this — the call itself has already used it.
   */
  previousExpectedRev: string | undefined;
  /**
   * Whether the arriving revision's generation number was strictly higher than
   * the one the identity map held. This is NOT the test the call itself uses:
   * that one is deliberately wider, so `false` here does not mean nothing was
   * applied. It exists for the live pull loop's same-device diagnostic, which
   * means "newer" in the strict sense.
   */
  generationStrictlyNewer: boolean;
  /**
   * The stripped document to hand to the pulled-doc listeners, or null when this
   * arrival changed nothing and there is nothing to hand over. WHEN to deliver
   * it is the caller's decision — see the note on `applyPulledDocToCache`. A
   * caller that ignores this field delivers nothing.
   *
   * Typed as `PouchDoc`, not `any`, on purpose: `any | null` collapses to plain
   * `any`, which would leave the hazard above with no signal in the type at all.
   */
  undeliveredListenerDoc: PouchDoc | null;
}

export const CloudStateChangeEvent = declareEvent<{ state: DatabaseState }>('cloud:state');
export const DayUpdatedEvent = declareEvent<{}>('database:dayUpdated');
export const DatabaseSyncProgressEvent = declareEvent<{}>('database:sync:progress');
export const FoodUpdatedEvent = declareEvent<{}>('database:foodUpdated');

// SINGLETON_EXISTS_TIMEOUT_MS moved to `database-wrapper-sync-start.ts` with the
// only code that reads it. SINGLETON_BATCH_TIMEOUT_MS below still lives here.
// L6/S2 (#3986): how long the batched boot-singleton read (one allDocs for all 13 docs) may
// take before we give up and fall back to the per-singleton path. Same convention as
// SINGLETON_EXISTS_TIMEOUT_MS; a touch longer because this fetches doc bodies, not just ids.
// Wall time on purpose; a timeout a phone's freeze caused is reported as info (#6096).
const SINGLETON_BATCH_TIMEOUT_MS = 15_000;

// df-7bc2239b5f74: how long the one keyed allDocs that decides a failed delete's marks may
// take. It runs on an error path, where the store may be the very thing that just failed, so
// a bound is needed — and a read that hits it answers "cannot tell", which preserves the
// user's data. Shorter than the boot reads above because nothing waits on the result except
// an error that is already travelling up to its caller.
const DELETE_OUTCOME_PROBE_TIMEOUT_MS = 10_000;

// How many document ids one "could not tell" report carries. A bulk delete can hold hundreds
// of records, and a report nobody can read is a report nobody reads; the full number is in
// `docCount` beside them.
const DELETE_OUTCOME_REPORT_ID_LIMIT = 20;

/**
 * Docs that must NEVER replicate — each device keeps and maintains its own copy.
 * Currently only the day-task-status cache (#4212): it is a derived cache that
 * every device can recompute from its own log entries, and syncing it produced
 * a conflict branch per device per day plus cross-device poisoning when one
 * device computed wrong values. Kept as one list so the replication filter, the
 * initial-sync selector, and the singleton-sync exclusion cannot drift apart.
 */
export const DEVICE_LOCAL_DOC_IDS: readonly string[] = [DAY_TASK_STATUS_CACHE_ID];

/**
 * DatabaseWrapper: PouchDB coordination layer with identity map.
 *
 * KEY INVARIANTS:
 * 1. Every document ID has at most ONE JavaScript object in memory (identity map via documentCache)
 * 2. All writes go through writeMutex to prevent race conditions between individual and bulk saves
 * 3. expectedRev tracks the last known _rev, so a body from the past can be told
 *    apart from a current one before it is written (see mergeRecordAgainstStoredDoc)
 * 4. On conflict, documents are merged (not last-write-wins) — against the version
 *    this device started from where one is remembered, and two-way otherwise
 *
 * USAGE PATTERN:
 * - Always use ensureCachedReference() for generated entries before saving
 * - Never create new objects for existing document IDs; reuse the canonical reference
 * - See postmortem: design-docs/postmortem-pouchdb-rev-conflicts.md
 */

export class DatabaseWrapper {
  private remoteOnly: boolean;
  private remoteDb: PouchDB;

  /** Set when a restore replaced the remote database but then could NOT destroy the stale local one
   * (even after retries). From that moment this wrapper must NEVER sync again.
   *
   * Why it has to be a wrapper-level latch and not just "don't reload the page": the surviving local
   * database still holds the PRE-restore documents, at higher rev generations than the rev-1 documents
   * the restore just uploaded. Any sync would push those old revisions up and they would WIN — silently
   * overwriting a restore that actually succeeded. Not reloading closes the obvious door, but sync has
   * several other entry points (`ensureSyncActive`, resume, and the Offline→Online handler, which calls
   * `initializeRemoteDb` and would happily build a FRESH remote because the restore nulled it). A
   * network blip alone — the very thing that starts this whole story — would be enough.
   *
   * There is deliberately no way to clear it: the only repair is clearing the app's storage, which ends
   * this page's life anyway. Erring toward "no sync" is always the safe direction here. */
  private localDbIsStaleAfterRestore = false;
  public impersonationDb: PouchDB;
  private localDb: PouchDB;
  // set externally — TelemetryService can't be injected here due to circular dependency
  public telemetry: { trackEvent(name: string, params: { [key: string]: any }): void } | null = null;
  private temporaryDb: PouchDB;
  public managedClientUid: string | null = null;
  public onNonRetryableAuthError: (() => void) | null = null;
  syncHandler: any; // no typings for PouchDB 7.x.x :(
  // #5306: the singleton pre-sync's replication, while one is running. A SECOND replication
  // on the same two handles, separate from `syncHandler` above, held here for one reason:
  // so `cancelSync` can cancel it. While nothing held it, nothing could cancel it, and it
  // outlived a shutdown to call handles that had already been closed. The sync-start code
  // sets and clears it — see `startSingletonDocSync` in database-wrapper-sync-start.ts.
  singletonSyncHandler: any;
  // tracks whether the live syncHandler above is an initial sync (true) or a live sync
  // (false). Lets ensureSyncActive() and the startSync guard distinguish a genuine
  // in-progress initial sync from a live sync that stranded the initial (#3493 Phase A).
  private syncHandlerIsInitial = false;
  singletons: { [docId: string]: Singleton } = {};

  /**
   * Identity map: ensures same document ID always returns same object reference.
   * WeakRef allows GC when no strong references exist.
   * Also tracks expectedRev to detect corruption, and saveState for serializing saves.
   */
  private documentCache: Map<string, DocumentCacheEntry> = new Map();

  /**
   * Singletons already reported by `reportNoCommonStartingVersion`, so each is
   * reported at most once while the app is running. Bounded by the number of
   * singletons, which is around a dozen.
   */
  private noCommonStartingVersionReported: Set<string> = new Set();

  /**
   * Documents already reported by `reportUnsavedContentAboutToBeReplaced`, so
   * each one is reported at most once while the app is running. The question
   * that report answers is how many DOCUMENTS this happens to, not how many
   * times over, and the condition is common enough that repeating it per
   * document would bury the answer.
   *
   * Bounded: past `MAX_UNSAVED_CONTENT_DOCS_REPORTED` this stops growing and
   * stops reporting. A session that has already produced that many is not going
   * to be made clearer by more of them, and an unbounded set of document ids
   * would be a slow leak in a long session.
   */
  private unsavedContentReplacedDocIds = new Set<string>();

  /** See `unsavedContentReplacedDocIds`. */
  private static readonly MAX_UNSAVED_CONTENT_DOCS_REPORTED = 200;

  /**
   * Documents whose sibling branch the identity map has already reported turning
   * away this session. Same shape and same reason as `unsavedContentReplacedDocIds`:
   * a document with two branches is read again and again while the branches stay
   * unmerged, and the number the report exists for is how many DOCUMENTS this
   * happens to. Bounded by the same cap.
   */
  private siblingRevisionRefusedDocIds = new Set<string>();

  // Global mutex for all write operations to prevent race conditions between
  // individual saves (saveRecordAsync) and bulk saves (saveBulkAsync)
  private writeMutex = new Mutex();

  // RW lock for shutdown safety: read = "I'm using the database", write = "I'm shutting down".
  // Prevents in-flight operations from racing with shutdown (which closes PouchDB handles).
  private rwLock = new ReadWriteLock();

  // which wait `shutdownAsync` is in, for the sign-out gave-up report (df-5c749cefc673)
  private shutdownPhase = DatabaseShutdownPhase.NotStarted;

  // Mutex for journal entry creation to prevent race conditions when
  // generating unique IDs for entries that should never overwrite each other
  private journalEntryMutex = new Mutex();

  // Serializes startSync so the syncHandler check-and-assign is atomic. The
  // handler is only assigned at the END of startSync (after several awaits on
  // remoteDb.allDocs/info), so without this lock two near-simultaneous triggers
  // — onConnectionStateChange(Online)→initializeRemoteDb and ensureSyncActive
  // (app resume) — both sail past the `syncHandler != null` guard, each create a
  // replication, and the later assignment orphans the earlier one (leaked live
  // replication + spurious StartSyncDuplicateHandler telemetry, #3493 Phase B).
  private syncMutex = new Mutex('DatabaseWrapper.startSync');

  // DocWatcher registry: maps document IDs to active watchers for reactive sync
  private docWatcherRegistry = new Map<string, Set<DocWatcher>>();
  // All active watchers — used for broadcasting new (untracked) docs to all watchers
  private allActiveWatchers = new Set<DocWatcher>();

  // Real-time changes listener ("fast lane"): bypasses the sync backlog to immediately
  // pick up new cross-device changes while live sync is still catching up on old docs
  private realTimeChangesHandler: any;

  // #3987 A2: how many times the errored real-time listener auto-restarted this
  // sync session (bounded by REALTIME_LISTENER_MAX_RESTARTS; reset when
  // endInitialSync starts the listener fresh)
  private realtimeListenerRestarts = 0;
  // #3987 A2: single-flight guard — a dying changes feed can emit several
  // 'error' events before the restart delay elapses; without this flag each
  // event would schedule its own restart, stacking stop/start cycles and
  // burning the whole restart budget in one burst
  private realtimeListenerRestartPending = false;
  // #3987 A2: handle of the scheduled restart, so stopRealTimeListener can
  // cancel it — a deliberate teardown must also kill the pending restart
  private realtimeListenerRestartTimer: ReturnType<typeof setTimeout> | null = null;

  // Local changes feed handle — stored so recreateLocalDbAsync() can cancel it
  private changesFeed: any = null;

  // Recreation gate: when set, database operations pause until recreation completes.
  // Unlike rwLock (which skips operations on shutdown), the gate pauses them — no data loss.
  private recreationGate: Promise<void> | null = null;
  private resolveRecreationGate: (() => void) | null = null;

  /** How many pieces of our own work are holding the remote handle detached right now
   * (df-ac72b56eef36). `purgeDatabaseAsync` and `restoreFromDocsAsync` both null `remoteDb`
   * on purpose for their whole duration, so `ensureSyncActive()` cannot restart sync against
   * a half-deleted database. While that is true, anything that needs the remote fails — and
   * those failures are our own doing, so they must go quiet rather than be reported as an
   * unresolved problem. See `isDatabaseTearingDown` in settlePulledDeletionsFromSyncAsync.
   *
   * A counter rather than a boolean, for the same reason `beginPurge`/`endPurge` is one: it is
   * raised and lowered in a `finally` beside that bracket and cannot be left set by an early
   * return. It stays per-wrapper on purpose — `databaseRecovery.isIntentionalDestruction()`
   * counts purges across ALL databases, so reusing it would silence real failures in a
   * database nobody is purging. */
  private remoteDetachedForOwnWork = 0;

  // priority sync: only sync log entries from the last N months during initial sync
  static PRIORITY_SYNC_MONTHS_BACK = 3;

  // The initial sync runs the priority window in TWO passes so the days the task
  // calendar shows arrive before the rest of it. This is the first pass's depth.
  //
  // Why 1 month and not the calendar's own window: the calendar shows `today+7`
  // back one month, but that span moves every single day, and the cutoff below is
  // hashed into the replication-checkpoint identity. A daily-moving first pass
  // would mint a fresh checkpoint every day and re-walk the whole changes feed on
  // the first start of each day — the exact boot freeze the month-floor was added
  // to stop. One month back, month-floored, spans 1-2 months, always covers the
  // calendar's window with margin, and is stable within a calendar month.
  static PRIORITY_SYNC_CALENDAR_MONTHS_BACK = 1;

  // month-floored (#4219): the cutoff is part of the initial-sync selector and
  // PouchDB hashes the selector into the replication-checkpoint identity, so a
  // daily-moving cutoff reset the sync bookmark every day and forced a full
  // changes-feed re-walk on the first app start of each day (the boot freeze).
  // logic + tests live in sync-view-heal-decisions.ts.
  private static getPrioritySyncCutoffDate(): string {
    return computePrioritySyncCutoff(DateTime.utc(), DatabaseWrapper.PRIORITY_SYNC_MONTHS_BACK);
  }

  /** Cutoff for the first initial-sync pass — the task calendar's days. */
  private static getCalendarSyncCutoffDate(): string {
    return computePrioritySyncCutoff(DateTime.utc(), DatabaseWrapper.PRIORITY_SYNC_CALENDAR_MONTHS_BACK);
  }

  /**
   * The allDocs key ranges that together cover the "priority window": every
   * non-log doc plus recent/future logs. Shared by the pull catch-up
   * (#3862/#3987 A6) and the conflict-canary scan (#3987 B3) so the two can
   * never drift apart.
   *
   * The docs AFTER the `log/` namespace are NOT optional: `plan/…`,
   * `settings/…`, `schedule/…`, `workout/…` all sort after `log/` (`p`/`s`/`w`
   * > `l`), and those long-lived multi-device docs are exactly what both
   * consumers exist to cover. They are covered here by the second range running
   * all the way to the end of the keyspace.
   *
   * The ranges themselves come from the pure helper the initial-sync doc count
   * also uses, so the two descriptions of "which ids does the priority window
   * contain" cannot drift. Each range carries its own `inclusive_end: false`.
   */
  private static getPrioritySyncKeyRanges(): DocIdKeyRange[] {
    return buildInitialSyncIdRanges(DatabaseWrapper.getPrioritySyncCutoffDate());
  }

  // when true, initial sync was trivially completed because the remote DB was empty (new user).
  // prevents writing a stale recentlySynced flag that would skip real sync on subsequent reloads.
  private initialSyncSkippedEmpty = false;

  // this is used to track if the initial sync has completed true/false for whether successful, undefined if not yet completed
  private initialSyncCompleted: boolean | undefined = undefined;
  /**
   * Resolves when initial sync completes (true) or fails (false).
   *
   * FOR USE INSIDE THIS CLASS ONLY. The retry loops below replace this field with a
   * fresh deferred once per round, so anything that read the property and is
   * suspended on that object is never resolved by anything afterwards. Code outside
   * the wrapper waits through `waitForInitialSyncAsync`
   * (`client/src/services/user-db.task-status.ts`), which subscribes to
   * `onInitialSyncSettled` below and gives up after a cap. See
   * `design-docs/sync-integrity.md`.
   */
  whenInitialSyncCompleted = DeferredPromise.defer<boolean | undefined>();
  /**
   * Resolves when the FIRST initial pass (the task calendar's window) is done —
   * i.e. when there is enough data to render the calendar with real values.
   *
   * ONE-SHOT for the life of this wrapper, deliberately never re-deferred. Boot
   * reads it once and awaits that value; re-arming it across a sync restart would
   * orphan the promise boot is already holding and strand the splash until the
   * timeout. A fresh sign-in builds a fresh wrapper, so a later login gets a fresh
   * gate anyway.
   *
   * The boot screen waits on this so the calendar fills in behind the loading
   * progress bar instead of behind a spinner on an already-visible page. It is
   * deliberately also resolved by `reportInitialSyncOutcome` below, which every
   * terminal outcome funnels through (success, failure, offline skip, the
   * recently-synced fast path), whether it arrives through
   * `setInitialSyncCompleted` or, for a failed round the retry loop will try
   * again, `recordInitialSyncRoundFailed` (#6554). That means this promise can never outlive the
   * sync itself and so can never wedge boot — the worst case is that it settles
   * at the same moment the old code would have finished.
   */
  whenCalendarWindowSyncCompleted = DeferredPromise.defer<boolean | undefined>();

  setInitialSyncCompleted(value: boolean) {
    // Guard: once initial sync is marked as successful (by assureInitialSyncAsync's "recently synced"
    // fast path or by a completed sync), don't allow stale error handlers from a concurrent
    // startSync(true) to regress the flag to false. This prevents an orphan sync started by
    // initializeRemoteDb() from corrupting state after assureInitialSyncAsync already declared success.
    if (this.initialSyncCompleted === true && value === false) {
      console.log(`Db:${this.internalDatabaseIdentifier}: Ignoring setInitialSyncCompleted(false) — already succeeded`);
      return;
    }
    this.initialSyncCompleted = value;
    this.reportInitialSyncOutcome(value);
  }

  /**
   * One round of the initial sync failed, and the retry loop will try again (#6554).
   *
   * Reports the failure exactly as `setInitialSyncCompleted(false)` does: the retry loop
   * wakes, boot's calendar-window wait is released, and the settled listeners hear
   * `false`. The difference is the read gate. Online it stays open, so reads keep going to
   * the server while the retry loop waits; the local store is still half downloaded, and
   * reading it would answer "does not exist" for every document not yet downloaded. Only
   * offline does the gate close. See `readGateAfterFailedInitialSyncRound`.
   */
  recordInitialSyncRoundFailed() {
    // same guard as setInitialSyncCompleted: a late error from a superseded sync must not
    // undo a sync that already succeeded
    if (this.initialSyncCompleted === true) {
      console.log(`Db:${this.internalDatabaseIdentifier}: Ignoring a failed initial sync round — already succeeded`);
      return;
    }
    const isOnline = this.onlineStateService.connectionState === ConnectionState.Online;
    this.initialSyncCompleted = readGateAfterFailedInitialSyncRound(isOnline);
    this.reportInitialSyncOutcome(false);
  }

  /** Tell everyone waiting on the initial sync how the latest round ended. */
  private reportInitialSyncOutcome(value: boolean) {
    this.lastInitialSyncOutcome = value;
    this.whenInitialSyncCompleted.resolve(value);
    // the calendar pass is implied done (or moot) once the whole sync settles
    this.whenCalendarWindowSyncCompleted.resolve(value);
    this.notifyInitialSyncSettled(value);
  }

  // #6554: how the latest initial-sync round ended, for `onInitialSyncSettled`'s late
  // registrants. It is NOT `initialSyncCompleted`: that field decides where reads go, and a
  // round that failed while online leaves it `undefined` so reads stay on the server.
  // Cleared whenever a new round starts, the same way `initialSingletonSyncOutcome` is.
  private lastInitialSyncOutcome: boolean | undefined = undefined;

  // #4435: everyone waiting for "the initial sync settled". A promise cannot do
  // this job, because a promise settles ONCE. The retry loop runs several rounds
  // and re-defers `whenInitialSyncCompleted` between them, so a waiter that saw
  // one round answer `false` is left holding a promise that will never speak
  // again — and the round that finally succeeds tells it nothing.
  private initialSyncSettledListeners = new Set<(completed: boolean) => void>();

  /**
   * Call `listener` every time the initial sync reaches a terminal outcome —
   * success, failure, offline skip — including the repeat rounds a retry runs.
   *
   * If the sync has ALREADY settled, `listener` is called once with that outcome
   * rather than waiting for the next one, so registering late cannot lose an
   * answer. That call is deferred to a microtask on purpose: callers register
   * from inside their own flush logic, and calling straight back into them from
   * here would re-enter a method that is still running.
   *
   * Returns a function that removes the listener. Call it — a listener kept alive
   * holds its whole closure for as long as the wrapper lives.
   */
  onInitialSyncSettled(listener: (completed: boolean) => void): () => void {
    this.initialSyncSettledListeners.add(listener);
    const alreadySettled = this.lastInitialSyncOutcome;
    if (alreadySettled !== undefined) {
      void Promise.resolve().then(() => {
        // the caller may have unsubscribed while the microtask was queued
        if (!this.initialSyncSettledListeners.has(listener)) return;
        this.callInitialSyncSettledListener(listener, alreadySettled);
      });
    }
    return () => { this.initialSyncSettledListeners.delete(listener); };
  }

  private notifyInitialSyncSettled(value: boolean): void {
    // iterate a copy: a listener is expected to unsubscribe itself from inside
    // its own call, which would otherwise mutate the set we are walking
    for (const listener of [...this.initialSyncSettledListeners]) {
      this.callInitialSyncSettledListener(listener, value);
    }
  }

  private callInitialSyncSettledListener(listener: (completed: boolean) => void, value: boolean): void {
    try {
      listener(value);
    } catch (err) {
      // never let one listener break the sync-completion path — every terminal
      // sync outcome funnels through here, so a throw that escaped would take
      // the boot gate and the live-sync handover with it. Reported rather than
      // swallowed: a listener that throws is a defect somebody has to see.
      console.error(`Db:${this.internalDatabaseIdentifier}: initial-sync settled listener failed:`, describeErrorForLog(err));
      this.logging.trackSilentProblem('InitialSyncSettledListenerFailed', {
        db: this.internalDatabaseIdentifier,
        completed: value,
        error: describeError(err),
      });
    }
  }

  private whenInitialSingletonSyncCompleted = DeferredPromise.defer<boolean | undefined>();
  /**
   * `coveredEveryServerSingleton` (#6248) is false when the pre-sync "completed" without
   * knowing which singletons the server holds: the existence check was dropped, so it synced
   * nothing. The boot sync carries on exactly as before, but the listeners are told the
   * pre-sync did NOT succeed, because they use success to conclude "a document still missing
   * locally is not on the server either".
   */
  setInitialSingletonSyncCompleted(value: boolean, coveredEveryServerSingleton = true) {
    this.whenInitialSingletonSyncCompleted.resolve(value);
    const outcomeForListeners = value && coveredEveryServerSingleton;
    this.initialSingletonSyncOutcome = outcomeForListeners;
    // iterate a copy: a listener is expected to unsubscribe itself from inside its own call
    for (const listener of [...this.initialSingletonSyncSettledListeners]) {
      this.callInitialSyncSettledListener(listener, outcomeForListeners);
    }
  }

  // #6248: the singleton pre-sync's latest outcome, and who is waiting for it. A listener set
  // for the same reason `onInitialSyncSettled` uses one (#4435): the retry path replaces the
  // pre-sync's deferred promise every round, so a caller holding one of those promises is never
  // told about the round that finally works.
  private initialSingletonSyncOutcome: boolean | undefined = undefined;
  private initialSingletonSyncSettledListeners = new Set<(completed: boolean) => void>();

  /** Is there a server database this wrapper can sync with right now? False for a warm
   *  (local-only) start until it connects, which means no singleton pre-sync can run (#6248). */
  hasRemoteDatabase(): boolean {
    return this.remoteDb != null;
  }

  /**
   * Call `listener` every time the singleton pre-sync (the small sync that downloads only
   * the singleton documents) reaches an outcome, including the repeat rounds of a retry.
   * If it has ALREADY reached one, `listener` is called once with it, on a microtask, exactly
   * like `onInitialSyncSettled`. Returns a function that removes the listener.
   *
   * The start-up waits on this when a singleton read was dropped: once the pre-sync is done,
   * this device's own database holds every singleton the server has (#6248).
   */
  onInitialSingletonSyncSettled(listener: (completed: boolean) => void): () => void {
    this.initialSingletonSyncSettledListeners.add(listener);
    const alreadySettled = this.initialSingletonSyncOutcome;
    if (alreadySettled !== undefined) {
      void Promise.resolve().then(() => {
        // the caller may have unsubscribed while the microtask was queued
        if (!this.initialSingletonSyncSettledListeners.has(listener)) return;
        this.callInitialSyncSettledListener(listener, alreadySettled);
      });
    }
    return () => { this.initialSingletonSyncSettledListeners.delete(listener); };
  }

  public whenGlobalFoodDbAvailable = DeferredPromise.defer<boolean>();
  private queriesToSave: { name: string; map: (doc: PouchDoc) => void }[] = [];
  private whenQueriesReady = DeferredPromise.defer<boolean>();

  // Serializes first-time PouchDB view index builds so the shared `_local/mrviews` registry
  // doc isn't upserted concurrently (#3466 — see ViewBuildSerializer for the full mechanism).
  private viewBuildSerializer = new ViewBuildSerializer();
  private syncTasksCompleted = 0;
  private syncTasksCount = 0;

  // The total number of documents the running initial sync pass has to download,
  // measured once by the background pre-count. It is a fixed denominator, not a
  // remaining count — it never decreases as documents arrive. null means the
  // count has not answered (or failed), and the progress counters then fall back
  // to PouchDB's rougher `pending` estimate.
  //
  // Why the pre-count exists at all — and why `pending` is a poor total — is
  // explained once, above `computePullSyncTaskCounters` in
  // initial-sync-progress.ts. Do not restate the measurement here.
  private initialSyncDocsToPull: number | null = null;
  // What the pass's cumulative `docs_read` was when the pre-count sampled the
  // local database. The count subtracts documents this device already holds, and
  // some of those may be ones this very pass just wrote, so the numerator has to
  // start from the same moment or the bar reaches 100% early and then freezes.
  private initialSyncDocsReadBaseline = 0;
  // The most recent cumulative `docs_read` this pass has reported, so the
  // pre-count can take the baseline above without waiting for the next batch.
  private lastPullDocsRead = 0;
  // Bumped on every startSyncInternal. The pre-count captures it before its
  // remote reads and re-checks it after, so a count belonging to a superseded
  // pass can never install itself over the pass that replaced it.
  private syncGeneration = 0;
  // Bound on the pre-count's remote read. It runs alongside the sync rather than
  // before it, so a slow or hung count costs nothing but the estimate — but it
  // must not hold a promise (and its response buffer) open indefinitely.
  private static readonly INITIAL_SYNC_DOC_COUNT_TIMEOUT_MS = 30_000;

  // epoch ms of the most recent live-sync activity ('active' or 'change'). The checkpoint
  // integrity check (verifyPushCheckpointIntegrityAsync) waits for sync to settle before
  // running so it doesn't race a push that is still in flight (Sentry 3ZD race-409 fix).
  private lastSyncActivityTime_ms = 0;
  // consecutive settle-skips, capped so a chatty-but-healthy sync can't starve the check
  // (the settle window and the skip cap live in database-wrapper-push-integrity.ts)
  private checkpointSettleSkips = 0;
  // pull-side resume catch-up cooldown (#3862): don't re-run on rapid background/foreground cycles
  private static readonly PULL_CATCHUP_COOLDOWN_MS = 45_000;
  // #3987 A2: delay before an errored real-time listener restarts — never restart
  // synchronously inside the error event
  private static readonly REALTIME_LISTENER_RESTART_DELAY_MS = 5_000;

  // Accumulates cases where sync pulls a newer rev tagged with our own deviceId.
  // Known false positives: multi-tab (tabs share localStorage.deviceId but have separate
  // in-memory caches) and cross-session (old db__trans from a previous session on the same device).
  // Kept as a diagnostic array for debugging; not reported to Sentry.
  private unexpectedSyncRevs: Array<{ docId: string; pulledRev: string; expectedRev: string; isOwnSession: boolean }> = [];

  // stores the most recent sync error for inclusion in Sentry reports
  private lastSyncError: LastSyncError | null = null;

  // counts consecutive 401/403 sync errors; used to suppress toasts for transient auth failures
  private consecutiveAuthErrors = 0;

  // Revisions this app authored through its own local save path. The local changes feed
  // (attachChangesFeed) flags an "unflushed local write" only for a revision in this set,
  // which excludes sync-pulled echoes and pass-through grafts (both already on the server) so
  // they don't trip the stale-unflushed-change watchdog (reportStaleUnflushedChangeIfNeeded).
  // See LocalRevTracker for the full mechanism (Sentry 429/3ZD false-positive fix).
  // Sized for the #4314 conflict-resolver's burst, not just ordinary user saves.
  //
  // ONE tracker, deliberately: the changes handler consumes only from this
  // instance, so giving the resolver its own would mean its entries were never
  // consulted and every revision it wrote would classify as a sync echo — the
  // exact opposite of the intent.
  //
  // A drain can record up to roughly two revisions per document (the merged
  // winner plus its tombstones) across a run, and at the default 200 entries
  // that burst would evict genuine user-save entries oldest-first. A dropped
  // entry makes a real local write look like a sync echo, which would move
  // APP-429 — the very signal the Phase C rollout is read against.
  private locallyAuthoredRevs = new LocalRevTracker(600);

  /** Record a revision this app just wrote to the local user DB (Fix for Sentry 429/3ZD). */
  private recordLocallyAuthoredRev(id: string | undefined, rev: string | undefined) {
    // only the user DB drives the stale-unflushed-change watchdog
    if (!this.isUserDb()) return;
    this.locallyAuthoredRevs.record(id, rev, DateTime.utc().toMillis());
    // #6788: every confirmed write the app makes to the user's store passes through here, so
    // this is where the store stops being one the app may abandon by itself. Telemetry events
    // do not count — see isUserDataWrite
    if (isUserDataWrite(id)) this.markLocalStoreWrittenOnce();
  }

  /** Saves of user data that have passed the operation gate and not finished (#6788). A store
   *  with one still under way is never given up: the save may yet land in it. */
  private userWritesInFlight = 0;

  /** Whether this page already recorded that the user's store confirmed a write (#6788). */
  private localStoreWrittenMarked = false;

  /**
   * Record, once per page, that the user's local store confirmed a write (#6788).
   *
   * Called only AFTER a write was confirmed, because only a confirmed write is known to be in
   * the store; a save still under way is counted separately (`userWritesInFlight`). A
   * storage that refuses the marker is reported, because a missing marker is what lets the
   * app abandon the store. See `local-store-generation.ts`.
   */
  private markLocalStoreWrittenOnce() {
    if (this.localStoreWrittenMarked) return;
    this.localStoreWrittenMarked = true;
    if (!markLocalStoreWritten(browserLocalStorageOrNull(), this.databaseIdLocal)) {
      this.logging.trackSilentProblem('LocalStoreWrittenMarkerRefused', {
        db: this.internalDatabaseIdentifier,
      });
    }
  }

  /** The #6788 report fields. A storage that throws leaves the store facts out rather than
   *  guessing them. */
  private silentStoreReportFields(): Record<string, boolean> {
    const fields: Record<string, boolean> = { after_purchase_reload: pageStartedAfterPurchaseReload() };
    try {
      const facts = this.silentLocalStoreFacts;
      fields.has_synced_on_this_device = facts.hasSyncedOnThisDevice;
      fields.store_ever_written = facts.everWritten;
    } catch {
      // not reported on its own: this runs while building a report that is already going out
    }
    return fields;
  }

  /**
   * What the recovery needs to decide whether it may switch this store to a fresh name
   * without asking (#6788). Reads localStorage, which can throw when storage is blocked;
   * the caller treats a throw as "may not".
   */
  get silentLocalStoreFacts(): SilentLocalStoreFacts {
    const storage = browserLocalStorageOrNull();
    return {
      databaseId: this.internalDatabaseIdentifier,
      // an impersonating admin's temporary copy is not this device's own store either
      remoteOnly: !!this.remoteOnly || !!this.temporaryDb,
      hasSyncedOnThisDevice: this.hasSyncedOnThisDeviceMarker,
      origin: readLocalStoreOrigin(storage, this.databaseIdLocal),
      everWritten: hasLocalStoreBeenWritten(storage, this.databaseIdLocal),
      userWritesInFlight: this.userWritesInFlight > 0,
      alreadyAutoSwitched: hasAutoSwitchedLocalStore(storage, this.localDatabaseBaseName),
    };
  }

  /**
   * Return true (and consume the entry) if this exact `${id}@${rev}` was authored locally
   * by this app. A miss means the change is a sync-pulled echo or a graft, not a genuine
   * unflushed local write.
   */
  private consumeLocallyAuthoredRev(id: string | undefined, rev: string | undefined): boolean {
    return this.locallyAuthoredRevs.consume(id, rev, DateTime.utc().toMillis());
  }

  /**
   * Write out every diagnostic a sync run has been accumulating.
   *
   * This is the ONE place a sync diagnostic is flushed from. A replication can end in three
   * ways — finished, gone idle, or failed — and each of those endings calls this. Listing the
   * diagnostics separately at each of the three meant a new one had to be remembered in three
   * places, with nothing catching a miss: one left out of the failure ending would silently
   * lose every count from a sync that ended badly, which is the case most worth measuring.
   * So a new sync diagnostic is added HERE and reaches all three endings.
   */
  private flushSyncDiagnostics() {
    this.flushUnexpectedSyncRevs();
  }

  private flushUnexpectedSyncRevs() {
    if (this.unexpectedSyncRevs.length > 0) {
      // not reported to Sentry — known false positives from multi-tab and cross-session scenarios
      console.log(`[SYNC] ${this.unexpectedSyncRevs.length} unexpected same-device rev(s) pulled (likely multi-tab or cross-session):`,
        this.unexpectedSyncRevs.map(e => `${e.docId} ${e.expectedRev}→${e.pulledRev}`).join(', '));
      this.unexpectedSyncRevs = [];
    }
  }

  private serializeSyncError(err: any): any {
    if (!err) return null;
    return {
      name: err.name,
      message: err.message,
      status: err.status,
      reason: err.reason,
      error: err.error,
      result: err.result,
    };
  }

  /** 401/403 won't self-resolve via PouchDB retry — cancel sync instead of retrying forever */
  private isNonRetryableSyncError(err: any): boolean {
    return err?.status === 401 || err?.status === 403;
  }

  /**
   * Handle auth errors (401/403) from sync 'denied' and 'error' events.
   * Transient errors (cookie race on Capacitor, brief offline during auth retry) trigger a
   * delayed auto-restart. Persistent errors escalate to user-visible toast after 3 consecutive failures.
   */
  private handleSyncAuthError(err: any, source: 'denied' | 'error', logHeader: string): void {
    this.consecutiveAuthErrors++;

    // coach management: 403 = access revoked — always immediate
    if (this.managedClientUid && this.onNonRetryableAuthError) {
      console.error(`${logHeader} Sync ${source} (status=${err?.status}): coach access revoked`);
      this.cancelSync();
      this.updateState(DatabaseState.Offline);
      this.onNonRetryableAuthError();
      return;
    }

    // below threshold: transient — auto-restart after delay (same pattern as "Load failed" handler)
    if (this.consecutiveAuthErrors < 3) {
      console.log(`${logHeader} Sync ${source} (transient auth error ${this.consecutiveAuthErrors}/3, status=${err?.status}), will auto-restart`);
      this.cancelSync();
      this.updateState(DatabaseState.Offline);
      const delay = 2000 + (this.consecutiveAuthErrors - 1) * 1000;
      setTimeout(() => {
        // if sync hasn't been restarted by ensureSyncActive() or another trigger
        if (!this.syncHandler && this.remoteDb) {
          void this.startSync(this.shouldRestartWithInitialSync());
        }
      }, delay);
      return;
    }

    // at threshold: persistent — escalate (original behavior)
    console.error(`${logHeader} Sync ${source} (non-retryable, status=${err?.status}, attempts=${this.consecutiveAuthErrors}): `, err);
    // #3992 R5: record every escalation so the 3-attempt threshold and the
    // 2s/3s backoff can be tuned on data. deliberately BEFORE the counter
    // reset below, so `attempts` reports the real count. no behavior change
    // in this phase — the event only provides the data a tuning needs.
    // connection_state is NOT passed here: every event already carries it via
    // the standard props in getCommonEventParams (#3992 R4).
    this.telemetry?.trackEvent('sync_auth_error_escalation', {
      source,
      status: err?.status ?? null,
      attempts: this.consecutiveAuthErrors,
      managed_client: this.managedClientUid != null,
    });
    this.consecutiveAuthErrors = 0;
    this.cancelSync();
    this.updateState(DatabaseState.Offline);
    this.logging.trackAbnormalError(
      `${logHeader} Sync ${source} (non-retryable, status=${err?.status})`,
      untranslatedString('Database synchronization error.'),
    );
  }

  /** Snapshot of the routing-relevant fields, so the pure helpers in
   *  database-wrapper-passthrough.ts can be unit tested without coupling
   *  to the wrapper's private fields. */
  private passThroughState(): PassThroughDbState {
    return {
      remoteDb: this.remoteDb,
      localDb: this.localDb,
      temporaryDb: this.temporaryDb,
      remoteOnly: this.remoteOnly,
      initialSyncCompleted: this.initialSyncCompleted,
      // read fresh on every call, never captured once: the marker is written mid-session
      // when the first full download finishes, and cleared mid-session by
      // clearRecentlySyncedMarks when the local store is thrown away
      hasSyncedOnThisDevice: this.hasSyncedOnThisDeviceMarker,
      isUserDb: this.isUserDb(),
    };
  }

  /** Choose which DB to read from. May return remoteDb during the pass-through
   *  window (initial sync not yet complete) so users land in the app fast on
   *  cold start; in that case the read wrappers graft the result into local
   *  via bulkDocs(new_edits:false) so subsequent writes extend the same rev
   *  chain. Writes still always go to local via getWriteDb(). Coach
   *  impersonation (`temporaryDb`) short-circuits with no pass-through.
   *
   *  Since #5068 the USER's own database stops passing through once this device has
   *  ever finished a download of it; a shared, download-only database still passes
   *  through in every case. Logic lives in database-wrapper-passthrough.ts for
   *  unit-testability; see design-docs/pass-through-db.md. */
  getReadDb(): DbHandle | null {
    return chooseReadDbPure(this.passThroughState());
  }

  /**
   * The error EVERY read on this wrapper throws when it could not run at all.
   * One helper, so the wrapper gives exactly ONE answer to "the database is not
   * available" — see `DatabaseUnavailableError` for why that matters and what it
   * replaced.
   *
   * It RETURNS the error for the caller to `throw`, rather than throwing itself.
   * That keeps the `throw` keyword at the call site, so TypeScript's control-flow
   * analysis narrows correctly afterwards without depending on `never`-return
   * inference through a method call.
   *
   * `callSite` is required. It is where the refused call was made from, captured by the
   * refusing method as its first statement, before any `await` (#6205). Read
   * `captureDatabaseCallSite` for why that is the only point where it still names the caller.
   */
  private unavailable(reason: DatabaseUnavailableReason, callSite: Error): DatabaseUnavailableError {
    return new DatabaseUnavailableError(this.internalDatabaseIdentifier, reason, undefined, callSite);
  }

  /**
   * Name a local database operation for the stall report, tagged with THIS
   * wrapper's database (#4401).
   *
   * The format itself lives in `localOpName`; this only saves every call site in
   * the class from repeating `this.internalDatabaseIdentifier`. See `localOpName`
   * for why the tag is load-bearing rather than decoration.
   */
  private localOpName(operation: string): string {
    return localOpName(this.internalDatabaseIdentifier, operation);
  }

  /** Choose which DB to write to. Writes never pass through to remote — they
   *  always go to local (or to temporaryDb when impersonating). The sync
   *  pipeline pushes them to remote in the background. */
  getWriteDb(): DbHandle | null {
    return chooseWriteDbPure(this.passThroughState());
  }

  /** If recreation is in progress, waits for it to complete. Returns false if shutdown. */
  private async waitForRecreationGate(): Promise<boolean> {
    if (this.recreationGate) {
      await this.recreationGate;
    }
    return !this.rwLock.isShuttingDown;
  }

  /**
   * The entry gate every refusing read and write shares: wait out a recreation, then take the
   * read lock, and throw `DatabaseUnavailableError` if either is refused.
   *
   * When it returns, the caller HOLDS the read lock and must release it in its own `finally`.
   * When it throws, no lock is held, so the caller must call it before entering that `try`.
   *
   * `callSite` comes from the caller, which captures it as its own first statement: by the time
   * this runs the caller's code is already off the stack on iOS (#6205). The gate wait and the
   * lock are taken back to back with no `await` between them, exactly as when each method wrote
   * them out itself (df-f32603d0dabd).
   */
  private async enterReadSectionAsync(callSite: Error): Promise<void> {
    if (!await this.waitForRecreationGate()) throw this.unavailable(DatabaseUnavailableReason.ShuttingDown, callSite);
    if (!this.rwLock.acquireRead()) throw this.unavailable(DatabaseUnavailableReason.ReadLockRefused, callSite);
  }

  // used only for internal identification and logging
  internalDatabaseIdentifier: string; // if set to "user", this is a special per-user db
  // id used to save the database in the browser
  private databaseIdLocal: string;
  // id uset to save the database on the server
  private databaseIdServer: string;

  /**
   * The name this database would have had before any local-store reset (#4401). The
   * generation suffix is applied on top of it, and a reset bumps the generation stored
   * against THIS name — so the base name has to be remembered separately from the name
   * actually opened.
   */
  private localDatabaseBaseName: string = '';

  /**
   * Choose the local store name to open, applying any local-store reset this device has
   * been through (#4401).
   *
   * A device that has never needed a reset opens exactly the name it always has —
   * generation 0 is the plain base name — so this changes nothing for anybody who is
   * not already stuck.
   */
  private setLocalDatabaseId(baseName: string) {
    this.localDatabaseBaseName = baseName;
    this.databaseIdLocal = currentLocalStoreName(
      browserLocalStorageOrNull(), baseName);
    if (this.databaseIdLocal !== baseName) {
      console.log(`Db:${this.internalDatabaseIdentifier}: using local store ${this.databaseIdLocal} (this device has been reset)`);
    }
  }

  /** The pre-reset name of this database's local store (#4401). */
  get localDatabaseBaseNameForReset(): string {
    return this.localDatabaseBaseName;
  }

  /** Returns the local database name (for SQLite cleanup on logout). */
  get localDatabaseName(): string | null {
    return this.databaseIdLocal || null;
  }

  /** #4506: true from the moment `shutdownAsync` STARTS, not from the moment it finishes,
   *  and never cleared again. Why that edge, and which callers depend on it:
   *  design-docs/pass-through-db.md, "Which databases a sweep may act on". */
  get isShuttingDown(): boolean {
    return this.rwLock.isShuttingDown;
  }

  /** F24 (#3991): has the first sync of this database completed in THIS session?
   *  undefined means it has not finished yet — not that it failed. */
  get initialSyncCompletedState(): boolean | undefined {
    return this.initialSyncCompleted;
  }

  /**
   * Would a read RIGHT NOW go to the server rather than to this device's own store?
   *
   * The honest form of a question callers kept trying to answer from
   * `initialSyncCompletedState` alone, which is only half of it: `chooseReadDb` passes a read
   * through only while that state is `undefined` AND a remote handle exists. On a fully
   * offline boot no remote handle is ever built, so the state stays `undefined` and the read
   * still goes local — a caller testing the state alone refuses in exactly the case it meant
   * to serve. Asking the router itself cannot drift from what the router does.
   *
   * Added for the device's staple snapshot (food staples plan 4c, #5625), which must not pull
   * the whole curated staple set over the network on the boot path, but must read it when the
   * device already holds it and there is no network at all.
   */
  get readsPassThroughToServer(): boolean {
    return this.remoteDb != null && this.getReadDb() === this.remoteDb;
  }

  /** F24 (#3991): has this local database ever finished an initial sync on this device, in any
   *  session? The markers survive restarts, so this is what tells an offline boot on a device the
   *  user has used before apart from a device that has genuinely never talked to the server.
   *
   *  Either marker counts, and the "settled" one is the reason this is not a one-liner. A user who
   *  signs up on this device syncs against an EMPTY remote, and that case deliberately withholds
   *  the recently-synced marker (it means "safe to boot from local data without syncing", which an
   *  empty remote does not earn). Reading only that marker therefore mistook a brand-new signup for
   *  an existing account that had never been downloaded here, and the gate held them on the splash
   *  forever asking them to connect so we could load an account the server does not have.
   *
   *  Exposed as a getter so callers never have to rebuild the localStorage keys by hand. */
  get hasSyncedOnThisDeviceMarker(): boolean {
    if (!this.databaseIdLocal) return false;
    return DatabaseWrapper.hasSyncedOnThisDeviceMarkerForLocalStore(this.databaseIdLocal);
  }

  /** The same answer as `hasSyncedOnThisDeviceMarker`, for a database no wrapper has opened yet:
   *  the warm-resume check asks it about `global_common` before anything is open
   *  (df-e30dd9684cf6). Takes the BASE name and maps it the way `setLocalDatabaseId` does, so a
   *  device that went through a #4401 reset reads the markers of the store it will really open. */
  static hasSyncedOnThisDeviceMarkerFor(baseName: string): boolean {
    return DatabaseWrapper.hasSyncedOnThisDeviceMarkerForLocalStore(
      currentLocalStoreName(browserLocalStorageOrNull(), baseName));
  }

  private static hasSyncedOnThisDeviceMarkerForLocalStore(localStoreName: string): boolean {
    return !!localStorage[`${DatabaseWrapper.dbRecentlySyncedKeyPrefix}${localStoreName}`]
      || !!localStorage[`${DatabaseWrapper.dbInitialSyncSettledKeyPrefix}${localStoreName}`];
  }

  private connectionStateSubscription: PubSubRegistration;

  constructor(
    private pubSub: PubSubService,
    private auth: AuthService,
    private dbProvider: DatabaseWrapperProvider,
    private logging: LoggingService,
    private databaseRecovery: DatabaseRecoveryService,
    private onlineStateService: OnlineStateService,
    private debug: DebugService,
  ) {
    // the push-stall clock must not run across a background stretch (df-280a5f714662);
    // installs once per page however many wrappers are constructed
    installPushStallClockResetOnHide(document, sessionStorage);
  }

  getState(): DatabaseState {
    return this.dbProvider.databaseStates[this.internalDatabaseIdentifier];
  }

  /** lastSyncError stringified for Sentry problem contexts — nested objects
   *  in breadcrumb data get normalized to "[Object]" beyond a shallow depth,
   *  so the raw object would lose its inner error fields. */
  private stringifiedLastSyncError(): string | null {
    return this.lastSyncError ? JSON.stringify(this.lastSyncError) : null;
  }

  /** Small read-only snapshot of this database's sync health, used by the
   *  splash stall telemetry (#3698) so a stuck boot reports which database
   *  is in which state. The fields themselves are private — this is the
   *  only sanctioned way to read them from outside. */
  getSyncDiagnostics() {
    return {
      id: this.internalDatabaseIdentifier,
      initialSyncCompleted: this.initialSyncCompleted,
      dbState: this.getState(),
      lastSyncError: this.stringifiedLastSyncError(),
      hasRemoteDb: this.remoteDb != null,
      syncHandlerActive: this.syncHandler != null,
      // #4506: this report keeps every wrapper, so it can list more than one row for the
      // same id. This flag is what tells a live wrapper from a superseded one.
      shuttingDown: this.rwLock.isShuttingDown,
    };
  }

  getSyncTasksCount() {
    return this.syncTasksCount;
  }

  getSyncTasksCompleted() {
    return this.syncTasksCompleted;
  }

  // Calling this before initialization will tell the wrapper to not use the local Db, but only access remote directly.
  setRemoteOnly() {
    console.assert(this.localDb == null, 'This can only be called before initialization');
    this.remoteOnly = true;
  }

  isUserDb() {
    return this.internalDatabaseIdentifier == 'user';
  }

  // The one shared database whose `$text/` content is localized, so its pull is
  // filtered by locale (#4284). The other shared databases (global_foods,
  // global_testcases, _users) have no `$text/` docs and sync unfiltered.
  static GLOBAL_COMMON_DB_ID = 'global_common';
  isGlobalCommonDb() {
    return this.internalDatabaseIdentifier === DatabaseWrapper.GLOBAL_COMMON_DB_ID;
  }

  // The ids a shared-db pull must never bring down: the two server-only design
  // docs plus every device-local cache. Same list `replicationFilter` and the
  // userDb initial-sync selector exclude — folded into the #4284 locale selector
  // so switching from the JS filter to the Mango selector changes nothing but the
  // locale dimension.
  static syncExcludedDocIds(): readonly string[] {
    return ['_design/view', '_design/probe', ...DEVICE_LOCAL_DOC_IDS];
  }

  /** the temporary database this wrapper writes to, or null when it writes to its own local one */
  getTemporaryDb(): PouchDB | null {
    return this.temporaryDb ?? null;
  }

  // route the primary database to a temporary one (used for impersonation and coach managing client).
  // #6448: only once, at start-up. a routed wrapper never changes target in place, because the
  // session holds documents read from the old target and anything that saves them would write
  // them into the new one. leaving a temporary target goes through a reload.
  setTemporaryDb(db: any) {
    const change = decideTemporaryDbChange(this.temporaryDb, db);
    if (change === TemporaryDbChange.Unchanged) return;
    if (change === TemporaryDbChange.Refused) {
      this.logging.trackSilentProblem('TemporaryDbSwitchRefused', {
        database: this.internalDatabaseIdentifier,
        clearing: db == null,
      });
      throw new Error('setTemporaryDb: a routed database cannot change its write target in place; reload instead (#6448)');
    }
    this.temporaryDb = db;
    // clear document cache — cached _rev values belong to the previous database
    // and would cause 409 conflicts against the new database
    this.documentCache.clear();
  }

  async initAsUserDatabaseAsync(user: UserAuth, singletonDocs: string[], managedClientUid?: string) {
    for (let docId of singletonDocs) {
      this.singletons[docId] = new Singleton();
    }

    this.internalDatabaseIdentifier = 'user';
    if (managedClientUid) {
      this.managedClientUid = managedClientUid;
      this.setLocalDatabaseId(localCoachClientDbBaseName(managedClientUid));
    } else {
      this.setLocalDatabaseId(localUserDbBaseName(user.uid));
    }
    await this.startLocalDatabaseAsync();
    // Fire-and-forget initial sync. On a device that has never finished a download
    // of this database, reads pass through to remote (getReadDb) and graft into
    // local until sync completes; on one that has, they read local throughout
    // (#5068). See #3580 and design-docs/pass-through-db.md.
    void this.assureInitialSyncAsync();
  }

  async initAsUserDatabaseLocalOnlyAsync(user: UserAuth, singletonDocs: string[], managedClientUid?: string) {
    for (let docId of singletonDocs) {
      this.singletons[docId] = new Singleton();
    }

    this.internalDatabaseIdentifier = 'user';
    if (managedClientUid) {
      this.managedClientUid = managedClientUid;
      this.setLocalDatabaseId(localCoachClientDbBaseName(managedClientUid));
    } else {
      this.setLocalDatabaseId(localUserDbBaseName(user.uid));
    }
    await this.startLocalDatabaseAsync(true);
  }

  async initAsSharedDatabaseLocalOnlyAsync(user: UserAuth, databaseID: string) {
    console.assert(!databaseID.startsWith('http'));
    if (databaseID == undefined) {
      console.error('Db: trying to initalize undefined database');
      return;
    }
    this.internalDatabaseIdentifier = databaseID;
    this.setLocalDatabaseId(localSharedDbBaseName(databaseID, user.uid));
    await this.startLocalDatabaseAsync(true);
  }

  async connectRemoteAndSyncAsync() {
    if (this.remoteOnly) return;
    await this.initializeRemoteDb();
  }

  // The remote-only counterpart of connectRemoteAndSyncAsync: attach the server handle of a
  // remote-only wrapper that was started before the CouchDB login (the food database on a warm
  // restart, #6790). Does nothing once a handle exists, so a handle the connection-state listener
  // already attached is not touched.
  async connectRemoteOnlyAsync() {
    if (!this.remoteOnly || this.remoteDb != null) return;
    await this.initializeRemoteDb();
  }

  async initAsSharedDatabaseAsync(user: UserAuth, databaseID: string) {
    console.assert(!databaseID.startsWith('http'));

    if (databaseID == undefined) {
      console.error('Db: trying to initalize undefined database');
      return;
    }

    this.internalDatabaseIdentifier = databaseID;
    this.setLocalDatabaseId(localSharedDbBaseName(databaseID, user.uid));

    await this.startLocalDatabaseAsync();
    // Fire-and-forget initial sync — reads pass through to remote (getReadDb)
    // and graft into local until sync completes. The "recently synced" fast-path
    // (< 60 min) still short-circuits to `initialSyncCompleted = true` immediately;
    // first install / stale-cache cases run sync in the background while the
    // user already has the app. See #3580 and design-docs/pass-through-db.md.
    void this.assureInitialSyncAsync();
  }

  // Both keys are suffixed with `databaseIdLocal` (which encodes the per-user
  // / per-managed-client local PouchDB name), so the flag is 1:1 with the
  // local DB it summarizes. Suffixing with anything coarser (e.g. just the
  // database type) lets a different user's flag fire the fast-path against
  // an empty local DB.
  static dbRecentlySyncedKeyPrefix = 'db_lastSynced_utc_'
  static dbBackgroundSyncCompleteKeyPrefix = 'db_backgroundSyncComplete_'
  // F24 (#3991): "this device has finished an initial sync for this database at least once."
  //
  // This is deliberately NOT the same thing as `dbRecentlySyncedKeyPrefix` above. That marker
  // means "local data is good enough to boot from without waiting for a sync", which is why it
  // is withheld when the remote was empty (see `initialSyncSkippedEmpty` — a brand-new signup
  // has nothing to copy down, and letting a reload take the fast path would boot empty
  // singletons). But "the remote was empty" is still a COMPLETED sync, and the offline-boot
  // gate needs to know that: a user who signed up on this device and quit before finishing
  // onboarding has no recently-synced marker, yet they are emphatically NOT an existing account
  // waiting to be downloaded. Gating on the recently-synced marker trapped exactly those users
  // on the splash forever, telling them to connect so we could load an account that does not
  // exist on the server. So this second marker records the weaker, honest fact.
  static dbInitialSyncSettledKeyPrefix = 'db_initialSyncSettled_'
  /**
   * Race promise against a timeout that pauses while `document.hidden`.
   * Mobile browsers (Safari, Chrome) suspend network while backgrounded but
   * `setTimeout` keeps ticking, causing false timeouts during initial sync.
   */
  private async awaitSyncWithTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<{ succeeded: T; timedOut: boolean }> {
    return new Promise<{ succeeded: T; timedOut: boolean }>((resolve) => {
      let settled = false;
      let elapsed = 0;
      let lastTick = Date.now();
      let timerId: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        if (timerId != null) {
          clearInterval(timerId);
          timerId = null;
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };

      // tick every second, only accumulating elapsed time while visible
      const tick = () => {
        const now = Date.now();
        if (!document.hidden) {
          elapsed += now - lastTick;
        }
        lastTick = now;
        if (elapsed >= timeoutMs && !settled) {
          settled = true;
          cleanup();
          this.lastSyncError = { source: 'timeout', error: { message: `Timed out after ${timeoutMs}ms (foreground)` }, timestamp: DateTime.utc().toISO() };
          resolve({ succeeded: false as any, timedOut: true });
        }
      };

      // when tab becomes visible again, reset lastTick so background time isn't counted
      const onVisibilityChange = () => {
        if (!document.hidden) {
          lastTick = Date.now();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
      timerId = setInterval(tick, 1000);

      promise.then((result) => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve({ succeeded: result, timedOut: false });
        }
      }).catch((err) => {
        if (!settled) {
          settled = true;
          cleanup();
          // unexpected error from the deferred promise — shouldn't happen, but propagate as failure
          console.error(`awaitSyncWithTimeout: unexpected rejection:`, err);
          resolve({ succeeded: false as any, timedOut: false });
        }
      });
    });
  }

  /**
   * The boot-time initial sync: decides whether this device must wait for a
   * download before the app is usable, and keeps retrying that download while
   * the device stays online. Fire-and-forget by every caller — boot waits on the
   * deferred promises it resolves, never on this promise.
   *
   * The body of this lives in `database-wrapper-initial-sync.ts`. Everything it
   * needs is handed over as an explicit context object rather than reached for
   * through `this`, so the boot sync can be read — and unit-tested — without the
   * whole document-access API around it. Every gate, including both early
   * returns, moved with it; this method only builds the context.
   */
  async assureInitialSyncAsync() {
    return assureInitialSyncInternalAsync(DatabaseWrapper.buildInitialSyncContext(this));
  }

  /**
   * The bridge between this class and the extracted initial-sync code.
   *
   * Every entry is a plain forward to a member of this class, so the boot sync
   * sees exactly the values and behaviour it saw when it lived inside this file.
   * Reads are accessors rather than captured values because the sync awaits for
   * many minutes and everything it reads can change underneath it — the
   * connection state flips, a shutdown nulls `localDb`, and a concurrently
   * kicked sync sets `initialSyncCompleted`; the code re-reads them for exactly
   * that reason.
   *
   * The length of this list is not an accident to be tidied away — it is the
   * measured size of the coupling between the replication engine and the
   * document store, and it shrinks only when a later slice moves more of the
   * engine out. See deferred finding `df-3c79746add23`.
   */
  private static buildInitialSyncContext(wrapper: DatabaseWrapper): InitialSyncContext {
    return {
      // ---- state the initial sync only reads ----
      get remoteOnly() { return wrapper.remoteOnly; },
      get routedToTemporaryDb() { return !!wrapper.temporaryDb; },
      get localDb() { return wrapper.localDb; },
      get internalDatabaseIdentifier() { return wrapper.internalDatabaseIdentifier; },
      get initialSyncCompleted() { return wrapper.initialSyncCompleted; },
      get initialSyncSkippedEmpty() { return wrapper.initialSyncSkippedEmpty; },
      get singletonDocIds() { return Object.keys(wrapper.singletons); },
      get connectionStateIsOnline() { return wrapper.onlineStateService.connectionState === ConnectionState.Online; },
      get connectionStateIsOffline() { return wrapper.onlineStateService.connectionState === ConnectionState.Offline; },
      get connectionState() { return wrapper.onlineStateService.connectionState; },

      // ---- values that live on this class ----
      get couchServerUrl() { return AppEnvironment.pouchConfig.couch_serverurl; },
      get recentlySyncedStorageKey() { return `${DatabaseWrapper.dbRecentlySyncedKeyPrefix}${wrapper.databaseIdLocal}`; },
      get initialSyncSettledStorageKey() { return `${DatabaseWrapper.dbInitialSyncSettledKeyPrefix}${wrapper.databaseIdLocal}`; },

      // ---- database recovery, and the reporting route the original code used ----
      get databaseRecovery() { return wrapper.databaseRecovery; },
      get reportingLogging() { return wrapper.dbProvider.logging; },

      // ---- translations, called at the moment the message is shown ----
      translations: {
        retrying: () => LL.errors.syncRetry.retrying(),
        stillRetrying: () => LL.errors.syncRetry.stillRetrying(),
      },

      // ---- things it asks this class to do ----
      isUserDb: () => wrapper.isUserDb(),
      isUserNewAsync: () => wrapper.dbProvider.appService.isUserNew(),
      awaitFirstConnectionProbeAsync: () => wrapper.onlineStateService.awaitFirstProbeAsync(FIRST_PROBE_GATE_TIMEOUT_MS),
      resolveCalendarWindowSyncGate: () => wrapper.whenCalendarWindowSyncCompleted.resolve(undefined),
      awaitInitialSingletonSyncAsync: (timeoutMs: number) =>
        wrapper.awaitSyncWithTimeout(wrapper.whenInitialSingletonSyncCompleted, timeoutMs),
      awaitInitialSyncAsync: (timeoutMs: number) =>
        wrapper.awaitSyncWithTimeout(wrapper.whenInitialSyncCompleted, timeoutMs),
      restartSyncForSingletonRetry: () => {
        wrapper.whenInitialSingletonSyncCompleted = DeferredPromise.defer<boolean | undefined>();
        // a new round has started, so the last round's outcome no longer answers anybody (#6248)
        wrapper.initialSingletonSyncOutcome = undefined;
        wrapper.lastSyncError = null;
        wrapper.cancelSync();
        if (wrapper.remoteDb) {
          // also reset full sync deferred since startSync(true) drives both
          wrapper.initialSyncCompleted = undefined;
          wrapper.lastInitialSyncOutcome = undefined;
          wrapper.whenInitialSyncCompleted = DeferredPromise.defer<boolean | undefined>();
          void wrapper.startSync(true);
        }
      },
      restartSyncForInitialSyncRetry: () => {
        wrapper.initialSyncCompleted = undefined;
        // a new round has started, so the last round's outcome no longer answers anybody (#6554)
        wrapper.lastInitialSyncOutcome = undefined;
        wrapper.whenInitialSyncCompleted = DeferredPromise.defer<boolean | undefined>();
        wrapper.lastSyncError = null;
        wrapper.cancelSync();
        if (wrapper.remoteDb) {
          void wrapper.startSync(true);
        }
      },
      publishRetryMessage: (retryMessage: string | null) =>
        wrapper.pubSub.publish(DatabaseSyncProgressEvent, { retryMessage }),
      sleepAsync: async (ms: number) => { await sleep(ms); },
      cancelSync: () => wrapper.cancelSync(),
      setInitialSyncCompleted: (value: boolean) => wrapper.setInitialSyncCompleted(value),
      stringifiedLastSyncError: () => wrapper.stringifiedLastSyncError(),
    };
  }

  isBackgroundSyncComplete(): boolean {
    const bgKey = `${DatabaseWrapper.dbBackgroundSyncCompleteKeyPrefix}${this.databaseIdLocal}`;
    return !!localStorage[bgKey];
  }

  // Forget everything this device believes about having synced. Called from the paths that genuinely
  // THROW THE LOCAL DATA AWAY — database recovery from a dead/corrupt IndexedDB, and
  // restoreFromDocsAsync, which destroys the local database so the next boot resyncs from scratch.
  //
  // Every "this device has data" marker MUST be cleared here. The F24 offline-boot gate reads these
  // markers to decide whether a signed-in user booting offline has local data (boot normally) or has
  // nothing (hold the splash with an honest message instead of showing empty onboarding). A marker
  // that survives a wipe tells the gate "this device has data" when it has none — and the user is
  // dropped into empty onboarding as if their account never existed. That is the exact lie the gate
  // was built to prevent, so leaving a marker out of this list silently defeats it.
  // (Also clears the warm-resume flag so database recovery forces a cold start.)
  static clearRecentlySyncedMarks() {
    for (let key in localStorage) {
      if (key.startsWith(DatabaseWrapper.dbRecentlySyncedKeyPrefix)
          || key.startsWith(DatabaseWrapper.dbInitialSyncSettledKeyPrefix)
          || key.startsWith(DatabaseWrapper.dbBackgroundSyncCompleteKeyPrefix)) {
        delete localStorage[key];
      }
    }
    // clear warm resume flags so database recovery forces cold start
    clearWarmResumeFlag();
  }

  public async shutdownAsync() {
    console.log(`Db:${this.internalDatabaseIdentifier}: shutting down database...`);

    if (this.connectionStateSubscription) {
      this.connectionStateSubscription.unsubscribe();
    }

    this.cancelSync();

    // the live local _changes feed is not part of sync and `cancelSync` does not touch it,
    // so without this it outlives the wrapper: it goes on firing against a store this
    // wrapper no longer owns, and on a wrapper superseded by a second start-up it would go
    // on firing for the rest of the page's life.
    if (this.changesFeed) {
      this.changesFeed.cancel();
      this.changesFeed = null;
    }

    // wait for all in-flight operations to finish, then permanently lock
    this.shutdownPhase = DatabaseShutdownPhase.WaitingReadLock;
    await this.rwLock.acquireWrite();

    // now safe to close PouchDB handles — no operations can be in-flight
    if (this.remoteDb) {
      this.shutdownPhase = DatabaseShutdownPhase.ClosingRemote;
      await this.remoteDb.close();
    }

    // The local store handle is shared with any other live wrapper open on the same name,
    // so closing it here can close the connection the LIVE wrapper is using. See
    // `mayCloseLocalStore`, and design-docs/pass-through-db.md, "A shutdown does not always
    // close the local store, and it must not".
    if (this.localDb) {
      if (mayCloseLocalStore(this, this.dbProvider.allDatabases)) {
        this.shutdownPhase = DatabaseShutdownPhase.ClosingLocal;
        await this.localDb.close();
      } else {
        console.log(`Db:${this.internalDatabaseIdentifier}: leaving local store ${this.databaseIdLocal} open — a live wrapper is still using it`);
      }
    }

    this.localDb = null;
    this.remoteDb = null;

    // no further sync outcome can arrive from a wrapper with no handles, so
    // holding these closures alive would only keep their callers from being
    // collected (#4435)
    this.initialSyncSettledListeners.clear();

    this.shutdownPhase = DatabaseShutdownPhase.Done;
    console.log(`Db:${this.internalDatabaseIdentifier}: database shutdown completed.`);
  }

  /**
   * Where `shutdownAsync` is, and how many read sections it is still waiting for. Read by the
   * sign-out teardown only when a database step gives up, so the report names the stuck wait
   * (df-5c749cefc673; design-docs/boot-sequence.md, the #4762 section).
   */
  describeShutdown(): DatabaseShutdownDescription {
    return { shutdown_phase: this.shutdownPhase, shutdown_open_readers: this.rwLock.readerCount };
  }

  /**
   * Record this coach copy's local store name so sign-out can delete it. (df-a00755b05133)
   *
   * "Back to my account" keeps the copy and closes it, so at sign-out the provider no
   * longer holds it, and on SQLite nothing else can name the file. The note is
   * `SQLITE_COACH_COPY_NAMES_KEY`; why it must survive both sign-out wipes is in
   * design-docs/android-local-database-engine.md, "Some notes must survive BOTH
   * localStorage wipes".
   *
   * A failure here does not stop the coach working, so it is reported rather than
   * thrown: the cost is one client copy sign-out cannot find, which is exactly the leak
   * this note exists to prevent, so it must reach Sentry.
   */
  private recordCoachCopyNameForSignOut() {
    try {
      const storage = browserLocalStorageOrNull();
      if (!storage) throw new Error('localStorage is not available');
      const updated = recordCoachCopyName(storage.getItem(SQLITE_COACH_COPY_NAMES_KEY), this.databaseIdLocal);
      if (updated !== undefined) storage.setItem(SQLITE_COACH_COPY_NAMES_KEY, updated);
    } catch (err) {
      this.logging.trackSilentProblem('CoachCopyNameNotRecorded', {
        db_name: this.databaseIdLocal,
        ...errorReportFields(err),
      });
    }
  }

  /**
   * Record, before the user's store is first opened on this device, whether it already
   * existed (#6788).
   *
   * The automatic switch to a fresh store needs positive proof that the store is new:
   * missing markers are not proof, because the recovery clears the synced markers while the
   * data stays, and a store written before #6788 has no written marker. So just before the
   * first open, ask the browser which IndexedDB databases exist. Asked once per store name;
   * every answer is recorded, including "could not tell", so a device never pays for the
   * question twice. Only a clean "not listed" permits the switch later.
   *
   * Skipped where no switch can happen: not the user's own store, no local store, a SQLite
   * device (nothing to list in IndexedDB), or a store this device has already synced.
   */
  private async recordLocalStoreOriginIfUnknownAsync(): Promise<void> {
    if (this.internalDatabaseIdentifier !== 'user' || this.managedClientUid || this.remoteOnly || usesSqliteEngine()) return;
    try {
      const storage = browserLocalStorageOrNull();
      if (readLocalStoreOrigin(storage, this.databaseIdLocal) !== undefined) return;
      // a store this device has synced holds data, so there is nothing to ask; undefined
      // records it as not proven new
      const listedNames = this.hasSyncedOnThisDeviceMarker ? undefined : await this.listPouchDatabasesForOriginAsync();
      const origin = decideLocalStoreOrigin(listedNames, this.databaseIdLocal);
      if (!recordLocalStoreOrigin(storage, this.databaseIdLocal, origin)) {
        this.logging.trackSilentProblem('LocalStoreOriginRefused', { db: this.internalDatabaseIdentifier });
      }
    } catch (err) {
      // this is a diagnostic for a later decision and must never stop the store opening; an
      // unrecorded origin already forbids the automatic switch
      this.logging.trackSilentProblem('LocalStoreOriginCheckFailed', {
        db: this.internalDatabaseIdentifier, ...errorReportFields(err),
      });
    }
  }

  /**
   * The browser's list of PouchDB IndexedDB databases, or undefined when it cannot say in time
   * (#6788). Undefined proves nothing, which `decideLocalStoreOrigin` reads as not proven new.
   */
  private async listPouchDatabasesForOriginAsync(): Promise<string[] | undefined> {
    if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return undefined;
    const timer = runningTimeTimer();
    try {
      return await withLocalStoreTimeout(
        listPouchIndexedDbNamesAsync(indexedDB, { excludeViewIndexes: true }),
        this.localOpName('indexedDB.databases(origin)'),
        DatabaseWrapper.kLocalStoreOriginProbeMs, timer.schedule, timer.clear,
      );
    } catch (err) {
      // reported, and the cautious answer follows: an unanswered question proves nothing
      this.logging.trackSilentInfo('LocalStoreOriginUnknown', {
        db: this.internalDatabaseIdentifier, ...errorReportFields(err),
      });
      return undefined;
    }
  }

  /** How long the #6788 origin check waits for the browser's list of databases. Paid once per
   *  store name, only on a device that never synced it; a cold phone can take a moment. */
  private static readonly kLocalStoreOriginProbeMs = 3_000;

  private async startLocalDatabaseAsync(skipRemote: boolean = false) {
    //PouchDB.plugin(pouchdbDebug)
    //PouchDB.debug.enable('*');
    // PouchDB.debug.disable('*');

    if (!this.remoteOnly) {
      // if this is a coach's copy of a client database, note its name BEFORE the file
      // exists, so a boot killed right after cannot leave a file sign-out cannot name
      if (this.managedClientUid) this.recordCoachCopyNameForSignOut();
      console.log(`Db:${this.internalDatabaseIdentifier}: initializing database ${this.databaseIdLocal}`);
      // #6788: must run BEFORE the open below, which is what creates the store
      await this.recordLocalStoreOriginIfUnknownAsync();
      this.localDb = new PouchDB(this.databaseIdLocal, {
        auto_compaction: true, // non-leaf revisions will not be retained - we don't need them
        ...getLocalAdapterOpts(
          makeLocalCorruptionReporter(this.logging, this.internalDatabaseIdentifier),
          makeLocalAdapterFaultReporter(this.logging, this.internalDatabaseIdentifier),
        ),
      });
      // parallel view queries (warm-up + workout loading) cause PouchDB's internal
      // mrview databases to exceed the default 10-listener limit on 'destroyed' events
      this.localDb.setMaxListeners(20);
    }

    // Subscribe to online/offline state changes
    this.connectionStateSubscription = this.pubSub.subscribe(
      connectionStateChangedEvent,
      (state: ConnectionState) => this.onConnectionStateChanged(state)
    );
    
    // Initialize based on current auth state if already logged in.
    // L5 (#3986 / #442): when we are NOT going to initialize the remote DB (offline, no
    // couch auth yet, or a skipRemote start), still commit the view design docs to the LOCAL
    // db. commitQueriesAsync is the only place whenQueriesReady is resolved; on a fully
    // offline boot initializeRemoteDb never runs, so without this every view-backed read
    // (runQueryAsync) hangs forever. Writing the views locally works offline and resolves
    // whenQueriesReady; a later successful initializeRemoteDb re-commits idempotently.
    const followup = decideStartLocalDbFollowup({
      skipRemote,
      hasCouchAuth: !!this.auth.couch,
      online: this.onlineStateService.connectionState === ConnectionState.Online,
      hasLocalDb: !!this.localDb,
    });
    if (followup === 'init-remote') {
      void this.initializeRemoteDb();
    } else if (followup === 'commit-queries-locally') {
      void this.commitQueriesAsync();
    }

    this.attachChangesFeed();
    this.attachLocalDbErrorListener();
  }

  /** Attach a live changes feed to the local user DB for tracking last-change time.
   *  Uses getWriteDb() (always local, never pass-through to remote) — the feed
   *  observes local replication events; routing it to remoteDb during pass-through
   *  would duplicate the sync pipeline's own change-delivery path. */
  private attachChangesFeed() {
    let writeDb = this.getWriteDb();
    if (writeDb && this.isUserDb()) {
      // #4010 B3: before tracking this session's writes, check whether the PREVIOUS session
      // ended with one still unflushed (cross-session gap closure; one-shot per session)
      this.checkPersistedUnflushedWriteOnce();
      const myDeviceId = getDeviceId();
      this.changesFeed = writeDb.changes({
        since: 'now',
        live: true,
        include_docs: true,
      }).on('change', (change) => {
        // if initial sync hasn't completed, skip change tracking — sync-pulled
        // writes are not real user changes and would fire a false silent-stall
        // Sentry signal (#3274 — flags drive that diagnostic, not UI offline state)
        if (this.initialSyncCompleted !== true) return;
        // if doc payload is missing, nothing to filter on
        if (!change.doc) return;

        // if the doc wouldn't replicate (`_design/view`, `_design/probe` —
        // see replicationFilter), it never triggers sync 'change', so leaving
        // it in the tracking would produce false-stale flag state on the next
        // paused event
        if (!this.replicationFilter(change.doc)) return;

        // if the change originated on another device, skip it — cheap pre-filter.
        // sync pulls and the realtime-listener bulkDocs writes carry the originating
        // writer's deviceId in db__trans, so other-device writes are definitely not
        // local unflushed writes (#3274 — flags drive that diagnostic, not UI offline
        // state).
        const transactionDeviceId = getTransactionDeviceId(change.doc);
        if (transactionDeviceId && transactionDeviceId !== myDeviceId) return;

        // if this exact revision was NOT authored locally by this app, it is a
        // sync-pulled echo (CouchDB replays our own just-pushed docs through _changes
        // carrying our deviceId) or a pass-through graft — both already on the server,
        // so they must not look like an unflushed local write. Only a revision we wrote
        // through the local save path (recordLocallyAuthoredRev) is a genuine pending
        // flush. consume the entry so the set self-prunes (Sentry 429/3ZD false-positive
        // fix). NOTE: this replaces the old own-or-null-deviceId heuristic, which could
        // not tell our own pushed-then-echoed doc from a fresh local edit.
        if (!this.consumeLocallyAuthoredRev(change.doc._id, change.doc._rev)) return;

        sessionStorage.lastUserDbChangeTime_utc = DateTime.utc().toISO();
        delete sessionStorage.lastUserDbSyncTime_utc;
        // #4010 B3: mirror the tracking pair to localStorage so the NEXT session can tell
        // whether this one ended with the write still unflushed (the flags themselves stay
        // session-scoped — the mirror is only read once at boot by the cross-session check)
        localStorage.cs_lastUserDbChangeTime_utc = sessionStorage.lastUserDbChangeTime_utc;
        localStorage.removeItem('cs_lastUserDbSyncTime_utc');
      });
    }
  }

  /**
   * #4010 B3: one-shot boot check closing the documented Phase-1 gap — a local write left
   * unflushed when the app was KILLED was invisible to the session-scoped diagnostic. Reads
   * the localStorage mirror of the tracking pair; when the previous session ended with a
   * pending write (change persisted, never acked, older than 30 s), reports the volume
   * metric `sync_unflushed_survived_restart` with the write's age. The marker is cleared
   * either way so one stranded write reports exactly once. The write itself is NOT lost —
   * this session's initial sync pushes it — the metric measures how often (and for how
   * long) user data sat on-device only across a session boundary.
   */
  private checkPersistedUnflushedWriteOnce() {
    // once per session — attachChangesFeed can re-attach after sync restarts
    if (sessionStorage.persistedUnflushedChecked) return;
    sessionStorage.persistedUnflushedChecked = '1';

    const decision = decidePersistedUnflushedReport(
      localStorage.cs_lastUserDbChangeTime_utc,
      localStorage.cs_lastUserDbSyncTime_utc,
      DateTime.utc().toMillis(),
    );
    if (decision.shouldReport) {
      console.log(`Db:${this.internalDatabaseIdentifier}: previous session ended with an unflushed local write (age ${Math.round(decision.ageMs / 1000)}s)`);
      this.telemetry?.trackEvent('sync_unflushed_survived_restart', {
        age_ms: Math.round(decision.ageMs),
      });
    }
    // clear the change marker either way — a reported write must not re-report next boot,
    // and a fresh one gets written by the changes feed on the next local write
    localStorage.removeItem('cs_lastUserDbChangeTime_utc');
  }

  /**
   * True when the most recent sync error looks like a transient network failure and is
   * recent enough to explain the current pause. On iOS the network can drop or be suspended
   * while `connectionState` still reads online (it only flips via a `ping.svg` probe), so
   * connectionState alone misses these — a fresh network-shaped sync error does not. Used to
   * gate the stale-unflushed-change (429) and checkpoint-drift (3ZD) diagnostics so a known
   * network drop is not mislabeled as a silent stall / data-loss risk.
   */
  private hasRecentNetworkSyncError(withinMs = 30_000): boolean {
    return isRecentNetworkSyncError(this.lastSyncError, DateTime.utc().toMillis(), withinMs);
  }

  /**
   * The shared "is this a genuine anomaly?" gate of the two diagnostics in this
   * class, fed with the database state as well as the connection, foreground and
   * recent-network-error facts. See {@link isSyncDiagnosticEnvironmentHealthy}.
   */
  private isEnvironmentHealthyForSyncDiagnostics(): boolean {
    return isSyncDiagnosticEnvironmentHealthy({
      databaseIsOffline: this.dbProvider.databaseStates[this.internalDatabaseIdentifier] === DatabaseState.Offline,
      connectionStateIsOnline: this.onlineStateService.connectionState === ConnectionState.Online,
      documentHidden: document.hidden,
      recentNetworkSyncError: this.hasRecentNetworkSyncError(),
    });
  }

  /**
   * Sync went idle (`paused` with no error info) but our flags say we have a
   * local own-device, replicating write that sync never acknowledged with a
   * `'change'` event. Post-#3274 the changes feed only flips these flags for
   * such writes, so this state is genuinely anomalous — bug-territory worth a
   * Sentry signal. Gated to avoid noise:
   * - skipped while we already know we're offline (sync error / network ping
   *   fail / auth failure already drove DatabaseState.Offline — the legitimate
   *   "stuck waiting to flush" case, not a silent stall);
   * - skipped under a 30s staleness threshold to avoid transient false
   *   positives during normal in-flight pushes and warm-resume catch-up;
   * - reported at most once per session via a sessionStorage flag, since one
   *   alert per affected user-session is enough to investigate.
   */
  private reportStaleUnflushedChangeIfNeeded() {
    // if no change is tracked, nothing to diagnose
    const changeIso = sessionStorage.lastUserDbChangeTime_utc;
    if (!changeIso) return;

    // if the change is fresh (<30s), it's normal in-flight state, not a stall
    const ageMs = DateTime.utc().diff(DateTime.fromISO(changeIso)).as('milliseconds');
    if (ageMs <= 30_000) return;

    // if the environment explains the wait, it is not a silent stall: the DB was already
    // published Offline by the error/denied/auth handlers, the network is known offline,
    // the app is backgrounded (iOS suspends networking; the resume path re-checks — Sentry
    // 429 false-positive: users who finish a workout and pocket the phone), or a transient
    // network failure just happened while connectionState still reads online
    if (!this.isEnvironmentHealthyForSyncDiagnostics()) return;

    // if we've already reported once this session, stay quiet — one alert per
    // affected user-session is enough to investigate
    if (sessionStorage.unflushedChangeReported) return;
    sessionStorage.unflushedChangeReported = DateTime.utc().toISO();

    this.dbProvider.logging.trackSilentProblem(
      `Db:${this.internalDatabaseIdentifier}: sync paused with stale unflushed local change`,
      {
        ageMs,
        lastUserDbChangeTime_utc: changeIso,
        connectionState: this.onlineStateService.connectionState,
        documentHidden: document.hidden,
        lastSyncError: this.lastSyncError,
      },
    );
  }

  /**
   * Detect a live-sync PUSH that keeps failing while the app is online
   * (df-ed6d17c8ec6f). This is the case the stale-unflushed diagnostic above
   * cannot see: a working PULL keeps bumping `lastUserDbSyncTime_utc`, so the
   * stale-unflushed check never even runs, yet the user's own writes never reach
   * the server. `.on('error')` sets `lastSyncError` and the next clean `.on('paused')`
   * nulls it, so that field looks healthy too. The only signal that is not masked
   * is a PUSH ack — `lastUserDbPushAckTime_utc`, written only when a push batch
   * actually reaches the server.
   *
   * Called from every live-sync `paused` except the "Load failed" one, which restarts
   * sync and returns early. Keeps a "stuck since" clock in
   * sessionStorage that runs only while an own-device write is unacked AND the
   * environment is healthy; the pure {@link decidePushStallReport} clears it the
   * moment either stops holding, and {@link installPushStallClockResetOnHide} clears
   * it whenever the page is hidden, so the window it measures is a continuously
   * online-and-foregrounded stretch. Reports once per session via
   * `trackSilentProblem`. See design-docs/sync-integrity.md.
   */
  private reportPushStallIfNeeded() {
    const changeIso = sessionStorage.lastUserDbChangeTime_utc;
    const pushAckIso = sessionStorage.lastUserDbPushAckTime_utc;
    const nowMs = DateTime.utc().toMillis();

    // the same gate the stale-unflushed diagnostic trusts for "this is a genuine
    // anomaly, not a legitimate offline/backgrounded wait"
    const environmentHealthy = this.isEnvironmentHealthyForSyncDiagnostics();

    const storedStuckSince = sessionStorage[PUSH_STUCK_SINCE_STORAGE_KEY];
    const parsedStuckSince = storedStuckSince != null ? parseInt(storedStuckSince, 10) : NaN;

    const decision = decidePushStallReport({
      hasUnackedLocalWrite: isLocalWriteUnackedByPush(changeIso, pushAckIso),
      environmentHealthy,
      pushStuckSinceMs: Number.isFinite(parsedStuckSince) ? parsedStuckSince : null,
      nowMs,
      minStallMs: PUSH_STALL_MIN_MS,
      alreadyReported: !!sessionStorage.pushStallReported,
    });

    // persist the clock (null clears it)
    if (decision.nextStuckSinceMs == null) {
      delete sessionStorage[PUSH_STUCK_SINCE_STORAGE_KEY];
    } else {
      sessionStorage[PUSH_STUCK_SINCE_STORAGE_KEY] = String(decision.nextStuckSinceMs);
    }

    if (decision.report) {
      // report at most once per session
      sessionStorage.pushStallReported = DateTime.utc().toISO();
      const stuckForMs = decision.nextStuckSinceMs != null ? nowMs - decision.nextStuckSinceMs : 0;
      console.log(`Db:${this.internalDatabaseIdentifier}: live sync push has not reached the server for ${Math.round(stuckForMs / 1000)}s while online`);
      this.dbProvider.logging.trackSilentProblem(
        `Db:${this.internalDatabaseIdentifier}: live sync push not reaching server`,
        {
          stuckForMs,
          lastUserDbChangeTime_utc: changeIso,
          lastUserDbPushAckTime_utc: pushAckIso,
          connectionState: this.onlineStateService.connectionState,
          documentHidden: document.hidden,
          lastSyncError: this.lastSyncError,
        },
      );
    }
  }

  /** True when the user DB likely has a local write that has not been uploaded.
   * Uses the same sessionStorage timestamps the silent-stall diagnostic uses.
   * Their real semantics (see design-docs/sync-integrity.md): the change
   * timestamp is written on own-device local writes; the sync timestamp is
   * DELETED on every own-device local write and re-set by sync 'change'
   * events in EITHER direction (inbound pulls included). So: change set +
   * sync missing = definitely unflushed; change newer than sync = likely
   * unflushed. This is a heuristic, not an exact ledger: an inbound pull can
   * bump the sync timestamp while our own push is still in flight, and since
   * both timestamps only move forward, no amount of re-reading can detect
   * that case. That residual race is ACCEPTED — it is bounded by the drain
   * wait giving the in-flight push time to finish, and by the OFFLINE
   * sign-out branch never consulting these timestamps at all (it always
   * warns). Only meaningful for the user DB (shared DBs are pull-only).
   *
   * Session-scope caveat: sessionStorage timestamps cover only the current
   * browser session. Unflushed writes from a *previous* session that never
   * synced are not detected. This is accepted for Phase 1: the dominant
   * data-loss case is "logged workout in the gym offline, signs out in the
   * same session". The offline branch of the sign-out guard does not depend
   * on these timestamps — offline sign-out always warns. */
  hasUnflushedLocalChanges(): boolean {
    if (!this.isUserDb()) return false;
    const changeRaw = sessionStorage.lastUserDbChangeTime_utc;
    if (!changeRaw) return false;
    const syncRaw = sessionStorage.lastUserDbSyncTime_utc;
    if (!syncRaw) return true; // local write happened and no sync event since (the write deleted the sync mark)
    return DateTime.fromISO(changeRaw) > DateTime.fromISO(syncRaw);
  }

  /** Wait (bounded) for the running live sync to upload pending local writes.
   * Resolves true when nothing is unflushed anymore, false on timeout.
   * Polls because the 'paused' event does not distinguish push-drained from
   * pull-idle; 500 ms granularity is fine for a one-shot pre-sign-out wait.
   * The streak-and-timeout logic lives in the pure pollUntilDrainedAsync helper
   * (sign-out-guard.ts) so it is unit-tested under node; this method just wires
   * in the real dirty-check, sleep, and clock. */
  async whenPushDrainedAsync(timeoutMs: number): Promise<boolean> {
    return pollUntilDrainedAsync({
      isStillDirty: () => this.hasUnflushedLocalChanges(),
      sleep,
      now: () => Date.now(),
      timeoutMs,
    });
  }

  /** Attach an error listener to the local PouchDB instance for IDB death detection. */
  private attachLocalDbErrorListener() {
    if (this.localDb) {
      this.localDb.on('error', err => {
        console.error(`Db:${this.internalDatabaseIdentifier}: localDb error:`, err);
        // if this is an IndexedDB connection error, attempt recovery
        if (this.databaseRecovery.interceptDatabaseError(err)) {
          return;
        }
        this.dbProvider.logging.trackAbnormalException(err);
      });
    }
  }

  /**
   * Recreate the local PouchDB instance after IndexedDB connection death.
   * Operations are paused via the recreation gate during the swap, then resume
   * against the new instance — no data loss, no page reload.
   */
  async recreateLocalDbAsync(): Promise<boolean> {
    if (this.remoteOnly) return true;

    // A wrapper whose shutdown has started must not be recreated, because recreating one
    // REVIVES it: `shutdownAsync` has already nulled `localDb`, so the close step below
    // throws, `swapLocalStoreAsync` deliberately swallows that and opens a new handle
    // anyway, and the wrapper comes back with a re-attached changes feed and a restarted
    // sync — on a database the app is deliberately tearing down. The provider's sweep
    // filters shut-down wrappers out of its list, but it reads that list once and then
    // awaits each wrapper in turn, so a shutdown that STARTS during the sweep is still in
    // the snapshot. Only the wrapper itself can answer that, which is why the guard is
    // here. See design-docs/pass-through-db.md, "Which databases a sweep may act on".
    // A shutdown that starts AFTER this check, while the swap below is in flight, is
    // caught by the two checks around the swap.
    //
    // It answers true, like the remoteOnly line above, because true means "there is
    // nothing here to recreate" and not "the recreation worked". False would be a lie
    // with a cost: the provider turns one false into a false for the whole sweep, and
    // LoggingService turns that into recoverFromDatabaseError — a full page reload for a
    // database that is being closed on purpose.
    if (this.rwLock.isShuttingDown) {
      this.logging.trackSilentInfo('GracefulRecoverySkippedForShuttingDownDb', {
        db: this.internalDatabaseIdentifier,
      });
      return true;
    }

    const logHeader = `Db:${this.internalDatabaseIdentifier}:`;

    try {
      // open the gate — all database operations will pause here
      this.recreationGate = new Promise<void>(resolve => { this.resolveRecreationGate = resolve; });

      // stop active sync
      this.cancelSync();

      // cancel the changes feed
      if (this.changesFeed) {
        this.changesFeed.cancel();
        this.changesFeed = null;
      }

      // Close the old handle, open a new one, and PROVE the new one answers. Every
      // step of that is bounded (#4401): a store that has stopped answering does not
      // answer `close()` either, and the unbounded close this replaced could never
      // settle and — because it never settled — never threw, so the catch below never
      // ran and the recreation gate stayed open, blocking every read for ever.
      const swap = await swapLocalStoreAsync({
        databaseId: this.internalDatabaseIdentifier,
        closeOld: () => this.localDb.close(),
        openNew: () => {
          this.localDb = new PouchDB(this.databaseIdLocal, {
            auto_compaction: true,
            ...getLocalAdapterOpts(
              makeLocalCorruptionReporter(this.logging, this.internalDatabaseIdentifier),
              makeLocalAdapterFaultReporter(this.logging, this.internalDatabaseIdentifier),
            ),
          });
          // re-attach listeners
          this.attachChangesFeed();
          this.attachLocalDbErrorListener();
        },
        probeNew: () => this.localDb.info(),
        bound: withBootLocalStoreTimeout,
        log: (message: string) => console.log(message),
        // the guard above read the flag once; a shutdown can start while the close is
        // awaited, and opening a new handle after that is the same revival by a later door.
        // if we stop, drop the old handle in the same synchronous step: it is already
        // closed, and the shutdown's own close on it would reject with "database is
        // closed" and skip the rest of the shutdown
        shouldAbandon: () => {
          if (!this.rwLock.isShuttingDown) return false;
          this.localDb = null;
          return true;
        },
      });

      // close the gate — paused operations resume against the new instance. This happens
      // whether or not the new handle answered: a still-silent store is reported below,
      // and leaving the gate shut would freeze every waiting read on top of that.
      this.resolveRecreationGate();
      this.recreationGate = null;
      this.resolveRecreationGate = null;

      // if the shutdown started during the swap — before the new handle was opened
      // (abandoned) or while it was being probed — stop here: rewriting the design doc
      // and restarting sync would revive a database being closed on purpose. This comes
      // before the !swap.ok check on purpose: a probe that failed because the shutdown
      // closed the new handle is not a failed repair, and false would reload the page.
      // True, for the same reason as the guard at the top.
      if (swap.abandoned || this.rwLock.isShuttingDown) {
        this.logging.trackSilentInfo('GracefulRecoverySkippedForShuttingDownDb', {
          db: this.internalDatabaseIdentifier,
        });
        return true;
      }

      if (!swap.ok) {
        // the replacement handle is no better than the one it replaced. Say so and let
        // the caller escalate — reporting this as a successful recovery is what would
        // stop the user ever reaching the rung that gives up and tells them.
        this.logging.trackSilentProblem('LocalStoreRecreationDidNotHelp', {
          db: this.internalDatabaseIdentifier,
          close_gave_up: swap.closeGaveUp,
          probe_gave_up: swap.probeGaveUp,
          // #6788: whether this store had anything to lose, and whether the start followed
          // the purchase reload
          ...this.silentStoreReportFields(),
        });
        return false;
      }

      // #4401: rewrite the view design doc against the NEW handle. Recovery used to skip
      // this entirely — initializeRemoteDb early-returns while remoteDb is still set, so
      // commitQueriesAsync never ran again and the design doc stayed on the handle that
      // was just thrown away. Every view-backed read would then miss until the next boot.
      void this.commitQueriesAsync();

      // restart sync, if there is a server connection to sync with. A recovery during
      // start-up can run before the sign-in to the sync server has finished (or after it
      // failed, or offline), so remoteDb is still null; startSync would refuse and file
      // StartSyncWithoutRemoteDb for a state nothing is wrong with (df-9f13c1cce377).
      // initializeRemoteDb starts sync itself once it creates the remote handle.
      if (this.remoteDb) {
        const needsInitialSync = !this.initialSyncCompleted;
        void this.startSync(needsInitialSync);
      } else {
        console.log(`${logHeader} local store recreated — sync waits for the remote connection`);
      }

      // notify all watchers so pages re-derive their UI from the new PouchDB instance
      this.notifyAllWatchersChanged();

      return true;
    } catch (err) {
      console.error(`${logHeader} recreation failed:`, err);
      this.reportLocalStoreRecreationFailed(err, 'recreation');
      // if gate was opened but not closed, close it so operations don't hang forever
      if (this.resolveRecreationGate) {
        this.resolveRecreationGate();
        this.recreationGate = null;
        this.resolveRecreationGate = null;
      }
      return false;
    }
  }

  /**
   * Park every later read and write of this database until the hold is released (#6788).
   *
   * Used while the app decides whether to give up this store for a fresh name: a write that
   * landed in it after that decision would be stranded in a store nothing reads again. It
   * reuses the recreation gate, which every read and write already waits on. Returns the
   * release, or undefined when a recreation already holds the gate — the caller must then not
   * switch, because a recreation is running on the store it meant to give up.
   */
  holdOperationsForStoreSwitch(): (() => void) | undefined {
    if (this.recreationGate) return undefined;
    let release: () => void = () => {};
    this.recreationGate = new Promise<void>(resolve => { release = resolve; });
    const gate = this.recreationGate;
    return () => {
      // only clear the gate this hold set; a later recreation owns its own
      if (this.recreationGate === gate) this.recreationGate = null;
      release();
    };
  }

  /** Attempt graceful PouchDB recreation. Returns true if recovery succeeded. */
  async attemptGracefulRecoveryAsync(): Promise<boolean> {
    if (this.recreationGate) return true; // already in progress
    try {
      return await this.recreateLocalDbAsync();
    } catch (err) {
      // a throw from outside the recreation's own try (its shutdown check)
      this.reportLocalStoreRecreationFailed(err, 'outside-recreation');
      return false;
    }
  }

  /**
   * A local-store recreation that threw (#5191). False sends the recovery service on to a full
   * page reload, and until now only a Mixpanel "failed" event said so, without the error.
   */
  private reportLocalStoreRecreationFailed(err: unknown, stage: 'recreation' | 'outside-recreation'): void {
    this.logging.trackSilentProblem('LocalStoreRecreationFailed', {
      db: this.internalDatabaseIdentifier,
      stage,
      ...errorReportFields(err),
    });
  }

  async onConnectionStateChanged(state: ConnectionState) {
    console.log(`Db:${this.internalDatabaseIdentifier}: Connection state changed to ${state}`);
    
    if (state === ConnectionState.Online) {
      // When coming online, auth is already refreshed by OnlineStateService
      await this.initializeRemoteDb();
    } else {
      // When going offline, stop sync
      this.cancelSync();
    }
  }

  async initializeRemoteDb() {
    // a restore that replaced the remote but could not destroy the stale local db has poisoned this
    // wrapper — see `localDbIsStaleAfterRestore`. re-attaching a remote here is what would let the old
    // local revisions push over the restore, so refuse.
    if (this.localDbIsStaleAfterRestore) {
      console.error(`Db:${this.internalDatabaseIdentifier}: refusing to initialize the remote db — the local db is stale after a restore and must not sync`);
      return;
    }

    // The wrapper is being torn down, so a remote handle attached now is one nobody will
    // ever read from and nobody will ever close. `shutdownAsync` closes `remoteDb` and nulls
    // it only at the very END of its sequence, after an unbounded wait for in-flight readers,
    // so an assignment landing after that point overwrites the null with a live PouchDB object
    // that leaks for the rest of the page's life.
    //
    // The route in is a CALLER that straddles the shutdown, not an await inside this function:
    // `openCommonDbAsync` tests `isShuttingDown` at entry and then awaits before it calls
    // `connectRemoteAndSyncAsync`, and `PubSubService.publish` iterates a snapshot of its
    // handler array, so the connection-state handler still fires once after `shutdownAsync`
    // has unsubscribed it.
    //
    // ONE check is enough here, and only because nothing between this line and the
    // `this.remoteDb = new PouchDB(...)` assignment below awaits — so entry and assignment are
    // the same instant. Introduce an await before that assignment and this check has to move
    // down with it, the way `startSync` has to ask three times for exactly that reason.
    //
    // `isShuttingDown` is true from the instant the shutdown starts and is never cleared. See
    // design-docs/pass-through-db.md, "Which databases a sweep may act on".
    if (this.isShuttingDown) {
      console.log(`Db:${this.internalDatabaseIdentifier}: not initializing the remote db — the database is shutting down`);
      this.logging.trackSilentInfo('RemoteInitSkippedForShuttingDownDb', {
        db: this.internalDatabaseIdentifier,
        where: 'initialize-remote-db',
      });
      return;
    }

    const couchAuth = this.auth.couch;
    if (!couchAuth || !this.auth.user) {
      console.log(`Db:${this.internalDatabaseIdentifier}: Cannot initialize - no auth`);
      return;
    }

    const role = this.auth.getAuthRole();
    console.log(`Db:${this.internalDatabaseIdentifier}: Initializing remote DB for ${couchAuth.user} - role: ${authRoleToStrings(role).join('+')}`);
    // skip all if already initialized
    if (this.remoteDb != null) {
      // if initial sync never completed, restart as initial sync so setInitialSyncCompleted is called
      const needsInitialSync = !this.initialSyncCompleted;
      console.log(`Db:${this.internalDatabaseIdentifier}: DB already initialized, restarting sync (initial=${needsInitialSync}).`);
      void this.startSync(needsInitialSync);
      return;
    }

    try {
      // properly handle specific remote names for userdb in couch
      if (this.isUserDb()) {
        if (this.managedClientUid) {
          this.databaseIdServer = 'userdb-' + asciiToHex(this.managedClientUid);
        } else {
          this.databaseIdServer = 'userdb-' + couchAuth.userHex;
        }
      } else {
        this.databaseIdServer = this.internalDatabaseIdentifier;
      }

      this.remoteDb = new PouchDB(AppEnvironment.pouchConfig.couch_serverurl + this.databaseIdServer, { fetch: this.auth.getFetchFunction() });
      const createdRemoteDb = this.remoteDb;

      if (this.internalDatabaseIdentifier === 'global_foods') {
        this.whenGlobalFoodDbAvailable.resolve(true);
      }

      await this.commitQueriesAsync();

      // if a restore, purge or shutdown took the handle away during that await, nobody wants this
      // sync any more — a restore waiting for the handle can take it the moment it appears, and may
      // even have finished and lowered its own-detach counter by now, so do not leave the refusal to
      // startSync, which would report the missing handle as a problem
      if (this.remoteDb !== createdRemoteDb) {
        console.log(`Db:${this.internalDatabaseIdentifier}: remote db was detached during initialization — not starting sync`);
        return;
      }

      // Start sync. If assureInitialSyncAsync's "recently synced" fast-path already set
      // initialSyncCompleted = true, start live sync directly — an orphan initial sync would
      // produce unnecessary error logs on transient failures and risk corrupting state via
      // setInitialSyncCompleted(false). When initialSyncCompleted is still undefined,
      // assureInitialSyncAsync is waiting on whenInitialSyncCompleted so we start initial sync
      // to resolve that deferred. When false, a previous sync failed (e.g., offline start then
      // came online) so we retry with initial sync.
      if (this.initialSyncCompleted === true) {
        void this.startSync(false);
      } else {
        void this.startSync(true);
      }
    } catch (err) {
      console.error(`Db:${this.internalDatabaseIdentifier}: initializeRemoteDb failed:`, err);
      this.lastSyncError = { source: 'initializeRemoteDb', error: this.serializeSyncError(err), timestamp: DateTime.utc().toISO() };
      this.setInitialSingletonSyncCompleted(false);
      this.setInitialSyncCompleted(false);
    }
  }

  updateState(state: DatabaseState) {
    const prev = this.dbProvider.databaseStates[this.internalDatabaseIdentifier];
    this.dbProvider.databaseStates[this.internalDatabaseIdentifier] = state;
    // during bulk sync, on('change') fires repeatedly with the same Syncing state —
    // skip re-publishing if nothing changed to avoid flooding the UI thread
    if (prev === state) return;
    this.pubSub.publish(CloudStateChangeEvent, { state: this.dbProvider.userDatabasesStatus() });
  }

  /** One-shot push replication: pushes all pending local changes to remote CouchDB.
   * Used after bulk local writes (e.g. coach claim) to ensure data reaches the server
   * before the app reloads or navigates away.
   *
   * It SAYS what it did (#4801). This used to answer `Promise<void>`, and every way
   * it can decline to run was a plain `console.log`, so a caller could not tell a
   * completed push from one that never happened. That is how a coach-invite claim
   * came to tell a client "Connected to your coach!" while the coach's program had
   * never left their phone: a failed boot leaves the wrapper with no remote database,
   * this returned quietly, and the next sign-out wiped the only copy.
   *
   * A caller that is about to act on the data being saved must ask
   * `didPushReachServer` rather than reading the outcome itself — the rule about
   * which outcomes count belongs in one place. */
  async pushToRemoteAsync(timeoutMs = 30_000): Promise<PushToRemoteOutcome> {
    if (!await this.waitForRecreationGate()) return PushToRemoteOutcome.SkippedShuttingDown;
    if (!this.rwLock.acquireRead()) {
      console.log(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync skipped — shutting down`);
      return PushToRemoteOutcome.SkippedShuttingDown;
    }
    try {
      if (!this.localDb || !this.remoteDb) {
        console.log(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync skipped — no local/remote db`);
        return PushToRemoteOutcome.SkippedNoDatabase;
      }
      console.log(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync starting one-shot push`);
      // BOTH fields are load-bearing. `awaitSyncWithTimeout` reports a REJECTED replication
      // as `{ succeeded: false, timedOut: false }` — it turns the rejection into a
      // console.error and resolves — so reading `timedOut` alone would call a push that
      // failed on a dropped network or an expired session a completed one, which is exactly
      // the false "your program is safe on the server" this whole change removes.
      const { succeeded, timedOut } = await this.awaitSyncWithTimeout(
        this.localDb.replicate.to(this.remoteDb, { filter: this.replicationFilter.bind(this) }),
        timeoutMs,
      );
      if (timedOut) {
        console.error(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync timed out after ${timeoutMs}ms`);
        return PushToRemoteOutcome.TimedOut;
      }
      if (!succeeded) {
        console.error(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync failed`);
        return PushToRemoteOutcome.Failed;
      }
      console.log(`Db:${this.internalDatabaseIdentifier}: pushToRemoteAsync completed`);
      return PushToRemoteOutcome.Pushed;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  // How long the pre-switch one-shot locale pull may run before we give up. Same
  // pausing-while-hidden timeout the initial sync uses; a switch that cannot fetch
  // in this window is treated as a failure so the caller keeps the old language.
  static PULL_LOCALE_TEXT_TIMEOUT_MS = 60_000;

  /**
   * One-shot pull of a SINGLE language's `$text/` documents into the local
   * database (#4284). Run before a language switch reload so the app never comes
   * up in the new language with its documents missing — the English fallback is
   * deliberately silent, so a half-translated screen would be reported nowhere.
   *
   * Returns whether that language's documents are now on the device. Refuses
   * (returns false) when offline, when there is no remote db yet (a warm resume
   * that never connected), or mid-recreation. The caller then keeps the old
   * language and tells the user rather than switching into a half-English app.
   */
  async pullLocaleTextAsync(
    segment: TextLocaleSegment,
    timeoutMs = DatabaseWrapper.PULL_LOCALE_TEXT_TIMEOUT_MS,
  ): Promise<boolean> {
    if (this.onlineStateService.connectionState === ConnectionState.Offline) {
      console.log(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync skipped — offline`);
      return false;
    }
    if (!await this.waitForRecreationGate()) return false;
    if (!this.rwLock.acquireRead()) {
      console.log(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync skipped — shutting down`);
      return false;
    }
    try {
      if (!this.localDb || !this.remoteDb) {
        console.log(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync skipped — no local/remote db`);
        return false;
      }
      console.log(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync pulling $text/${segment}/`);
      // checkpoint on target (local): we can't write the source, and a local
      // checkpoint keeps this one-shot from re-walking if it is called again.
      // awaitSyncWithTimeout NEVER rejects — a rejected replication resolves as
      // { succeeded: false, timedOut: false } — so both flags must be checked, or
      // an online-but-failed pull would look like success and switch into a
      // half-English app.
      // #5688: a one-shot pull must be stopped the moment it fails, or PouchDB
      // keeps it running unseen and moves this checkpoint past documents it
      // skipped — a later retry of the switch would then find nothing to pull
      const { succeeded, timedOut } = await this.awaitSyncWithTimeout(
        cancelOneShotReplicationOnError(this.localDb.replicate.from(this.remoteDb, {
          checkpoint: 'target',
          selector: localeTextRangeSelector(segment),
          style: 'main_only',
        }), `Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync`),
        timeoutMs,
      );
      if (timedOut || !succeeded) {
        console.error(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync failed (timedOut=${timedOut}, succeeded=${!!succeeded})`);
        // record it like the timeout / catch paths do, so a failed switch is
        // debuggable from lastSyncError and not only from the caller's alert
        this.lastSyncError = { source: 'pullLocaleText', error: { message: timedOut ? `Timed out after ${timeoutMs}ms` : 'replication did not succeed' }, timestamp: DateTime.utc().toISO() };
        return false;
      }
      console.log(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync completed`);
      return true;
    } catch (err) {
      console.error(`Db:${this.internalDatabaseIdentifier}: pullLocaleTextAsync failed:`, err);
      this.lastSyncError = { source: 'pullLocaleText', error: this.serializeSyncError(err), timestamp: DateTime.utc().toISO() };
      return false;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  /**
   * On-demand sync: pull log entries for a specific date range from remote.
   * Used when navigating to a day outside the priority sync window before
   * background sync has completed. Fetches docs directly via allDocs (B-tree
   * index on _id) instead of replication to avoid scanning the entire changes feed.
   */
  async syncLogDateRangeAsync(centerDate: DateTime, radiusDays: number = 7): Promise<boolean> {
    if (!await this.waitForRecreationGate()) return false;
    if (!this.rwLock.acquireRead()) return false;
    try {
      if (!this.remoteDb || !this.localDb) return false;

      const startDate = centerDate.minus({ days: radiusDays }).toISODate();
      const endDate = centerDate.plus({ days: radiusDays + 1 }).toISODate();

      const logHeader = `Db:${this.internalDatabaseIdentifier}: [on-demand sync]`;
      const onDemandSyncFailureReportedKey = `onDemandLogSyncFailureReported_${this.internalDatabaseIdentifier}`;
      console.log(`${logHeader} Pulling log entries for ${startDate} to ${endDate}`);

      try {
        // fetch docs directly from remote using _id range (fast B-tree lookup)
        const result = await this.remoteDb.allDocs({
          startkey: `log/${startDate}`,
          endkey: `log/${endDate}\ufff0`,
          include_docs: true,
        });

        const docs = result.rows
          .filter(row => row.doc && !row.doc._deleted)
          .map(row => row.doc);

        if (docs.length > 0) {
          // write to local PouchDB without creating new revisions
          await this.localDb.bulkDocs(docs, { new_edits: false });
          console.log(`${logHeader} On-demand sync completed: ${docs.length} docs for ${startDate} to ${endDate}`);
        } else {
          console.log(`${logHeader} On-demand sync: no docs found for ${startDate} to ${endDate}`);
        }
        // a pull that worked ends any outage reported below, so the next one is reported again
        delete sessionStorage[onDemandSyncFailureReportedKey];
        return true;
      } catch (err) {
        console.log(`${logHeader} On-demand sync failed:`, err);
        // false leaves the range unmarked, so every later load of an old day retries. being
        // offline explains itself, and so does a local-store recreation closing the handle
        // under this pull (the recreation reports itself). anything else — a failure on a
        // working connection, or the local write refusing the pulled docs — is reported once
        // per outage, like SyncIntegrityCheckFailed (#5191)
        const failureClass = classifySyncFailure(err);
        const explained = failureClass === 'network' || this.recreationGate != null || this.rwLock.isShuttingDown;
        if (!explained && !sessionStorage[onDemandSyncFailureReportedKey]) {
          sessionStorage[onDemandSyncFailureReportedKey] = 'true';
          this.logging.trackSilentProblem('OnDemandLogSyncFailed', {
            db: this.internalDatabaseIdentifier,
            failureClass,
            ...errorReportFields(err),
          });
        }
        return false;
      }
    } finally {
      this.rwLock.releaseRead();
    }
  }

  /**
   * Force-push a specific document to the remote, bypassing PouchDB replication.
   * Reads the doc from local PouchDB and writes it straight to the remote
   * CouchDB over HTTP. Returns `{ pushed: true }` on success; on failure
   * `{ pushed: false }` plus a `failureClass` ('network' / 'http-<status>' /
   * 'other', see classifySyncFailure) so the checkpoint-drift report can say WHY
   * the heal failed (#4010 B4). Never throws.
   *
   * **How the write is sent depends on the case, and since #4407 Step 3 the two
   * dominant cases no longer re-parent it.** `resolveForcePushBody` returns a
   * `send` field saying which transport to use:
   *
   * - `preserve-local-rev` (the drift case, and creating a document the server
   *   does not have) — send the DEVICE's own revision with its history, via
   *   `bulkDocs({ new_edits: false })`, so the server grafts it onto the tree it
   *   already holds. This is the fix: writing the device's body onto the
   *   SERVER's revision made CouchDB start a rival chain, which split the
   *   document into a shown half and a hidden half. Measured in production over
   *   the 7 days to 2026-08-25, that split 508 of 658 landed repairs.
   * - `put-on-remote-rev` (bodies already equal, or a merged body) — the
   *   original `put` onto the remote's current `_rev`. Neither case ever split
   *   anything, and a merged body has no local revision to preserve.
   *
   * An earlier version of this comment said the direct PUT "avoids revision tree
   * incompatibilities between the custom SQLite adapter and CouchDB's
   * replication protocol". That claim was withdrawn in the same changelist as
   * the fix — see `design-docs/sync-integrity.md`, "Why PouchDB Replication
   * Doesn't Work for Push Recovery", which explains why the running app disproves
   * it. Do not reinstate it.
   *
   * #4407 Step 1: a write that lands can leave the document with a second, hidden
   * branch — see didForcePushFork. `trigger` says WHICH caller asked for the
   * push, so the per-save sites and the drift-recovery sweep can be told apart
   * in the telemetry. It is a parameter and not a return value on purpose: the
   * two per-save callers discard the result (`void ...`) and must keep doing so,
   * or a workout save would start waiting on a network round trip.
   */
  async forcePushDocAsync(docId: string, trigger: ForcePushTrigger = 'unknown'): Promise<ForcePushResult> {
    if (!this.localDb || !this.remoteDb) return { pushed: false, failureClass: 'other' };
    const logHeader = `Db:${this.internalDatabaseIdentifier}:`;
    // device-local docs must NEVER reach the server — this direct PUT bypasses
    // every replication filter, and exactly that leaked the poisoned
    // day-task-status cache to the server in #4219. the sampling in
    // verifyPushCheckpointIntegrityAsync already skips these ids, so this
    // guard should never fire; if it does, a new caller slipped in — report it.
    if (DEVICE_LOCAL_DOC_IDS.includes(docId)) {
      console.warn(`${logHeader} forcePushDocAsync refused device-local doc ${docId}`);
      this.logging.trackSilentProblem('ForcePushDeviceLocalDocBlocked', {
        db: this.internalDatabaseIdentifier, docId,
      });
      return { pushed: false, failureClass: 'other' };
    }
    // hoisted so the conflict re-check in the catch can compare against the local
    // generation, and so the merge decision below can read the remote body (#3987 A3)
    let localDoc: any;
    let remoteDoc: any;
    // #4407 Step 1: filled in only once the PUT has landed, and reported after
    // the try/catch below — see the comment at the report site for why it must
    // not be reported from inside it.
    let landedForkReport: { [key: string]: any } | null = null;
    // the revision history the preserving push needs. Kept in its own variable
    // and NOT left on localDoc, because localDoc is deep-cloned into the body the
    // put-on-remote-rev path sends, and a `_revisions` field on an ordinary write
    // is not something to hand CouchDB by accident.
    let localRevisions: { start: number, ids: string[] } | undefined;
    try {
      // ONE read, with the history. Reading twice (once for the decision, once
      // for the push) let a local save land in between, after which the pushed
      // revision was not the one local_gen reported and not the one the 409
      // re-check below compared against.
      const localDocWithHistory: any = await this.localDb.get(docId, { revs: true });
      localRevisions = localDocWithHistory._revisions;
      localDoc = { ...localDocWithHistory };
      delete localDoc._revisions;

      // get remote's current doc (its _rev for the update, its body for the
      // diverged-remote comparison)
      try {
        remoteDoc = await this.remoteDb.get(docId);
      } catch (err: any) {
        if (!isPouchNotFound(err)) throw err;
        // doc doesn't exist on remote yet — will create it
      }

      // #3987 A3 (defect D4): this used to be an unconditional overwrite of the
      // remote body with the local one — last-write-wins, clobbering a concurrent
      // other-device edit. resolveForcePushBody (pure, unit-tested) keeps the
      // overwrite only when the bodies are equal (a content no-op) and otherwise
      // applies a recency-aware merge (db__trans decides value ties, local wins
      // when recency is unknown — internal #42). Deep-cloning inside the helper avoids
      // mutating the identity-mapped local doc. The merged body is deliberately
      // NOT written back into the local identity map here — it returns on the
      // next pull and flows through applyPulledDocToCache normally.
      const { docToPut, mergedDivergedRemote, branch, send } = resolveForcePushBody(localDoc, remoteDoc ?? null);
      if (mergedDivergedRemote) {
        console.warn(`${logHeader} forcePushDocAsync: remote body for ${docId} diverged from local — applying recency-aware merge instead of overwrite`);
        if (!sessionStorage.forcePushMergedReported) {
          sessionStorage.forcePushMergedReported = '1';
          this.logging.trackSilentProblem('ForcePushMergedDivergedRemote', { docId });
        }
      }

      // #4407 Step 3 — how the body is SENT, which is the whole fix.
      //
      // The two branches that used to split the document (`remote-behind` and
      // `remote-absent`) now push the device's own revision together with its
      // history, so the server grafts it onto the one tree it already holds
      // instead of starting a rival chain beside it. The other two branches are
      // unchanged: neither ever split anything, and the merged branch could not
      // use this path anyway — its body is a genuinely new write, so there is no
      // local revision to preserve.
      //
      // `usedSend` is what actually happened, which is not always what the
      // decision asked for: the history check below can send a
      // 'preserve-local-rev' decision down the old path.
      let usedSend = send;

      // if the local history does not reach the revision the server is on, a
      // new_edits:false push would be stored as a SECOND branch and reported as a
      // success — exactly the failure this step removes. Fall back to the old
      // re-parenting write, which at least repairs the drift, and label it so the
      // case is countable rather than invisible.
      if (send === 'preserve-local-rev' && !localHistoryReachesRemoteRev(localRevisions, remoteDoc?._rev)) {
        usedSend = 'put-on-remote-rev';
        // re-parent onto the server's revision, which is what the old code did
        // unconditionally. The remote-absent branch never reaches here — it has no
        // remote revision, so the check answers true for it — but the guard keeps
        // that from turning into an accidental `_rev: undefined` if a later branch
        // is added to the preserving path.
        if (remoteDoc?._rev) docToPut._rev = remoteDoc._rev;
        console.log(`${logHeader} forcePushDocAsync: local history for ${docId} does not reach remote rev — falling back to the re-parenting write`);
      }

      if (usedSend === 'preserve-local-rev') {
        // new_edits:false asks CouchDB to store these revisions as given rather
        // than mint a new one. The body sent is the locally-read one WITH its
        // history — not docToPut, which serves the put path and on the
        // remote-absent branch carries no _rev at all, which CouchDB refuses here.
        const bulkResult = await this.remoteDb.bulkDocs([{ ...localDoc, _revisions: localRevisions }], { new_edits: false });
        // NOTE the return shape is inverted from an ordinary bulkDocs: it answers
        // with ERROR ROWS ONLY, so success is an EMPTY array and any row at all is
        // a failure. Anything that is NOT an empty array is treated as a failure,
        // including a shape we do not recognise: answering `pushed: true` on a
        // refused write would leave the drift in place with nothing saying so, and
        // would stop the drift sweep retrying it. Throwing routes it into the
        // catch below, which reports { pushed: false } with a failureClass.
        if (!Array.isArray(bulkResult) || bulkResult.length > 0) {
          const firstFailure: any = Array.isArray(bulkResult) ? bulkResult[0] : bulkResult;
          throw Object.assign(
            new Error(`forcePushDocAsync: revision-preserving push refused for ${docId}: ${describeError(firstFailure)}`),
            { status: firstFailure?.status, name: firstFailure?.name ?? firstFailure?.error },
          );
        }
      } else {
        await this.remoteDb.put(docToPut);
      }
      console.log(`${logHeader} forcePushDocAsync completed for ${docId}`);
      // #4407 Step 1: gather what the sync_force_push event needs, HERE, where
      // the decision and both revisions are still in scope — but report it after
      // the try/catch, never inside it. See didForcePushFork in
      // database-wrapper-force-push.ts for what a fork is and why only two of
      // the four cases count as one.
      landedForkReport = {
        // #4407 Step 3: on the preserving path the server now holds the DEVICE's
        // own revision, so there is one tree and no second branch — that is
        // provable from what was sent rather than inferred, and there is no
        // "parent revision" for didForcePushFork to reason about. The old rule
        // still governs the PUT path, including the history-gap fall-back above.
        //
        // `forked` means THIS WRITE CAUSED a fork. A document that already had
        // conflicting revisions before this push still reports false.
        forked: usedSend === 'preserve-local-rev'
          ? false
          : didForcePushFork(localDoc._rev, docToPut._rev),
        branch,
        // what the send actually was, which differs from the decision's when the
        // history check refused. Without this the fall-back would be invisible.
        send: usedSend,
        doc_type: docTypeFromId(docId),
        local_gen: parseRevGeneration(localDoc._rev),
        // null, not 0, when the doc was absent from the server — "no remote" and
        // "remote at generation 0" must not read the same in the data
        remote_gen: remoteDoc?._rev ? parseRevGeneration(remoteDoc._rev) : null,
        trigger,
      };
    } catch (err: any) {
      // a 409 conflict here usually means the doc already reached the server by another path —
      // most often normal live-sync replication delivered it while this force-push was in
      // flight (the integrity check races the push it was triggered alongside). re-read the
      // remote and ask isCaughtUp409 (pure, unit-tested) whether the server has reached our
      // generation: if it has, there is no local-ahead drift left for this check to recover,
      // so this is NOT a failure (Sentry 3ZD race-409 false positive). That helper's own
      // comment carries the rest of the rule, including what it deliberately does not assert.
      // Only a genuinely-behind remote, a failed re-check, or a non-conflict error (e.g.
      // network loss) is a real failure.
      const isConflict = isPouchConflict(err);
      // why the re-check below could not settle the 409, carried into the drift report (#5191)
      let recheckFailureClass: SyncFailureClass | undefined;
      if (isConflict && localDoc) {
        try {
          const localGen = parseRevGeneration(localDoc._rev);
          const remoteDocAfterConflict = await this.remoteDb.get(docId);
          const remoteGen = parseRevGeneration(remoteDocAfterConflict._rev);
          if (isCaughtUp409(localGen, remoteGen)) {
            console.log(`${logHeader} forcePushDocAsync conflict for ${docId}, but remote is already at generation ${remoteGen} >= local ${localGen} — no local-ahead drift to recover`);
            return { pushed: true };
          }
          console.log(`${logHeader} forcePushDocAsync conflict for ${docId} and remote still behind (remote ${remoteGen} < local ${localGen})`);
        } catch (recheckErr) {
          console.log(`${logHeader} forcePushDocAsync conflict re-check failed for ${docId}:`, recheckErr);
          recheckFailureClass = classifySyncFailure(recheckErr);
        }
      }
      console.log(`${logHeader} forcePushDocAsync failed for ${docId}:`, err);
      return { pushed: false, failureClass: classifySyncFailure(err), recheckFailureClass };
    }

    // #4407 Step 1 — report the fork measurement.
    //
    // OUTSIDE the try/catch on purpose, and this placement is load-bearing. By
    // the time the report is built the PUT has already succeeded. Reporting from
    // inside the try would mean that a throw anywhere in trackEvent lands in the
    // catch above, is not a 409, and makes this method answer { pushed: false }
    // for a write that landed. The drift-recovery caller would then record a
    // false heal failure and force-push the same document again on the next
    // sweep — which is the very operation that splits a document. Telemetry must
    // not be able to break the thing it is measuring.
    //
    // Fired on BOTH outcomes, split and not split, so the rate has a
    // denominator; an event that only fired on a split would give a numerator
    // with nothing to divide it by. And fired ONLY where the PUT landed: a
    // refused PUT (the 409 re-check above, a network failure) writes nothing to
    // the server and so cannot split anything, so counting it would put a case
    // in the denominator that can never appear in the numerator.
    //
    // trackEvent, not trackSilentProblem: the latter writes a console.error,
    // which fails the E2E browser-error gate, and a split is not (yet) something
    // a human should be paged about.
    //
    // the null check is a type guard, not a real branch — every path through the
    // catch above returns, so reaching here means the PUT landed
    if (landedForkReport) this.telemetry?.trackEvent('sync_force_push', landedForkReport);
    return { pushed: true };
  }

  /**
   * Periodic integrity check: samples recent local docs and verifies they exist
   * on CouchDB with the same (or newer) revision, and force-pushes any that
   * drifted.
   *
   * The body of this lives in `database-wrapper-push-integrity.ts`. Everything it
   * needs is handed over as an explicit context object rather than reached for
   * through `this`, so the check can be read — and unit-tested — without the
   * whole document-access API around it. Every gate, including the settle guard
   * and the cooldown, moved with it; this method only builds the context.
   */
  async verifyPushCheckpointIntegrityAsync(): Promise<void> {
    return verifyPushCheckpointIntegrityInternalAsync(DatabaseWrapper.buildPushIntegrityContext(this));
  }

  /**
   * The bridge between this class and the extracted push-integrity code.
   *
   * Every entry is a plain forward to a member of this class, so the check sees
   * exactly the values and behaviour it saw when it lived inside this file.
   * Reads are accessors rather than captured values because the check awaits
   * several times and the connection state, the sync error and the database
   * handles can all change underneath it.
   *
   * The length of this list is not an accident to be tidied away — it is the
   * measured size of the coupling between the replication engine and the
   * document store. See deferred finding `df-3c79746add23`.
   */
  private static buildPushIntegrityContext(wrapper: DatabaseWrapper): PushIntegrityContext {
    return {
      // ---- state the check only reads ----
      get localDb() { return wrapper.localDb; },
      get remoteDb() { return wrapper.remoteDb; },
      get internalDatabaseIdentifier() { return wrapper.internalDatabaseIdentifier; },
      get databaseIsReady() { return wrapper.dbProvider.databaseStates[wrapper.internalDatabaseIdentifier] === DatabaseState.Ready; },
      get lastSyncActivityTime_ms() { return wrapper.lastSyncActivityTime_ms; },
      get connectionStateIsOnline() { return wrapper.onlineStateService.connectionState === ConnectionState.Online; },
      get connectionState() { return wrapper.onlineStateService.connectionState; },
      get lastSyncError() { return wrapper.lastSyncError; },
      deviceLocalDocIds: DEVICE_LOCAL_DOC_IDS,
      get telemetry() { return wrapper.telemetry; },
      get logging() { return wrapper.logging; },

      // ---- state the check also writes back ----
      get checkpointSettleSkips() { return wrapper.checkpointSettleSkips; },
      set checkpointSettleSkips(value) { wrapper.checkpointSettleSkips = value; },

      // ---- things it asks this class to do ----
      isUserDb: () => wrapper.isUserDb(),
      forcePushDocAsync: (docId: string, trigger: ForcePushTrigger) => wrapper.forcePushDocAsync(docId, trigger),
      hasRecentNetworkSyncError: () => wrapper.hasRecentNetworkSyncError(),
    };
  }

  /**
   * Pull-side resume catch-up (#3862). The pull half of the live `db.sync` is a long-lived
   * CouchDB `_changes` longpoll that can die silently on mobile across background /
   * network-suspend cycles: no `error`/`denied`/`'Load failed'` event fires, so none of the
   * auto-restart handlers trigger, and the device stops receiving other devices' changes while
   * its own pushes keep working (push is driven by the local changes feed + forcePushDocAsync).
   * `ensureSyncActive()` skips a sync that *looks* healthy, which is exactly this state — so
   * there is no pull-side equivalent to `verifyPushCheckpointIntegrityAsync`. This closes that
   * gap in two steps:
   *
   *  1. Fetch the user's recent data directly from the server (checkpoint-INDEPENDENT, so it
   *     works even if the live pull's checkpoint is stuck or its connection is dead) and graft
   *     in anything missing — bringing the device up to date immediately.
   *  2. If anything was missing, that is proof the live pull was stuck, so restart the live
   *     sync to rebuild a fresh download connection for the rest of the session. If nothing was
   *     missing, the sync was healthy — leave it untouched (no needless churn).
   *
   * User DB only, steady-state live sync only, cooldown-gated. Never throws. Called from the
   * resume/online path after `verifyPushCheckpointIntegrityAsync` + `ensureSyncsActive` — these
   * are all fire-and-forget (`void`), so the ordering is source-order, not awaited. That is fine
   * here: the catch-up issues NO push (it only grafts docs already on the server and restarts the
   * mutex-serialized live sync), so it cannot create the push 409 race the push check guards.
   *
   * The body of this lives in `database-wrapper-pull-catchup.ts`. Everything it
   * needs is handed over as an explicit context object rather than reached for
   * through `this`, so the catch-up can be read — and unit-tested — without the
   * whole document-access API around it. Every gate, including the two early
   * returns above, moved with it; this method only builds the context.
   */
  async ensurePullCatchupAsync(): Promise<PullCatchupStats> {
    return ensurePullCatchupInternalAsync(DatabaseWrapper.buildPullCatchupContext(this));
  }

  /**
   * The bridge between this class and the extracted pull-catchup code.
   *
   * Every entry is a plain forward to a member of this class, so the catch-up
   * sees exactly the values and behaviour it saw when it lived inside this file.
   * Reads are accessors rather than captured values because the catch-up awaits
   * several times and a database recreation may null `localDb` / `remoteDb`
   * underneath it — the code re-reads them for exactly that reason.
   *
   * The length of this list is not an accident to be tidied away — it is the
   * measured size of the coupling between the replication engine and the
   * document store, and it shrinks only when a later slice moves more of the
   * engine out. See deferred finding `df-3c79746add23`.
   */
  private static buildPullCatchupContext(wrapper: DatabaseWrapper): PullCatchupContext {
    return {
      // ---- state the catch-up only reads ----
      get temporaryDb() { return wrapper.temporaryDb; },
      get remoteDb() { return wrapper.remoteDb; },
      get localDb() { return wrapper.localDb; },
      get internalDatabaseIdentifier() { return wrapper.internalDatabaseIdentifier; },
      get initialSyncCompleted() { return wrapper.initialSyncCompleted; },
      get syncHandler() { return wrapper.syncHandler; },
      get syncHandlerIsInitial() { return wrapper.syncHandlerIsInitial; },
      get connectionStateIsOnline() { return wrapper.onlineStateService.connectionState === ConnectionState.Online; },
      get connectionState() { return wrapper.onlineStateService.connectionState; },
      get logging() { return wrapper.logging; },
      get databaseRecovery() { return wrapper.databaseRecovery; },
      get telemetry() { return wrapper.telemetry; },

      // ---- constants that live on this class ----
      pullCatchupCooldownMs: DatabaseWrapper.PULL_CATCHUP_COOLDOWN_MS,
      getPrioritySyncKeyRanges: () => DatabaseWrapper.getPrioritySyncKeyRanges(),

      // ---- things it asks this class to do ----
      isUserDb: () => wrapper.isUserDb(),
      waitForRecreationGateAsync: () => wrapper.waitForRecreationGate(),
      acquireReadLock: () => wrapper.rwLock.acquireRead(),
      releaseReadLock: () => wrapper.rwLock.releaseRead(),
      replicationFilter: (doc: PouchDoc) => wrapper.replicationFilter(doc),
      collectAppliedPulledDoc: (doc: any, listenerDocsOut: any[]) => wrapper.collectAppliedPulledDoc(doc, listenerDocsOut),
      deliverPulledDocsToListeners: (docs: any[]) => wrapper.deliverPulledDocsToListeners(docs),
      hasRecentNetworkSyncError: () => wrapper.hasRecentNetworkSyncError(),
      stringifiedLastSyncError: () => wrapper.stringifiedLastSyncError(),
      cancelSync: () => wrapper.cancelSync(),
      startSync: (initial: boolean) => wrapper.startSync(initial),
    };
  }

  // How often one device runs the conflict-canary scan (#3987 B3) now lives in
  // sync-conflict-resolve-decisions.ts as CONFLICT_SCAN_COOLDOWN_HOURS, beside the pure
  // decideConflictScanRun that applies it — the cadence gained a second way in
  // (#4314's resume marker), and that decision is unit-tested rather than inline.

  /**
   * Conflict canary (#3987 B3, audit risk R2): measure how often user DBs
   * actually hold PouchDB `_conflicts` leaves BEFORE any auto-resolution is
   * designed — prevalence is completely unmeasured today. Scans the LOCAL db
   * once per day per device (localStorage cooldown — per-device cadence, not
   * per-session) over the same three priority-window key ranges the pull
   * catch-up uses (shared helper, so the two cannot drift apart). Always fires
   * the Mixpanel volume metric `sync_conflict_scan`, ALSO when the count is 0
   * — the zero baseline is the point — plus a once-per-session Sentry signal
   * when conflicts exist.
   *
   * NOTE: local `_conflicts` only shows conflicts this device has replicated.
   * That is exactly the actionable population — server-only conflict branches
   * are invisible to clients and are Phase C's concern (see
   * design-docs/sync-integrity.md).
   *
   * Called fire-and-forget from the foreground health tick (A5) after the
   * push/pull checks — never awaited on any user-facing path.
   */
  async scanForConflictsAsync(autoResolveEnabled = false, archiveRestoreEnabled = false): Promise<void> {
    // same impersonation guard as ensurePullCatchupAsync: reads route to
    // temporaryDb during coach/admin impersonation, so a scan here would
    // sample the admin's own local DB and mislabel the telemetry
    if (this.temporaryDb) return;
    if (!this.isUserDb() || !this.localDb) return;
    const dbState = this.dbProvider.databaseStates[this.internalDatabaseIdentifier];
    if (dbState !== DatabaseState.Ready) return;

    // once per day per device, OR sooner when a previous drain stopped with work
    // left and made progress (#4314 R6). Both keys are localStorage, not
    // sessionStorage, so a reload does not reset the cadence, and both are
    // deliberately NOT in the signOut allow-list (local-storage-hygiene.ts):
    // they describe the signed-in user's own database, so a different account on
    // the same device should start fresh rather than inherit either one.
    const runDecision = decideConflictScanRun(
      localStorage.lastConflictScan_utc,
      localStorage.conflictDrainResumeAfter_utc,
      DateTime.utc().toMillis(),
    );
    if (!runDecision.shouldRun) return;

    // Clear the resume marker NOW, the moment its decision has been consumed.
    // Only a run that ends capped-with-progress sets it again. Clearing it later
    // — say, on the branch where no drain ran — would leave it stranded whenever
    // something throws in between, and while it is set every six-minute tick
    // re-runs the whole enumerating sweep. Clearing it first makes that
    // impossible regardless of what fails afterwards: the worst case is one
    // continuation that does not happen, and the daily cadence still covers it.
    delete localStorage.conflictDrainResumeAfter_utc;

    // guard the multi-await scan against a concurrent localDb recreation —
    // same protection ensurePullCatchupAsync uses
    if (!await this.waitForRecreationGate()) return;
    if (!this.rwLock.acquireRead()) return;
    const logHeader = `Db:${this.internalDatabaseIdentifier}: [conflict scan]`;
    try {
      if (!this.localDb) return; // recreation may have nulled it while we waited

      // read the priority window locally, asking PouchDB to surface conflict leaves.
      //
      // This sweep is charged to its OWN allowance and never to the resolver's
      // work budget (#4314 R6b.1). Charging it to the budget it feeds would
      // deadlock the drain: on a heavy account the sweep alone would exhaust the
      // budget, the run would resolve nothing, and the no-progress rule would
      // then back the device off for a day — every day, permanently, on exactly
      // the accounts that most need draining.
      const enumerationStartedMs = DateTime.utc().toMillis();
      const rows: { doc?: any }[] = [];
      for (const range of DatabaseWrapper.getPrioritySyncKeyRanges()) {
        const result = await this.localDb.allDocs({ ...range, include_docs: true, conflicts: true });
        rows.push(...result.rows);
      }
      const enumerationMs = DateTime.utc().toMillis() - enumerationStartedMs;
      if (enumerationMs > CONFLICT_RESOLVE_ENUMERATION_BUDGET_MS) {
        // not fatal — the resolver still gets its full budget and its
        // minimum-one-document floor — but a sweep this slow is worth knowing about
        console.log(`${logHeader} enumeration took ${enumerationMs}ms, over its ${CONFLICT_RESOLVE_ENUMERATION_BUDGET_MS}ms allowance`);
      }

      // record the scan time now — a clean zero-conflict scan still counts for the cooldown
      localStorage.lastConflictScan_utc = DateTime.utc().toISO();

      // count via the pure helper (skips _design/ rows and unconflicted docs)
      const summary = summarizeConflictScan(rows);
      console.log(`${logHeader} ${summary.conflictedDocCount} conflicted doc(s), ${summary.totalConflictRevs} conflict rev(s) across ${rows.length} row(s)`);

      // fire the volume metric BEFORE resolving, so the number describes the
      // backlog this run found rather than what it left behind — and only once
      // per daily cycle. A continuation must not re-fire it as a fresh daily
      // sample: it is a once-per-device-per-day metric and the ramp's cohort
      // comparison depends on that cadence (#4314 R6).
      if (!runDecision.isContinuation) {
        this.telemetry?.trackEvent('sync_conflict_scan', {
          conflicted_doc_count: summary.conflictedDocCount,
          doc_types: summary.docTypes.join(','),
          total_conflict_revs: summary.totalConflictRevs,
          // without this the drain cannot be split by cohort, and measuring the
          // ramp is the whole point of gating it
          auto_resolve_enabled: autoResolveEnabled,
        });
      }
      // once-per-session Sentry signal only when something is actually conflicted
      if (summary.conflictedDocCount > 0 && !sessionStorage.conflictScanReported) {
        sessionStorage.conflictScanReported = '1';
        this.logging.trackSilentInfo('UserDbConflictsPresent', {
          // stringify so Sentry doesn't normalize the array to "[Object]"
          conflictedDocs: JSON.stringify(summary.docSummaries),
        });
      }

      // #6847 — restore closed log entries from this device's own conflict archive BEFORE the
      // resolver runs: its archive sweep is what would delete them. Whether or not anything is
      // conflicted right now, because the archive holds closes from earlier runs.
      if (archiveRestoreEnabled) {
        await this.restoreFromConflictArchiveAsync(logHeader);
      }

      // #4314 Phase C — drain the backlog, still inside the rwLock read section
      if (autoResolveEnabled && summary.conflictedDocCount > 0) {
        await this.resolveConflictedDocsAsync(summary.docSummaries.map(entry => entry.id), logHeader);
      }
    } catch (err) {
      // the canary must never break the health tick, but its failure must not
      // be silent either — gate the report once per session (the tick re-runs
      // every 6 minutes, and the cooldown was deliberately NOT written on the
      // failure path, so a persistent failure would otherwise report each tick)
      console.error(`${logHeader} failed:`, (err as any)?.message ?? err);
      if (!sessionStorage.conflictScanFailedReported) {
        sessionStorage.conflictScanFailedReported = '1';
        this.logging.trackSilentProblem('ConflictScanFailed', {
          db: this.internalDatabaseIdentifier,
          name: (err as any)?.name,
          message: (err as any)?.message,
        });
      }
    } finally {
      this.rwLock.releaseRead();
    }
  }


  /**
   * #4314 Phase C — drain this device's conflict backlog.
   *
   * Called from inside `scanForConflictsAsync`'s `rwLock` read section, so the
   * lock order matches `saveRecordAsync`'s (read lock, then the write mutex per
   * document) and cannot invert. The resolver itself lives in the pass-through
   * module and is dependency-injected; everything database-shaped is supplied
   * here.
   *
   * Never throws — the caller's catch already surfaces a scan failure, and a
   * drain failure must not break the health tick.
   */
  private async resolveConflictedDocsAsync(candidateIds: string[], logHeader: string): Promise<void> {
    if (!this.localDb) return;
    const failedStages: { docId: string, stage: string, error: string }[] = [];
    // #6415: losing revisions whose stored content is gone, found by the run
    // (closed on a never-synced document, left in place on a synced one).
    // Reported apart from failedStages so they cannot hide a real failure.
    const bodyMissingRevs: { docId: string, rev: string, outcome: MissingBodyOutcome }[] = [];

    // resolved per call, never captured once: conflictWorkDbHandle says why
    const db = this.conflictWorkDbHandle();

    const result = await resolveDocConflictsAsync({
      getWithConflictsAsync: (docId: string) => db().get(docId, { conflicts: true }) as Promise<any>,
      getRevisionAsync: (docId: string, rev: string) => db().get(docId, { rev }) as Promise<any>,
      putDocAsync: async (doc: any) => {
        const response: any = await db().put(doc);
        return { rev: response.rev };
      },
      // ONE write per losing revision, never a batch holding several revisions
      // of the same document — see the resolver's own comment for why that shape
      // is kept (it is a deliberate choice, not a workaround; #4431 measured the
      // native-adapter trap it was first written for and found it does not bite
      // here).
      deleteRevisionAsync: async (docId: string, rev: string, transactionStamp: string | undefined) => {
        // Mark it as a BRANCH close, not a document deletion. The tombstone
        // replicates, and on the user's other device the pull handler evicts the
        // identity-map entry for any deleted document it receives — which would
        // be wrong here, because closing a losing branch leaves the document
        // itself alive (its winner is untouched). Without the marker the other
        // device drops the canonical object for a LIVE document while pages may
        // still hold the old one, and the next read registers a second object
        // for the same id — the identity-map violation this class reports as a
        // silent problem. The marker travels because a deletion keeps the fields
        // written with it.
        const tombstone: any = { _id: docId, _rev: rev, _deleted: true, db__conflictClose: true };
        // stamp the tombstone, which purgeDatabaseAsync deliberately does not do
        // for its conflict-rev rows. This is an improvement on that precedent,
        // not a copy of it.
        if (transactionStamp) tombstone.db__trans = transactionStamp;
        const response: any = await db().put(tombstone);
        return response?.rev;
      },
      // the identity map holds WeakRefs, so a live ref means something in the
      // app still references this document — usually a long-lived cache, not
      // only the page on screen (#5842). The resolver needs its revision, not
      // just whether it exists: a held copy on the winner can still have its
      // losing branches closed.
      heldCopy: (docId: string) => this.heldConflictCopy(docId),
      withWriteLock: <T>(work: () => Promise<T>) => this.writeMutex.exclusiveCall(work),
      archive: this.conflictArchiveDeps(db, logHeader),
      readRunConditions: () => this.conflictWorkRunConditions(),
      onWinnerWritten: (docId: string, newRev: string, mergedBody: any) => this.adoptLocallyWrittenWinner(docId, newRev, mergedBody),
      onRevisionClosed: (docId: string, rev: string) => {
        this.recordLocallyAuthoredRev(docId, rev);
      },
      nowMs: () => DateTime.utc().toMillis(),
      yieldAsync: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
      deviceId: getDeviceId(),
      logError: (message: string) => console.error(`${logHeader} ${message}`),
      reportFailure: (docId: string, stage: string, err: unknown) => {
        failedStages.push({ docId, stage, error: describeError(err) });
        console.error(`${logHeader} ${docId} failed at ${stage}: ${describeErrorForLog(err)}`);
      },
      reportMissingBody: (docId: string, rev: string, outcome: MissingBodyOutcome) => {
        bodyMissingRevs.push({ docId, rev, outcome });
        console.warn(`${logHeader} ${docId} losing revision ${rev} has no stored content (${outcome})`);
      },
      // only a document that never leaves the device may have a body-missing
      // revision closed: on a synced one the server may still hold the content
      neverSyncs: (docId: string) => DEVICE_LOCAL_DOC_IDS.includes(docId),
    }, candidateIds);

    console.log(`${logHeader} resolved ${result.resolvedDocCount} doc(s), closed ${result.deletedRevCount} rev(s), ${result.documentsRemaining} remaining`);

    // a run that stopped with work left AND made progress resumes on the next
    // six-minute tick; a run that resolved nothing leaves no marker and falls
    // back to the daily cooldown, so progress-or-back-off holds and an infinite
    // retry loop is not reachable (#4314 R6)
    // the scan already cleared any previous marker, so this only ever SETS one
    if (result.documentsRemaining > 0 && result.madeProgress) {
      localStorage.conflictDrainResumeAfter_utc = DateTime.utc().toISO();
    }

    // only report a run that actually did something — a no-op run every day on
    // every device would swamp the numbers this is measured with
    // the archive counters are included in the emission condition on purpose: the
    // sweep runs on EVERY drain, so a run where every candidate turned out to be
    // already resolved by sync would otherwise report nothing at all — dropping
    // exactly the within-retention eviction alarm that says the sizing is wrong
    if (result.resolvedDocCount > 0 || result.failedDocCount > 0 || result.deferredOpenDocCount > 0
      || result.missingBodyRevCount > 0
      || result.archiveBackstopEvictedCount > 0 || result.archiveEvictedWithinRetentionCount > 0
      || result.archiveFailureCount > 0) {
      this.telemetry?.trackEvent('sync_conflicts_resolved', {
        resolved_doc_count: result.resolvedDocCount,
        merged_doc_count: result.mergedDocCount,
        closed_without_merge_count: result.closedWithoutMergeCount,
        winner_changed_count: result.winnerChangedCount,
        closed_g1_count: result.closedG1Count,
        closed_g2_count: result.closedG2Count,
        closed_g3_count: result.closedG3Count,
        missing_stamp_count: result.missingStampCount,
        adopted_higher_rank_count: result.adoptedHigherRankCount,
        deleted_rev_count: result.deletedRevCount,
        archived_count: result.archivedCount,
        failed_doc_count: result.failedDocCount,
        unreadable_rev_count: result.unreadableRevCount,
        missing_body_rev_count: result.missingBodyRevCount,
        deferred_open_doc_count: result.deferredOpenDocCount,
        archive_backstop_evicted_count: result.archiveBackstopEvictedCount,
        archive_evicted_within_retention_count: result.archiveEvictedWithinRetentionCount,
        archive_failure_count: result.archiveFailureCount,
        doc_types: result.docTypes.join(','),
        capped: result.capped,
        duration_ms: result.durationMs,
        wall_ms: result.wallMs,
        documents_remaining: result.documentsRemaining,
      });
    }

    // nothing is swallowed: any document that failed surfaces once per session
    const archiveFailureCount = result.archiveFailureCount;
    if ((failedStages.length > 0 || archiveFailureCount > 0) && !sessionStorage.conflictResolveFailedReported) {
      sessionStorage.conflictResolveFailedReported = '1';
      const first = failedStages[0];
      this.logging.trackSilentProblem('ConflictResolveFailed', {
        db: this.internalDatabaseIdentifier,
        failedDocCount: result.failedDocCount,
        archiveFailureCount,
        docId: first?.docId,
        stage: first?.stage ?? 'archive-sweep',
        error: first?.error,
      });
    }

    // #6415: a losing revision whose content was already gone on this device.
    // Problem tier, not info: the lost content is not repaired, and on a synced
    // document the revision is not even closed. The known cases date from before
    // the v1.31.5 #4417 fix; one on a later build would mean that fix is
    // incomplete. Once per session, like the failure canary above, and kept off
    // it so a real resolver failure still reaches Sentry on its own. Every entry
    // is listed, so a support person can find each document and revision.
    if (bodyMissingRevs.length > 0 && !sessionStorage.conflictLosingBodyMissingReported) {
      sessionStorage.conflictLosingBodyMissingReported = '1';
      this.logging.trackSilentProblem('ConflictLosingBodyMissing', {
        db: this.internalDatabaseIdentifier,
        count: bodyMissingRevs.length,
        closedCount: bodyMissingRevs.filter(entry => entry.outcome === MissingBodyOutcome.Closed).length,
        docId: bodyMissingRevs[0].docId,
        revisions: bodyMissingRevs.map(entry => `${entry.docId}@${entry.rev}:${entry.outcome}`).join(','),
      });
    }
  }

  /**
   * #6847 — restore log entries the conflict resolver closed, from this device's own archive.
   *
   * Called from inside `scanForConflictsAsync`'s `rwLock` read section, BEFORE the resolver,
   * whose archive sweep would otherwise delete the copies first. The step lives in
   * database-wrapper-conflict-archive-restore.ts; everything database-shaped is supplied here,
   * through the same handles the resolver uses.
   *
   * Never throws — the caller's catch already surfaces a scan failure.
   */
  private async restoreFromConflictArchiveAsync(logHeader: string): Promise<void> {
    if (!this.localDb) return;
    const failedStages: { docId: string, stage: string, error: string }[] = [];
    const db = this.conflictWorkDbHandle();

    const result = await restoreFromConflictArchiveAsync({
      archive: this.conflictArchiveDeps(db, logHeader),
      readLeavesAsync: (docId: string) => db().get(docId, { open_revs: 'all', revs: true }) as Promise<any>,
      putDocAsync: async (doc: any) => {
        const response: any = await db().put(doc);
        return { rev: response.rev };
      },
      heldCopy: (docId: string) => this.heldConflictCopy(docId),
      withWriteLock: <T>(work: () => Promise<T>) => this.writeMutex.exclusiveCall(work),
      readRunConditions: () => this.conflictWorkRunConditions(),
      onWinnerWritten: (docId: string, newRev: string, body: any) => this.adoptLocallyWrittenWinner(docId, newRev, body),
      nowMs: () => DateTime.utc().toMillis(),
      yieldAsync: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
      deviceId: getDeviceId(),
      reportFailure: (docId: string, stage: string, err: unknown) => {
        failedStages.push({ docId, stage, error: describeError(err) });
        console.error(`${logHeader} [archive restore] ${describeRestoreFailure(docId, stage, err)}`);
      },
    });

    console.log(`${logHeader} [archive restore] judged ${result.judgedRowCount} archived cop(ies), restored ${result.restoredDocCount} entr(ies), ${result.pendingRowCount} left`);

    // rows left unjudged after a pass that made progress continue on the next six-minute tick,
    // through the resolver's own resume marker; a pass that judged nothing waits a day
    if (result.pendingRowCount > 0 && result.madeProgress) {
      localStorage.conflictDrainResumeAfter_utc = DateTime.utc().toISO();
    }

    // only a pass that did something is reported, so a judged archive does not send an event a day
    if (result.judgedRowCount > 0 || result.deferredDocCount > 0 || failedStages.length > 0) {
      this.telemetry?.trackEvent('sync_conflict_archive_restore', {
        judged_row_count: result.judgedRowCount,
        restored_count: result.restoredDocCount,
        merge_shape_count: result.mergeShapeDocCount,
        report_only_count: result.reportOnlyDocCount,
        not_damaged_count: result.notDamagedDocCount,
        unreadable_count: result.unreadableDocCount,
        other_count: result.otherDocCount,
        deferred_count: result.deferredDocCount,
        failed_count: failedStages.length,
        pending_rows: result.pendingRowCount,
        restored_doc_types: result.restoredDocTypes.join(','),
        engine: usesSqliteEngine() ? 'sqlite' : 'indexeddb',
        duration_ms: result.durationMs,
      });
    }

    // nothing is swallowed: a failed read or write surfaces once per session
    if (failedStages.length > 0 && !sessionStorage.conflictArchiveRestoreFailedReported) {
      sessionStorage.conflictArchiveRestoreFailedReported = '1';
      const first = failedStages[0];
      this.logging.trackSilentProblem('ConflictArchiveRestoreFailed', {
        db: this.internalDatabaseIdentifier,
        failedCount: failedStages.length,
        docId: first.docId,
        stage: first.stage,
        error: first.error,
      });
    }
  }

  /**
   * The local database for conflict work (#4314, #6847), resolved per call rather than captured
   * once. A recreation swaps in a NEW PouchDB instance and closes the old one without ever
   * nulling the field, so a captured handle would keep a whole run issuing reads and writes
   * against a closed database — safe (the writes fail, so nothing is deleted) but a pointless
   * burst of failures instead of a stop.
   */
  private conflictWorkDbHandle(): () => DbHandle {
    return () => {
      const current = this.localDb;
      if (!current) throw new Error('local database is gone');
      return current;
    };
  }

  /** The conflict archive's `_local/` calls (#4314), shared by the resolver and the #6847 restore step. */
  private conflictArchiveDeps(db: () => DbHandle, logHeader: string): ConflictArchiveDeps {
    return {
      getLocal: (id: string) => db().get(id) as Promise<any>,
      putLocal: (doc: any) => db().put(doc) as Promise<any>,
      removeLocal: async (id: string) => {
        const existing: any = await db().get(id);
        return db().remove(existing);
      },
      logError: (message: string) => console.error(`${logHeader} ${message}`),
    };
  }

  /**
   * The live copy the identity map holds of a document, for conflict work. The identity map holds
   * WeakRefs, so a live ref means something in the app still references this document — usually a
   * long-lived cache, not only the page on screen (#5842). Its revision matters, not just whether
   * it exists: a held copy on the winner can still have its losing branches closed.
   */
  private heldConflictCopy(docId: string): HeldCopy | undefined {
    const held = this.documentCache.get(docId)?.ref.deref();
    return held ? { rev: held._rev } : undefined;
  }

  /** The abort conditions a conflict run re-checks between documents (#4314 R6b.4, #6847). */
  private conflictWorkRunConditions() {
    return {
      appVisible: !document.hidden,
      online: this.onlineStateService.connectionState === ConnectionState.Online,
      signedIn: this.isUserDb(),
      impersonating: !!this.temporaryDb,
      shuttingDown: this.rwLock.isShuttingDown,
      // recreateLocalDbAsync nulls localDb only once a shutdown has started — otherwise
      // it closes the old instance and assigns a new one — so the recreation GATE is the
      // only real signal
      databaseRecreating: !this.localDb || !!this.recreationGate,
    };
  }

  /**
   * After the resolver's merge or the #6847 restore wrote a new winner on this device: the
   * identity map must see the new revision, or the next save would collide with a rev it does not
   * know about. Pass the body carrying its POST-PUT _rev, which applyPulledDocToCache needs to
   * match generations.
   */
  private adoptLocallyWrittenWinner(docId: string, newRev: string, body: any): void {
    const applied = this.applyPulledDocToCache({ ...body, _rev: newRev });
    // one doc at a time here, so deliver it straight away
    if (applied.undeliveredListenerDoc) {
      this.deliverPulledDocsToListeners([applied.undeliveredListenerDoc]);
    }
    this.recordLocallyAuthoredRev(docId, newRev);
  }

  /**
   * Which revisions the user-db PUSH offers the server (#4433).
   *
   * A document that has forked keeps several competing revisions, called
   * branches. Only one of them is the winner — the one the app shows. The
   * changes feed's `style` decides which of a document's revisions the push
   * mentions to the server at all:
   *
   *  - `main_only` mentions ONLY the winning revision.
   *  - `all_docs` mentions every branch tip. This is PouchDB's own default.
   *
   * The rest of the sync deliberately uses `main_only`, which is a speed
   * measure. The push cannot, and this is the reason: when the conflict
   * auto-resolve (#4314) closes a losing branch, it does that by writing a
   * deletion marker — a tombstone — ON that losing branch. A deleted revision
   * can never be a winner while a live one exists, so under `main_only` the
   * tombstone is never mentioned, never asked about, and never sent. The branch
   * would close on the device and stay open on the server for ever, which is
   * exactly the pile-up #4314 exists to drain.
   *
   * Read by `client/src/e2e/repro/conflict-auto-resolve-4314.spec.ts`, so the
   * test drives the real setting instead of a copy that could drift from it.
   */
  static readonly USER_DB_PUSH_CHANGES_STYLE = 'all_docs';

  // used as callback in all replications
  replicationFilter(doc: PouchDoc) {
    // don't sync design docs that are only needed server-side
    if (doc._id === '_design/view' || doc._id === '_design/probe') return false;
    // don't sync device-local docs: each device keeps its own copy (#4212 —
    // syncing the derived day-task-status cache produced daily conflict
    // branches and let one sick device poison every other device's calendar)
    if (DEVICE_LOCAL_DOC_IDS.includes(doc._id)) return false;
    return true;
  }

  /**
   * Start a sync.
   *
   * The boolean form is kept because every existing caller means one of two
   * things by it, and both map cleanly: `true` = "run the initial sync from the
   * beginning", `false` = "go live". Keeping it also makes the retry paths
   * correct for free — a recovery that calls `startSync(true)` re-enters at the
   * FIRST initial pass rather than resuming mid-sequence and skipping a tier.
   * Only the internal advance from one initial pass to the next names a phase.
   */
  async startSync(initialOrPhase: boolean | SyncPhase) {
    const phase = typeof initialOrPhase === 'boolean'
      ? (initialOrPhase ? SyncPhase.CalendarWindow : SyncPhase.Live)
      : initialOrPhase;
    // Backstop for the poisoned-wrapper state (see `localDbIsStaleAfterRestore`). `initializeRemoteDb`
    // already refuses, but sync has several entry points (ensureSyncActive, the online-state handler,
    // resume) and only ONE of them needs to slip through to overwrite a restore that succeeded. Guard
    // the funnel they all pass through, not just the door we happen to know about.
    if (this.localDbIsStaleAfterRestore) {
      console.error(`Db:${this.internalDatabaseIdentifier}: refusing to sync — the local db is stale after a restore and would overwrite it`);
      return;
    }

    // The wrapper is being torn down, so there is nobody left to serve the data this
    // sync would fetch. Guarded HERE, at the funnel, for the same reason as the line
    // above: sync has several entry points and every one of them tests `remoteDb` at
    // most. That test cannot see this state — `shutdownAsync` nulls the handles only
    // at the very end, after an unbounded wait for in-flight readers, so for the whole
    // of that wait both handles are still set and every one of those callers sails
    // through. The initial-sync retry loop is the caller that really does it: its
    // 60-second wait can elapse inside that window, and its own shutdown test reads
    // `localDb`, which is nulled just as late.
    //
    // What it costs to miss: `shutdownAsync` runs its one `cancelSync()` BEFORE the
    // wait, so a replication started after that is one nothing will ever cancel, and
    // it goes on running for the rest of the page's life (df-5cfce29dea04).
    //
    // `isShuttingDown` is true from the instant the shutdown starts and is never
    // cleared. See design-docs/pass-through-db.md, "Which databases a sweep may act
    // on", which makes this same argument for the provider's sweeps.
    if (this.isShuttingDown) {
      console.log(`Db:${this.internalDatabaseIdentifier}: not starting a sync — the database is shutting down`);
      this.logging.trackSilentInfo('StartSyncSkippedForShuttingDownDb', {
        db: this.internalDatabaseIdentifier,
        phase,
        where: 'start-sync',
      });
      return;
    }

    // Serialize through syncMutex so the `syncHandler` check-and-assign below is
    // atomic (the assign happens only at the very end, after several awaits).
    // Every in-body re-entrant call is fire-and-forget `void this.startSync(...)`,
    // so the outer call releases the lock before the inner one acquires it —
    // the non-reentrant Mutex never deadlocks here.
    //
    // L12 (#3986, #3898 leftover): max-hold watchdog. The two known awaits inside
    // startSyncInternal are bounded, but there is no guard against a newly-introduced un-timed
    // await re-creating #3898 (mutex held forever → every later startSync blocks → sync dead).
    // If the hold exceeds SYNC_MUTEX_MAX_HOLD_MS, report SyncMutexHeldTooLong (so the new await is
    // caught) and restart sync once this hold finally releases. Best-effort: if the await is truly
    // hung forever the mutex never releases and the restart can't fire, but the report still lands.
    //
    // The 120 s threshold is a deliberate ~10x margin over the bounded awaits' worst case, so a
    // healthy hold can never trip it. If a legitimate hold ever grew past the threshold and then
    // completed, the cost per such hold is one Sentry report plus one queued sync restart — noisy
    // in telemetry (which is the point: it tells us to raise the bound) but harmless to sync.
    //
    // The timer is armed INSIDE the exclusiveCall callback — i.e. only once the lock is actually
    // held — so heldMs measures hold time only, not the time this call spent queued behind a prior
    // holder. Arming it before acquisition would let a legitimately long OTHER hold trip this
    // still-waiting call's watchdog with an inflated held_ms and a spurious restart.
    //
    // #6234: the limit counts RUNNING time, so a phone that froze the app mid-hold does not wake
    // to a false report; held_ms stays wall time and not_running_ms says how much of it was frozen.
    return this.syncMutex.exclusiveCall(async () => {
      let restartAfterRelease = false;
      const heldWatchdog = armSyncMutexHoldWatchdog(({ heldMs, notRunningMs }) => {
        console.warn(`Db:${this.internalDatabaseIdentifier}: syncMutex held ${Math.round(heldMs)}ms — too long`);
        this.logging.trackSilentProblem('SyncMutexHeldTooLong', {
          database: this.internalDatabaseIdentifier,
          held_ms: Math.round(heldMs),
          not_running_ms: Math.round(notRunningMs),
          diagnostics: this.getSyncDiagnostics(),
        });
        restartAfterRelease = true;
      });
      try {
        return await this.startSyncInternal(phase);
      } finally {
        heldWatchdog.cancel();
        if (restartAfterRelease) {
          // fire-and-forget: this queues on syncMutex and runs after the current hold releases.
          console.warn(`Db:${this.internalDatabaseIdentifier}: restarting sync after syncMutex was held too long`);
          void this.startSync(false);
        }
      }
    });
  }

  /**
   * Work out how many documents this initial sync pass actually has to download,
   * and publish it as the progress bar's denominator.
   *
   * This runs ALONGSIDE the replication, never before it. Counting first would
   * add two remote round trips to the front of every cold boot, which works
   * directly against the tiered sync's whole point — getting the calendar's days
   * onto the screen sooner. Running it concurrently costs the sync nothing: the
   * bar switches from the rough `pending` estimate to a real document total as
   * soon as the count answers, and if the count is slow or fails the bar simply
   * keeps the estimate it already had. On one device measurement it answered
   * before the first pull batch; on a slower link or a larger account it will
   * take longer, which the baseline below is there to handle.
   *
   * The reads are ids-only (`include_docs: false`), so no document body is
   * transferred — only the ranges' id + rev rows.
   *
   * Order matters. Every LOCAL read happens first and the `docs_read` baseline
   * is taken straight after them, before any remote read. The local reads are
   * what decide which documents are subtracted from the total, so the numerator
   * has to start counting from that same moment — otherwise a pass that already
   * downloaded some documents has them missing from the total while still
   * counting them as done, and the bar reaches 100% partway through and freezes.
   * Local reads are also fast, which keeps the two measurements close together.
   */
  // Runs for the CALENDAR pass only, never the priority pass — the rule and the
  // reason both live in `shouldPreCountInitialSyncDocs` (#4435). The `phase`
  // parameter stays anyway: this method is correct for either pass, and which
  // pass pays is a separate policy decision that belongs with the caller.
  private async countInitialSyncDocsAsync(phase: SyncPhase, logHeader: string): Promise<void> {
    // capture the pass this count belongs to; a newer pass invalidates it
    const generation = this.syncGeneration;
    // hold the two databases by value so a teardown between the awaits cannot
    // turn into a TypeError on a null field. It does NOT stop the calls
    // themselves failing — a destroyed PouchDB rejects — so the catch below
    // re-checks the generation before deciding a failure is worth reporting.
    const remoteDb = this.remoteDb;
    const localDb = this.localDb;
    if (remoteDb == null || localDb == null) return;
    const cutoff = phase === SyncPhase.CalendarWindow
      ? DatabaseWrapper.getCalendarSyncCutoffDate()
      : DatabaseWrapper.getPrioritySyncCutoffDate();
    const ranges = buildInitialSyncIdRanges(cutoff);
    try {
      // for each range, read the ids this device already holds
      const localIdsByRange: Set<string>[] = [];
      for (const range of ranges) {
        const local = await localDb.allDocs({ ...range, include_docs: false }) as { rows: { id: string }[] };
        localIdsByRange.push(new Set<string>(local.rows.map(row => row.id)));
      }
      // the local picture is now fixed, so this is the moment the numerator
      // must count from
      const docsReadBaseline = this.lastPullDocsRead;
      if (generation !== this.syncGeneration) return;

      let docsToPull = 0;
      // read the remote ranges one at a time, and count each one as it arrives
      // rather than collecting them: on a large account a range can carry
      // thousands of ids, and only the local sets (already held above) plus the
      // range being counted are in memory at once
      for (let i = 0; i < ranges.length; i++) {
        const remote = await timeout(
          DatabaseWrapper.INITIAL_SYNC_DOC_COUNT_TIMEOUT_MS,
          remoteDb.allDocs({ ...ranges[i], include_docs: false }) as Promise<{ rows: { id: string }[] }>,
        );
        // if the pass this count was measuring is gone, stop before the next
        // (possibly large) remote read rather than paying for a discarded answer
        if (generation !== this.syncGeneration) return;
        docsToPull += countInitialSyncDocsToPull(
          remote.rows.map(row => row.id),
          localIdsByRange[i],
          DatabaseWrapper.syncExcludedDocIds(),
        );
      }
      if (generation !== this.syncGeneration) return;
      console.log(`${logHeader} initial sync has ${docsToPull} document(s) to download`);
      this.initialSyncDocsReadBaseline = docsReadBaseline;
      this.initialSyncDocsToPull = docsToPull;
    } catch (err) {
      // if this pass has already been superseded, the failure is just the old
      // pass being torn down — expected, and reporting it would be noise
      if (generation !== this.syncGeneration) return;
      // same for a wrapper that was shut down under us (sign-out, account
      // switch, impersonation swap). `syncGeneration` is only bumped by
      // startSyncInternal, and this count reads localDb/remoteDb by value
      // rather than through the read lock, so a teardown rejects the in-flight
      // allDocs with the generation UNCHANGED — the check above cannot see it.
      // Same convention as the initial-sync retry loop above.
      if (this.localDb == null || this.remoteDb == null) {
        console.log(`${logHeader} document pre-count stopped — database was shut down`);
        return;
      }
      // otherwise never swallow it: the bar silently keeps its rough `pending`
      // estimate, so without a report a permanently failing count would look
      // like nothing more than a slightly odd-looking progress bar.
      //
      // INFO tier, not problem. Nothing here is actionable and nothing is
      // broken for the user: the progress bar degrades to the estimate it used
      // before this change. The remaining reachable causes are an offline blip
      // mid-count and the 30 s bound expiring on a slow link or a very large
      // account — both expected-in-the-wild, neither a defect. Key names match
      // the sibling sync canaries (PullCatchupFailed, now in
      // database-wrapper-pull-catchup.ts, and ConflictScanFailed) so a Sentry
      // search on `db` finds all three.
      this.logging.trackSilentInfo('InitialSyncDocCountFailed', {
        db: this.internalDatabaseIdentifier,
        phase,
        name: err?.name,
        message: err?.message,
        status: err?.status,
      });
    }
  }

  /**
   * The body of this lives in `database-wrapper-sync-start.ts`.
   *
   * Everything it needs is handed over as an explicit context object rather
   * than reached for through `this`, so the sync-start code can be read — and
   * unit-tested — without the whole document-access API around it. This method
   * stays here because `startSync` above owns the mutex and the phase decision.
   */
  private async startSyncInternal(phase: SyncPhase) {
    return startSyncInternalAsync(DatabaseWrapper.buildSyncStartContext(this), phase);
  }

  /**
   * The bridge between this class and the extracted sync-start code.
   *
   * Every entry is a plain forward to a member of this class, so the sync-start
   * code sees exactly the values and behaviour it saw when it lived inside this
   * file. The read/write pairs are accessors on purpose: the extracted code
   * assigns to them (`ctx.syncTasksCompleted += 1`) and those writes have to
   * land back on the wrapper, which is the state every other part of the class
   * reads.
   *
   * The length of this list is not an accident to be tidied away — it is the
   * measured size of the coupling between the replication engine and the
   * document store, and it shrinks only when a later slice moves more of the
   * engine out. See deferred finding `df-3c79746add23`.
   */
  private static buildSyncStartContext(wrapper: DatabaseWrapper): SyncStartContext {
    return {
      // ---- state the sync-start code only reads ----
      get remoteOnly() { return wrapper.remoteOnly; },
      get temporaryDb() { return wrapper.temporaryDb; },
      get remoteDb() { return wrapper.remoteDb; },
      get localDb() { return wrapper.localDb; },
      get isShuttingDown() { return wrapper.isShuttingDown; },
      get remoteDetachedForOwnWork() { return wrapper.remoteDetachedForOwnWork; },
      get internalDatabaseIdentifier() { return wrapper.internalDatabaseIdentifier; },
      get databaseIdLocal() { return wrapper.databaseIdLocal; },
      get initialSyncCompleted() { return wrapper.initialSyncCompleted; },
      get singletons() { return wrapper.singletons; },
      get documentCache() { return wrapper.documentCache; },
      get pulledDocListenerCount() { return wrapper.pulledDocListeners.length; },
      get unexpectedSyncRevs() { return wrapper.unexpectedSyncRevs; },
      get whenCalendarWindowSyncCompleted() { return wrapper.whenCalendarWindowSyncCompleted; },
      get auth() { return wrapper.auth; },
      get logging() { return wrapper.logging; },
      get databaseRecovery() { return wrapper.databaseRecovery; },
      get telemetry() { return wrapper.telemetry; },
      databaseRef: wrapper,

      // ---- constants that live on this class ----
      syncExcludedDocIds: () => DatabaseWrapper.syncExcludedDocIds(),
      deviceLocalDocIds: DEVICE_LOCAL_DOC_IDS,
      getCalendarSyncCutoffDate: () => DatabaseWrapper.getCalendarSyncCutoffDate(),
      getPrioritySyncCutoffDate: () => DatabaseWrapper.getPrioritySyncCutoffDate(),
      userDbPushChangesStyle: DatabaseWrapper.USER_DB_PUSH_CHANGES_STYLE,
      dbBackgroundSyncCompleteKeyPrefix: DatabaseWrapper.dbBackgroundSyncCompleteKeyPrefix,

      // ---- state the sync-start code writes back ----
      get syncHandler() { return wrapper.syncHandler; },
      set syncHandler(value) { wrapper.syncHandler = value; },
      get singletonSyncHandler() { return wrapper.singletonSyncHandler; },
      set singletonSyncHandler(value) { wrapper.singletonSyncHandler = value; },
      get syncHandlerIsInitial() { return wrapper.syncHandlerIsInitial; },
      set syncHandlerIsInitial(value) { wrapper.syncHandlerIsInitial = value; },
      get lastSyncError() { return wrapper.lastSyncError; },
      set lastSyncError(value) { wrapper.lastSyncError = value; },
      get syncTasksCount() { return wrapper.syncTasksCount; },
      set syncTasksCount(value) { wrapper.syncTasksCount = value; },
      get syncTasksCompleted() { return wrapper.syncTasksCompleted; },
      set syncTasksCompleted(value) { wrapper.syncTasksCompleted = value; },
      get syncGeneration() { return wrapper.syncGeneration; },
      set syncGeneration(value) { wrapper.syncGeneration = value; },
      get initialSyncDocsToPull() { return wrapper.initialSyncDocsToPull; },
      set initialSyncDocsToPull(value) { wrapper.initialSyncDocsToPull = value; },
      get initialSyncDocsReadBaseline() { return wrapper.initialSyncDocsReadBaseline; },
      set initialSyncDocsReadBaseline(value) { wrapper.initialSyncDocsReadBaseline = value; },
      get lastPullDocsRead() { return wrapper.lastPullDocsRead; },
      set lastPullDocsRead(value) { wrapper.lastPullDocsRead = value; },
      get lastSyncActivityTime_ms() { return wrapper.lastSyncActivityTime_ms; },
      set lastSyncActivityTime_ms(value) { wrapper.lastSyncActivityTime_ms = value; },
      get initialSyncSkippedEmpty() { return wrapper.initialSyncSkippedEmpty; },
      set initialSyncSkippedEmpty(value) { wrapper.initialSyncSkippedEmpty = value; },
      get realtimeListenerRestarts() { return wrapper.realtimeListenerRestarts; },
      set realtimeListenerRestarts(value) { wrapper.realtimeListenerRestarts = value; },
      get consecutiveAuthErrors() { return wrapper.consecutiveAuthErrors; },
      set consecutiveAuthErrors(value) { wrapper.consecutiveAuthErrors = value; },

      // ---- things it asks this class to do ----
      getDeviceId: () => getDeviceId(),
      isUserDb: () => wrapper.isUserDb(),
      isGlobalCommonDb: () => wrapper.isGlobalCommonDb(),
      replicationFilter: (doc: PouchDoc) => wrapper.replicationFilter(doc),
      serializeSyncError: (err: any) => wrapper.serializeSyncError(err),
      setInitialSyncCompleted: (value: boolean) => wrapper.setInitialSyncCompleted(value),
      recordInitialSyncRoundFailed: () => wrapper.recordInitialSyncRoundFailed(),
      setInitialSingletonSyncCompleted: (value: boolean, coveredEveryServerSingleton?: boolean) =>
        wrapper.setInitialSingletonSyncCompleted(value, coveredEveryServerSingleton),
      startSync: (initialOrPhase: boolean | SyncPhase) => wrapper.startSync(initialOrPhase),
      cancelSync: () => wrapper.cancelSync(),
      flushSyncDiagnostics: () => wrapper.flushSyncDiagnostics(),
      isNonRetryableSyncError: (err: any) => wrapper.isNonRetryableSyncError(err),
      handleSyncAuthError: (err: any, source: 'denied' | 'error', logHeader: string) => wrapper.handleSyncAuthError(err, source, logHeader),
      applyPulledDocToCache: (doc: any) => wrapper.applyPulledDocToCache(doc),
      notifyDocWatchers: (docId: string) => wrapper.notifyDocWatchers(docId),
      deliverPulledDocsToListeners: (docs: any[]) => wrapper.deliverPulledDocsToListeners(docs),
      settlePulledDeletionsAsync: (tombstones: PulledDeletionToSettle[]) => wrapper.settlePulledDeletionsFromSyncAsync(tombstones),
      stopRealTimeListener: () => wrapper.stopRealTimeListener(),
      startRealTimeListener: () => wrapper.startRealTimeListener(),
      shouldRestartWithInitialSync: () => wrapper.shouldRestartWithInitialSync(),
      reportStaleUnflushedChangeIfNeeded: () => wrapper.reportStaleUnflushedChangeIfNeeded(),
      reportPushStallIfNeeded: () => wrapper.reportPushStallIfNeeded(),
      countInitialSyncDocsAsync: (syncPhase: SyncPhase, logHeader: string) => wrapper.countInitialSyncDocsAsync(syncPhase, logHeader),
      publishSyncProgress: (payload: { [key: string]: any }) => wrapper.pubSub.publish(DatabaseSyncProgressEvent, payload),
      updateStateSyncing: () => wrapper.updateState(DatabaseState.Syncing),
      updateStateReady: () => wrapper.updateState(DatabaseState.Ready),
      updateStateOffline: () => wrapper.updateState(DatabaseState.Offline),
      isStateReady: () => wrapper.dbProvider.databaseStates[wrapper.internalDatabaseIdentifier] == DatabaseState.Ready,
    };
  }

  cancelSync() {
    this.stopRealTimeListener();

    // #5306: the singleton pre-sync is a SEPARATE replication, and it is cancelled BEFORE the
    // early return below. It can still be running when `syncHandler` is already null — the
    // main replication finishing does not end it — and while nothing cancelled it, it outlived
    // `shutdownAsync` and called handles that `remoteDb.close()` had already shut, which
    // PouchDB answers with "database is closed".
    if (this.singletonSyncHandler != null) {
      console.log(`Db:${this.internalDatabaseIdentifier}: cancelling singleton pre-sync`);
      const singletonSync = this.singletonSyncHandler;
      this.singletonSyncHandler = null;
      singletonSync.cancel();
    }

    if (this.syncHandler == null) {
      return; // just in case
    }

    console.log(`Db:${this.internalDatabaseIdentifier}: cancelling sync`);
    this.syncHandler.cancel(info => console.log(`Db:${this.internalDatabaseIdentifier}: replication canceled: `, info));
    this.syncHandler = null;
    this.syncHandlerIsInitial = false;
  }

  /**
   * Fast-lane listener: opens a CouchDB _changes feed starting from the current sequence
   * so new cross-device changes are picked up immediately, bypassing the sync backlog.
   * Only runs on user DBs while live sync is catching up on old documents.
   */
  private startRealTimeListener() {
    if (!this.isUserDb() || !this.remoteDb) return;

    const deviceId = getDeviceId();
    const logHeader = `Db:${this.internalDatabaseIdentifier}:`;

    this.realTimeChangesHandler = this.remoteDb.changes({
      since: 'now',
      live: true,
      include_docs: true,
    })
    .on('change', async (change: any) => {
      const doc = change.doc;
      if (!doc || doc._deleted) return;
      if (!this.replicationFilter(doc)) return;

      const transactionParts = (doc.db__trans && doc.db__trans.split('|')) || [];
      const transactionDeviceId = transactionParts[1] || null;

      // only process changes from other devices
      if (transactionDeviceId === deviceId) return;

      // write to localDb so it's available for reads
      try {
        await this.localDb.bulkDocs([doc], { new_edits: false });
      } catch (err) {
        // grafting the fast-lane doc failed — the doc still arrives later via
        // normal live sync, but this used to be a silent bare return (#3987 A2,
        // defect D3): log always, report once per session
        console.error(`${logHeader} real-time listener graft failed for ${doc._id}`, err);
        if (!sessionStorage.realtimeListenerGraftFailedReported) {
          sessionStorage.realtimeListenerGraftFailedReported = '1';
          this.logging.trackSilentProblem('RealtimeListenerGraftFailed', {
            db: this.internalDatabaseIdentifier,
            docId: doc._id,
            name: (err as any)?.name,
            message: (err as any)?.message,
          });
        }
        return;
      }

      const applied = this.applyPulledDocToCache(doc);
      // one doc at a time here, so deliver it straight away
      if (applied.undeliveredListenerDoc) {
        this.deliverPulledDocsToListeners([applied.undeliveredListenerDoc]);
      }
    })
    .on('error', (err: any) => {
      // the fast-lane listener used to die here with an empty handler — one
      // failed longpoll silently ended the fast lane for the whole session
      // (#3987 A2, defect D3): log always, report once per session, then
      // restart a bounded number of times.
      // if the request was simply dropped (lost signal, or cut off while the
      // app was in the background), log it only: the restart below is built
      // to recover from exactly that, and reporting it buried real listener
      // failures under 100+ network drops (#6090)
      if (isTransientFetchError(err)) {
        console.log(`${logHeader} real-time listener request dropped, will restart`, err?.message);
      } else {
        console.error(`${logHeader} real-time listener error`, err);
        if (!sessionStorage.realtimeListenerErrorReported) {
          sessionStorage.realtimeListenerErrorReported = '1';
          this.logging.trackSilentProblem('RealtimeListenerError', {
            db: this.internalDatabaseIdentifier,
            name: err?.name,
            message: err?.message,
            restarts: this.realtimeListenerRestarts,
          });
        }
      }
      // single-flight: if a restart is already scheduled, further error events
      // from the same dying feed must not stack more restarts or burn the budget
      if (this.realtimeListenerRestartPending) return;
      if (shouldRestartRealtimeListener(this.realtimeListenerRestarts)) {
        this.realtimeListenerRestarts++;
        this.realtimeListenerRestartPending = true;
        // raw setTimeout on purpose: DatabaseWrapper is a plain service class
        // with no safeTimeout helper, and the restart must NOT run
        // synchronously inside the error event. The callback re-checks the
        // handler so a cancelSync/stopRealTimeListener that ran in the
        // meantime (handler nulled) skips the restart instead of resurrecting
        // a listener that was deliberately torn down.
        this.realtimeListenerRestartTimer = setTimeout(() => {
          this.realtimeListenerRestartPending = false;
          this.realtimeListenerRestartTimer = null;
          if (this.realTimeChangesHandler == null) return;
          console.log(`${logHeader} restarting real-time listener (restart ${this.realtimeListenerRestarts} of ${REALTIME_LISTENER_MAX_RESTARTS})`);
          this.stopRealTimeListener();
          this.startRealTimeListener();
        }, DatabaseWrapper.REALTIME_LISTENER_RESTART_DELAY_MS);
      } else {
        // budget used up: logged only, not reported, because a session that
        // drops its connection a few times reaches this through dropped
        // requests alone, and the ordinary live sync still delivers every
        // change, only slower (#6090)
        console.log(`${logHeader} real-time listener restart budget used up (${REALTIME_LISTENER_MAX_RESTARTS}); fast lane off until the next sync session, live sync still delivers changes`);
      }
    });
  }

  /**
   * Update the in-memory document cache and notify watchers for a doc that arrived from
   * remote, after it has already been written to local PouchDB. `doc` is a raw
   * PouchDB doc (with `_id` / `_rev`).
   *
   * This is the ONE copy of that rule; all three channels a pulled document can
   * arrive through call it. See `design-docs/reactive-sync.md` for the channels
   * and `design-docs/sync-integrity.md` for what a same-generation refresh does
   * to a document a user is looking at.
   *
   * #4249: this function is the pulled-doc listener collection point that is NOT the pull loop — the third of three (see `design-docs/reactive-sync.md`, which names all three), and
   * naming it here rather than its callers is load-bearing. Both the fast-lane
   * real-time listener and the pull catch-up graft land their docs through here
   * with `new_edits: false`, and the later live replication then skips those
   * revisions (`revs_diff`) — so they never reappear in the pull loop's
   * `info.change.docs`. A listener hooked only into the pull loop would miss
   * every cross-device change that arrives in the post-cold-start backlog
   * window, permanently.
   *
   * Deletions never reach here through the fast lane or the catch-up graft: the
   * listener returns early on `_deleted` and the graft skips tombstones. Pulled
   * deletions arrive through the live pull loop instead, which handles them in
   * its own branch — that is where the tombstone-body capture lives.
   *
   * A never-cached document is announced whichever device wrote it, on all three
   * channels. The live pull loop used to stay silent about one carrying this
   * device's own id; #5189 measured that silence on live for a week and removed
   * it. The numbers are in `design-docs/reactive-sync.md`.
   *
   * This method never delivers anything to the pulled-doc listeners itself. It
   * hands the document back in `undeliveredListenerDoc` and the caller decides
   * WHEN, because that timing is the caller's business and getting it wrong is
   * expensive: a caller handling one document delivers straight away, while a
   * caller looping over thousands (the pull catch-up, the live pull loop)
   * collects them and delivers ONE batch after the loop — per-document delivery
   * there is the work that froze cold starts in #4243.
   */
  private applyPulledDocToCache(doc: any): PulledDocApplyResult {
    // NOTE the stripped copy of the document is built by stripPouchInternals
    // below, and only once this method knows it is going to use it. Every early
    // return here is an arrival that changes nothing, and the live pull loop
    // meets those far more often than it meets a real change — building one
    // object per document there is exactly the per-document work this path must
    // not do (#4243).
    const cachedEntry = this.documentCache.get(doc._id);
    const previousExpectedRev = cachedEntry?.expectedRev;
    if (!cachedEntry) {
      // a doc we have not cached yet — no cached copy to update, so all this can
      // do is tell the app it arrived, by broadcasting to all watchers
      this.notifyDocWatchers(doc._id);
      return { wasCached: false, previousExpectedRev, generationStrictlyNewer: false, undeliveredListenerDoc: this.stripPouchInternals(doc) };
    }

    const pulledRevNum = parseInt(doc._rev.split('-')[0], 10) || 0;
    const currentExpectedRevNum = parseInt((previousExpectedRev || '').split('-')[0], 10) || 0;
    const generationStrictlyNewer = pulledRevNum > currentExpectedRevNum;
    // process if rev is newer, or same generation but different hash — a sibling
    // branch, which the identity map below applies only when the fold merged it
    // with this device's copy (#4577) and otherwise turns away (mh-physique#5045)
    const isNewerOrConflict = doc._rev !== previousExpectedRev && pulledRevNum >= currentExpectedRevNum;
    if (!isNewerOrConflict) {
      // an older or identical revision coming back is the expected "echo" of our
      // own push in the CouchDB replication protocol — nothing to do
      return { wasCached: true, previousExpectedRev, generationStrictlyNewer, undeliveredListenerDoc: null };
    }

    const isSaving = cachedEntry.saveState?.saving || this.singletons[doc._id]?.isSaving;
    if (isSaving) {
      // save in progress — only update expectedRev, conflict handler will merge
      cachedEntry.expectedRev = doc._rev;
      return { wasCached: true, previousExpectedRev, generationStrictlyNewer, undeliveredListenerDoc: null };
    }

    // past every early return, so this arrival really is a change and the
    // stripped copy is going to be used
    const cleanDoc = this.stripPouchInternals(doc);
    // read BEFORE the identity map updates the canonical object in place
    const existingRef = cachedEntry.ref.deref();
    // #4577: for a singleton, fold the arriving version into what this device
    // holds instead of replacing it outright. `bodyToApply` is the arriving
    // version unchanged for everything else, so nothing but singletons changes
    // behaviour here. The comparison below uses the body actually being applied,
    // so watchers fire on what the app will really see.
    const { body: bodyToApply, folded } = this.foldPulledSingletonIntoLocal(doc._id, existingRef, cleanDoc, doc);
    // if the arriving version was folded, give the merged body the ARRIVING revision.
    // The merge keeps this device's own `_rev`, and a database body carrying the
    // revision the shared object already has is kept out as an echo (#5904) — so
    // without this every folded arrival was dropped and the app went on showing the
    // old singleton until a reload. See design-docs/sync-integrity.md, "A folded
    // arrival carries the arriving revision".
    if (folded) bodyToApply._rev = doc._rev;
    // whether the body about to be applied differs from what the shared object
    // holds. Taken HERE, before the apply, because that is the last moment the
    // shared object still holds its own content — afterwards both sides are the
    // same object and the comparison can only answer "equal".
    const bodyDiffersFromSharedContent = !existingRef || !deepCompareEQ(existingRef, bodyToApply);
    // a sibling branch may replace the shared object only when the fold really
    // merged it with this device's copy; an unmerged body carries only the other
    // branch (df-f8cf38ad9fa8)
    const { action: appliedAction } = this.getCachedDocumentReportingAction(bodyToApply, siblingPolicyForArrival(folded));
    // the identity map turns away a database body from an earlier generation than
    // the one the shared object already holds (#4860), and a sibling branch that
    // was not folded (mh-physique#5045), and it SAYS so, so nothing below has
    // to predict it. The gate at the top of this method compares against
    // `expectedRev`, which is what this device BELIEVED the document was at; the
    // identity map compares against the shared object's own revision. Those two
    // are normally the same, and when they are not, an arrival can pass the gate
    // and still be older than the object — the identity map then keeps its own
    // content and this arrival changes nothing.
    const sharedContentWasKept = isSharedContentKept(appliedAction);
    // A kept shared object did not change, so nothing downstream may say it did:
    // waking every watcher would be a pointless refresh, and the listeners are
    // handed what the app now HOLDS, which after a refusal is the content it kept
    // rather than the older body that just arrived.
    const dataChanged = !sharedContentWasKept && bodyDiffersFromSharedContent;
    // the database holds the ARRIVING version, not the merged one — the merged
    // one exists only in memory until this device next saves. so that is what a
    // later arrival must be compared against.
    // except for a sibling branch the identity map turned away: this device stays
    // on its own branch, and the other branch is not an ancestor of it, so it
    // cannot be the starting point of the next fold. The fold does not check that
    // (the save path does, in canUseRememberedMergeBase), so remembering it would
    // count the other branch's values as edits this device made and let them beat
    // the resolver's merge on the next arrival (df-f8cf38ad9fa8). The starting
    // point already remembered, if any, still describes this device's branch.
    if (appliedAction !== CanonicalUpdateAction.KeepSharedContentOverSibling) {
      this.rememberSingletonDatabaseBody(doc._id, cleanDoc, RememberedBodySource.Server);
    }
    // force-update _rev and expectedRev: getOrRegisterCanonicalReference only updates
    // _rev for strictly newer generations, but a folded singleton can arrive at the
    // same generation with a different hash. Skipped when the identity map kept its
    // own content — forcing the arriving revision onto content it kept would leave
    // that content carrying a revision it never had, which turns the next save into
    // a conflict; after a refused sibling the shared object stays on its own branch
    // and the next save extends that branch.
    if (!sharedContentWasKept) {
      const updatedRef = cachedEntry.ref.deref();
      if (updatedRef) updatedRef._rev = doc._rev;
      cachedEntry.expectedRev = doc._rev;
    }
    if (dataChanged) {
      this.notifyDocWatchers(doc._id);
    }
    return {
      wasCached: true,
      previousExpectedRev,
      generationStrictlyNewer,
      // the listeners are told what the app now HOLDS, which after a singleton
      // merge is the merged body rather than the arriving one (#4577)
      undeliveredListenerDoc: dataChanged ? bodyToApply : null,
    };
  }

  /**
   * Remember a singleton's body as the database now holds it, so a version that
   * arrives from the server later can be merged against it rather than replacing
   * what this device has changed since (#4577).
   *
   * Does nothing for any document that is not a singleton — see
   * `Singleton.lastKnownDatabaseBody` for why the bound matters.
   */
  private rememberSingletonDatabaseBody(docId: string | undefined, body: any, source: RememberedBodySource): void {
    if (!docId || body == null) return;
    const singleton = this.singletons[docId];
    if (!singleton) return;
    // if the latest body is being replaced by another revision, keep it as an earlier
    // one: an arrival built on another device's save needs it to merge from (#6404)
    const outgoing = singleton.lastKnownDatabaseBody;
    if (outgoing != null && outgoing._rev !== body._rev) {
      const before = singleton.previousDatabaseBodies ?? [];
      const outgoingEntry = { body: outgoing, source: singleton.lastKnownDatabaseBodySource ?? RememberedBodySource.Server };
      const kept = rememberPreviousDatabaseBody(before, outgoingEntry);
      singleton.previousDatabaseBodies = kept;
      // keep the revisions of the bodies that no longer fit, so a later merge can tell
      // that the bodies still kept are too old for an arrival built on one of these
      const letGo = [outgoingEntry, ...before].filter(entry => !kept.includes(entry)).map(entry => entry.body?._rev);
      if (letGo.length > 0) {
        singleton.forgottenDatabaseBodyRevs = rememberForgottenRevs(singleton.forgottenDatabaseBodyRevs ?? [], letGo);
      }
    }
    singleton.lastKnownDatabaseBody = deepClone(body);
    singleton.lastKnownDatabaseBodySource = source;
  }

  /**
   * Fold a version of a singleton that arrived from the server into what this
   * device currently holds, and report what that took (#4577).
   *
   * Returns the body the caller should apply to the canonical object, and whether
   * that body really is a merge with this device's copy. When there is nothing to
   * merge against — the document is not a singleton, or this device has never
   * seen a database copy of it — the arriving version is handed straight back
   * with `folded: false`, which is exactly the behaviour that was there before.
   * The flag decides whether the identity map may apply a sibling branch
   * (`siblingPolicyForArrival`, df-f8cf38ad9fa8): an unmerged body carries only
   * the other branch.
   *
   * Why this is not simply "the newest version wins": the arriving version can be
   * OLDER in content than what this device holds while still being the one the
   * server accepted last. That is how a customer lost the progression of two whole
   * training sessions — a second device saved a several-day-old copy of the whole
   * training plan, and every exercise result recorded in the meantime was replaced
   * by the value that copy had simply been carrying. Comparing both sides against
   * the version they started from tells the two apart.
   */
  private foldPulledSingletonIntoLocal(
    docId: string,
    localBody: PouchDoc | undefined,
    incomingBody: PouchDoc,
    // the arrival as it came, before its internals were stripped: it may carry _revisions
    rawArrival: { _revisions?: { start?: number; ids?: string[] } },
  ): { body: PouchDoc; folded: boolean } {
    const singleton = this.singletons[docId];
    if (!singleton || singleton.lastKnownDatabaseBody == null || localBody == null) {
      return { body: incomingBody, folded: false };
    }

    // choose the version both sides were built on. Usually that is this device's latest
    // remembered body. For an arrival built on another device's save it is an earlier
    // one, and merging against the latest would hand everything this device saved since
    // to the other branch (#6404). See design-docs/sync-integrity.md, "The version both
    // devices started from".
    const { base, choice } = chooseFoldBase<PouchDoc>({
      latest: singleton.lastKnownDatabaseBody,
      previous: (singleton.previousDatabaseBodies ?? []).map(entry => entry.body),
      forgottenRevs: singleton.forgottenDatabaseBodyRevs ?? [],
      incomingRev: incomingBody._rev,
      // read here, after the singleton check above, so the pull path does no such work
      // for the thousands of documents that are not singletons
      incomingAncestry: ancestryRevsOf(rawArrival),
    });
    if (choice === FoldBaseChoice.NoCommonAncestor) {
      this.reportNoCommonStartingVersion(docId, singleton, incomingBody, base != null);
    }
    // if there is nothing to merge from, hand the arriving body back unmerged. For a
    // sibling the identity map then keeps this device's branch
    // (KeepSharedContentOverSibling), like every other document.
    if (base == null) {
      return { body: incomingBody, folded: false };
    }

    const result = mergeAgainstCommonBase<any>(base, localBody, incomingBody);
    if (result.mergedWithoutBase) {
      return { body: incomingBody, folded: false };
    }

    const context = {
      docId,
      localOnlyChanges: result.localOnlyChangeCount,
      guessedFields: result.guessedFieldCount,
      // true means this device's own id stamped the arriving version — usually a
      // second tab, which the report's wording cannot say (df-81b53714850e)
      writerWasThisDevice: this.collidingWriterWasThisDevice(incomingBody),
    };
    switch (decideMergeReportTier(result)) {
      case MergeReportTier.Problem:
        // both sides changed the same field, so one of the two values was picked
        // by a rule rather than known to be right — somebody may need to look.
        // (the no-starting-version case cannot reach here: it returned above.)
        this.logging.trackSilentProblem('sync-merge: both devices changed the same field', context);
        break;
      case MergeReportTier.Info:
        // every difference had exactly one author, so nothing was guessed and
        // nothing was lost — reported only so the rate stays visible
        this.logging.trackSilentInfo('sync-merge: kept this device\'s unsaved changes', context);
        break;
      case MergeReportTier.Nothing:
        // this device had changed nothing, so the arriving version simply wins —
        // the ordinary case, and it must stay silent
        break;
    }
    return { body: result.merged as PouchDoc, folded: true };
  }

  /**
   * Report that a version of a singleton arrived which was built on another device's
   * save, and nothing this device remembers is a version both were built on (#6404).
   *
   * Problem tier, because it is not repaired: what this device saved on its own
   * branch is not carried into the merge (or, for a sibling, the two branches stay
   * apart), so one device's edit can be lost. The count says whether the handful of
   * earlier bodies a singleton keeps is enough. Once per singleton per session.
   */
  private reportNoCommonStartingVersion(docId: string, singleton: Singleton, incomingBody: PouchDoc, mergedAgainstOwnLatest: boolean): void {
    if (this.noCommonStartingVersionReported.has(docId)) return;
    this.noCommonStartingVersionReported.add(docId);
    this.logging.trackSilentProblem('sync-merge: arriving version was built on another branch and no common starting version is remembered', {
      docId,
      incomingRev: incomingBody._rev,
      latestRememberedRev: singleton.lastKnownDatabaseBody?._rev,
      earlierRememberedRevs: (singleton.previousDatabaseBodies ?? []).map(entry => entry.body?._rev).join(','),
      // how many earlier bodies were let go: above zero, the starting version may simply
      // have been dropped, and the caps are what to look at
      forgottenRevCount: (singleton.forgottenDatabaseBodyRevs ?? []).length,
      // true: a later generation, merged against this device's own latest body, so what
      // it saved on its branch was not kept. false: a sibling, left unmerged.
      mergedAgainstOwnLatest,
    });
  }

  /**
   * Was the version a merge collided with stamped with THIS device's id
   * (df-81b53714850e)?
   *
   * The merge reports are worded for two real devices, and cannot say when the
   * collision was in fact same-device — most often a second tab, which shares
   * this browser profile's device id and its local store. Do NOT read a true
   * here as proof of a second tab: this device's own echo can produce it too.
   *
   * `undefined` means the arriving version carried no writer stamp at all, which
   * is a third answer and must not be flattened into either of the other two.
   *
   * See `design-docs/sync-integrity.md`, "Two tabs of one browser are two
   * writers".
   */
  private collidingWriterWasThisDevice(incoming: unknown): boolean | undefined {
    const writer = getTransactionDeviceId(incoming as Pick<PouchDoc, 'db__trans'> | null | undefined);
    if (!writer) return undefined;
    return writer === getDeviceId();
  }

  /**
   * A copy of a raw PouchDB document with the internals stripped off — every
   * `_`-prefixed field except `_id` and `_rev`. Those internals are what a
   * replication document carries (`_revisions` can be 50+ hashes on a
   * conflict-heavy document, `_conflicts` a list of leaves), and none of it
   * belongs in the identity map or in anything handed to a listener.
   *
   * A method rather than a closure inside the caller, so the hot pull path
   * allocates nothing at all for an arrival that turns out to change nothing.
   *
   * Returns `PouchDoc`, not `any`: this is the only thing ever assigned to
   * `PulledDocApplyResult.undeliveredListenerDoc`, so an `any` here would make
   * that field's declared type unenforced at both of its real write sites.
   */
  private stripPouchInternals(doc: any): PouchDoc {
    const cleanDoc = {} as any;
    for (const key of Object.keys(doc)) {
      if (key.startsWith('_') && key !== '_id' && key !== '_rev') continue;
      cleanDoc[key] = doc[key];
    }
    return cleanDoc;
  }

  /**
   * `applyPulledDocToCache` for a caller that is walking a whole batch: apply the
   * document, and collect anything the pulled-doc listeners should hear about
   * into `listenerDocsOut`, so the caller delivers ONE batch after its loop
   * instead of one delivery per document (#4243).
   */
  private collectAppliedPulledDoc(doc: any, listenerDocsOut: any[]): void {
    const applied = this.applyPulledDocToCache(doc);
    if (applied.undeliveredListenerDoc) {
      listenerDocsOut.push(applied.undeliveredListenerDoc);
    }
  }

  private stopRealTimeListener() {
    // if a bounded auto-restart is pending, a deliberate teardown must cancel it
    // too — otherwise the stale timer would fire later and restart a listener
    // that was intentionally stopped (or churn a freshly started one). when the
    // restart callback itself calls this method the timer has already fired and
    // both fields are already cleared, so this is a no-op there. (#3987 A2)
    if (this.realtimeListenerRestartTimer != null) {
      clearTimeout(this.realtimeListenerRestartTimer);
      this.realtimeListenerRestartTimer = null;
      this.realtimeListenerRestartPending = false;
    }
    if (this.realTimeChangesHandler) {
      this.realTimeChangesHandler.cancel();
      this.realTimeChangesHandler = null;
    }
  }

  /**
   * Canonical initial-vs-live decision for any sync RESTART (resume, auth-error retry, network-
   * pause retry). When `initialSyncCompleted !== true`, getReadDb() routes reads to remoteDb for
   * a shared DB, and for a user DB this device has never downloaded here (see #3580 pass-through
   * and #5068), so a live-only restart would strand reads on the network — restart with initial
   * sync to populate local PouchDB. Restarting with initial sync is right for a user DB that now
   * reads local too: local still has to be populated. When initial sync has
   * already succeeded (=== true), restart live: re-running initial would be wasted work.
   * (Initial boot uses initializeRemoteDb's own decision, not this helper.)
   *
   * Note: PouchDB checkpoints sync progress, so a cancel+restart of an in-progress initial sync
   * (e.g., on a quick background/resume cycle) resumes from the last sequence number rather than
   * re-replicating from scratch — the existing `stranded` check in ensureSyncActive guards
   * against cancelling an actively-running initial sync, so resume churn doesn't compound.
   */
  private shouldRestartWithInitialSync(): boolean {
    return this.initialSyncCompleted !== true;
  }

  /** Restart sync if it's not active or appears stuck. Used when app resumes from background. */
  ensureSyncActive() {
    if (!this.remoteDb) return;

    // reopen pass-through gate on Offline → Online: if initial sync previously
    // exhausted retries while offline (closed the gate to false), we are now
    // back online and want reads to route to remote again while sync retries.
    // without this, the user would see a stale-empty local DB until the
    // background initial sync eventually completes. see #3580 R5.
    //
    // also reset whenInitialSyncCompleted: the prior exhaustion resolved it
    // with `false`; without a reset, subsequent awaiters would see the stale
    // value when the eventual retry succeeds. mirrors the resets at the
    // retry-loop body inside assureInitialSyncAsync.
    if (this.initialSyncCompleted === false && this.onlineStateService.connectionState === ConnectionState.Online) {
      console.log(`Db:${this.internalDatabaseIdentifier}: Reopening pass-through gate after Offline → Online transition`);
      this.initialSyncCompleted = undefined;
      this.lastInitialSyncOutcome = undefined;
      this.whenInitialSyncCompleted = DeferredPromise.defer<boolean | undefined>();
    }

    if (this.syncHandler) {
      // if a genuine initial sync is in progress (assureInitialSyncAsync waits on its deferred), don't interfere
      if (this.initialSyncCompleted === undefined && this.syncHandlerIsInitial) {
        return;
      }

      // stranded = a live sync is running but the initial sync never completed (see
      // shouldRestartWithInitialSync) — its shared-db reads are stuck on the remote primary, so force a restart
      const stranded = this.initialSyncCompleted === undefined && !this.syncHandlerIsInitial;

      // otherwise only force-restart if sync appears stuck (db offline, or initial sync failed)
      const dbState = this.dbProvider.databaseStates[this.internalDatabaseIdentifier];
      if (!stranded && dbState !== DatabaseState.Offline && this.initialSyncCompleted !== false) {
        return; // sync is healthy, no restart needed
      }

      console.log(`Db:${this.internalDatabaseIdentifier}: Force-restarting sync after app resume (state=${dbState}, initialCompleted=${this.initialSyncCompleted}, stranded=${stranded})`);
      this.cancelSync();
    }

    const needsInitial = this.shouldRestartWithInitialSync();
    console.log(`Db:${this.internalDatabaseIdentifier}: Starting ${needsInitial ? 'initial' : 'live'} sync after app resume`);
    void this.startSync(needsInitial);
  }

  /** Clear all data from this database, both locally and remotely. Used primarily for the userdb when deleting private data
   * Note that this `DatabaseWrapper` object is not usable after this call.
   */
  async purgeDatabaseAsync(): Promise<void> {
    /* NOTE: We cannot destroy remote databases as Couch will complain that we are not a system admin.
     * Also, other devices can have local replicas that would restore the server's version. So instead, we mark all records on the server as deleted.
     * Locally, we can actually purge for better performance.
     */
    // Detach the remote handle for the duration, as restoreFromDocsAsync does: `ensureSyncActive()`
    // gives up when `this.remoteDb` is null, so leaving it attached lets sync restart part-way
    // through a purge and replicate against a half-deleted database.
    // Raising `remoteDetachedForOwnWork` alongside says the detach is OURS, so a pulled-deletion
    // repair that trips over the missing handle goes quiet instead of filing a problem report for
    // documents this purge is about to delete anyway (df-ac72b56eef36). Lowered in the `finally`.
    const remoteDb = this.remoteDb;
    this.remoteDb = null;
    this.remoteDetachedForOwnWork++;
    // true once the handle has been closed, after which putting it back would hand out a dead handle
    let remoteClosed = false;

    this.databaseRecovery.beginPurge();
    try {
      console.log(`Db:${this.internalDatabaseIdentifier} : purging database...`);

      this.cancelSync();

      // if remote is available, mark all records as deleted on the server
      if (remoteDb) {
        const transaction = DatabaseWrapper.getTransactionId();
        // every losing branch this purge closed, across all its passes — reported at the end. Each
        // revision appears once however many passes saw it; see mergeConflictRevDeletes.
        let conflictRevsToDelete: PurgeDeleteRow[] = [];
        // the user's documents seen by the MOST RECENT listing. A listing that finds none is the proof
        // the deletion worked; anything still here when the passes run out is a document the user would
        // otherwise have been told was deleted.
        let remainingUserDocIds: string[] = [];

        // List, delete, list again — see purge-plan.ts for why the outcome is judged by re-listing the
        // server rather than by reading each `bulkDocs` row. `bulkDocs` does not throw on a per-document
        // failure, so a document whose revision moved under us comes back as a `conflict` row and
        // survives; re-listing catches that AND lets the next pass delete it at its new revision.
        for (let pass = 1; pass <= PURGE_MAX_LIST_PASSES; pass++) {
          // A dropped request must cost a retry, not the whole deletion. These are one-shot calls, so
          // PouchDB's `retry: true` (which covers replication only) does nothing for them — see
          // restore-retry.ts, written after the same net::ERR_NETWORK_CHANGED blip killed a restore.
          // Only a transient fetch error is repeated, and once the attempts run out the error is
          // rethrown as before, so a real fault is still never hidden.
          //
          // `<any>` for the same reason restoreFromDocsAsync passes it: PouchDB's typings resolve this
          // allDocs overload to `unknown`, and the rows below then stop type-checking.
          const allDocsResult = await this.retryRemoteCallOnTransientErrorAsync<any>(
            `listing remote docs to delete (pass ${pass})`,
            () => remoteDb.allDocs({
              include_docs: true,
              startkey: '',
              endkey: '\ufff0',
              conflicts: true,
            }),
            undefined,
            PURGE_RETRY_LOG_PREFIX,
          );

          const plan = planPurgeDeletePass(allDocsResult.rows, transaction);
          remainingUserDocIds = plan.userDocIds;

          // The first pass always writes — it IS the deletion. A later pass only re-drives when one of
          // the user's own documents is still there, which is the only thing repeating can usefully fix:
          // a `_design/*` document the server will not let this client delete would otherwise cost a
          // wasted round on every pass. And the last pass never writes, because nothing would then check
          // whether that write worked — its listing is the verdict.
          const isWorthDeleting = pass === 1 || remainingUserDocIds.length > 0;
          if (!isWorthDeleting || pass === PURGE_MAX_LIST_PASSES) break;

          conflictRevsToDelete = mergeConflictRevDeletes(conflictRevsToDelete, plan.conflictRevDeletes);
          await this.writePurgeDeleteChunksAsync(remoteDb, plan.deletes, pass);
        }

        // #3992 R3: if the purge deleted standing conflict revisions, report
        // how many — complements the #3987 sync_conflict_scan canary (the
        // scan measures standing prevalence, this measures what purge deletes)
        if (conflictRevsToDelete.length > 0) {
          this.telemetry?.trackEvent('sync_conflicts_pruned', { count: conflictRevsToDelete.length });
        }

        // The user asked for their data to be erased and is about to be told it was. Say so only once
        // the server has confirmed it holds none of it. The caller keeps them signed in and shows the
        // failure, so they can try again — instead of walking away believing a deletion that did not
        // happen. Thrown before close(), so the catch below still puts the remote handle back.
        if (remainingUserDocIds.length > 0) {
          throw new PurgeIncompleteError(remainingUserDocIds, PURGE_MAX_LIST_PASSES);
        }

        // close() releases the local handle rather than making a network call, so it gets no retry.
        await remoteDb.close();
        remoteClosed = true;
      } else {
        console.warn(`Db:${this.internalDatabaseIdentifier} : remote not available during purge, skipping remote deletion`);
      }

      // if local is available, destroy it (purge IndexedDB)
      if (this.localDb) {
        await this.localDb.destroy();
        this.localDb = null; // must not access after destroying!
      }

      // The local database no longer exists, so "there is a local write that has not reached
      // the server" is provably false and the markers saying otherwise are stale. Clear them
      // HERE, because nothing else ever can: `cancelSync()` ran at the top of this method, and
      // `lastUserDbSyncTime_utc` only moves on a sync `change` event, which can never fire
      // again. Left set, they wedge the #3988 sign-out guard that our caller runs moments later.
      // Inside the `try` on purpose: a purge that FAILED leaves the local database intact and
      // the user signed in, where the markers are still telling the truth.
      // Why it wedges, and what it cost: design-docs/sync-integrity.md, "The change-tracking flags".
      if (this.isUserDb()) {
        delete sessionStorage.lastUserDbChangeTime_utc;
        delete sessionStorage.lastUserDbSyncTime_utc;
        localStorage.removeItem('cs_lastUserDbChangeTime_utc');
        localStorage.removeItem('cs_lastUserDbSyncTime_utc');
      }

      console.log(`Db:${this.internalDatabaseIdentifier} : purged`);
    } catch (err) {
      // Put the remote handle back. A failed purge does not reload the app — the user stays on the
      // User Profile page and can tap "Delete Private Data" again — and left detached, that second
      // attempt would take the "remote not available" branch above, wipe only the local copy and
      // report success. Skipped once the handle is closed: it cannot serve a retry, and re-attaching
      // it would also let ensureSyncActive() try to sync through it.
      if (remoteDb && !remoteClosed) {
        this.remoteDb = remoteDb;
      }
      console.error(`Db:${this.internalDatabaseIdentifier} : database purge error`, err);
      throw err;
    } finally {
      this.databaseRecovery.endPurge();
      this.remoteDetachedForOwnWork--;
    }
  }

  /** Send one purge pass's delete rows to the remote, in chunks.
   *
   * Kept beside `purgeDatabaseAsync` (as `writeRemoteChunkAsync` is kept beside the restore) so the
   * pass loop up there reads as the decisions it makes — whether this pass writes at all, and whether
   * the server is empty yet — rather than as chunking arithmetic. */
  private async writePurgeDeleteChunksAsync(remoteDb: PouchDB, deletes: PurgeDeleteRow[], pass: number): Promise<void> {
    const uploadChunkSize = 1000;

    // for each chunk of at most uploadChunkSize documents
    for (let i = 0; i < deletes.length; i += uploadChunkSize) {
      const chunk = deletes.slice(i, i + uploadChunkSize);

      // Retried for the same reason as the allDocs call in the caller. A retry can re-send a chunk the
      // server already applied, so those rows come back as conflicts against a now-stale _rev. That is
      // also why this result is not judged row by row: an already-applied delete and a genuinely
      // refused one look alike here, and the next pass's listing tells them apart by asking whether the
      // document is actually gone.
      const results: { ok: boolean; id: string; rev: string }[] = await this.retryRemoteCallOnTransientErrorAsync(
        `deleting docs ${i}-${Math.min(i + uploadChunkSize, deletes.length)} (pass ${pass})`,
        () => remoteDb.bulkDocs(chunk),
        undefined,
        PURGE_RETRY_LOG_PREFIX,
      );
      console.log(`Db:${this.internalDatabaseIdentifier} : bulk docs deletion ${i}-${i + uploadChunkSize} of ${deletes.length} (pass ${pass}) results: `, results);
    }
  }

  /** Run a one-shot remote CouchDB call, retrying it if the network drops under it.
   *
   * Thin instance-side wrapper over the pure `retryOnTransientErrorAsync` (in `restore-retry.ts`, where
   * it is unit-tested) — this supplies the real clock and the real transient-error predicate. */
  private async retryRemoteCallOnTransientErrorAsync<T>(
    operationLabel: string,
    call: () => Promise<T>,
    onRetried?: () => void,
    logPrefix?: string,
  ): Promise<T> {
    return retryOnTransientErrorAsync(call, {
      operationLabel,
      maxAttempts: RESTORE_REMOTE_MAX_ATTEMPTS,
      isTransient: isTransientFetchError,
      sleepAsync: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
      onRetry: onRetried,
      logPrefix,
    });
  }

  /** Run one `bulkDocs` chunk against the remote, retrying a dropped request, and verify that every
   * document in it was actually written (`bulkDocs` reports per-doc failures in its RESULT instead of
   * throwing). Returns the ids of conflict rows that are worth REPORTING — see below and
   * `assertBulkDocsChunkSucceeded`. */
  private async writeRemoteChunkAsync(remoteDb: PouchDB, chunk: any[], operationLabel: string): Promise<string[]> {
    let wasRetried = false;
    const result = await this.retryRemoteCallOnTransientErrorAsync(
      operationLabel,
      () => remoteDb.bulkDocs(chunk),
      () => { wasRetried = true; },
    );
    const conflictIds = assertBulkDocsChunkSucceeded(result, operationLabel);

    // A RETRIED chunk's conflicts are the normal signature of an at-least-once write, not documents we
    // failed to write — so they must not be reported.
    //
    // The client cannot tell a dropped REQUEST from a dropped RESPONSE: `fetch` rejects with the same
    // "Failed to fetch" either way, and `net::ERR_NETWORK_CHANGED` produces exactly that. So a retry can
    // re-send a chunk CouchDB has ALREADY applied, and every row then comes back `conflict` against the
    // now-stale `_rev` — even though the documents were written (or deleted) perfectly.
    //
    // Reporting those would raise a false alarm in precisely the network-blip case this retry exists to
    // survive. And it would not be harmless noise: `trackSilentProblem` ends in a `console.error`, which
    // trips the E2E unexpected-browser-error gate — so the restore would succeed and the nightly would go
    // red anyway, which is the whole thing we are trying to fix. Keep the console breadcrumb, drop the
    // report.
    if (wasRetried && conflictIds.length > 0) {
      console.log(`[restore] ${operationLabel}: ${conflictIds.length} conflict row(s) after a retry — expected, the first attempt most likely landed and only its response was lost`);
      return [];
    }

    return conflictIds;
  }

  /** After a restore's upload, check that the server holds exactly what was restored, and repair it if not.
   *
   * Cancelling sync does not abort a push already on the wire, so documents from before the restore can land
   * after its first listing: they escape the delete pass, or graft onto a restored document as a second
   * branch with no error. A document that survived the wipe shows up here as a stray or a replaced one, so
   * the delete pass's own conflicts need no separate handling. Judged by listing again, as the purge does:
   * the last listing gets no repair after it and is the verdict. The decisions are `planRestoreRepairPass`
   * (restore-retry.ts); the full story is in design-docs/boot-sequence.md.
   *
   * Tolerates what it cannot repair — the restore is otherwise complete — but never silently. Throws only
   * what the remote calls throw, which the caller treats as a failed restore. */
  private async verifyAndRepairRestoredRemoteAsync(
    remoteDb: PouchDB,
    docsToUpload: any[],
    unexpectedUploadConflicts: string[],
    survivedDeleteCount: number,
    log: (msg: string) => void,
  ): Promise<void> {
    const chunkSize = 1000;
    const docsToUploadById = new Map<string, any>(docsToUpload.map(doc => [doc._id, doc]));
    const uploadedIds = new Set<string>(docsToUploadById.keys());
    let conflictedIds = new Set<string>(unexpectedUploadConflicts);
    const repairedIds = new Set<string>();
    let unrepaired: RestoreRepairPlan | null = null;

    // for each listing, until one proves the server clean or the listings run out
    for (let listing = 1; listing <= RESTORE_MAX_VERIFY_LISTINGS; listing++) {
      const rows = await this.listRemoteRevisionsPagedAsync(remoteDb, chunkSize, `listing remote docs to verify the restore (listing ${listing})`);
      const plan = planRestoreRepairPass(rows, uploadedIds, conflictedIds);

      // if the server holds exactly what was restored, this listing is the proof
      if (plan.strayIds.length === 0 && plan.replacedIds.length === 0) {
        unrepaired = null;
        break;
      }
      unrepaired = plan;
      if (listing === RESTORE_MAX_VERIFY_LISTINGS) break;

      log(`listing ${listing} found ${plan.strayIds.length} stray and ${plan.replacedIds.length} replaced doc(s) — repairing: ${plan.strayIds.concat(plan.replacedIds).slice(0, 20).join(', ')}`);
      plan.strayIds.forEach(id => repairedIds.add(id));
      plan.replacedIds.forEach(id => repairedIds.add(id));

      // a delete whose revision moved again comes back as a conflict; the next listing still shows that
      // document, so it is repaired again or reported — its conflict ids are not needed here
      for (let i = 0; i < plan.deletes.length; i += chunkSize) {
        await this.writeRemoteChunkAsync(remoteDb, plan.deletes.slice(i, i + chunkSize), `deleting leftover docs ${i}-${Math.min(i + chunkSize, plan.deletes.length)} (listing ${listing})`);
      }

      // upload the restored copies again. a conflict here means the id came back yet again, and the next
      // listing must treat that document as replaced even if it shows no losing branch
      const reuploads = plan.replacedIds.map(id => docsToUploadById.get(id)).filter(doc => doc != null);
      const reuploadConflictIds: string[] = [];
      for (let i = 0; i < reuploads.length; i += chunkSize) {
        reuploadConflictIds.push(...await this.writeRemoteChunkAsync(remoteDb, reuploads.slice(i, i + chunkSize), `re-uploading replaced docs ${i}-${Math.min(i + chunkSize, reuploads.length)} (listing ${listing})`));
      }
      conflictedIds = new Set(reuploadConflictIds);
    }

    // a fixture snapshot whose id is already on the server is never replaced (snapshots are preserved), so
    // that conflict still means a document this restore did not write
    const preservedNotUploadedIds = unexpectedUploadConflicts.filter(id => isPreservedByRestoreId(id));
    const unrepairedIds = unrepaired ? unrepaired.strayIds.concat(unrepaired.replacedIds) : [];
    if (unrepairedIds.length > 0 || preservedNotUploadedIds.length > 0) {
      // still wrong after every repair round: tolerated, never silent
      this.logging.trackSilentProblem('RestoreUnexpectedDocConflicts', {
        survivedDeleteCount,
        unrepairedCount: unrepairedIds.length,
        unrepairedIds: unrepairedIds.slice(0, 20),
        preservedNotUploadedIds: preservedNotUploadedIds.slice(0, 20),
        repairedCount: repairedIds.size,
      });
    } else if (repairedIds.size > 0) {
      // the final listing proved the repair worked
      this.logging.trackSilentInfo('RestoreRepairedLateArrivingDocs', {
        survivedDeleteCount,
        uploadConflictCount: unexpectedUploadConflicts.length,
        repairedCount: repairedIds.size,
        repairedIds: Array.from(repairedIds).slice(0, 20),
      });
    }
  }

  /** List every document on the remote with its winning revision and losing branches, one page at a time.
   *
   * `_conflicts` is only returned with the document bodies, so listing a whole database in one request
   * would download and parse every body at once — on a large account that is a big request and a big
   * allocation. Paging bounds both; only the id, revision and losing branches of each row are kept. */
  private async listRemoteRevisionsPagedAsync(remoteDb: PouchDB, pageSize: number, operationLabel: string): Promise<RestoreVerifyRow[]> {
    const rows: RestoreVerifyRow[] = [];
    let startkey = '';

    // for each page, until a page comes back shorter than asked for
    for (let page = 1; ; page++) {
      // `<any>`: PouchDB's typings resolve this allDocs overload to `unknown` (same as the purge's call)
      const result = await this.retryRemoteCallOnTransientErrorAsync<any>(
        `${operationLabel}, page ${page}`,
        () => remoteDb.allDocs({ include_docs: true, conflicts: true, startkey, endkey: '\ufff0', limit: pageSize }),
      );
      for (const row of result.rows) {
        rows.push({ id: row.id, value: { rev: row.value?.rev }, doc: { _conflicts: row.doc?._conflicts } });
      }
      if (result.rows.length < pageSize) return rows;
      // start the next page just AFTER the last id ('\u0000' is the smallest character that can follow it).
      // NOT "start at the last id and skip 1": a late push can delete that document between two pages, and
      // the skip would then drop the next real document
      startkey = result.rows[result.rows.length - 1].id + '\u0000';
    }
  }

  /**
   * Replace all user documents on the remote database with the provided docs,
   * then destroy the local database so the next app load does a fresh sync.
   * Preserves snapshot/ and _design/ docs on the remote.
   *
   * ON FAILURE THIS LEAVES THE WRAPPER UNUSABLE, AND THAT IS THE CALLER'S PROBLEM TO FINISH.
   * The restore cancels sync and detaches `remoteDb` before it writes anything, and it does NOT put
   * them back if it throws — so afterwards this wrapper has no remote and dead sync, and the remote
   * itself may be half-deleted. There is no sane in-place recovery, so the contract is: a caller that
   * catches MUST tell the user and then reload the app. The reload is what heals the remote, too —
   * the local database still holds the old docs, so the fresh boot's sync pushes them back over the
   * partial delete. A caller that merely swallows the error would leave the user on a live-looking
   * page whose database is dead and whose next "Restore" tap cannot work.
   *
   * Letting the rejection escape unhandled is NOT an option either: app-sentry's unhandled-rejection
   * handler treats a transient fetch error as background-sync noise and silently swallows it.
   */
  async restoreFromDocsAsync(docs: any[]): Promise<void> {
    const log = (msg: string) => console.log(`[restore] ${msg}`);
    const t0 = performance.now();
    const elapsed = () => `${((performance.now() - t0) / 1000).toFixed(1)}s`;

    // true once the remote holds the restored data — from that moment a failure is NOT a failed
    // restore, and the caller's reload-and-resync recovery would corrupt rather than repair
    let remoteReplaced = false;

    // true once the remote handle has been detached, so the `finally` lowers the counter exactly
    // as often as it was raised — the "remote database not available" throw below runs BEFORE the
    // detach, and an unbalanced decrement would silence real failures for the rest of the session
    let remoteDetached = false;

    // wait for the server-database handle if the background couch login has not attached it yet,
    // which is the normal state for a few seconds after a warm resume (see the constant's note).
    // BEFORE cancelSync, on purpose: attaching the handle starts a sync, and the cancel below is
    // what stops that sync before the handle is detached. A sync start that the attach makes AFTER
    // the detach is stopped quietly by the `remoteDetachedForOwnWork` counter. Outside the purge
    // bracket below, because that bracket silences recovery and error reports, and nothing is
    // being destroyed yet while this waits.
    const waitStartMs = performance.now();
    const attachOutcome = await waitForRemoteAttachAsync({
      isAttached: () => this.remoteDb != null,
      shouldGiveUp: () => this.isShuttingDown || this.localDbIsStaleAfterRestore,
      timeoutMs: RESTORE_REMOTE_ATTACH_WAIT_MS,
      pollMs: RESTORE_REMOTE_ATTACH_POLL_MS,
      sleepAsync: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
      nowMs: () => performance.now(),
    });
    const waitedMs = Math.round(performance.now() - waitStartMs);
    if (attachOutcome === RemoteAttachWaitOutcome.AttachedAfterWait) {
      log(`waited ${waitedMs}ms for the remote database to be attached`);
      this.logging.trackSilentInfo('RestoreWaitedForRemoteDb', { db: this.internalDatabaseIdentifier, waitedMs });
    }

    // bracket with beginPurge so post-destroy "database is closed" rejections from
    // queued PouchDB ops don't trigger interceptDatabaseError/graceful-recovery
    // (which would race with the caller's location.reload())
    this.databaseRecovery.beginPurge();
    try {
      this.cancelSync();

      if (!this.remoteDb) {
        throw new Error(`Remote database not available for restore: ${this.internalDatabaseIdentifier} (waited ${waitedMs}ms, ${attachOutcome})`);
      }

      // detach remoteDb from instance so ensureSyncActive() won't restart sync
      // during the long-running restore operations. As in purgeDatabaseAsync, the counter says
      // the detach is OURS so a pulled-deletion repair that trips over the missing handle goes
      // quiet rather than filing a problem report (df-ac72b56eef36). The local flag is what the
      // `finally` lowers against: the `!this.remoteDb` throw above can leave this line unreached.
      const remoteDb = this.remoteDb;
      this.remoteDb = null;
      this.remoteDetachedForOwnWork++;
      remoteDetached = true;

      // fetch all remote doc ids and revs (no doc bodies needed)
      log(`fetching remote doc list...`);
      const allDocsResult: RemoteAllDocsResult = await this.retryRemoteCallOnTransientErrorAsync<any>(
        'fetching remote doc list',
        () => remoteDb.allDocs(),
      );
      log(`fetched ${allDocsResult.rows.length} remote docs (${elapsed()})`);

      // delete non-snapshot, non-design docs
      const docsToDelete = allDocsResult.rows
        .filter(row => !isPreservedByRestoreId(row.id))
        .map(row => ({
          _id: row.id,
          _rev: row.value.rev,
          _deleted: true,
        }));

      log(`deleting ${docsToDelete.length} docs...`);
      const chunkSize = 1000;
      // conflicts from the two passes mean DIFFERENT things and are judged separately (see below)
      const deleteConflictIds: string[] = [];
      const uploadConflictIds: string[] = [];
      for (let i = 0; i < docsToDelete.length; i += chunkSize) {
        const chunkEnd = Math.min(i + chunkSize, docsToDelete.length);
        deleteConflictIds.push(...await this.writeRemoteChunkAsync(remoteDb, docsToDelete.slice(i, i + chunkSize), `deleting docs ${i}-${chunkEnd}`));
        log(`  deleted ${chunkEnd}/${docsToDelete.length} (${elapsed()})`);
      }

      // A restore moves documents from this device to the SERVER, so it has to consult the
      // device-local list itself (#4798) — it writes straight to the remote with `bulkDocs`, so
      // `replicationFilter`, the initial-sync selector and `forcePushDocAsync`'s refusal (with its
      // `ForcePushDeviceLocalDocBlocked` canary) are all bypassed and none of them can see this.
      // The delete pass above already removed any copy the server had, so skipping these here is
      // also what CLEANS a server database that a pre-guard restore had put one on.
      const uploadableDocs = withoutDeviceLocalDocs(docs, DEVICE_LOCAL_DOC_IDS);
      const skippedDeviceLocalCount = docs.length - uploadableDocs.length;
      if (skippedDeviceLocalCount > 0) {
        log(`skipping ${skippedDeviceLocalCount} device-local doc(s) — those never go to the server`);
      }

      // upload new docs (strip _rev so they're created as new)
      const docsToUpload = uploadableDocs.map(doc => {
        const clean = { ...doc };
        delete clean._rev;
        return clean;
      });

      log(`uploading ${docsToUpload.length} docs...`);
      for (let i = 0; i < docsToUpload.length; i += chunkSize) {
        const chunkEnd = Math.min(i + chunkSize, docsToUpload.length);
        uploadConflictIds.push(...await this.writeRemoteChunkAsync(remoteDb, docsToUpload.slice(i, i + chunkSize), `uploading docs ${i}-${chunkEnd}`));
        log(`  uploaded ${chunkEnd}/${docsToUpload.length} (${elapsed()})`);
      }

      // Conflicts do not fail the restore. `_design/*` and `settings` conflict on every healthy restore
      // (see `assertBulkDocsChunkSucceeded`); any other upload conflict means the server already held a
      // document with that id, so the restored copy was NOT written. Note both lists have ALREADY had
      // retried chunks' conflicts removed by `writeRemoteChunkAsync`.
      const unexpectedUploadConflicts = uploadConflictIds.filter(id => !isExpectedRestoreConflictId(id));

      // check what the server really holds, and repair it, BEFORE the point of no return below — a failure
      // in there is still a failed restore. See `verifyAndRepairRestoredRemoteAsync`.
      await this.verifyAndRepairRestoredRemoteAsync(remoteDb, docsToUpload, unexpectedUploadConflicts, deleteConflictIds.length, log);

      // THE POINT OF NO RETURN. the remote now holds the restored data, so the caller's failure
      // recovery (reload → sync pushes the still-intact local docs back up) would no longer be a
      // repair — it would push the OLD docs over the restore we just completed. Everything below is
      // therefore treated as "the restore succeeded"; a failure here must not be reported as a failed
      // restore. See `RestoreAfterRemoteReplacedError`.
      remoteReplaced = true;

      // closing the remote only releases a handle — it cannot lose data, so it must not be able to
      // fail the restore.
      log(`closing remote db... (${elapsed()})`);
      try {
        await remoteDb.close();
      } catch (err) {
        console.error(`[restore] closing the remote db failed — ignored, the remote is already restored`, err);
      }

      // Destroying the local db is NOT optional, and this is the most dangerous step in the method: the
      // local database still holds the PRE-restore docs at higher rev generations than the rev-1 documents
      // we just uploaded. If it survives and anything re-attaches sync to it, those old docs win and
      // overwrite the restore. So give it a few attempts before giving up.
      log(`destroying local db... (${elapsed()})`);
      if (this.localDb) {
        await retryOnTransientErrorAsync(() => this.localDb.destroy(), {
          operationLabel: 'destroying the local db',
          maxAttempts: RESTORE_LOCAL_DESTROY_MAX_ATTEMPTS,
          isTransient: () => true, // any failure here is worth another go — the alternative is a corrupt restore
          sleepAsync: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
        });
        this.localDb = null;
      }

      // the restore is COMPLETE at this point: the remote holds the new data and the stale local db is
      // gone. what is left is bookkeeping, and it must not be able to turn a finished restore into a
      // reported failure (which would tell the user to clear storage they have no reason to clear).
      try {
        DatabaseWrapper.clearRecentlySyncedMarks();
      } catch (err) {
        console.error('[restore] clearing the recently-synced marks failed — ignored, the restore is complete', err);
        // a surviving "recently synced" mark lets the next start trust the device copy this
        // restore just destroyed. the restore itself stays complete, so report, never throw (#5191)
        this.logging.trackSilentProblem('RestoreClearSyncedMarksFailed', {
          db: this.internalDatabaseIdentifier,
          ...errorReportFields(err),
        });
      }
      log(`done (${elapsed()})`);
    } catch (err) {
      // A failure AFTER the remote was replaced is a different animal from a failure during the write,
      // and the caller must not treat them alike: the data IS restored, so the "reload and we will put
      // your data back" recovery would actively undo it.
      if (remoteReplaced) {
        // Latch the wrapper against syncing ONLY if the stale local database actually survived. That is
        // the thing that makes syncing destructive — it still holds the pre-restore docs at higher rev
        // generations than the rev-1 docs we just uploaded, so any sync would push them over the restore.
        // Telling the caller not to reload is not enough on its own: an Offline→Online transition would
        // rebuild the remote and restart sync with no reload at all.
        //
        // If `destroy()` succeeded and something after it threw, there is no local database left to push
        // anything, so there is nothing to protect against — and latching would only make us tell the
        // user to clear storage they do not need to clear.
        if (this.localDb) {
          this.localDbIsStaleAfterRestore = true;
        }
        throw new RestoreAfterRemoteReplacedError(err);
      }
      throw err;
    } finally {
      this.databaseRecovery.endPurge();
      if (remoteDetached) this.remoteDetachedForOwnWork--;
    }
  }

  /**
   * The scaffolding every read method needs, written once.
   *
   * In order: wait until any in-progress database recreation has finished (and
   * refuse the read if the wrapper is shutting down instead), take the shared
   * read lock, get the database handle to read through, run the caller's query,
   * and release the read lock whatever happens.
   *
   * Any error the query throws is offered to `interceptDatabaseError` first.
   * When that claims it, the failure has started a database recovery, and the
   * caller gets `DatabaseUnavailableError(Recovering)` — the one error type this
   * wrapper promises for "I could not read", so that it is never confused with
   * "there is nothing there". See `design-docs/pass-through-db.md`.
   *
   * The gate and the lock come from `enterReadSectionAsync`, which the write methods
   * share. `getByIdRangeLocalOnlyAsync` reads the local database directly and returns
   * `null` instead of refusing; it uses neither helper.
   */
  private async withReadDbAsync<T>(body: (db: DbHandle) => Promise<T>): Promise<T> {
    const callSite = captureDatabaseCallSite();
    await this.enterReadSectionAsync(callSite);
    try {
      const db = this.getReadDb();
      if (db == null) throw this.unavailable(DatabaseUnavailableReason.NoDatabaseHandle, callSite);
      return await body(db);
    } catch (err) {
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        throw this.unavailable(DatabaseUnavailableReason.Recovering, callSite); // unavailable, not empty
      }
      // #6278: a PouchDB error's own stack never names the reader, so hang the caller on it
      attachReadCallSite(err, callSite);
      throw err;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  /**
   * The tail every multi-document read shares: drop the rows that hold no
   * document (a keyed `allDocs` returns one of those per missing id) and the
   * ones that are deleted, run each remaining document through the identity map
   * so callers get the app's single shared object for it, register the caller's
   * watcher for each, then graft the raw documents into the local database.
   *
   * Returns the identity-mapped objects; the graft is deliberately handed the
   * raw ones.
   *
   * The watcher is optional, and that is load-bearing rather than a convenience.
   * Registering it BEFORE the graft is what makes the graft notify that one
   * watcher instead of taking its untracked-document branch. When there is no
   * watcher to register, the graft must be told `notifyWatchers: false`, or that
   * untracked branch broadcasts a "new document" notification to every active
   * watcher in the app.
   */
  private async cacheAndGraftRowsAsync<Type extends PouchDoc>(db: DbHandle, rows: any[], watcher?: DocWatcher): Promise<Type[]> {
    // #4798: a range read cannot name the ids it will get back, so a device-local document
    // could arrive here from the pass-through remote. Drop it BEFORE the identity map:
    // `getCachedDocument` overwrites the app's canonical object for that id in place, so
    // filtering only the graft below would stop the local write and still hand the app
    // another device's body — which is the half-measure this change exists to avoid. No
    // current caller's id range covers such a document; this keeps that from mattering.
    const isDeviceLocalFromRemote = (doc: PouchDoc) =>
      db === this.remoteDb && DEVICE_LOCAL_DOC_IDS.includes(doc._id);
    const docs = rows.map(row => row.doc)
      .filter(doc => doc && !doc._deleted && !isDeviceLocalFromRemote(doc)) as Type[];
    // df-285c6dbcc7af: where this device already holds a NEWER copy, use that instead of the
    // server's older one — see localCopiesAheadOfPassThroughAsync.
    const aheadById = await this.localCopiesAheadOfPassThroughAsync(db, docs);
    const cachedDocs = docs.map(doc => {
      const cached = this.getCachedDocument(aheadById.get(doc._id) ?? doc);
      if (watcher) {
        this.registerDocWithWatcher(cached._id, watcher);
      }
      return cached;
    });
    // nothing to graft for a document this device is ahead on: the local store already holds
    // the newer copy, so writing the server's older body back would do nothing but wake the
    // watchers for a body the app did not adopt
    const graftDocs = aheadById.size > 0 ? docs.filter(doc => !aheadById.has(doc._id)) : docs;
    await this.graftPassThroughDocs(db, graftDocs, watcher ? undefined : { notifyWatchers: false });
    return cachedDocs;
  }

  /**
   * Is this read about to fetch a DEVICE-LOCAL document from the pass-through remote? (#4798)
   *
   * A device-local document (the day-task-status cache) is derived data that each device
   * works out for itself and must never share. Replication is filtered in both directions
   * to enforce that — but the pass-through window is not replication. On a device that has
   * never downloaded this database here, `getReadDb()` still answers with the REMOTE database
   * until the first sync finishes (#5068 closed that window for a device that HAS), so boot read
   * this document from the server, grafted it into the local store, and `processFetchedSingletonAsync`
   * then made the server's body the copy the app used. A device therefore adopted whatever
   * some other device had left on the server, which is exactly the poisoning that making
   * the document device-local (#4212) set out to end.
   *
   * `chooseReadDb` cannot decide this on its own — it is never told which document is being
   * read. So the read wrappers ask here instead, and read from this device's own store.
   *
   * False when there is no local store (a `remoteOnly` wrapper — the all-users and food
   * databases, which hold no device-local documents anyway), because there would be nowhere
   * else to read from.
   */
  private isPassThroughReadOfDeviceLocalDoc(db: DbHandle, docId: string): boolean {
    return db === this.remoteDb && this.localDb != null && DEVICE_LOCAL_DOC_IDS.includes(docId);
  }

  /**
   * Read one device-local document from THIS device's store, bounded (#4798).
   *
   * Bounded for the same reason the pass-through graft is (#4401): a local store that has
   * stopped answering must not stop the boot in silence. A read that times out rejects with
   * the local-store-not-answering error.
   *
   * What happens to that rejection differs by caller, deliberately. The boot batch
   * (`getTypedRecordsByKeysAsync`) catches it and carries on without the document, because
   * the batch's other documents are what the boot needs — and an absent day-task-status
   * cache is the ordinary cold-start state, rebuilt from the user's own log entries. The
   * two single-document wrappers let it travel, exactly as they already do for any other
   * failed read, so it reaches `interceptDatabaseError` and starts the usual local-store
   * recovery.
   */
  private async getDeviceLocalDocAsync(docId: string): Promise<any> {
    const operationName = this.localOpName('localDb.get:deviceLocalDoc');
    return withBootLocalStoreTimeout(
      trackLocalOpAsync(operationName, () => this.localDb.get(docId)),
      operationName,
    );
  }

  /**
   * Ask this device's own store whether it holds a NEWER copy of anything a pass-through
   * read just fetched from the server, and answer with those newer copies by document id
   * (#5068 follow-up — deferred finding df-285c6dbcc7af). An EMPTY map is the answer in
   * every ordinary case, and for an account below officer nothing is read at all.
   *
   * Why the routing cannot answer this, why it is per document, what it deliberately leaves
   * alone, and the save path an officer used to lose an edit down:
   * `design-docs/pass-through-db.md`, "The officer case, and why it is answered per DOCUMENT
   * rather than per database". `isPassThroughReadOfDeviceLocalDoc` above is the same shape
   * for the same reason (#4798).
   *
   * REVISIONS FIRST, BODIES ONLY WHERE THEY ARE NEEDED. The scan asks for revisions alone,
   * because that is all the comparison needs, and reads bodies in a second call for the few
   * ids that turn out to be ahead. `cacheAndGraftRowsAsync` serves prefix reads — every
   * `$text/` help document in `global_common`, for instance — so fetching a body per id here
   * would pull hundreds of them to answer a question `row.value.rev` already answers. It
   * would also break what `local-store-timeout.ts` says about every caller of
   * `withBootLocalStoreTimeout`: that the operation it bounds is small, which is why 15 s is
   * not a performance budget.
   *
   * ONE BOUND FOR THE WHOLE CHECK, not one per read. `LOCAL_STORE_BOOT_OP_TIMEOUT_MS` is 15 s
   * precisely because that is well under the 30 s at which the splash watchdog reports a hang
   * (`SPLASH_HANG_TELEMETRY_MS`), so two of those in series would reach the very line the
   * bound exists to stay under. Bounding the pair keeps one pass-through read's worst case
   * where it was before this check existed.
   */
  private async localCopiesAheadOfPassThroughAsync(db: DbHandle, docs: any[]): Promise<Map<string, any>> {
    const mayFindLocalAhead = passThroughReadMayFindLocalAhead({
      readFromRemote: db === this.remoteDb,
      hasLocalDb: this.localDb != null,
      isUserDb: this.isUserDb(),
      deviceWritesSharedDbs: this.auth.getAuthRole() >= AuthRole.Officer,
    });
    if (!mayFindLocalAhead) return new Map();

    const serverDocsById = new Map<string, any>();
    for (const doc of docs) {
      if (doc && typeof doc._id === 'string') serverDocsById.set(doc._id, doc);
    }
    if (serverDocsById.size === 0) return new Map();

    const operationName = this.localOpName('localDb.allDocs:passThroughLocalAheadCheck');
    try {
      return await withBootLocalStoreTimeout(
        this.findLocalCopiesAheadAsync(serverDocsById),
        operationName,
      );
    } catch (err) {
      // the read still answers from the server, so the boot carries on — but a local store
      // that cannot answer this is the same store every later local operation queues behind,
      // and that has to reach a future debugger rather than vanish here
      this.logging.trackSilentProblem('pass-through local-copy check failed', {
        db: this.internalDatabaseIdentifier,
        docCount: serverDocsById.size,
        error: describeErrorForLog(err),
      });
      return new Map();
    }
  }

  /**
   * The two local reads behind `localCopiesAheadOfPassThroughAsync`, unbounded HERE on purpose
   * — the caller bounds the pair once, for the reason its comment gives. Each read still names
   * itself through `trackLocalOpAsync`, so a stall report can say which of the two stopped.
   *
   * Any failure travels up to the caller's one catch, whose fallback is the same whichever
   * read failed: leave the server's copy in place, which is what the read does without this
   * check at all.
   */
  private async findLocalCopiesAheadAsync(serverDocsById: Map<string, any>): Promise<Map<string, any>> {
    const aheadById = new Map<string, any>();
    const revisionRows = await this.localAllDocsForAheadCheckAsync(
      'localDb.allDocs:passThroughLocalAheadRevs',
      [...serverDocsById.keys()],
      false,
    );

    const aheadIds: string[] = [];
    for (const row of revisionRows) {
      // a key this device does not hold has no `value` at all, and a local delete it has not
      // pushed answers with `value.deleted` — both take the same path as before this check
      // existed, so the server's body is used
      const localRev = row?.value?.deleted ? null : row?.value?.rev;
      if (!localRev) continue;
      const serverDoc = serverDocsById.get(row.key);
      const relation = compareLocalCopyToPassThroughBody(localRev, serverDoc?._rev);
      if (relation === PassThroughLocalCopyRelation.Ahead) {
        aheadIds.push(row.key);
      } else if (relation === PassThroughLocalCopyRelation.DivergedAtSameGeneration) {
        // neither copy is newer, so nothing here can say which is right — the server's body
        // is used exactly as it is today. Reported because it means this device wrote a
        // revision the server settled differently, which nothing else would tell us.
        this.logging.trackSilentProblem('pass-through read: this device and the server disagree at the same revision generation', {
          db: this.internalDatabaseIdentifier,
          docId: row.key,
          localRev,
          remoteRev: serverDoc?._rev,
        });
      }
    }
    if (aheadIds.length === 0) return aheadById;

    const bodyRows = await this.localAllDocsForAheadCheckAsync(
      'localDb.allDocs:passThroughLocalAheadBodies',
      aheadIds,
      true,
    );

    for (const row of bodyRows) {
      const localDoc = row?.doc;
      // a body that vanished between the two reads is not substituted — a read that could not
      // be answered leaves the server's copy in place, as every other failure here does
      if (!localDoc || localDoc._deleted) continue;
      aheadById.set(row.key, localDoc);
      this.logging.trackSilentProblem('pass-through read: this device holds a newer copy than the server', {
        db: this.internalDatabaseIdentifier,
        docId: row.key,
        localRev: localDoc._rev,
        remoteRev: serverDocsById.get(row.key)?._rev,
      });
    }
    return aheadById;
  }

  /**
   * One keyed `allDocs` for the local-copy-ahead check, named so a stall report can say which
   * of the two reads stopped answering (#4401). It does NOT bound itself and does not catch:
   * `localCopiesAheadOfPassThroughAsync` bounds and catches the pair — see its comment.
   */
  private async localAllDocsForAheadCheckAsync(
    operation: string,
    keys: string[],
    includeDocs: boolean,
  ): Promise<any[]> {
    const result = await trackLocalOpAsync(
      this.localOpName(operation),
      () => this.localDb.allDocs(includeDocs ? { keys, include_docs: true } : { keys }),
    ) as { rows: any[] };
    return result?.rows ?? [];
  }

  async getRecordAsync(id: string, watcher: DocWatcher): Promise<any> {
    return this.readOneRecordAsync(id, watcher);
  }

  async getTypedRecordAsync<Type>(id: string, watcher: DocWatcher): Promise<Type> {
    return await this.readOneRecordAsync(id, watcher) as Type;
  }

  /**
   * The one body behind `getRecordAsync` and `getTypedRecordAsync` (df-da3a17c4bc3c). The two
   * used to be the same code written twice, so every rule about reading one document had to
   * be copied into both by hand. A rule added here reaches both reads.
   */
  private async readOneRecordAsync(id: string, watcher: DocWatcher): Promise<any> {
    return this.withReadDbAsync(async db => {
      // #4798: a device-local document is never fetched from the pass-through remote —
      // see isPassThroughReadOfDeviceLocalDoc.
      if (this.isPassThroughReadOfDeviceLocalDoc(db, id)) {
        const localDoc = await this.getDeviceLocalDocAsync(id);
        const cached = this.getCachedDocument(localDoc);
        this.registerDocWithWatcher(cached._id, watcher);
        // no graft: this document came from the local store, which is where a graft
        // would have written it
        return cached;
      }
      const freshData = await db.get(id);
      // df-285c6dbcc7af: prefer this device's own copy when it is newer than the server's
      const aheadById = await this.localCopiesAheadOfPassThroughAsync(db, [freshData]);
      const localAhead = aheadById.get(id);
      const doc = this.getCachedDocument(localAhead ?? freshData);
      // register watcher BEFORE graft so notifyDocWatchers takes the
      // "specific watcher" branch instead of the untracked-broadcast branch
      this.registerDocWithWatcher(doc._id, watcher);
      await this.graftPassThroughDocs(db, localAhead ? [] : [freshData]);
      return doc;
    });
  }

  /**
   * L6/S2 (#3986): read many docs by explicit id in ONE bounded pass-through allDocs, instead
   * of one round-trip per doc. Used to batch the 13 boot singleton reads (cold:userDb.init) —
   * during the pass-through window each per-doc read is a remote round trip bounded only by the
   * 90 s fetch timeout, so 13 sequential ones dominate cold-native boot and were a big share of
   * cold:userDb.init hangs. Returns a docId→doc map of the docs that exist (missing ids are
   * simply absent), applying the SAME caching + watcher registration + pass-through graft as
   * getTypedRecordAsync. The remote read is bounded by `timeoutMs`; on timeout OR any failure
   * it throws so the caller can fall back to the per-singleton path (never defaults-for-all).
   *
   * #4798 added a second, smaller cost to the boot bound: device-local ids are not asked of
   * the remote at all, and each is read from this device's own store afterwards under its own
   * `LOCAL_STORE_BOOT_OP_TIMEOUT_MS`. So the worst case is `timeoutMs` plus that bound per
   * device-local key — one key today. Those reads never throw; a failure leaves the document
   * out of the map, which the caller already treats as "not present".
   */
  async getTypedRecordsByKeysAsync(keys: string[], watcher: DocWatcher, timeoutMs: number = SINGLETON_BATCH_TIMEOUT_MS): Promise<Map<string, any>> {
    return this.withReadDbAsync(async db => {
      // #4798: the boot batch carries the day-task-status cache, and this read runs while
      // the pass-through window is open — so without this split it fetched that document
      // from the server and handed the server's body to the app. Device-local ids are read
      // from this device's own store instead; see isPassThroughReadOfDeviceLocalDoc.
      const deviceLocalKeys = keys.filter(key => this.isPassThroughReadOfDeviceLocalDoc(db, key));
      const remoteKeys = deviceLocalKeys.length > 0
        ? keys.filter(key => !deviceLocalKeys.includes(key))
        : keys;

      const result = remoteKeys.length > 0
        // #6096: wall time on purpose (a request open across a freeze is likely dead, and the
        // caller retries at once); the timeout error says whether a freeze caused it
        ? await timeoutNotingFreezeAsync(timeoutMs, db.allDocs({ keys: remoteKeys, include_docs: true })) as { rows: any[] }
        : { rows: [] as any[] };
      // collectFoundDocs skips missing keys (row.error) and deleted docs.
      const foundRaw = collectFoundDocs(result.rows);
      const found = new Map<string, any>();

      // A device-local document this device does not hold is the ordinary cold-start
      // state: it is left out of the map, the caller falls back to an empty cache, and
      // the app rebuilds it from the user's own log entries. A local store that has
      // stopped answering is treated the same way rather than failing the whole batch —
      // the batch's other documents are what the boot needs.
      for (const key of deviceLocalKeys) {
        let localDoc: any = null;
        try {
          localDoc = await this.getDeviceLocalDocAsync(key);
        } catch (err) {
          // "this device has no copy yet" is the ordinary cold-start answer and says
          // nothing. Anything else is the local store failing to answer a read it should
          // have answered, which nothing downstream can tell apart from an absent
          // document — so report it rather than let it vanish.
          if (!isPouchNotFound(err)) {
            this.logging.trackSilentProblem('DeviceLocalBootReadFailed', {
              db: this.internalDatabaseIdentifier,
              docId: key,
              error: describeErrorForLog(err),
            });
          }
        }
        if (localDoc == null || localDoc._deleted) continue;
        const cached = this.getCachedDocument(localDoc);
        this.registerDocWithWatcher(cached._id, watcher);
        found.set(key, cached);
      }

      // df-285c6dbcc7af: prefer this device's own copy for any of these the server is behind on
      const aheadById = await this.localCopiesAheadOfPassThroughAsync(db, [...foundRaw.values()]);
      const graftDocs: any[] = [];
      for (const [id, rawDoc] of foundRaw) {
        const localAhead = aheadById.get(id);
        const cached = this.getCachedDocument(localAhead ?? rawDoc);
        // register watcher BEFORE graft so notifyDocWatchers takes the "specific watcher" branch
        this.registerDocWithWatcher(cached._id, watcher);
        found.set(id, cached);
        // nothing to graft where this device is ahead — the local store holds the newer copy
        if (!localAhead) graftDocs.push(rawDoc);
      }
      // #4401: bracket the graft apart from the bounded read above (see the
      // BootPhase.PassThroughGraft doc for why).
      await bootTimings.measureAsync(
        BootPhase.PassThroughGraft,
        () => this.graftPassThroughDocs(db, graftDocs),
      );
      return found;
    });
  }

  /** Get content of all documents that start with a given id head.
   * NOTE: If the prefix is by type, e.g. `$exercise/` prefer using `getAllOfType()` instead, for type safety.
  */
  async getByIdHeadAsync(headId: string, watcher: DocWatcher, options?: { limit?: number }): Promise<any[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({
        include_docs: true,
        startkey: headId,
        endkey: headId + '\ufff0',
        limit: options && options.limit,
      });
      return this.cacheAndGraftRowsAsync<any>(db, result.rows, watcher);
    });
  }

  /** The ids of all documents that start with a given id head, without their content. Nothing is
   * cached and no watcher is registered, because no document is read. Used where only "which
   * documents exist" matters, e.g. which official recipes a user is missing (#6371).
   * THROWS `DatabaseUnavailableError` when the read could not run, like every other read.
   */
  async getIdsByIdHeadAsync(headId: string): Promise<string[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({
        include_docs: false,
        startkey: headId,
        endkey: headId + '￰',
      });
      return result.rows.map(row => row.id);
    });
  }

  /** The ids of all documents whose _id falls in the inclusive key range [startKey, endKey],
   * without their content. Like `getIdsByIdHeadAsync`, nothing is cached and no watcher is
   * registered. Used where only "does such a document exist" matters, e.g. whether any workout
   * was saved before a given day (df-afa259234d1a).
   * THROWS `DatabaseUnavailableError` when the read could not run, like every other read.
   */
  async getIdsByIdRangeAsync(startKey: string, endKey: string): Promise<string[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({
        include_docs: false,
        startkey: startKey,
        endkey: endKey,
      });
      return result.rows.map(row => row.id);
    });
  }

  // Read all non-deleted docs whose _id falls in the inclusive key range
  // [startKey, endKey]. This is the same as getByIdHeadAsync (same caching,
  // watcher registration, and pass-through grafting) but for an explicit key
  // range instead of a single prefix. It lets a date-range read use ONE
  // allDocs query instead of one prefix scan per day. That matters on native:
  // each query is a round-trip over the single Capacitor↔SQLite bridge, so
  // during a sync write-storm a per-day loop of ~90 queries gets starved one
  // wait at a time, while a single range query waits only once. See
  // getLogEntriesInRangeFromDiskAsync in user-db.service.ts.
  // THROWS `DatabaseUnavailableError` when the read could NOT run (db shutting
  // down / recovering / no handle) — "unavailable" is not "no docs in range".
  // It used to return `null` for that, which callers were free to write off as
  // `?? []`; see the error class for why every read now throws instead.
  async getByIdRangeAsync(startKey: string, endKey: string, watcher: DocWatcher, options?: { limit?: number }): Promise<any[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({
        include_docs: true,
        startkey: startKey,
        endkey: endKey,
        limit: options && options.limit,
      });
      return this.cacheAndGraftRowsAsync<any>(db, result.rows, watcher);
    });
  }

  /**
   * Like `getByIdRangeAsync`, but reads ONLY what this device already has on
   * disk — never the remote, and never grafting or notifying anything.
   *
   * This exists for one caller shape: work triggered BY the sync that needs to
   * look at what the sync has brought down so far (#4249 Phase 2's reactive
   * day-status flush). `getByIdRangeAsync` cannot be used there, and not merely
   * because it would be slower:
   *
   *  - It reads through `getReadDb()`, which routes to the REMOTE database
   *    while the initial sync is incomplete, on the devices that still pass
   *    through (#3580, narrowed by #5068). A flush would
   *    then make a network round trip per flush, during the exact window the
   *    connection is already saturated by the bulk pull.
   *  - It calls `graftPassThroughDocs`, which writes into local PouchDB and
   *    notifies document watchers. A sync-triggered read that notifies watchers
   *    is a feedback edge back into the machinery that triggered it — the shape
   *    of loop that froze cold starts in #4243.
   *
   * So this is a loop-breaker, not an optimization. An incomplete answer is the
   * POINT: mid-sync, "this device does not have it yet" is exactly the fact the
   * caller needs, and treating a missing document as absent-for-good is the
   * #4212 cache-poisoning bug. Callers must never persist anything derived from
   * this read while the initial sync is still running.
   *
   * Returns `null` when the read could not run at all (shutting down /
   * recovering) — "unavailable" is not "no docs in range".
   *
   * NOTE this is the OPPOSITE of `getByIdRangeAsync`, which throws
   * `DatabaseUnavailableError` for those same conditions. The `null` is kept
   * here because this method's one caller is the reactive display refresh,
   * which reads "could not read" as "skip this tick and retry" rather than as
   * an error to surface — see `refreshDisplayOnlyStatusesAsync`.
   */
  async getByIdRangeLocalOnlyAsync(startKey: string, endKey: string): Promise<any[] | null> {
    if (!await this.waitForRecreationGate()) return null;
    if (!this.rwLock.acquireRead()) return null;
    try {
      // deliberately localDb, NOT getReadDb() — see the comment above
      const db = this.localDb;
      if (db == null) return null;
      const result = await db.allDocs({
        include_docs: true,
        startkey: startKey,
        endkey: endKey,
      });
      // no identity-map caching and no watcher registration either: this read
      // is a one-off snapshot for a computation, and registering watchers would
      // make every later change to these docs wake a caller that has no view to
      // update.
      return result.rows.map(row => row.doc).filter(doc => doc && !doc._deleted);
    } catch (err) {
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        return null; // intercepted during recovery — unavailable, not empty
      }
      throw err;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  // note: this returns an array, not a map, and the result is not guaranteed to be in the same order as the input - missing docs will be filtered out.
  async getByIdListAsync<T extends PouchDoc>(ids: string[], watcher: DocWatcher): Promise<T[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({
        include_docs: true,
        keys: ids,
      });
      return this.cacheAndGraftRowsAsync<T>(db, result.rows, watcher);
    });
  }

  /** Get content of all documents of given type. Those have ids that start with `<typename>/`. */
  async getAllOfType<Type extends PouchDoc>(typeName: string, options?: { limit?: number, watcher?: DocWatcher }): Promise<Type[]> {
    console.assert(!typeName.endsWith('/'));
    return this.withReadDbAsync(async db => {
      const headId = typeName + '/';
      const result = await db.allDocs({
        include_docs: true,
        startkey: headId,
        endkey: headId + '\ufff0',
        limit: options && options.limit,
      });

      // the watcher is optional here, and passing it straight through matters:
      // with no watcher, cacheAndGraftRowsAsync tells the graft not to notify,
      // which is what stops it broadcasting to every active watcher in the app.
      return this.cacheAndGraftRowsAsync<Type>(db, result.rows, options?.watcher);
    });
  }

  async getAllDataUncachedAsync(): Promise<any[]> {
    return this.withReadDbAsync(async db => {
      const result = await db.allDocs({ include_docs: true });
      const docs = result.rows.map(row => row.doc).filter(doc => !doc._deleted);
      // deliberately NOT cacheAndGraftRowsAsync: this admin-only path hands back
      // the raw documents rather than the app's shared cached objects, so it must
      // not run them through the identity map. It skips the per-doc notify for
      // the same reason that helper does when it has no watcher.
      await this.graftPassThroughDocs(db, docs, { notifyWatchers: false });
      return docs;
    });
  }

  /** NOTES:
   * * While we can run queries by name on remote or local databases,
   * we can only create queries on local databases (mainly userDb!). For global
   * databases like foods, we expect the query to already exist there, as it will
   * be ran by the server.
   *
   * * The `mapFunction()` function has to be a pure JavaScript function - cannot use TypeScript features,
   * newer ES features (not even `let`!), or global identifiers imported from other modules (e.g. enums!).
   *
   * * It **cannot use closures** either, since it will be converted to a string and evaled later!
   *
   * To be properly convertible to string it has to be declared with `let foo = function (doc:PouchDoc) { ...something... }`
   * i.e. it *cannot* be an arrow function, *nor* a named function!
   */
  createQuery(queryName: string, mapFunction: (doc: PouchDoc) => void) {
    console.assert(this.remoteDb == null && this.localDb == null, 'All query specs need to be prepared before DB is initialized.');
    this.queriesToSave.push({ name: queryName, map: mapFunction });
  }

  async commitQueriesAsync() {
    this.updateState(DatabaseState.Syncing);

    // we can write queries only for local databases
    if (!this.localDb || this.queriesToSave.length <= 0) {
      this.whenQueriesReady.resolve(true);
      return;
    }

    await this.writeViewDesignDocAsync();

    this.whenQueriesReady.resolve(true);
  }

  /**
   * Write the `_design/view` doc holding all registered view map functions to
   * the local DB. Extracted from commitQueriesAsync so the view self-heal
   * (#4219) can re-write the design doc mid-session with a bumped heal salt.
   */
  private async writeViewDesignDocAsync(): Promise<void> {
    if (!this.localDb || this.queriesToSave.length <= 0) return;

    // #4343: once per device (per version of the constant), bump every view's
    // heal salt so all view indexes rebuild from scratch — devices that ran
    // the broken SQLite changes feed can carry views permanently missing docs
    const didScheduleGlobalRebuild = await this.applyGlobalViewRebuildIfNeededAsync();

    // Read the two documents this write is built from — the per-device heal salts
    // (#4219) and the existing design doc's revision. BOTH reads are bounded: before
    // #4401 they were not, and in production the store accepted them and answered
    // neither, which stopped the boot here in complete silence. `kind: 'bail'` means a
    // recovery has taken the failure over; we return, and commitQueriesAsync still
    // resolves whenQueriesReady, or every view-backed read would hang until the
    // recovery finishes. See readViewDesignInputsAsync for the whole argument.
    const inputs = await readViewDesignInputsAsync({
      databaseId: this.internalDatabaseIdentifier,
      getLocal: (docId: string, operationName: string) => trackLocalOpAsync(
        operationName,
        () => this.localDb.get(docId),
      ),
      boundLocalRead: withBootLocalStoreTimeout,
      intercept: (err: unknown) => this.databaseRecovery.interceptDatabaseError(err),
      trackSilentProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      errorFields: (err: unknown) => errorReportFields(err),
    });
    if (inputs.kind === 'bail') return;
    this.viewHealSalts = inputs.salts;
    const designReadFailed = inputs.designReadFailed;

    let views = {};
    for (let i = 0; i < this.queriesToSave.length; i++) {
      let query = this.queriesToSave[i];

      views[query.name] = { map: this.renderViewMapString(query.name, query.map) };
    }

    let design = {
      _id: '_design/view',
      _rev: undefined,
      views: views,
    };

    if (inputs.designRev != null) {
      design._rev = inputs.designRev;
    }

    try {
      await timeout(10_000, this.localDb.put(design));
    } catch (err) {
      console.warn(`Db:${this.internalDatabaseIdentifier}: commitQueriesAsync put failed/timed out:`, err);
      // if another writer committed the SAME views a moment ago, this tab's queries
      // work anyway and there is nothing for anybody to look at (df-81b53714850e)
      if (await this.viewDesignAlreadyCommittedByAnotherWriterAsync(err, views)) {
        console.log(`Db:${this.internalDatabaseIdentifier}: another writer had already committed the same _design/view — this tab's queries are fine`);
      } else {
        // design doc write failed — queries won't work until next session, but sync can proceed.
        // L5 (#3986) makes offline boots depend on this local commit for view-backed reads, so a
        // silent failure here means runQueryAsync runs against missing views all session — surface it.
        this.logging.trackSilentProblem('CommitQueriesFailed', {
          database: this.internalDatabaseIdentifier,
          // when the read above failed we wrote without a revision, so a conflict here
          // is the consequence of that failure rather than something unexplained
          designReadFailed,
          ...errorReportFields(err),
        });
      }
    }

    // after the salted design doc is committed, drop the orphaned old index
    // stores. Failure only wastes disk (the rebuild itself is committed via
    // the design doc), but surface repeated failures — same convention as the
    // per-view heal (#4219)
    if (didScheduleGlobalRebuild) {
      try {
        // #4401: unbounded, and heavy — see trackLocalOpAsync
        await trackLocalOpAsync(
          this.localOpName('localDb.viewCleanup'),
          () => this.localDb.viewCleanup(),
        );
      } catch (err) {
        console.log(`Db:${this.internalDatabaseIdentifier}: viewCleanup after global rebuild failed (stale index stores remain):`, err);
        this.logging.trackSilentProblem('ViewHealCleanupFailed', {
          db: this.internalDatabaseIdentifier, view: '<global-rebuild>', ...errorReportFields(err),
        });
      }
    }
  }

  /**
   * Did the `_design/view` write fail only because another writer had already
   * committed the SAME views (df-81b53714850e)?
   *
   * Fails closed in every direction: anything other than a conflict, a re-read
   * that does not answer, or stored views that differ all answer false and the
   * caller reports as it always did. Never throws — it runs inside the caller's
   * own catch, and a throw here would replace a reported failure with an
   * unreported one.
   *
   * See `design-docs/sync-integrity.md`, "Two tabs of one browser are two
   * writers".
   */
  private async viewDesignAlreadyCommittedByAnotherWriterAsync(
    err: unknown,
    intendedViews: Record<string, { map: string }>,
  ): Promise<boolean> {
    if (!isPouchConflictError(err)) return false;
    try {
      const operationName = this.localOpName(`localDb.get:${VIEW_DESIGN_DOC_ID}(after-conflict)`);
      const stored = await withBootLocalStoreTimeout(
        trackLocalOpAsync(operationName, () => this.localDb.get(VIEW_DESIGN_DOC_ID)),
        operationName,
      );
      return storedViewDesignMatchesIntended(intendedViews, stored);
    } catch {
      // the store would not tell us what is in there, so we cannot say the write
      // was harmless — let the caller report
      return false;
    }
  }

  // per-device heal salts by view name, loaded from `_local/view-heal` before
  // any design-doc write. a nonzero salt is prepended to the view's map string
  // as a comment — the changed string gives the view a NEW internal index
  // store, so PouchDB rebuilds it from scratch on the next query (#4219)
  private viewHealSalts: Record<string, number> = {};

  /** Render the stored map string for a view, applying its heal salt (#4219). */
  private renderViewMapString(viewName: string, mapFunction: (doc: PouchDoc) => void): string {
    const salt = this.viewHealSalts[viewName];
    const mapString = mapFunction.toString();
    if (!salt) return mapString;
    // prepended as a comment: pouchdb evaluates the string as `return (<map>);`,
    // and a leading comment inside the parentheses is valid there
    return `/* heal-salt:${salt} (#4219 wedged-index rebuild) */ ${mapString}`;
  }

  // a view heal is a full index rebuild — never loop it. one bump per view
  // per day is enough: if the rebuilt index still lies, the raw-docs
  // cross-check keeps the calendar correct anyway (#4219)
  private static readonly VIEW_HEAL_MIN_HOURS_BETWEEN_BUMPS = 24;

  // #4343: version of the one-time global view rebuild. The iOS SQLite
  // adapter's changes feed used to let the map/reduce checkpoint skip past
  // present docs, so devices that ran the broken feed can carry view indexes
  // that are permanently missing documents — and the fixed feed never
  // revisits seqs behind a checkpoint. Bumping this constant makes every such
  // device rebuild ALL its view indexes from scratch exactly once (per DB).
  // Only applies where the SQLite adapter runs.
  //
  // #4981 phase 2: an Android device migrating onto SQLite has NEVER run the
  // broken iOS feed, so it has nothing to repair — but it also carries no stamp,
  // which would make its first SQLite boot rebuild every view index and fire a
  // rollout report for damage that cannot exist. The decision is to exempt it:
  // phase 3 stamps this version at the moment it writes the engine marker. See
  // design-docs/android-local-database-engine.md, "The global view rebuild".
  // The number itself lives in `sync-view-heal-decisions.ts` so #4981 phase 3's
  // node-tested migration can read it too — that module loads under plain node, this
  // class does not. Read it as GLOBAL_VIEW_REBUILD_VERSION, imported at the top.

  /**
   * #4343: run the one-time global view rebuild if this device has not done
   * it yet. Bumps EVERY registered view's heal salt in `_local/view-heal`
   * (a changed salt = a fresh index store, rebuilt lazily on the view's next
   * query) and stamps `globalRebuildVersion` so it never repeats. Gated to
   * the capacitor-sqlite adapter — the IndexedDB adapters never had the
   * broken feed, so rebuilding there would be wasted work.
   * Returns true when a rebuild was scheduled. Never throws.
   */
  private async applyGlobalViewRebuildIfNeededAsync(): Promise<boolean> {
    if (!this.localDb) return false;
    // only the custom SQLite adapter (native iOS) had the broken changes feed
    if ((this.localDb as any).adapter !== 'capacitor-sqlite') return false;
    const logHeader = `Db:${this.internalDatabaseIdentifier}:`;
    try {
      let doc: any;
      try {
        // #4401: unbounded local read on the boot path — see trackLocalOpAsync
        doc = await trackLocalOpAsync(
          this.localOpName('localDb.get:_local/view-heal(rebuild)'),
          () => this.localDb.get('_local/view-heal'),
        );
      } catch (err) {
        if (!isPouchNotFound(err)) throw err;
        doc = { _id: '_local/view-heal' };
      }

      const viewNames = this.queriesToSave.map(query => query.name);
      const decision = decideGlobalViewRebuild(
        doc.globalRebuildVersion, GLOBAL_VIEW_REBUILD_VERSION, doc.salts, viewNames,
      );
      if (!decision.shouldRebuild) {
        return false;
      }

      doc.salts = decision.newSalts;
      doc.globalRebuildVersion = GLOBAL_VIEW_REBUILD_VERSION;
      // stamp the per-view bump times too, so the #4219 per-view heal's 24h
      // rate limit does not schedule a second full rebuild right after this one
      doc.lastBumpUtc = doc.lastBumpUtc || {};
      const nowIso = DateTime.utc().toISO();
      for (const viewName of viewNames) {
        doc.lastBumpUtc[viewName] = nowIso;
      }
      // #4401: unbounded local write on the boot path — see trackLocalOpAsync
      await trackLocalOpAsync(
        this.localOpName('localDb.put:_local/view-heal'),
        () => this.localDb.put(doc),
      );

      console.log(`${logHeader} global view rebuild scheduled (version ${GLOBAL_VIEW_REBUILD_VERSION}, ${viewNames.length} views) — indexes rebuild on next query`);
      // one Sentry report per device: rollout visibility for the #4343 heal
      // (how many devices actually run the rebuild). INFORMATIONAL tier, not
      // problem tier: by the time this line runs the salts are bumped and
      // _local/view-heal is written, so the rebuild is arranged and nothing is
      // left for a person to act on. The tier is not only a label — problem
      // tier also marks the session as one in which something went wrong, which
      // switches off the store review prompt for the rest of it (#4625,
      // session-health.ts). A routine maintenance action must not do that.
      this.logging.trackSilentInfo('GlobalViewRebuildScheduled', {
        db: this.internalDatabaseIdentifier,
        version: GLOBAL_VIEW_REBUILD_VERSION,
        views: viewNames.length,
      });
      return true;
    } catch (err) {
      // never block DB init / view creation on the rebuild bookkeeping — but
      // surface it: a device that cannot record the rebuild keeps its
      // possibly-corrupt view indexes
      console.log(`${logHeader} applyGlobalViewRebuildIfNeededAsync failed:`, err);
      this.logging.trackSilentProblem('GlobalViewRebuildFailed', {
        db: this.internalDatabaseIdentifier, ...errorReportFields(err),
      });
      return false;
    }
  }

  /**
   * Request a from-scratch rebuild of one view's local index (#4219): bump the
   * view's per-device heal salt (persisted in `_local/view-heal`), re-write
   * the design doc with the salted map string (a changed string = a new
   * internal index store, built lazily on the next query), and clean up the
   * orphaned old store. Rate-limited to one bump per view per day.
   * Returns true when a rebuild was scheduled. Never throws.
   */
  async requestViewHealAsync(viewName: string): Promise<boolean> {
    if (!this.localDb) return false;
    const logHeader = `Db:${this.internalDatabaseIdentifier}:`;
    try {
      let doc: any;
      try {
        doc = await this.localDb.get('_local/view-heal');
      } catch (err) {
        if (!isPouchNotFound(err)) throw err;
        doc = { _id: '_local/view-heal' };
      }
      doc.salts = doc.salts || {};
      doc.lastBumpUtc = doc.lastBumpUtc || {};

      // the bump/refuse decision (24h rate limit + salt increment) is pure and
      // unit-tested in sync-view-heal-decisions.ts
      const decision = decideViewHealBump(
        doc.salts[viewName], doc.lastBumpUtc[viewName], DateTime.utc(),
        DatabaseWrapper.VIEW_HEAL_MIN_HOURS_BETWEEN_BUMPS,
      );
      if (!decision.shouldBump) {
        return false;
      }

      doc.salts[viewName] = decision.newSalt;
      doc.lastBumpUtc[viewName] = DateTime.utc().toISO();
      await this.localDb.put(doc);

      // writeViewDesignDocAsync reloads the salts from the just-written doc
      console.log(`${logHeader} view heal scheduled for '${viewName}' (salt ${doc.salts[viewName]}) — index rebuilds on next query`);
      await this.writeViewDesignDocAsync();

      // drop the orphaned old index store; failure only wastes disk, the
      // rebuild itself is already committed via the design doc — but count
      // the condition so repeated cleanup failures become visible
      try {
        await this.localDb.viewCleanup();
      } catch (err) {
        console.log(`${logHeader} viewCleanup after heal failed (stale index store remains):`, err);
        this.logging.trackSilentProblem('ViewHealCleanupFailed', {
          db: this.internalDatabaseIdentifier, view: viewName, ...errorReportFields(err),
        });
      }
      return true;
    } catch (err) {
      // never break the calling read path over a failed heal — the raw-docs
      // cross-check keeps results correct; but surface the failed heal itself
      console.log(`${logHeader} requestViewHealAsync failed for '${viewName}':`, err);
      this.logging.trackSilentProblem('ViewHealFailed', {
        db: this.internalDatabaseIdentifier, view: viewName, ...errorReportFields(err),
      });
      return false;
    }
  }

  /**
   * Run a map/reduce view query on the local DB.
   *
   * THROWS `DatabaseUnavailableError` when the query could NOT run (db shutting
   * down / recovering / no handle), because "unavailable" is not "no rows".
   * It used to return `null` for that, which callers were free to write off as
   * `?? []` — and mostly did, which is the bug this replaced. See the error
   * class for the reasoning.
   */
  async runQueryAsync(query: string, keys: string[], options?: any): Promise<{
    id: string,
    key: any,
    value: any,
    doc: any
  }[]> {
    const callSite = captureDatabaseCallSite();
    await this.enterReadSectionAsync(callSite);
    try {
      // view queries (map/reduce) are inherently local: the client builds its design docs only
      // on the local DB (ensureViewExists no-ops on remoteDb), so a view cannot run on the
      // pass-through remote — querying it 404s. use getWriteDb() (local, or temporaryDb when a
      // coach is impersonating) so a deep-link load during initial sync returns whatever is
      // already synced locally instead of erroring (#3469). see design-docs/pass-through-db.md.
      let db = this.getWriteDb()
      if (db == null) {
        throw this.unavailable(DatabaseUnavailableReason.NoDatabaseHandle, callSite);
      }
      await this.whenQueriesReady;

      // don't run meaningless empty queries (where string is null, or empty)
      if (keys) {
        keys = keys.filter(s => s);
        if (keys.length === 0) {
          return [];
        }
      } else if (!options || !(options.startkey || options.endkey)) {
        throw new Error('runQueryAsync: keys or startkey/endkey must be provided');
      }

      const runQuery = async (): Promise<{ rows: any[] }> => db.query('view/' + query, keys ? { keys, ...options } : { ...options });

      // run the query, creating the view on first miss. returns null when the error was
      // intercepted during recovery/shutdown — that null is INTERNAL, read only by the
      // view-build serializer below; the caller gets a throw for the same condition.
      const attemptQuery = async (): Promise<{ rows: any[] } | null> => {
        // first try running the query directly
        try {
          return await runQuery();
          // if that fails
        } catch (err) {
          if (this.databaseRecovery.interceptDatabaseError(err)) {
            return null;
          }
          // try to create the view and run it again (this time, we let eventual failure pass through)
          await this.ensureViewExists(db, query);
          return await runQuery();
        }
      };

      // serialize the first build of each view so the shared `_local/mrviews` registry doc
      // isn't upserted concurrently (#3466). keyed by db instance, since getWriteDb() can
      // switch instances (localDb vs temporaryDb). a null result (intercepted during recovery)
      // does not count as built, so it stays serialized for the next attempt.
      const res = await this.viewBuildSerializer.query(db, query, attemptQuery, r => r != null);

      if (res == null) {
        // The query was intercepted during recovery/shutdown — unavailable, not
        // empty. `attemptQuery` keeps returning `null` internally because the
        // view-build serializer above uses exactly that to decide the view did
        // NOT get built and must stay serialized for the next attempt; only this
        // outward-facing answer becomes a throw.
        throw this.unavailable(DatabaseUnavailableReason.Recovering, callSite);
      }

      // if include_docs is true, process docs through identity map. no pass-through graft
      // here: the query runs on the local DB (getWriteDb above), never the remote, so there is
      // nothing remote to graft into local.
      if (options?.include_docs) {
        const processedRows = res.rows.map(row => {
          if (row.doc && row.doc._id) {
            row.doc = this.getCachedDocument(row.doc);
          }
          return row;
        });
        return processedRows;
      }

      return res.rows;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  // Check if query exists in a database, and if not create it and upload it.
  private async ensureViewExists(db: PouchDB, query: string) {
    let design: any = {
      _id: '_design/view',
      _rev: undefined,
      views: {},
    };
    try {
      design = await db.get('_design/view');
    } catch (err) {
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        return;
      }
      if (!isPouchNotFound(err)) {
        this.dbProvider.logging.trackAbnormalException(err);
      }
    }
    if (!design.views[query]) {
      if (db !== this.remoteDb) {
        for (let i = 0; i < this.queriesToSave.length; i++) {
          let queryToSave = this.queriesToSave[i];
          if (queryToSave.name === query) {
            // render through the heal salt so a recreated view keeps its
            // rebuilt (salted) identity — an unsalted re-put here would undo
            // a scheduled index heal (#4219)
            design.views[queryToSave.name] = { map: this.renderViewMapString(queryToSave.name, queryToSave.map) };
            await db.put(design);
          }
        }
      }
    }
  }

  async findViaIndexAsync(request: any): Promise<any> {
    const callSite = captureDatabaseCallSite();
    await this.enterReadSectionAsync(callSite);
    try {
      await this.whenQueriesReady;
      return await this.remoteDb.find(request);
    } catch (err) {
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        // a recovery cut this read short — the answer is unknown, not "no docs"
        throw this.unavailable(DatabaseUnavailableReason.Recovering, callSite);
      }
      throw err;
    } finally {
      this.rwLock.releaseRead();
    }
  }

  static getTransactionId(): TransactionId {
    return (DateTime.utc().toISO() + `|${getDeviceId()}`) as TransactionId;
  }

  /**
   * Updates target object in place with source data, preserving PouchDB identity fields.
   * Uses deepOverwriteCouchSafe to ensure _id, _rev, and db__* are never overwritten.
   */
  private updateDocumentInPlace<T extends PouchDoc>(target: T, source: T): void {
    deepOverwriteCouchSafe(target, source);
  }

  /**
   * Copies data from source to target, preserving PouchDB internal fields.
   * @param target - The object to copy data into
   * @param source - The object to copy data from
   * @param options.clearTarget - If true, clears non-db fields from target before copying
   * @param options.preserveDbFields - If true (default), preserves _* and db__* fields
   */
  private copyDocumentData<T extends PouchDoc>(
    target: T,
    source: Partial<T>,
    options: { clearTarget?: boolean; preserveDbFields?: boolean } = {}
  ): void {
    const { clearTarget = false, preserveDbFields = true } = options;

    const shouldPreserve = (key: string) =>
      preserveDbFields && isPouchInternalKey(key);

    if (clearTarget) {
      for (const key of Object.keys(target)) {
        if (!shouldPreserve(key)) {
          delete (target as any)[key];
        }
      }
    }

    for (const key of Object.keys(source)) {
      if (!shouldPreserve(key)) {
        (target as any)[key] = (source as any)[key];
      }
    }
  }

  /**
   * Returns the canonical object reference for a document, together with the
   * keep-or-overwrite decision this call made about it.
   * If a cached reference exists, updates it with the provided data.
   * If not, registers the provided object as canonical.
   *
   * # Why the decision is reported back (#4860's deferred finding)
   *
   * Since #4860 this method may turn a body away instead of applying it, and the
   * pull path needs to know which happened: three things it does next depend on
   * it. It used to work the answer out for itself by asking
   * `decideCanonicalUpdateAction` a second time, with hand-copied arguments — so
   * the rule lived in two places and only a comment kept them in step. Reporting
   * the decision from here is what makes that second copy unnecessary. Callers
   * that do not care read `doc` and ignore `action`; `getCachedDocument` and
   * `ensureCachedReference` do that for them.
   *
   * @param entry - The document data (may be fresh from DB or generated)
   * @param updateExpectedRev - Whether to update expectedRev tracking (default: true)
   * @param sourceIsDatabaseBody - Whether `entry` is a body the DATABASE just
   *   gave us (a disk read, a view row, or a version arriving from the server),
   *   as opposed to an object a caller built or edited. Only the caller knows
   *   which, and it decides two things: whether content about to be replaced is
   *   worth reporting, and whether this body may be remembered as what the
   *   database holds. Defaults to false, so a caller that has not thought about
   *   it records nothing and reports nothing — see `getCachedDocument`.
   * @param siblingPolicy - What to do with a database body that is a sibling
   *   branch of the shared object. Defaults to keeping the shared object; only
   *   the pull path passes `ApplyIncoming`, and only for a body the fold really
   *   merged with this device's copy (df-f8cf38ad9fa8).
   * @returns the canonical object as `doc`, and as `action` the
   *   `CanonicalUpdateAction` this call decided — or `undefined` when it decided
   *   nothing at all, which is every path where there were not two different
   *   objects to choose between: no `_id`, nothing cached for this id so the
   *   object was registered as the canonical one, or the caller handing back the
   *   very object the cache already holds. `undefined` therefore means "nothing
   *   was turned away", the same as `Overwrite` does for a reader asking only
   *   whether the shared object kept its own content.
   */
  private getOrRegisterCanonicalReference<T extends PouchDoc>(
    entry: T,
    updateExpectedRev: boolean = true,
    sourceIsDatabaseBody: boolean = false,
    siblingPolicy: SiblingRevisionPolicy = SiblingRevisionPolicy.KeepSharedContent
  ): { doc: T; action: CanonicalUpdateAction | undefined } {
    if (!entry?._id) return { doc: entry, action: undefined };

    const cacheEntry = this.documentCache.get(entry._id);
    const cached = cacheEntry?.ref.deref() as T | undefined;

    if (cached) {
      // what this call decided, reported back to the caller that needs it. Stays
      // undefined when the caller handed back the very object the cache already
      // holds: there were not two bodies to choose between, so nothing was
      // replaced and nothing was turned away.
      let updateAction: CanonicalUpdateAction | undefined;
      if (cached !== entry) {
        // Decide FIRST, then report, then act. The order matters: two of the
        // reports below describe something being replaced, and after #4860 a body
        // from the past that came from the database is NOT replaced, so reporting
        // before deciding would put events that never happened into both of them.
        // the sibling policy comes from the CALLER, because only the caller knows
        // whether this body was folded into the local one (#4577) and so carries
        // both branches. Being a singleton is not enough: a plain read of one never
        // folds, and neither does a pull with no usable starting version
        // (df-f8cf38ad9fa8). Everything unfolded keeps its own branch —
        // decideCanonicalUpdateAction says why.
        updateAction = decideCanonicalUpdateAction(
          cached._rev, entry._rev, !!cached._deleted, sourceIsDatabaseBody, siblingPolicy);
        // the shared object keeps what it holds. A body the DATABASE gave us from
        // an earlier generation than the one this device already holds cannot be
        // the truth (#4860), and a sibling branch from the database would throw this
        // device's own latest writes out of view (the canary of mh-physique#5045) —
        // see decideCanonicalUpdateAction for both arguments, and for why a
        // caller's own out-of-date copy still overwrites.
        const keepWhatWeHold = isSharedContentKept(updateAction);
        if (updateAction === CanonicalUpdateAction.KeepSharedContentOverSibling) {
          this.reportSiblingRevisionTurnedAway(cached, entry);
        }

        // say so when the shared object holds content that never reached the
        // database and is about to be replaced by a body that did. Must run
        // BEFORE the overwrite, which is the last moment both are readable — and
        // only when there really is going to be one.
        if (sourceIsDatabaseBody && !keepWhatWeHold) {
          this.reportUnsavedContentAboutToBeReplaced(cacheEntry, cached, entry);
        }
        // the body handed in is older than the shared object. Say so BEFORE
        // anything is copied, while both revisions are still readable. Whether
        // newer content is destroyed depends on which door it came through: a
        // caller's copy still overwrites (design-docs/sync-integrity.md says why
        // repairing THAT one from here is not possible), a database body is turned
        // away. Both are counted, and `identity_map_overwrite_refused` below is
        // what tells them apart.
        if (isOutOfDateCopy(updateAction)) {
          // #4669: the two revisions alone could not say what CAUSED any one of
          // these, and reading the report in production showed it firing for about
          // one person in three every day — so the answer mattered. The facts added
          // below name the cause, and every one was already in scope here.
          const contentDiffers = staleCopyWouldChangeContent(cached, entry);
          // `initialSyncCompleted` says whether the first sync has FINISHED, so the
          // answer to "was it still running?" is the opposite of it — and undefined
          // means nobody knows yet, which is its own answer rather than a false.
          const initialSyncStillRunning = this.initialSyncCompleted === undefined
            ? 'unknown'
            : (this.initialSyncCompleted === true ? 'false' : 'true');
          this.logging.trackSilentProblem(
            'identity-map: an out-of-date copy overwrote newer content in the shared document',
            {
              docId: entry._id, canonicalRev: cached._rev, staleCopyRev: entry._rev,
              // the revision the app BELIEVED this document was at. Read here on
              // purpose: the line further down that re-syncs it to the shared
              // object's own revision has not run yet, so this is still the value
              // that was in force when the out-of-date body arrived. It is a
              // revision string, so it is high-cardinality and stays out of the
              // tags below.
              expectedRev: cacheEntry?.expectedRev ?? null,
            },
            {
              // as Sentry TAGS as well, so they can be grouped and counted — see
              // SentryReportTags in problem-kind.ts for why context alone cannot be,
              // and design-docs/sync-integrity.md, "What the report said when it was
              // first read", for what each answer would mean for the real fix.
              //
              // did the out-of-date body come from the DATABASE (a read, or a version
              // arriving from the server) or from a CALLER holding a copy? This is the
              // one that decides which repair is worth building.
              identity_map_source: sourceIsDatabaseBody ? 'database-body' : 'caller-copy',
              // whether anything was actually lost. The overwrite leaves every "_"
              // and "db__" field alone, so two revisions differing only in the
              // save-time stamp destroy nothing and still land here.
              identity_map_content_differs: contentDiffers ? 'true' : 'false',
              // whether the initial sync was still running. In that window reads are
              // served by the REMOTE server, which can legitimately answer with an
              // older revision than this device has already produced locally.
              // `unknown` is a real state and not a stand-in for false — the flag is
              // deliberately undefined until the first sync has settled either way.
              identity_map_initial_sync: initialSyncStillRunning,
              // how far behind. One generation is the shape a read racing a save
              // makes; a copy somebody has been editing drifts further back.
              identity_map_rev_gap: describeRevGap(cached._rev, entry._rev),
              // did the app keep what it held, or let the older body win? Before
              // #4860 every event here was the second answer, so the counts from
              // then mix the two together — a reader comparing releases needs this
              // to tell the eras apart. See design-docs/sync-integrity.md.
              identity_map_overwrite_refused: keepWhatWeHold ? 'true' : 'false',
            }
          );
        }

        if (!keepWhatWeHold) {
          this.updateDocumentInPlace(cached, entry);
        }

        // if the cached object has lost its own _id, restore it from the incoming
        // document. updateDocumentInPlace above never copies a "_" key, so nothing
        // else can put it back, and a cached doc with no _id can never be saved
        // again. must stay ABOVE the _rev catch-up below — see
        // design-docs/sync-integrity.md, "A record that reaches the save path with
        // no _id". trackSilentInfo because the heal is already done on the line
        // above; it is reported only so its producer stays findable.
        if (!hasUsableDocId(cached)) {
          cached._id = entry._id;
          this.logging.trackSilentInfo('identity-map: restored missing _id on cached doc', {
            docId: entry._id,
            cachedRev: cached._rev,
            entryRev: entry._rev,
          });
        }

        // deepOverwriteCouchSafe preserves _rev on the target, so if the source
        // (e.g. from sync or db.get) has a newer _rev, accept it to prevent
        // stale-_rev conflicts on the next save
        if (entry._rev && cached._rev !== entry._rev) {
          const sourceRevNum = parseInt(entry._rev.split('-')[0], 10) || 0;
          const cachedRevNum = parseInt((cached._rev || '').split('-')[0], 10) || 0;
          if (sourceRevNum > cachedRevNum) {
            cached._rev = entry._rev;
          }
        }

        // the shared object now holds the database's own body, so that is what a
        // later change to it will be measured against. Only for a body that
        // really came from the database: remembering a caller's edited copy here
        // would make its edits count as "what the database holds", and the next
        // genuine loss would then look like no change at all.
        //
        // Inside this branch on purpose. It is the branch where
        // `updateDocumentInPlace` above already deep-clones the whole body, which
        // is what makes one more walk of it a constant factor on work that was
        // happening anyway rather than a new cost.
        //
        // Never after a refusal either (#4860): the shared object then holds this
        // device's own newer content, which the database does NOT hold.
        this.rememberDatabaseBodyIfWatched(cacheEntry, cached, sourceIsDatabaseBody && !keepWhatWeHold);
        // if the same revision was kept out (#5904) and nothing is remembered yet, remember
        // the INCOMING body: it is exactly what the database holds, while the shared object
        // may carry unsaved edits. This used to happen as a side effect of the overwrite, and
        // without it a document that is only ever read has no baseline, so the unsaved-content
        // report could not fire when a newer revision later replaced edits made to it. Once
        // per document, so the walk is not paid on every later read.
        if (updateAction === CanonicalUpdateAction.KeepSharedContentAtSameRevision
          && cacheEntry.lastKnownDbBodyFingerprint === undefined) {
          this.rememberDatabaseBodyIfWatched(cacheEntry, entry, true);
        }
      }
      if (updateExpectedRev && cached._rev) {
        cacheEntry.expectedRev = cached._rev;
      }
      return { doc: cached, action: updateAction };
    }

    // Not in cache or GC'd - register this object as canonical. Note this
    // REPLACES any entry that was here, so a fingerprint belonging to a document
    // object that has since been collected cannot be left behind to be compared
    // against its replacement.
    //
    // NO fingerprint is taken here, deliberately. This branch is the one every
    // row of a view read passes through for a document not yet in the cache, and
    // it did no walk of the body at all before — so taking one here would put a
    // whole-document walk on the per-row read path that must stay cheap (the
    // #4243 freeze, and the still-open per-document cost in #4717). The next
    // database body applied over this document records one, from the branch
    // above where a deep clone is already being paid for. What that costs is the
    // first loss on a document, which goes unreported; documents are read again
    // and again, so the rate this produces is a slight undercount rather than a
    // blind spot — and undercounting is the safe direction, because it can only
    // ever lose a report and never invent one.
    this.documentCache.set(entry._id, {
      ref: new WeakRef(entry),
      expectedRev: entry._rev,
    });
    // no decision to report: this object BECAME the canonical one, so there was
    // never a second body for the keep-or-overwrite rule to choose between.
    return { doc: entry, action: undefined };
  }

  /**
   * Record what the database holds for a document, but only when that record
   * could ever be used (#4802's finding).
   *
   * Every caller that makes the shared object equal to a database body goes
   * through here, so the rule deciding whether to spend a walk lives in one
   * place. It asks `isDocumentWatchedForUnsavedContent` — the SAME rule the
   * report asks before comparing. That sharing is load-bearing rather than tidy:
   * a document is only ever compared against a fingerprint recorded under this
   * rule, so if the two sides disagreed, a document could be compared against a
   * fingerprint from further back than the report believes, and content that was
   * in fact saved would be reported as work somebody lost.
   */
  private rememberDatabaseBodyIfWatched(
    cacheEntry: DocumentCacheEntry,
    body: PouchDoc,
    sourceIsDatabaseBody: boolean
  ): void {
    if (!sourceIsDatabaseBody) return;
    const docId = body._id;
    if (!docId) return;
    if (!isDocumentWatchedForUnsavedContent({
      isSingleton: this.singletons[docId] != null,
      cachedIsDeleted: !!body._deleted,
      alreadyReportedThisSession: this.unsavedContentReplacedDocIds.has(docId),
      reportedDocumentCount: this.unsavedContentReplacedDocIds.size,
      maxReportedDocuments: DatabaseWrapper.MAX_UNSAVED_CONTENT_DOCS_REPORTED,
    })) {
      return;
    }
    cacheEntry.lastKnownDbBodyFingerprint = bodyFingerprint(body);
  }

  /**
   * Say when the shared object holds content that never reached the database and
   * a body from the database is about to replace it (#4802's deferred finding).
   *
   * Nothing is repaired here and nothing about the overwrite changes. What this
   * buys is a number: how many documents per session lose work this way. That
   * number is what decides whether the real fix is worth building — see
   * `design-docs/sync-integrity.md`, which describes that fix (capturing the
   * starting version at the moment a page copies a document) and why it has not
   * been built.
   *
   * **Which way round the loss runs cannot be known here, and the report does not
   * claim to know.** Sometimes the shared object holds a page's unsaved edit and
   * the overwrite destroys it. But the backlog cache can leave content there that
   * came from neither the database nor a user — it keeps a cached entry's content
   * while stamping the stored revision onto it — and for that case the overwrite
   * is a REPAIR. And several loaders heal a document in place at read time
   * without saving it, which is entirely correct behaviour that this still
   * counts. All of them are the same thing seen from here: the shared object
   * holds something the database never had. So the report says exactly that and
   * no more, and `design-docs/sync-integrity.md` lists the producers, because
   * anybody judging the rate needs to know what is in it.
   *
   * **Info tier, deliberately, and it is the one judgement here worth arguing
   * with.** By the usual test in `design-docs/telemetry.md` this would be a
   * problem: nothing has been repaired by the time it runs. It is filed as an
   * always-on canary instead, the same shape as `UserDbConflictsPresent`.
   * Nobody can act on a single one of these — the mechanism behind it is
   * deliberately unfixed and already written down, so the only thing anybody
   * wants from it is the rate. And it is common: the workout page's own version
   * of this report fires two to four times in a single CI workout scenario, and
   * this one sees every document rather than one field of one page. At problem
   * tier that is precisely the noise #4382 removed, and it would also turn the
   * E2E browser-error gate red on every run. If the measured rate turns out to be
   * low, moving it to problem tier is the right follow-up.
   *
   * **Sentry only, not Mixpanel** (df-e7ce2d515270). Its rate is read in Sentry,
   * which gets the same count because both pass one rate limiter, and its
   * Mixpanel `sentry_error` rows were high-volume and unused. So Mixpanel shows
   * none of these after that change — that is not the mechanism going away.
   */
  private reportUnsavedContentAboutToBeReplaced(
    cacheEntry: DocumentCacheEntry,
    cached: PouchDoc,
    incoming: PouchDoc
  ): void {
    const docId = cached._id;
    if (!docId) return;
    // every cheap question first — the comparison below walks the whole document
    // and sits on the path every view read takes
    if (!shouldCompareCanonicalAgainstDatabaseBody({
      rememberedFingerprint: cacheEntry.lastKnownDbBodyFingerprint,
      isSingleton: this.singletons[docId] != null,
      cachedIsDeleted: !!cached._deleted,
      alreadyReportedThisSession: this.unsavedContentReplacedDocIds.has(docId),
      reportedDocumentCount: this.unsavedContentReplacedDocIds.size,
      maxReportedDocuments: DatabaseWrapper.MAX_UNSAVED_CONTENT_DOCS_REPORTED,
    })) {
      return;
    }

    const currentFingerprint = bodyFingerprint(cached);
    // could not be worked out, or unchanged since the database last gave it to us
    if (!currentFingerprint || currentFingerprint === cacheEntry.lastKnownDbBodyFingerprint) {
      return;
    }

    this.unsavedContentReplacedDocIds.add(docId);
    this.logging.trackSilentInfo(
      'identity-map: a database body replaced content this device had not saved',
      {
        docId,
        docType: docTypeFromId(docId),
        db: this.internalDatabaseIdentifier,
        canonicalRev: cached._rev,
        incomingRev: incoming._rev,
        // which of the two shapes this is — see describeIncomingRevRelation
        incomingRevRelation: describeIncomingRevRelation(cached._rev, incoming._rev),
        distinctDocsThisSession: this.unsavedContentReplacedDocIds.size,
      },
      MixpanelCopy.Skip
    );
  }

  /**
   * Say that a database body was turned away because it was a SIBLING of the shared
   * object — the same generation, a different hash — so the document has two
   * branches and this device kept its own (mh-physique#5045).
   *
   * Info tier, because the refusal IS the repair: nothing was lost, the shared
   * object still holds this device's latest writes, and the on-device conflict
   * resolver is what merges the branches later. What the number is for is how many
   * documents sit with two branches at once, which the `UserDbConflictsPresent`
   * canary counts at rest and this counts at the moment a read meets one.
   *
   * Once per document per session and bounded, like `reportUnsavedContentAboutToBeReplaced`,
   * because a conflicted document is read on every screen refresh until the
   * branches merge. Sentry only, for the same reason that report is (df-e7ce2d515270).
   */
  private reportSiblingRevisionTurnedAway(cached: PouchDoc, incoming: PouchDoc): void {
    const docId = cached._id;
    if (!docId) return;
    if (this.siblingRevisionRefusedDocIds.has(docId)) return;
    if (this.siblingRevisionRefusedDocIds.size >= DatabaseWrapper.MAX_UNSAVED_CONTENT_DOCS_REPORTED) return;
    this.siblingRevisionRefusedDocIds.add(docId);
    this.logging.trackSilentInfo(
      'identity-map: a sibling revision from the database was turned away',
      {
        docId,
        docType: docTypeFromId(docId),
        db: this.internalDatabaseIdentifier,
        canonicalRev: cached._rev,
        incomingRev: incoming._rev,
        distinctDocsThisSession: this.siblingRevisionRefusedDocIds.size,
      },
      MixpanelCopy.Skip
    );
  }

  /**
   * Returns cached object reference for document, updating it with fresh data.
   * If no cached reference exists (or was GC'd), caches the fresh data.
   *
   * `freshData` is a body the database just produced — that is what separates
   * this from `ensureCachedReference`, which takes an object a caller built or
   * edited. Only this one may be remembered as what the database holds, and only
   * this one reports the content it replaces.
   *
   * Hands back the document alone. A caller that also needs to know whether the
   * identity map kept its own content instead of applying this body calls
   * `getCachedDocumentReportingAction` below.
   */
  private getCachedDocument<T extends PouchDoc>(freshData: T): T {
    return this.getCachedDocumentReportingAction(freshData).doc;
  }

  /**
   * `getCachedDocument`, plus the keep-or-overwrite decision the identity map
   * made about this body. Identical in every other effect — same call, same
   * arguments, same document back.
   *
   * See `getOrRegisterCanonicalReference` for what `action` means, when it is
   * undefined, and why reporting it back is worth a second wrapper. The pull
   * path is the one caller that passes `siblingPolicy`, because it alone knows
   * whether the body was folded with this device's copy; every read leaves it at
   * the default, which keeps the shared object's own branch (df-f8cf38ad9fa8).
   */
  private getCachedDocumentReportingAction<T extends PouchDoc>(
    freshData: T,
    siblingPolicy: SiblingRevisionPolicy = SiblingRevisionPolicy.KeepSharedContent
  ): { doc: T; action: CanonicalUpdateAction | undefined } {
    return this.getOrRegisterCanonicalReference(freshData, true, true, siblingPolicy);
  }

  /**
   * Ensures an entry uses the canonical documentCache reference.
   * Use this when entries may have been created outside the normal database load path
   * (e.g., generated schedule entries, entries from backlogCache).
   *
   * - If entry has _id and is in cache, returns the cached reference (updated in-place with entry's data).
   * - If entry has _id but not cached, registers it as canonical and returns it.
   * - If entry has no _id (new entry), returns as-is (will be cached on save).
   */
  public ensureCachedReference<T extends PouchDoc>(entry: T): T {
    return this.getOrRegisterCanonicalReference(entry, true).doc;
  }

  /** Note that _rev field in the input object will be updated to prevent conflicts if you continue editing this same document after saving.
   *
   * Throws `DatabaseUnavailableError` when the write could not be attempted at all, exactly as
   * the read methods do (df-6ce1f9d6830e). Returning quietly here meant a caller could not tell
   * "the document was written" from "the write was refused", and every caller in the app read
   * the quiet return as success — the food editor went on to unpublish a food from the global
   * searchable database while the user's own copy of it survived, and nothing said so.
   *
   * The window is teardown, which is narrow, but it is not harmless: a server-side effect
   * started on the strength of a write that never happened outlives the session that is ending.
   *
   * **It also ANSWERS with what became of the write (df-068808a2796e).** Almost every caller
   * ignores that answer and should: `DroppedIntoRecovery` means a failure was taken over by
   * the database recovery so nothing was written, and for an ordinary save that is genuinely
   * handled — the in-memory record still holds the user's edit, so their next save of the
   * same object carries it in. `deleteRecordAsync` is the caller that cannot live with it,
   * because a deleted object is never saved again and its `_deleted` mark is left standing.
   * Nothing about an ordinary save's behaviour changed when this return value was added.
   */
  async saveRecordAsync(e: PouchDoc, transaction: TransactionId, caching: boolean = false): Promise<WriteOutcome> {
    // capture where the caller called from before the first await loses it. it serves both the
    // identity map bypass report below and any refusal this save throws (#6205)
    const callSite = captureDatabaseCallSite();
    const userWrite = isUserDataWrite(e?._id);
    await this.enterReadSectionAsync(callSite);
    try {
      if (userWrite) this.userWritesInFlight++;
      // Global mutex ensures no concurrent writes (individual or bulk) can race
      return await this.writeMutex.exclusiveCall(() => this.saveRecordSerializedAsync(e, transaction, caching, callSite));
    } finally {
      if (userWrite) this.userWritesInFlight--;
      this.rwLock.releaseRead();
    }
  }

  /**
   * Serializes saves for the same document ID to prevent conflicts from concurrent saves.
   * Since writeMutex serializes all writes globally, what is left for this method to do is
   * maintaining canonical object references (the identity map).
   * It does NOT coalesce saves of the same document, whatever the shape of the code below
   * suggests — see the comment on the save state further down.
   * IMPORTANT: All saves of the same _id must use the same object reference.
   */
  private async saveRecordSerializedAsync(e: PouchDoc, transaction: TransactionId, caching: boolean, callSite: Error): Promise<WriteOutcome> {
    const docId = e._id;

    if (!docId) {
      throw new Error('Cannot save document without id');
    }

    let cacheEntry = this.documentCache.get(docId);
    if (!cacheEntry) {
      // First save of this document - register in cache
      cacheEntry = { ref: new WeakRef(e), expectedRev: e._rev };
      this.documentCache.set(docId, cacheEntry);
    }

    const canonical = cacheEntry.ref.deref();

    // Enforce same object reference - catch programming errors
    // This should be rare now that identity map ensures same references
    if (canonical && canonical !== e) {
      this.logging.trackSilentProblem(
        `saveRecordAsync: Different object reference for same _id "${docId}". Identity map may have been bypassed.`,
        // both revisions are reported so a bypass carrying a body from the past
        // can be told apart from a harmless one where the two agree. That count
        // is what decides whether the real fix — capturing the starting version
        // at the moment a copy is made — is worth building. Read them BEFORE the
        // copy below, which overwrites the canonical's revision.
        { docId, callerStack: callSite.stack, canonicalRev: canonical._rev, callerRev: e._rev }
      );
      // copy the caller's data onto the canonical object, so the save writes the
      // newest content this device has. preserveDbFields: false copies EVERY key,
      // the caller's _rev included, and that is deliberate: a body from the past
      // must keep its old revision so the save is refused and merged rather than
      // written through. Do not give this branch a revision of its own — see
      // design-docs/sync-integrity.md for what that used to cost.
      this.copyDocumentData(canonical, e, { preserveDbFields: false });
      // Use canonical for save
      e = canonical;
    } else if (!canonical) {
      // WeakRef was GC'd - register this object as new canonical
      cacheEntry.ref = new WeakRef(e);
      cacheEntry.expectedRev = e._rev;
    }

    // Initialize or update save state
    if (!cacheEntry.saveState) {
      cacheEntry.saveState = { saving: false, pendingTransaction: null };
    }
    cacheEntry.saveState.pendingTransaction = transaction;

    // `saving` is set for readers OUTSIDE this function — applyPulledDocToCache uses it to
    // let an arriving revision move expectedRev and leave the merge to the conflict handler.
    // Nothing in here reads it, and an early return that did was deleted as unreachable
    // (df-972f237dcc2e). Why this function still reads like a coalescer: the retracted entry
    // df-88283a37ede5 in design-docs/deferred-findings/2026-09-19T1150Z-failed-local-food-delete.md.
    cacheEntry.saveState.saving = true;
    // the loop can run several saves for one call (a later transaction arrives while an
    // earlier one is in flight). The LAST one decides the answer, because it is the one
    // that says what the database now holds for this record.
    let outcome = WriteOutcome.Written;
    try {
      // the body of this loop holds the only call that writes anything. A second pass
      // cannot happen while one mutex serializes every write, so the loop looks as
      // pointless as the early return was — it is not. Do not remove it.
      while (cacheEntry.saveState.pendingTransaction) {
        const txn = cacheEntry.saveState.pendingTransaction;
        cacheEntry.saveState.pendingTransaction = null;
        outcome = await this.saveRecordInternalAsync(e, txn, callSite, caching);
      }
    } finally {
      cacheEntry.saveState.saving = false;
      // Clean up saveState when no longer needed
      if (!cacheEntry.saveState.pendingTransaction) {
        delete cacheEntry.saveState;
      }
    }
    return outcome;
  }

  /**
   * After a successful db.put, sync the in-memory record and the identity map
   * with the new revision: write the new _rev back onto the record, remember
   * the rev as locally authored (so the change listener can skip the echo),
   * and update or create the documentCache entry (preserving saveState if
   * present). No-op when the put reported no rev.
   */
  private applyPutResponseToCache(e: PouchDoc, response: { ok?: boolean; rev?: string }): void {
    if (!response.ok || !response.rev) {
      return;
    }
    e._rev = response.rev;
    this.recordLocallyAuthoredRev(e._id, response.rev);
    // the database now holds exactly what we just wrote, so that becomes the
    // version a later arrival from the server is compared against (#4577)
    this.rememberSingletonDatabaseBody(e._id, e, RememberedBodySource.OwnSave);
    // update expected _rev (preserve saveState if present)
    let entry = this.documentCache.get(e._id);
    if (entry) {
      entry.ref = new WeakRef(e);
      entry.expectedRev = response.rev;
    } else {
      entry = { ref: new WeakRef(e), expectedRev: response.rev };
      this.documentCache.set(e._id, entry);
    }
    // the database holds this body now, so it is also the version any later
    // change to the shared object is measured against. This is the other place
    // that answers "what did the database last have?" — see
    // `rememberDatabaseBodyIfWatched`. Missing it would make a document that was
    // in fact saved read as work somebody lost. The walk is nothing beside the
    // write that just landed.
    this.rememberDatabaseBodyIfWatched(entry, e, true);
  }

  /**
   * Merge a record that is about to be saved with the version the database
   * actually holds, using the version this device started from as the tie-breaker
   * where there is one (#4577).
   *
   * The record being saved is the LOCAL side and the stored document is the
   * INCOMING one, so identity fields stay with the record the caller is holding —
   * the same roles the two-way merge here has always used.
   *
   * The remembered version is only used when it is provably the one this record
   * started from, which `canUseRememberedMergeBase` decides by comparing
   * revisions. Skipping that check is worse than having no starting version at
   * all — see that function for what it costs.
   *
   * Falls back to the two-way merge, and says so through `mergedWithoutBase`,
   * whenever there is no usable starting version: that is every document except
   * the roughly dozen singletons, plus any singleton whose remembered version has
   * moved on from the record in hand.
   */
  private mergeRecordAgainstStoredDoc(record: PouchDoc, storedDoc: any): CommonBaseMergeResult<any> {
    const base = this.singletons[record._id]?.lastKnownDatabaseBody;
    const usableBase = canUseRememberedMergeBase(base?._rev, record._rev) ? base : null;
    return mergeAgainstCommonBase<any>(usableBase, record, storedDoc);
  }

  /**
   * Report what a merge on the save path had to do (#4577). Called only for a
   * save whose body was a copy from the past — the case that used to be written
   * through unchecked and destroy data.
   */
  private reportSaveMergeOutcome(docId: string, result: CommonBaseMergeResult<any>, record: PouchDoc, storedDoc: any): void {
    const context = {
      docId,
      localOnlyChanges: result.localOnlyChangeCount,
      guessedFields: result.guessedFieldCount,
      hadStartingVersion: !result.mergedWithoutBase,
      // so a later reader can see WHY there was no usable starting version
      recordRev: record._rev,
      storedRev: storedDoc?._rev,
      rememberedBaseRev: this.singletons[docId]?.lastKnownDatabaseBody?._rev,
      // true means this device's own id stamped the stored version — usually a
      // second tab, which the report's wording cannot say (df-81b53714850e)
      writerWasThisDevice: this.collidingWriterWasThisDevice(storedDoc),
    };
    switch (decideMergeReportTier(result)) {
      case MergeReportTier.Problem:
        if (result.mergedWithoutBase) {
          // Worth its own message rather than sharing the one below, because the
          // outcome is worse than "a value was picked by a rule". With no version
          // to reason from, nothing can tell who changed what, so the whole
          // document is settled by the two-way rule — and that rule leans towards
          // the body being saved, out of date or not, because a value-versus-value
          // tie goes to the newer db__trans write stamp and the saving device
          // always holds it. It is not a total loss: the two-way rule also keeps
          // the longer of two arrays and a real value over a missing one, whichever
          // side those came from. This is the shape that loses data, and it must
          // read that way without overstating it.
          this.logging.trackSilentProblem(
            'save-merge: stale body merged with no usable starting version, so differing fields were settled by the two-way rule, which leans towards the saving device',
            context
          );
          break;
        }
        // both sides changed the same field, so a value was picked by a rule
        // rather than known to be right
        this.logging.trackSilentProblem('save-merge: stale body merged with values chosen by rule', context);
        break;
      case MergeReportTier.Info:
        // every difference had exactly one author: the stale save landed without
        // losing anything, which is the repair working
        this.logging.trackSilentInfo('save-merge: stale body merged, database content kept', context);
        break;
      case MergeReportTier.Nothing:
        // the stale body changed nothing of its own, so there was nothing at stake
        break;
    }
  }

  /**
   * Shared merge-retry for a local put that hit a 409 conflict (#3987 A1).
   * The single-doc save path and the bulk save path both delegate here, so
   * there is exactly one merge-retry implementation: fetch the current doc
   * from the database, merge the in-memory record onto it
   * (mergeRecordAgainstStoredDoc — three-way against the version this device
   * started from where one is remembered; otherwise the two-way rule where value
   * ties go to the newer db__trans side, falling back to the local record when
   * recency is unknown, internal #42), and put again with the database's _rev.
   *
   * `bodyIsStale` says the caller already knew this record carried a revision
   * older than the one the app expected. It only decides whether the merge
   * outcome is reported: an ordinary racing save reaches this merge constantly
   * and is not a problem, so reporting those would bury the case that is (#4577).
   *
   * Answers `Written` when the record reached the database, `DroppedIntoRecovery`
   * when the failure was taken over by interceptDatabaseError (intercepted =
   * handled — e.g. an IndexedDB connection loss that triggers the recovery), and
   * `Lost` when the write is genuinely gone and the CALLER must surface the
   * drop: saveRecordInternalAsync throws (its long-standing behavior), while
   * saveBulkAsync reports each lost row via trackSilentProblem instead of
   * throwing, because a single bad row must not abort the other rows.
   *
   * The first two used to share one `true`, which is how a delete whose write was
   * swallowed by the recovery was answered as a success (df-068808a2796e). Nothing
   * about an ordinary SAVE changed: `Written` and `DroppedIntoRecovery` are treated
   * alike everywhere except the two delete methods.
   *
   * NOTE: callers already hold writeMutex — never call saveRecordAsync /
   * saveRecordSerializedAsync from in here (same mutex, would deadlock).
   */
  private async retryPutWithMergeAsync(db: DbHandle, e: PouchDoc, bodyIsStale: boolean = false): Promise<WriteOutcome> {
    const attemptedRev = e._rev; // capture before overwriting
    let existing: any;
    try {
      existing = await db.get(e._id);
    } catch (getErr) {
      if (this.databaseRecovery.interceptDatabaseError(getErr)) {
        // #5129: intercepted means the failure was TAKEN, not that the record was written.
        // Report it if the database was repaired in place, because nothing else records the
        // loss — and answer DroppedIntoRecovery rather than Written, so a delete on this rung
        // is not told it succeeded (df-068808a2796e).
        this.reportWriteDrop(e, WriteDropSite.MergeRetryRead);
        return WriteOutcome.DroppedIntoRecovery;
      }
      // what the read-db recovery answered, for the lost-write line below (undefined when
      // the get error was not a not-found, so the recovery never ran)
      let recoveryAnswer: ReadDbRecoveryAnswer | undefined;
      // pass-through recovery (#3987 follow-up): during the initial-sync
      // window, reads route to remote while writes go to local, so a record
      // can carry a revision the local db has not pulled yet. when the local
      // doc is missing, fetch the current doc from the read db and graft it
      // into local — then the normal merge + put below extends the grafted
      // revision tree and the write is saved instead of dropped. see
      // recoverMissingDocViaReadDbAsync in database-wrapper-save-recovery.ts
      // and design-docs/pass-through-db.md.
      if (isPouchNotFound(getErr)) {
        const readDb = this.getReadDb();
        const recovery = await recoverMissingDocViaReadDbAsync({
          readDb,
          writeDb: db,
          getFromReadDb: (docId) => readDb.get(docId),
          graftToLocal: (docs) => this.graftPassThroughDocs(readDb, docs, { notifyWatchers: false }),
          log: (message) => console.log(message),
          reportReadFailure: (docId, readErr) => this.logging.trackSilentProblem('PassThroughRecoveryReadFailed', {
            db: this.internalDatabaseIdentifier,
            doc_type: docTypeFromId(docId),
            ...errorReportFields(readErr),
          }),
        }, e._id);
        recoveryAnswer = recovery.answer;
        if (recovery.answer === ReadDbRecoveryAnswer.Recovered) {
          existing = recovery.doc;
        }
        // a DELETION whose recovery read failed stops here and is lost (df-4ebed2f69e4a).
        // v2 would answer it as a success no-op ("already deleted"), but nothing showed the
        // doc was gone: the server may still hold it live, so the deletion would land
        // nowhere and come back on the next pull. answering Lost lets the delete callers
        // put the mark back and say the delete did not happen.
        // a LIVE record whose read failed still goes to v2's fresh create, on purpose.
        // the local store lacks the server's revision, so the only other choice is to
        // write nothing, which loses the edit outright; the fresh create keeps it as a
        // conflict branch that the on-device conflict resolver merges back (#4314). see
        // design-docs/pass-through-db.md, "A recovery read that fails".
        const deletionWithUnknownServerState = recovery.answer === ReadDbRecoveryAnswer.ReadFailed && !!e._deleted;
        // v2 (#3987 follow-up — the canary tombstone shape): when the read db
        // could not supply the doc either, the local winner is deleted or the
        // doc is gone everywhere (both reason=deleted and reason=missing).
        // a deletion of an already-deleted doc is a success no-op; a live
        // record is retried as a fresh create (PouchDB grafts a rev-less
        // create onto a deleted winner). because this branch lives in the
        // SHARED merge-retry, it also heals the narrow single-doc race where
        // the doc is deleted between the single-doc caller's own not-found
        // probe and the re-fetch above — that race used to throw and now
        // recovers, which is an intentional improvement.
        if (existing == null && !deletionWithUnknownServerState) {
          const outcome = await this.tryRecoverPutOnDeletedOrAbsentDocAsync(db, e, attemptedRev, bodyIsStale);
          if (outcome !== WriteOutcome.Lost) {
            return outcome;
          }
        }
      }
      if (existing == null) {
        // both recoveries failed (or the get error was not a not-found):
        // the write is genuinely lost — report it as such. (the single-doc
        // caller additionally handles its vanished-doc case BEFORE
        // delegating here.)
        // reason / attemptedRev / passThrough give the forensic context
        // needed to classify the miss (stale remote rev vs deleted tombstone
        // vs closed window) — added while investigating the canary console
        // gate failure caused by drops during the initial-sync pass-through
        // window. NOTE: `.reason` is interpolated for DIAGNOSTICS ONLY —
        // control flow above uses isPouchNotFound per the CLAUDE.md rule
        // ("never check err.reason"); reason is the one field that tells a
        // missing doc ("missing") apart from a deleted one ("deleted").
        const passThroughActive = this.getReadDb() !== this.getWriteDb();
        console.error(`retryPutWithMergeAsync: could not read current doc for ${e._id}: ${(getErr as any)?.message || getErr} `
          + `(reason=${(getErr as any)?.reason}, attemptedRev=${attemptedRev}, passThrough=${passThroughActive}, `
          + `readDbRecovery=${recoveryAnswer ?? 'not-run'}, deleted=${!!e._deleted})`);
        return WriteOutcome.Lost;
      }
    }

    // merge conflicting documents instead of last-write-wins. When this device
    // remembers the version the database held before it started editing, that
    // version settles who CHANGED each field, which is the only way to tell a
    // value somebody wrote from one an older copy was merely carrying (#4577).
    // Without it — every document that is not a singleton — this falls back to
    // the two-way merge that has always been used here, so nothing changes.
    const mergeResult = this.mergeRecordAgainstStoredDoc(e, existing);
    const merged = mergeResult.merged;
    // #3992 R3: event-time conflict-merge telemetry (prevalence-at-rest is the
    // #3987 sync_conflict_scan canary's job; this measures merge frequency)
    this.telemetry?.trackEvent('sync_conflict_merged', {
      doc_type: docTypeFromId(e._id),
      flow: 'stale-rev',
    });
    // Report only when the body being saved was a copy from the past. An
    // ordinary racing save reaches this merge all the time and is not a problem,
    // so reporting those would bury the case that is (#4577).
    if (bodyIsStale) {
      this.reportSaveMergeOutcome(e._id, mergeResult, e, existing);
    }

    // copy merged data back to e (preserve object reference and db fields)
    this.copyDocumentData(e, merged, { clearTarget: true, preserveDbFields: true });

    // use DB's _rev for retry
    e._rev = existing._rev;

    // log the conflict — this is expected when sync and saves race at the IndexedDB level
    // (PouchDB replication can write a new _rev between our _rev check and db.put).
    // the merge handler resolves it correctly, so this is informational only.
    console.log(`retryPutWithMergeAsync: Conflict detected for ${e._id} (attemptedRev=${attemptedRev}, dbRev=${existing._rev}, merged=true)`);

    try {
      const response = await db.put(e);
      this.applyPutResponseToCache(e, response);
      return WriteOutcome.Written;
    } catch (retryErr) {
      if (this.databaseRecovery.interceptDatabaseError(retryErr)) {
        this.reportWriteDrop(e, WriteDropSite.MergeRetryPut);
        return WriteOutcome.DroppedIntoRecovery;
      }
      // the merge-retry put itself conflicted with a real 409. the shape that
      // reaches here (nightly #4010 / #3987 follow-up): during the pass-through
      // window the LOCAL doc is a tombstone that DESCENDS FROM the rev v1
      // grafted above (e.g. the user-switch cleanup deleted it locally while
      // the remote still has it live at the parent rev). the graft was then a
      // no-op — that rev is already a non-leaf — and this put cannot extend a
      // non-leaf rev, so it 409s. v1 returned the doc (non-null), so the v2
      // deleted/absent recovery never ran; run it now. for a LIVE record v2
      // issues a rev-less create, which PouchDB grafts over the tombstone (or,
      // if a live doc arrived via sync meanwhile, v2 merges onto it once).
      const err = retryErr as any;
      const isConflict = isPouchConflict(err);
      if (isConflict && !e._deleted) {
        const outcome = await this.tryRecoverPutOnDeletedOrAbsentDocAsync(db, e, attemptedRev, bodyIsStale);
        if (outcome !== WriteOutcome.Lost) {
          return outcome;
        }
      }
      // a conflicting DELETION cannot go through v2's fresh-create (that would
      // resurrect the doc). it also must NOT be blanket-excluded and surfaced:
      // the failing nightly shape (#4010 — "Cookie Conflict on User Switch"
      // WeighIn) is a delete whose target is ALREADY a tombstone locally but
      // still live on the remote, so the stale-rev delete 409s even though the
      // deletion's goal is already met. recoverDeleteOnConflictAsync re-probes
      // the current local winner: already-deleted/absent is a success no-op
      // (the local tombstone syncs to the remote); a LIVE winner (delete truly
      // un-landed) still answers WriteOutcome.Lost and surfaces below — the pre-existing
      // "un-landed deletions must surface" policy, without clobbering a
      // concurrent edit.
      if (isConflict && e._deleted) {
        const outcome = await this.tryRecoverDeleteOnConflictAsync(db, e, attemptedRev);
        if (outcome !== WriteOutcome.Lost) {
          return outcome;
        }
      }
      // the merge-retry put failed and could not be healed (a non-conflict
      // error such as an IndexedDB write failure, a deletion whose live winner
      // means it truly did not land, or a conflict v2 could not resolve).
      // log the ACTUAL cause before answering WriteOutcome.Lost: the bulk caller reports
      // the drop using the ORIGINAL bulkDocs row (a 409), so without this line
      // a non-conflict terminal failure is mis-attributed as a conflict in the
      // telemetry. `deleted=` disambiguates a lost deletion from a lost live
      // write (the #4010 observability gap). mirrors the forensic logging on
      // the get-catch path above.
      console.error(`retryPutWithMergeAsync: merge-retry put for ${e._id} failed and was not recovered `
        + `(status=${err?.status}, name=${err?.name}, deleted=${!!e._deleted}, message=${err?.message || retryErr}, attemptedRev=${attemptedRev})`);
      return WriteOutcome.Lost;
    }
  }

  /** #5129: the collaborators reportWriteDroppedIntoGracefulRecovery needs, built once
   *  here rather than at each of the sites that lost a write. */
  private writeDropReportDeps() {
    return {
      whenRecoverySettled: () => this.databaseRecovery.whenDatabaseRecoverySettledAsync(),
      trackSilentProblem: (name: string, context: Record<string, unknown>, tags?: Record<string, string>) =>
        this.logging.trackSilentProblem(name, context, tags),
    };
  }

  /** #5129: report a write this wrapper just lost to a database repair. One place rather
   *  than the same call written out at every rung of the save ladder, so a new rung cannot
   *  pass the wrong document. Fire-and-forget, like the helper it delegates to. */
  private reportWriteDrop(e: PouchDoc, site: WriteDropSite) {
    reportWriteDroppedIntoGracefulRecovery(this.writeDropReportDeps(), e._id, site);
  }

  /** Thin wiring for the v2 deleted/absent-doc put recovery: builds the
   *  injected deps from this wrapper's collaborators and delegates to the
   *  pure recoverPutOnDeletedOrAbsentDocAsync (kept in
   *  database-wrapper-save-recovery.ts for node-load unit testability).
   *  Extracted so retryPutWithMergeAsync's catch block stays flat. Answers
   *  `Written` when the write was saved or is a legitimate no-op (deletion of an
   *  already-deleted doc), `DroppedIntoRecovery` when the database recovery took a
   *  failure and nothing was written, and `Lost` when the caller must surface the
   *  drop. */
  private async tryRecoverPutOnDeletedOrAbsentDocAsync(db: DbHandle, e: PouchDoc, attemptedRev: string | undefined, bodyIsStale: boolean = false): Promise<WriteOutcome> {
    return recoverPutOnDeletedOrAbsentDocAsync({
      ...this.writeDropReportDeps(),
      putToWriteDb: (record) => db.put(record),
      getFromWriteDb: (docId) => db.get(docId),
      interceptDatabaseError: (err) => this.databaseRecovery.interceptDatabaseError(err),
      applyMergeOntoDbDoc: (record, dbDoc) => {
        // same three-way merge as the other conflict paths, for the same reason:
        // the record in hand may be a copy from the past (#4577)
        const mergeResult = this.mergeRecordAgainstStoredDoc(record, dbDoc);
        const merged = mergeResult.merged;
        // #3992 R3: this merge only runs when the v2 recovery found a live
        // doc where a deleted/absent one was expected — a distinct flow
        this.telemetry?.trackEvent('sync_conflict_merged', {
          doc_type: docTypeFromId(record._id),
          flow: 'deleted-recovery',
        });
        // same rule as the ordinary conflict path: report only a save whose body
        // was already known to be from the past (#4577)
        if (bodyIsStale) {
          this.reportSaveMergeOutcome(record._id, mergeResult, record, dbDoc);
        }
        this.copyDocumentData(record, merged, { clearTarget: true, preserveDbFields: true });
        record._rev = dbDoc._rev;
      },
      onSaved: (record, putResponse) => this.applyPutResponseToCache(record, putResponse as any),
      clearExpectedRev: (docId) => {
        const cacheEntry = this.documentCache.get(docId);
        if (cacheEntry) cacheEntry.expectedRev = undefined;
      },
      log: (message) => console.log(message),
      logError: (message) => console.error(message),
    }, e, attemptedRev);
  }

  /** Thin wiring for the delete-conflict recovery (nightly #4010): builds the
   *  injected deps and delegates to the pure recoverDeleteOnConflictAsync (kept
   *  in database-wrapper-save-recovery.ts for node-load unit testability). Only
   *  the read/classify collaborators are needed — the recovery never writes,
   *  so it takes neither putToWriteDb, onSaved nor the merge helper. Answers
   *  `Written` when the deletion's goal is already met locally (no-op success),
   *  `DroppedIntoRecovery` when the re-probe read was taken over by the database
   *  recovery, and `Lost` when the delete truly did not land and the caller must
   *  surface it. */
  private async tryRecoverDeleteOnConflictAsync(db: DbHandle, e: PouchDoc, attemptedRev: string | undefined): Promise<WriteOutcome> {
    return recoverDeleteOnConflictAsync({
      ...this.writeDropReportDeps(),
      getFromWriteDb: (docId) => db.get(docId),
      interceptDatabaseError: (err) => this.databaseRecovery.interceptDatabaseError(err),
      clearExpectedRev: (docId) => {
        const cacheEntry = this.documentCache.get(docId);
        if (cacheEntry) cacheEntry.expectedRev = undefined;
      },
      log: (message) => console.log(message),
      logError: (message) => console.error(message),
    }, e, attemptedRev);
  }

  /**
   * Recovery for a save conflict on a doc that no longer exists locally
   * (lost during an interrupted sync or never persisted): retry as a fresh
   * create by clearing _rev. If the fresh create ALSO conflicts, the doc
   * arrived via sync between the caller's probe and our put — merge the
   * record onto the synced doc and put once more. A genuinely lost write
   * THROWS so the caller surfaces the drop (#3987 D1); a failure handled by
   * interceptDatabaseError returns `DroppedIntoRecovery`, which says the failure
   * was taken over and NOTHING WAS WRITTEN. Only the delete paths act on that —
   * for an ordinary save it is handled exactly as `Written` is (df-068808a2796e).
   *
   * Sibling: the BULK path's equivalent is the pure
   * recoverPutOnDeletedOrAbsentDocAsync in database-wrapper-save-recovery.ts —
   * same fresh-create-then-merge shape, but it answers `Lost` instead of
   * throwing, and it short-circuits a _deleted record to a success no-op
   * (this single-doc helper instead fresh-creates the tombstone; same end
   * state, different mechanics). If you change ONE of them, check the other.
   *
   * NOTE: the caller already holds writeMutex — never call saveRecordAsync /
   * saveRecordSerializedAsync from in here (same mutex, would deadlock).
   */
  private async recoverVanishedDocConflictAsync(db: DbHandle, e: PouchDoc, attemptedRev: string | undefined, bodyIsStale: boolean = false): Promise<WriteOutcome> {
    // log as info — the fresh-create retry is a valid recovery path, not an error
    console.log(`saveRecordInternalAsync: Conflict for ${e._id} but document not found locally. Retrying as fresh create. (attemptedRev=${attemptedRev})`);
    delete e._rev;
    const lostCacheEntry = this.documentCache.get(e._id);
    if (lostCacheEntry) lostCacheEntry.expectedRev = undefined;
    try {
      const response = await db.put(e);
      this.applyPutResponseToCache(e, response);
      return WriteOutcome.Written;
    } catch (freshCreateErr) {
      // if the fresh create failed with anything OTHER than a conflict, there is
      // nothing further to merge against — surface the loss
      if (!isPouchConflict(freshCreateErr)) {
        if (this.databaseRecovery.interceptDatabaseError(freshCreateErr)) {
          this.reportWriteDrop(e, WriteDropSite.VanishedDocFreshCreate);
          return WriteOutcome.DroppedIntoRecovery;
        }
        // the write is genuinely lost — surface it like the normal conflict path (#3987 D1)
        console.error(`saveRecordInternalAsync: Fresh create retry failed (non-conflict) for ${e._id}: ${freshCreateErr?.message || freshCreateErr}`);
        throw freshCreateErr;
      }
      // the fresh create also conflicted: the document arrived via sync between
      // the caller's probe and our put — fetch it and merge our changes (same
      // approach as the normal conflict path)
      let syncedDoc: any;
      try {
        syncedDoc = await db.get(e._id);
      } catch (getErr2) {
        if (this.databaseRecovery.interceptDatabaseError(getErr2)) {
          this.reportWriteDrop(e, WriteDropSite.VanishedDocRead);
          return WriteOutcome.DroppedIntoRecovery;
        }
        // the write is genuinely lost — surface it like the normal conflict path (#3987 D1)
        console.error(`saveRecordInternalAsync: Fresh create conflict for ${e._id} but document still not found: ${getErr2?.message || getErr2}`);
        throw new Error(`Failed to save document ${e._id} — fresh-create conflict but current doc unreadable: ${getErr2?.message || getErr2}`);
      }
      // same three-way merge as the ordinary conflict path above, for the same
      // reason: the record in hand may be a copy from the past (#4577)
      const vanishedMergeResult = this.mergeRecordAgainstStoredDoc(e, syncedDoc);
      const merged = vanishedMergeResult.merged;
      // #3992 R3: event-time conflict-merge telemetry for the fresh-create
      // path (the doc arrived via sync between the caller's probe and the put)
      this.telemetry?.trackEvent('sync_conflict_merged', {
        doc_type: docTypeFromId(e._id),
        flow: 'fresh-create',
      });
      // same rule as the ordinary conflict path: report only a save whose body
      // was already known to be from the past (#4577)
      if (bodyIsStale) {
        this.reportSaveMergeOutcome(e._id, vanishedMergeResult, e, syncedDoc);
      }
      this.copyDocumentData(e, merged, { clearTarget: true, preserveDbFields: true });
      e._rev = syncedDoc._rev;
      console.log(`saveRecordInternalAsync: Fresh create conflict for ${e._id}, merged with synced doc (syncedRev=${syncedDoc._rev})`);
      try {
        const response = await db.put(e);
        this.applyPutResponseToCache(e, response);
        return WriteOutcome.Written;
      } catch (mergeRetryErr) {
        if (this.databaseRecovery.interceptDatabaseError(mergeRetryErr)) {
          this.reportWriteDrop(e, WriteDropSite.VanishedDocMergePut);
          return WriteOutcome.DroppedIntoRecovery;
        }
        // the write is genuinely lost — surface it like the normal conflict path (#3987 D1)
        console.error(`saveRecordInternalAsync: Merge retry also failed for ${e._id}: ${mergeRetryErr?.message || mergeRetryErr}`);
        throw mergeRetryErr;
      }
    }
  }

  /** Internal save implementation - use saveRecordAsync instead.
   *
   *  Answers whether the record actually reached the database. `DroppedIntoRecovery`
   *  means a failure was taken over by `interceptDatabaseError` and NOTHING WAS
   *  WRITTEN, which this method used to report as an ordinary success
   *  (df-068808a2796e). A genuinely lost write still throws, as it always has. */
  private async saveRecordInternalAsync(e: PouchDoc, transaction: TransactionId, callSite: Error, _caching: boolean = false): Promise<WriteOutcome> {
    if (!e._id) {
      throw new Error('Cannot save document without id');
    }
    if (!transaction) {
      throw new Error(`Cannot save document ${e._id} - valid transaction not provided`);
    }
    let db = this.getWriteDb();
    if (db == null) {
      // after shutdown, in-flight saves may still arrive. Refusing one is fine; pretending it
      // was written is not, so say so rather than returning (df-6ce1f9d6830e). The caller reads
      // this the same way it reads a refused READ — see `database-unavailable-error.ts`.
      throw this.unavailable(DatabaseUnavailableReason.NoDatabaseHandle, callSite);
    }

    e.db__trans = transaction; // note - using db__trans as pouchdb/couchdb reserve all members beginning with underscore

    // Compare the revision this body carries against the one we expected (#4577)
    const cacheEntry = this.documentCache.get(e._id);
    const revMismatch = classifySaveRevMismatch(cacheEntry?.expectedRev, e._rev);
    // a body from the past must not be written through as if it were current —
    // see the stale branch below, and the post-put check further down
    const bodyIsStale = revMismatch === SaveRevMismatch.BodyStale;
    if (cacheEntry && revMismatch === SaveRevMismatch.BodyNewer) {
      // Document has a newer _rev than expected - expectedRev is stale, update it
      // This shouldn't happen with proper sync handling, report for visibility
      this.logging.trackSilentProblem(
        `expectedRev stale for ${e._id}: ` +
        `expected ${cacheEntry.expectedRev}, found ${e._rev}. Updating expectedRev.`,
        { docId: e._id }
      );
      cacheEntry.expectedRev = e._rev;
    }
    // NOTE the stale case deliberately does nothing here. This used to raise the
    // body's _rev to the expected one and write it through, which made the save
    // land as an ordinary child of the current revision — no conflict, so the
    // merge below never ran, and a several-day-old copy of a document silently
    // replaced everything saved since. That is how a customer lost the
    // progression of two whole training sessions. Leaving the old _rev in place
    // makes PouchDB answer 409, which routes the save into retryPutWithMergeAsync
    // where the two versions are merged instead. See #4577 and
    // design-docs/sync-integrity.md.

    // capture the two revisions BEFORE the put. applyPutResponseToCache writes
    // the new revision onto both the record and the cache entry, so reading them
    // afterwards would report the same value twice and lose the mismatch the
    // report exists to describe.
    const revExpectedBeforePut = cacheEntry?.expectedRev;
    const revOnBodyBeforePut = e._rev;

    // try to put the doc into the database
    try {
      const response = await db.put(e);
      this.applyPutResponseToCache(e, response);
      if (bodyIsStale) {
        // a body from the past was expected to be refused, and was not: the
        // revision we expected is one the local database does not actually hold
        // (a graft that never landed, for instance). It has now been written
        // whole, so anything the other version changed is gone from this
        // revision — unresolved, and the one shape this fix does not cover.
        this.logging.trackSilentProblem('save: stale body landed with no conflict to merge against', {
          docId: e._id,
          revExpected: revExpectedBeforePut,
          revOnBody: revOnBodyBeforePut,
          revWritten: response?.rev,
        });
      }
      return WriteOutcome.Written;
      // in case of conflict
    } catch (err) {
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        // #5129: the save is over and nothing was written. Report it, because nothing
        // else records this one.
        this.reportWriteDrop(e, WriteDropSite.FirstPut);
        // ...and SAY so, rather than returning the same answer a real write gets
        // (df-068808a2796e). An ordinary save still treats this as done — its record
        // keeps the user's edit and the next save carries it in — but a delete has no
        // next save, and its `_deleted` mark is left standing on the shared object.
        return WriteOutcome.DroppedIntoRecovery;
      }

      // On conflict, fetch latest _rev and retry
      // This handles race conditions from concurrent saves and sync
      if (isPouchConflict(err)) {
        const attemptedRev = e._rev;  // Capture before overwriting
        try {
          // probe whether the doc still exists locally — a conflict on a
          // vanished doc has its own recovery (fresh create) in the catch below.
          // NOTE: retryPutWithMergeAsync re-fetches the doc itself, so the happy
          // path reads twice ON PURPOSE — the probe owns not-found detection,
          // the helper's own fetch keeps it self-contained for the bulk caller.
          // do not "optimize" one of the two reads away.
          await db.get(e._id);
        } catch (getErr) {
          if (isPouchNotFound(getErr)) {
            // document gone from local db (lost during interrupted sync or never
            // persisted) — recover by retrying as a fresh create; the helper throws
            // when the write is genuinely lost (#3987 D1)
            return await this.recoverVanishedDocConflictAsync(db, e, attemptedRev, bodyIsStale);
          }
          throw getErr;
        }

        // delegate to the shared merge-retry helper — single implementation,
        // also used by saveBulkAsync's conflict recovery (#3987 A1)
        // `outcome`, not `saved` — the answer can be DroppedIntoRecovery, which means
        // nothing was saved, and naming it after only one of its values is the conflation
        // this whole change exists to break (df-068808a2796e).
        const outcome = await this.retryPutWithMergeAsync(db, e, bodyIsStale);
        if (outcome === WriteOutcome.Lost) {
          throw new Error(`Failed to save document ${e._id} after conflict retry`);
        }
        return outcome;
      }

      throw err;
    }
  }

  // direct access, without our conflict resolution - used for user databases.
  // a refused write throws rather than returning, the same as saveRecordAsync
  // (df-fa9423f851b0): the caller handles the details itself, so it is the only
  // party that could react, and a quiet return reads as a completed put.
  async saveRecord_rawAsync(e: any): Promise<void> {
    const callSite = captureDatabaseCallSite();
    const userWrite = isUserDataWrite(e?._id);
    await this.enterReadSectionAsync(callSite);
    try {
      if (userWrite) this.userWritesInFlight++;
      let db = this.getWriteDb();
      if (db == null) throw this.unavailable(DatabaseUnavailableReason.NoDatabaseHandle, callSite);
      let response = await db.put(e);
      // update _rev so future writes don't cause conflicts
      if (response.ok && response.rev) {
        e._rev = response.rev;
        this.recordLocallyAuthoredRev(e._id, response.rev);
      }
    } finally {
      if (userWrite) this.userWritesInFlight--;
      this.rwLock.releaseRead();
    }
  }

  // a refused write throws rather than answering with no rows, the same as
  // saveRecordAsync and saveRecord_rawAsync (df-aa69b381d6d6): an empty row list
  // is also the answer for an empty batch, so a quiet return reads as a completed save.
  async saveBulkAsync(transaction: TransactionId, records: PouchDoc[]) {
    const callSite = captureDatabaseCallSite();
    const userWrite = records.some(record => isUserDataWrite(record?._id));
    await this.enterReadSectionAsync(callSite);
    try {
      if (userWrite) this.userWritesInFlight++;
    // Global mutex ensures no concurrent writes (individual or bulk) can race
    return await this.writeMutex.exclusiveCall(async () => {
      // for each record, keep it out of the bulkDocs call if it has no usable _id,
      // and stand a failure row in its place so the rest of the batch still lands.
      // PouchDB would otherwise invent a random uuid for it and either store a
      // document nothing can find again or answer 409 and lose the write. Two
      // ordering rules and the reasons behind them are in
      // design-docs/sync-integrity.md, "A record that reaches the save path with no
      // _id": this loop runs BEFORE the two below (they dereference every element,
      // so a hole in the array would abort the whole batch), and the failure row is
      // deliberately not reported here — recoverBulkSaveFailuresAsync stays the one
      // owner of dropped-write telemetry and reports it with identifying fields.
      const savableRecords: PouchDoc[] = [];
      const savableIndices: number[] = [];
      const results: any[] = new Array(records.length);
      for (let index = 0; index < records.length; index++) {
        const record = records[index];
        if (hasUsableDocId(record)) {
          savableRecords.push(record);
          savableIndices.push(index);
          continue;
        }
        results[index] = {
          error: true,
          status: 400,
          name: MISSING_ID_ROW_NAME,
          message: 'record reached saveBulkAsync without a usable _id',
        };
      }

      // detect identity map violations (should not happen if ensureCachedReference is used properly)
      for (const record of savableRecords) {
        const cached = this.documentCache.get(record._id)?.ref.deref();
        if (cached && cached !== record) {
          this.logging.trackSilentProblem(
            `saveBulkAsync: Identity map violation for ${record._id}. ` +
            `Cached _rev: ${cached._rev}, record _rev: ${record._rev}`,
            { docId: record._id }
          );
        }
      }

      for (let record of savableRecords) {
        record.db__trans = transaction;
      }
      let db = this.getWriteDb();
      if (db == null) throw this.unavailable(DatabaseUnavailableReason.NoDatabaseHandle, callSite);

      const savedResults = savableRecords.length > 0 ? await db.bulkDocs(savableRecords) : [];
      savedResults.forEach((result, position) => {
        results[savableIndices[position]] = result;
      });

      // Update documentCache and _rev for each successfully saved record
      // This ensures the identity map stays in sync with bulk saves
      results.forEach((result, index) => {
        if (result.ok && result.rev) {
          records[index]._rev = result.rev;
          this.recordLocallyAuthoredRev(records[index]._id, result.rev);
          const bulkEntry = { ref: new WeakRef(records[index]), expectedRev: result.rev };
          this.documentCache.set(records[index]._id, bulkEntry);
          // a save landed, so the database holds this body — same reason as in
          // `applyPutResponseToCache`, which the single-record path uses
          this.rememberDatabaseBodyIfWatched(bulkEntry, records[index], true);
        }
      });

      // #3987 A1 (defect D1): failed rows used to be silently dropped here —
      // a 409 conflict (pull replication bumping a doc's _rev under a stale
      // in-memory record) lost the edit/deletion with no retry and no
      // telemetry. Recover conflicted rows through the shared merge-retry
      // (we already hold writeMutex, so call the helper directly — never
      // saveRecordAsync, which would deadlock on the same mutex) and report
      // every row that stays lost. The merge helper preserves the db__trans
      // stamp applied above (preserveDbFields: true).
      await recoverBulkSaveFailuresAsync({
        retryPutWithMerge: record => this.retryPutWithMergeAsync(db, record),
        trackSilentProblem: (name, ctx) => this.logging.trackSilentProblem(name, ctx),
      }, records, results as BulkSaveResultRow[]);

      return results;
    });
    } finally {
      if (userWrite) this.userWritesInFlight--;
      this.rwLock.releaseRead();
    }
  }

  markForDeleteRecords(records: PouchDoc[]) {
    for (let record of records) {
      record._deleted = true;
    }
  }

  /**
   * Delete a batch of records, and answer with the ones that were NOT deleted.
   *
   * An empty array means every record in the batch is gone. A non-empty one means the
   * batch fell short WITHOUT throwing: `saveBulkAsync` wrote, and a row came back still
   * marked `error` because `recoverBulkSaveFailuresAsync` could not recover it. A record
   * whose row is MISSING from the result counts the same way, since nothing says its
   * write landed.
   *
   * A write refused before it was attempted — the recreation gate reporting a shutdown,
   * the read lock refusing, or `getWriteDb()` returning null — is NOT reported this way.
   * `saveBulkAsync` throws `DatabaseUnavailableError` for those (df-aa69b381d6d6), so it
   * reaches the catch below like any other throw.
   *
   * Callers used to be told nothing about any of that — this method returned `void`, so
   * a delete that removed nothing answered exactly like one that removed everything
   * (df-c387b6a15d2d). The returned records are the same ones whose `_deleted` mark is
   * taken off below, derived once so the answer and the mark can never disagree.
   *
   * A delete that THREW is not reported this way: the error already tells the caller,
   * and it travels on untouched.
   *
   * Generic so the answer keeps the caller's own element type: every record returned
   * comes straight out of `records`, and saying so in the signature is what spares each
   * caller a cast the compiler could not check.
   */
  async deleteRecordsAsync<T extends PouchDoc>(records: T[], transaction: TransactionId): Promise<T[]> {
    // remembered BEFORE the mark, so a delete that does not land can put it back
    // (df-1cb1333d7899) — see deleteRecordAsync below for why that matters
    const restorePoint = captureDeleteRestorePoint(records);
    this.markForDeleteRecords(records);

    // await rather than return: saveBulkAsync answers with the result rows, and this
    // method reports what those rows mean rather than passing them on. Callers that
    // want the rows themselves call saveBulkAsync directly.
    let results: BulkSaveResultRow[];
    try {
      results = await this.saveBulkAsync(transaction, records) as BulkSaveResultRow[];
    } catch (err) {
      // There are no rows to read, so ask what became of each record.
      // NOT "a throw means nothing landed": saveBulkAsync advances _rev on every
      // successful record right after bulkDocs returns, and then does cache
      // bookkeeping and the conflict recovery, any of which can throw with real
      // deletions already in the local store. Taking the mark off one of those would
      // resurrect it at the caller's next save, which is the very defect this fixes.
      await this.restoreMarksOnRecordsNotDeletedAsync(records, restorePoint);
      throw err;
    }

    // A row-level failure does NOT throw: recoverBulkSaveFailuresAsync recovers what it
    // can, rewrites those rows to ok, and reports the rest as dropped writes. So the
    // catch above sees nothing for a partly-failed bulk delete, and the records behind
    // the still-failed rows are the ones whose mark has to come off here.
    //
    // One more kind of row belongs in that set and does not look like it: a row whose
    // failure was taken over by the database recovery keeps `ok: true` — an ordinary bulk
    // SAVE has always treated that as handled — but nothing was written for it, and a
    // deleted object has no later save to carry the deletion in (df-068808a2796e).
    // `bulkRowLostTheWrite` is what knows the difference, and this is its only caller.
    const notDeletedIndices: number[] = [];
    for (let index = 0; index < records.length; index++) {
      if (bulkRowLostTheWrite(results, index)) {
        notDeletedIndices.push(index);
      }
    }
    const notDeleted = new Set(notDeletedIndices);
    restoreDeletedFlags(records, restorePoint, index => notDeleted.has(index));

    return notDeletedIndices.map(index => records[index]);
  }

  async deleteRecordAsync(e: any, transaction: TransactionId): Promise<void> {
    const callSite = captureDatabaseCallSite();
    // Instead of db.remove(), we need to set the _deleted flag and save the doc.
    // This facilitates conflict resolution between deleted and updated documents.
    //
    // The document is the CANONICAL object from the identity map, not a copy, so the flag
    // is visible to every holder of it the moment it is set. Leaving it set after a save
    // that threw is what df-1cb1333d7899 was about: the caller is told the delete failed
    // and reasonably believes the document is safe, and then their next ordinary save of
    // it writes the deletion through with nothing reporting it. So put the previous value
    // back before the error travels on — but only when this record's deletion is not in the
    // local store. An exception can arrive with the deletion already stored, because the
    // cache bookkeeping that follows a successful put can throw; clearing the mark there
    // would undo a deletion that really happened.
    //
    // The caller may also hand in an object that is NOT the canonical one, and then the
    // save runs somewhere else entirely: saveRecordSerializedAsync copies this body onto
    // the identity map's object — the _deleted just set included — and saves THAT. So the
    // undo has to reach both objects, or the shared one every other holder sees keeps a
    // mark this method was told to take back (df-7bc2239b5f74). What gets SAVED is
    // deliberately left alone: handing the canonical object to saveRecordAsync instead
    // would skip that bypass branch, and with it both the body copy and the report that is
    // our only measurement of how often a caller does this.
    const canonical = this.canonicalRecordFor(e);
    // listing it here also keeps it alive across the save, so the bypass branch runs rather
    // than the "WeakRef was GC'd" one — the same object either way, one less shape to think about
    const records = canonical ? [e, canonical] : [e];
    const restorePoint = captureDeleteRestorePoint(records);
    e._deleted = true;
    let outcome: WriteOutcome;
    try {
      outcome = await this.saveRecordAsync(e, transaction);
    } catch (err) {
      await this.restoreMarksOnRecordsNotDeletedAsync(records, restorePoint);
      throw err;
    }
    // A save can also END WITHOUT THROWING having written nothing: `db.put` failed and
    // `interceptDatabaseError` took the failure over, so every rung of the save ladder
    // returned normally and the catch above never ran (df-068808a2796e). That route is
    // exactly as destructive as the one that throws, and worse in one way — the recovery
    // usually repairs the database IN PLACE and the page carries on, so there is no reload
    // to wash the standing mark away. The mark then survives: a fresh body arriving from
    // the database cannot clear `_deleted` (updateDocumentInPlace never copies a `_` key)
    // and the identity map OVERWRITES a cached-deleted object with the user's current
    // content while keeping the mark, so their next ordinary save writes a tombstone
    // carrying their newest data.
    //
    // So treat it exactly as a throw: put the mark back on every record whose deletion the
    // local store does not hold, then tell the caller the delete did not happen. When the
    // store cannot answer — likely, mid-recovery — it answers `Unknown`, which
    // `deleteOutcomeNeedsRestore` already resolves as "take the mark off": the safe
    // direction, and a decision that path already made rather than a new one.
    if (outcome === WriteOutcome.DroppedIntoRecovery) {
      await this.restoreMarksOnRecordsNotDeletedAsync(records, restorePoint);
      // `Recovering` rather than a reason of its own: it is the same condition the read
      // path already names — the write had started and was intercepted mid-flight by a
      // database recovery — and it is transient for the same reason, so a caller that
      // retries after the recovery settles is doing the right thing.
      throw this.unavailable(DatabaseUnavailableReason.Recovering, callSite);
    }
  }

  /**
   * The identity map's object for this record, when the caller handed in a DIFFERENT one.
   * `undefined` in the ordinary case, where the caller already holds the canonical object
   * and there is no second object to keep in step.
   */
  private canonicalRecordFor(e: PouchDoc): PouchDoc | undefined {
    if (typeof e?._id !== 'string') return undefined;
    const canonical = this.documentCache.get(e._id)?.ref.deref();
    return canonical && canonical !== e ? canonical : undefined;
  }

  /**
   * Put the delete's mark back on every record the local store does NOT hold as deleted.
   *
   * This is the failure path of both delete methods, and the question it has to answer is
   * "is this document's deletion recorded?" — see `deleteOutcomesFromLocalStoreAsync`,
   * which answers it, and `deleteOutcomeNeedsRestore`, which says what each answer means.
   *
   * It never throws. The caller is in a `catch` and is about to rethrow the error the SAVE
   * produced, which is the error that belongs to the caller; a failure in here must not
   * replace it. `deleteOutcomesFromLocalStoreAsync` already answers `Unknown` rather than
   * rejecting, so there is nothing here that can fail — this comment is what keeps it that
   * way.
   */
  private async restoreMarksOnRecordsNotDeletedAsync(
    records: PouchDoc[],
    restorePoint: DeleteRestorePoint,
  ): Promise<void> {
    const outcomes = await this.deleteOutcomesFromLocalStoreAsync(records);
    restoreDeletedFlags(records, restorePoint, index => deleteOutcomeNeedsRestore(outcomes[index]));
  }

  /**
   * Ask the local write database what it now holds for each record a failed delete marked.
   *
   * This replaces the revision test the first version of this restore used
   * (df-1cb1333d7899): "the record's `_rev` has not moved" is only ever a PROXY for "the
   * deletion is not stored", and df-7bc2239b5f74 is the list of places the proxy answers
   * wrongly — a conflict retry moves the revision before a put that then fails, the
   * identity-map bypass advances the revision on a different object than the caller's, and
   * a recovery that finds the goal state already reached returns without touching the
   * revision at all. Reading the store answers the real question and needs no such list.
   *
   * ONE keyed `allDocs` answers a whole bulk delete. Reading the rows is
   * `deleteOutcomesFromLocalRows`, which is where the three-way answer and the reason for
   * each branch live; this method only performs the read and reports what it could not
   * decide.
   *
   * It NEVER rejects. The caller is in a `catch` about to rethrow the save's own error,
   * and a probe failure must not replace it. Every failure — no write handle, a read that
   * throws, a read that times out — answers `Unknown` for every record instead, which
   * restores the marks and reports itself.
   */
  private async deleteOutcomesFromLocalStoreAsync(records: PouchDoc[]): Promise<DeleteOutcome[]> {
    const db = this.getWriteDb();
    if (db == null) {
      // after shutdown there is no store to ask. Not reported: a shutdown is an ordinary
      // state with its own logging, and the save this follows was refused for the same
      // reason, so nothing here is news.
      return records.map(() => DeleteOutcome.Unknown);
    }

    // deduplicated: deleteRecordAsync lists the caller's object and the identity map's
    // object for the same document, and one answer serves both
    const keys = distinctDocIds(records);
    if (keys.length === 0) return records.map(() => DeleteOutcome.Unknown);

    let rowsByKey: Map<string, LocalWinnerRow>;
    try {
      const result = await timeout(
        DELETE_OUTCOME_PROBE_TIMEOUT_MS,
        trackLocalOpAsync(
          this.localOpName('writeDb.allDocs:deleteOutcomeProbe'),
          () => db.allDocs({ keys }),
        ),
      ) as { rows?: LocalWinnerRow[] };
      rowsByKey = new Map((result?.rows ?? [])
        .filter(row => typeof row?.key === 'string')
        .map(row => [row.key as string, row]));
    } catch (err) {
      // deliberately NOT routed through interceptDatabaseError: the save's own error has
      // already been offered to it further down the ladder, and offering a second failure
      // from the same moment risks starting a recovery twice.
      this.reportUnknownDeleteOutcomes(keys, errorReportFields(err));
      return records.map(() => DeleteOutcome.Unknown);
    }

    const outcomes = deleteOutcomesFromLocalRows(records, rowsByKey);
    const unknownIds = unknownOutcomeDocIds(records, outcomes);
    if (unknownIds.length > 0) this.reportUnknownDeleteOutcomes(unknownIds);
    return outcomes;
  }

  /**
   * Report the documents a failed delete could not be decided for. Their marks came off —
   * the data-preserving answer — so this is the one record that the app may now be holding
   * a live copy of a document the server has deleted.
   *
   * `passThrough` is the field to read first. While it is true, "this store does not hold
   * the document" is expected and says nothing; while it is false, the local store is a
   * full replica and an absent document probably IS gone, which would make leaving the
   * mark on the better answer for that case. Nobody can argue that from the code alone, so
   * it is measured instead (df-7bc2239b5f74).
   */
  private reportUnknownDeleteOutcomes(docIds: string[], errorFields?: Record<string, unknown>): void {
    this.logging.trackSilentProblem('Db: could not tell whether a failed delete landed', {
      database: this.internalDatabaseIdentifier,
      // the ids are bounded so one big bulk delete cannot write an unreadable report
      docIds: docIds.slice(0, DELETE_OUTCOME_REPORT_ID_LIMIT),
      docCount: docIds.length,
      passThrough: this.getReadDb() !== this.getWriteDb(),
      ...errorFields,
    });
  }

  /**
   * Read one singleton document for start-up, when the batched read did not answer for it.
   *
   * Only the READ: the post-fetch steps run afterwards in `processFetchedSingletonAsync`, in
   * the caller's fixed order. A dropped request (`isTransientFetchError`) and a database
   * recovery that took the failure both answer `Unreadable` rather than throwing, because a
   * re-throw reaches the cold-boot catch and turns a network drop into the fatal error page
   * (Sentry triage v1.25.0 Group 7). What happens to an unreadable document is the caller's
   * decision (#6248): it is looked for in this device's own database, and never handed out
   * as an empty default. Any other error still throws.
   */
  async readBootSingletonAsync<T>(docId: string): Promise<BootSingletonRead<T>> {
    try {
      // #4502: this read used to answer a database-recovery interception by handing back a
      // promise nobody held a reference to, so nothing could ever complete it. That is right
      // only for a recovery that RELOADS the page. A graceful recovery reopens the same stored
      // database in place and does not reload, so the database healed and this boot stayed
      // frozen. The helper waits for the recovery instead, and reads again once if it healed.
      const outcome = await readSingletonThroughRecoveryAsync<T>({
        read: () => this.withDocWatcherAsync(initWatcher => this.getTypedRecordAsync<T>(docId, initWatcher)),
        interceptDatabaseError: (err) => this.databaseRecovery.interceptDatabaseError(err),
        whenRecoverySettled: () => this.databaseRecovery.whenDatabaseRecoverySettledAsync(),
      });
      if (outcome.kind === 'doc') {
        return { kind: BootSingletonReadKind.Found, doc: outcome.doc };
      }
      if (outcome.kind === 'useDefault') {
        // Report the one case that nothing else records: the database WAS repaired and the
        // read still failed. (The other case, a recovery that is reloading the page, is
        // deliberately silent — that path has its own telemetry and the page is going away.)
        // The docId goes in the context, not the message, so per-singleton repeats collapse
        // into one Sentry issue.
        if (outcome.reason === SingletonDefaultReason.StillUnreadableAfterRepair) {
          this.logging.trackSilentProblem('Db: singleton boot read failed after database repair', {
            database: this.internalDatabaseIdentifier,
            docId,
          });
        }
        return { kind: BootSingletonReadKind.Unreadable };
      }
      throw outcome.err;
    } catch (err) {
      if (isPouchNotFound(err)) {
        console.log(`Db:${this.internalDatabaseIdentifier}: singleton ${docId} missing, creating new`);
        return { kind: BootSingletonReadKind.NotFound };
      }
      if (isTransientFetchError(err)) {
        // a dropped request: the auth wrapper's 90 s fetch-timeout abort, an iOS "Load failed"
        // or "Failed to fetch", or a PouchDB sync-cancel abort. Not reported here (#6248): what
        // matters is whether the start-up still found the document, and the caller reports
        // that. Until #6248 this sent 'Db: singleton boot read deferred (transient network)'
        // and carried on with the empty default, believing a sync would later fill it in.
        // Nothing does: the initial sync writes the document to this device's database and
        // never into the copy the app holds.
        console.log(`Db:${this.internalDatabaseIdentifier}: singleton ${docId} boot read dropped by the network:`, describeErrorForLog(err));
        return { kind: BootSingletonReadKind.Unreadable };
      }
      throw err;
    }
  }

  /**
   * Read singleton documents from THIS DEVICE's database, never the server (#6248).
   *
   * The start-up uses it for the documents its normal read could not get. `getReadDb()` is
   * deliberately not used: during the pass-through window it is the server, and the server
   * is exactly what just failed to answer. Answers the documents this device holds, by id,
   * through the identity map and registered with `watcher` like any other read. Throws when
   * the local store does not answer within the boot bound.
   */
  async readBootSingletonsFromDeviceAsync(ids: string[], watcher: DocWatcher): Promise<Map<string, any>> {
    const operationName = this.localOpName('localDb.allDocs:bootSingletonsFromDevice');
    const result = await withBootLocalStoreTimeout(
      trackLocalOpAsync(operationName, () => this.localDb.allDocs({ keys: ids, include_docs: true })),
      operationName,
    ) as { rows: any[] };
    const found = new Map<string, any>();
    // collectFoundDocs skips missing keys (row.error) and deleted docs
    for (const [id, rawDoc] of collectFoundDocs(result.rows)) {
      const cached = this.getCachedDocument(rawDoc);
      this.registerDocWithWatcher(cached._id, watcher);
      found.set(id, cached);
    }
    return found;
  }

  /**
   * L6/S2 (#3986): apply the post-fetch steps of singleton init to an already-fetched doc.
   * The boot path calls this for every singleton, in a fixed order, after all the reads are
   * done (the batch, `readBootSingletonAsync` for what the batch missed, and the device copy
   * for what neither could read). `fetchedDoc` is the cached doc, or null when there is none
   * (falls back to the default). Runs default-fill, the updater migration, deepNormalize, and
   * documentCache registration — everything EXCEPT the fetch + graft, which the caller
   * already did.
   *
   * `unreadAtBoot` (#6248) says the default stands in for a document that could not be read
   * at all. It marks the singleton so `saveSingletonAsync` refuses to write that default over
   * the user's real document.
   */
  async processFetchedSingletonAsync<T>(docId: string, fetchedDoc: T | null, defaultObj: T, updater?: (obj: T) => Promise<void>, unreadAtBoot = false): Promise<T> {
    let singleton = (this.singletons[docId] = new Singleton());
    singleton.unreadAtBoot = unreadAtBoot;
    singleton.doc = fetchedDoc as any;

    if (singleton.doc == null) {
      singleton.doc = defaultObj;
      singleton.doc._id = docId;
    } else {
      if (updater) {
        await updater(singleton.doc);
      }
      deepNormalize(singleton.doc, defaultObj);
    }
    // Track singleton in documentCache, even a new doc without _rev, so a revision another
    // device writes later reaches it through the live sync. NOTE the initial sync does NOT
    // update it: its passes never apply pulled documents to the identity map (#6248)
    if (singleton.doc._id) {
      this.documentCache.set(singleton.doc._id, { ref: new WeakRef(singleton.doc), expectedRev: singleton.doc._rev });
    }
    // if the database really did hold this document, remember it, so a version
    // arriving from the server can later be merged with whatever this device
    // changes from here on (#4577).
    //
    // ONLY when it did. `fetchedDoc` is null both for a document that does not
    // exist yet and for one start-up could not read at all (#6248, `unreadAtBoot`),
    // where a user DOES have a real document on the server. Remembering the default
    // as the shared starting point would make every field written on it count as a
    // change this device made, so it would beat the server's real values in a merge.
    // Leaving it unset makes the merge fall back to the arriving version.
    if (fetchedDoc != null) {
      this.rememberSingletonDatabaseBody(singleton.doc._id, singleton.doc, RememberedBodySource.Server);
    }
    return singleton.doc;
  }

  /**
   * The in-memory copy of a singleton document.
   *
   * REFUSES, rather than answering `undefined`, when start-up has not loaded this
   * document yet (#4801) — see `readLoadedSingletonDoc` for the whole reasoning.
   * The watcher is still registered first, so a caller that catches the refusal and
   * retries later is woken when the document does arrive.
   */
  getSingleton(docId: string, watcher: DocWatcher): any {
    let singleton = this.singletons[docId];
    this.registerDocWithWatcher(docId, watcher);
    return readLoadedSingletonDoc(singleton, docId, this.internalDatabaseIdentifier);
  }

  /**
   * Has start-up loaded this singleton document yet? (#4801)
   *
   * Asks the same question `getSingleton` answers by refusing, but as a plain
   * boolean and without registering a watcher — for a caller that wants to decide
   * whether it is safe to START something, rather than one that is reading a
   * document right now. The coach-invite claim is the reason it exists: it must
   * not begin writing into documents that were never loaded.
   */
  isSingletonLoaded(docId: string): boolean {
    return this.singletons[docId]?.doc != null;
  }

  /**
   * The singleton's body as the database last held it, and where this device got that copy
   * (`Singleton.lastKnownDatabaseBody`). For DIAGNOSIS only: the plan-save orphan canary
   * compares against it to tell an entry that arrived in the document from one this device
   * created (df-704db86556f7). The body is the remembered copy itself, so callers must only
   * read it. Undefined when nothing is remembered.
   */
  rememberedDatabaseBodyOf(docId: string): { body: any; source: RememberedBodySource } | undefined {
    const singleton = this.singletons[docId];
    if (singleton?.lastKnownDatabaseBody == null) return undefined;
    return {
      body: singleton.lastKnownDatabaseBody,
      source: singleton.lastKnownDatabaseBodySource ?? RememberedBodySource.Server,
    };
  }

  async saveSingletonAsync(transaction: TransactionId, docId: string) {
    const callSite = captureDatabaseCallSite();
    let singleton = this.singletons[docId];
    if (singleton == null) {
      console.error(`Db:${this.internalDatabaseIdentifier}: Unknown singleton: "${docId}"`);
      return;
    }
    // #6248: an empty default stands in for a document start-up could not read. Writing it
    // would overwrite the user's real document on the server, so refuse instead.
    if (singleton.unreadAtBoot) {
      throw this.unavailable(DatabaseUnavailableReason.SingletonNotReadAtBoot, callSite);
    }

    singleton.needsSaving_transaction = transaction;

    if (!singleton.isSaving) {
      singleton.isSaving = true;
      // the reset is in a `finally` because `saveRecordAsync` can now THROW where it used to
      // return quietly (df-6ce1f9d6830e). Left outside one, a single throw strands this flag
      // at true for the life of the wrapper — and every later save of THIS singleton then
      // sets `needsSaving_transaction`, finds the flag set, and returns without writing
      // anything and without an error. Settings, fitness state and the rest would stop
      // persisting in silence, which is the opposite of what making the write speak up is
      // for. `saveRecordSerializedAsync` puts its equivalent reset in a `finally` already.
      try {
        while (singleton.needsSaving_transaction) {
          let transaction = singleton.needsSaving_transaction;
          singleton.needsSaving_transaction = null;
          await this.saveRecordAsync(singleton.doc, transaction);
        }
      } finally {
        singleton.isSaving = false;
      }
    }
  }

  /**
   * Atomically create a unique journal entry that will NEVER overwrite existing entries.
   * Generates timestamp internally, finds unique ID, sets doc._id, and saves.
   * Uses mutex to ensure the entire operation is atomic.
   *
   * @param doc - The document to save (will have _id set)
   * @param type - Entry type suffix (e.g., "FitnessPlan", "IntakeForm")
   * @param transaction - Transaction ID
   * @param caching - Whether to use caching
   */
  async createUniqueJournalEntryAsync(
    doc: PouchDoc,
    type: string,
    transaction: TransactionId,
    caching: boolean = false
  ): Promise<void> {
    await this.journalEntryMutex.exclusiveCall(async () => {
      // Generate timestamp-based ID
      const timestamp = this.debug.getNowLocal().toUTC().toISO();
      const baseId = `log/${timestamp}-${type}`;

      // Find a unique ID (add _1, _2, etc. if collision)
      const candidateId = await this.withDocWatcherAsync(async journalWatcher => {
        let candidate = baseId;
        let counter = 0;
        while (true) {
          const existing = await this.getRecordAsync(candidate, journalWatcher).catch(() => null);
          if (!existing) {
            break;
          }
          counter++;
          candidate = `${baseId}_${counter}`;
        }
        return candidate;
      });

      // Set ID and save (still inside mutex)
      doc._id = candidateId;
      doc._rev = undefined;
      await this.saveRecordAsync(doc, transaction, caching);
    });
  }

  // --- DocWatcher infrastructure (reactive sync) ---
  // See doc-watcher.ts for full architecture description.
  // Registry maps doc IDs → Set<DocWatcher>. When sync pulls a change, notifyDocWatchers
  // iterates the set and calls notify() on each watcher, which accumulates the ids and
  // delivers them as one batch once the database goes quiet (#4358 delivery contract).

  /** Creates a DocWatcher with a deregistration closure that cleans up the registry on destroy.
   *
   *  `options.label` is worth passing for any watcher whose callback does real
   *  work — it is the only thing that names the watcher in the stuck-callback
   *  report below. A throwaway one-off read watcher does not need it.
   *
   *  A callback that starts asynchronous work should RETURN its promise rather
   *  than `void` it, so the watcher can keep two runs from overlapping. See the
   *  DocWatcher class comment (#4358). */
  createDocWatcher(callback: DocWatcherCallback, options: DocWatcherOptions = {}): DocWatcher {
    const watcher = new DocWatcher(callback, (w, docIds) => {
      this.allActiveWatchers.delete(w);
      for (const docId of docIds) {
        const watchers = this.docWatcherRegistry.get(docId);
        if (watchers) {
          watchers.delete(w);
          if (watchers.size === 0) this.docWatcherRegistry.delete(docId);
        }
      }
    }, {
      ...options,
      onCallbackError: options.onCallbackError ?? ((err, label) => {
        // a watcher callback that fails is OUR bug and must surface — but an
        // unavailable database during our OWN sign-out wipe is expected. Same
        // split the pulled-doc listeners use above; the console line moves with
        // the report because the E2E gate fails on any console.error that is
        // not allow-listed.
        if (isDatabaseTornDown(err)) {
          console.log(`Db:${this.internalDatabaseIdentifier}: watcher callback skipped `
            + `— our own teardown (${(err as any).reason}, ${label})`);
          return;
        }
        console.error(`Db:${this.internalDatabaseIdentifier}: watcher callback failed (${label})`, err);
        this.logging.trackAbnormalException(err);
      }),
      onCallbackStuck: options.onCallbackStuck ?? ((info) => {
        // a callback that never settles held single-flight open, so the watcher
        // releases the guard and carries on — but a promise that never settles
        // is still a real bug in that page, so it has to be visible (#4358)
        this.logging.trackSilentProblem('DocWatcherCallbackStuck', {
          db: this.internalDatabaseIdentifier,
          label: info.label,
          // `onCallbackStuck` in doc-watcher.ts defines both of these
          stuckMs: info.stuckMs,
          hiddenMs: info.hiddenMs,
          pendingSample: info.pendingSample.join(','),
        });
      }),
      onRetriesExhausted: options.onRetriesExhausted ?? ((info) => {
        // the watcher has given up re-delivering this batch on its own. It kept
        // the ids, so nothing is lost — but this page will not catch up until
        // something else notifies this watcher, and a callback that failed this
        // many times running is a real bug in that page. Each individual
        // failure already reported through `onCallbackError` above; this says
        // the retrying stopped, which none of those do.
        this.logging.trackSilentProblem('DocWatcherRetriesExhausted', {
          db: this.internalDatabaseIdentifier,
          label: info.label,
          failures: info.failures,
          pendingSample: info.pendingSample.join(','),
        });
      }),
    });
    this.allActiveWatchers.add(watcher);
    return watcher;
  }

  /**
   * Runs one asynchronous read (or a short group of reads) against a throwaway
   * DocWatcher, and destroys that watcher afterwards whether the body succeeded
   * or threw. Use this for any read that is NOT subscribing to changes.
   *
   * The `finally` is the whole point: a read that could not happen throws
   * `DatabaseUnavailableError`, and a watcher left registered after that keeps
   * receiving the all-watchers `notifyNew` broadcast for the rest of the
   * session. How to call it, and where to put your own `catch`:
   * `design-docs/reactive-sync.md`, "One-Off Database Reads".
   */
  async withDocWatcherAsync<T>(body: (watcher: DocWatcher) => Promise<T>): Promise<T> {
    const watcher = this.createDocWatcher(() => {});
    try {
      return await body(watcher);
    } finally {
      watcher.destroy();
    }
  }

  /**
   * The synchronous twin of `withDocWatcherAsync`, for a read that does no I/O
   * and therefore returns a value rather than a promise — `getSingleton` is the
   * one this exists for. It can still throw (since #4801 it refuses a document
   * start-up never loaded), so it needs the same `finally`.
   */
  withDocWatcher<T>(body: (watcher: DocWatcher) => T): T {
    const watcher = this.createDocWatcher(() => {});
    try {
      return body(watcher);
    } finally {
      watcher.destroy();
    }
  }

  /** Links a document to a watcher. Called by getters after fetching a doc. */
  registerDocWithWatcher(docId: string, watcher: DocWatcher): void {
    if (!watcher) return;
    watcher.addDocId(docId);
    let watchers = this.docWatcherRegistry.get(docId);
    if (!watchers) {
      watchers = new Set();
      this.docWatcherRegistry.set(docId, watchers);
    }
    watchers.add(watcher);
  }

  /**
   * Wait for a specific document to change via replication (DocWatcher notification).
   * Resolves when the watcher fires for the given docId, or after timeout.
   * Useful after a server function modifies a doc server-side — call this to wait
   * until the change syncs back to local PouchDB before proceeding.
   */
  waitForDocChangeAsync(docId: string, timeoutMs: number = 10000): Promise<void> {
    return new Promise<void>((resolve) => {
      const watcher = this.createDocWatcher((changedDocIds) => {
        if (changedDocIds.some(change => change.id === docId)) {
          clearTimeout(timeout);
          watcher.destroy();
          resolve();
        }
      });
      this.registerDocWithWatcher(docId, watcher);

      const timeout = setTimeout(() => {
        console.log(`waitForDocChangeAsync: timed out waiting for ${docId} after ${timeoutMs}ms`);
        watcher.destroy();
        resolve();
      }, timeoutMs);
    });
  }

  /** Notify ALL active watchers that their watched docs may have changed (e.g. after IDB recovery). */
  notifyAllWatchersChanged(): void {
    for (const watcher of this.allActiveWatchers) {
      for (const docId of this.docWatcherRegistry.keys()) {
        watcher.notify(docId);
      }
    }
  }

  /** Graft pass-through-read docs into local PouchDB so subsequent writes
   *  extend the remote revision tree instead of forking an orphan local tree.
   *  Pass the `db` the caller actually read from — the helper no-ops when
   *  it's not `this.remoteDb`, so call sites can pass `db` unconditionally
   *  without an `if (db === this.remoteDb)` guard. Logic lives in
   *  database-wrapper-passthrough.ts for unit-testability; see
   *  design-docs/pass-through-db.md.
   *
   *  IMPORTANT: callers must registerDocWithWatcher BEFORE calling this
   *  helper for any doc whose caller intends to receive reactive updates.
   *  When `notifyWatchers` is true (default) the helper calls
   *  notifyDocWatchers per graft, which broadcasts notifyNew to every
   *  active watcher when the docId is untracked. Pass `notifyWatchers:
   *  false` for read paths whose caller does NOT supply a watcher (e.g.
   *  watcher-less getAllOfType / getAllDataUncachedAsync) so the graft
   *  populates local PouchDB without storming unrelated watchers.
   *  (runQueryAsync reads local-only and never grafts — see its comment.) */
  private async graftPassThroughDocs(
    db: DbHandle | null,
    docs: (PouchDoc | null | undefined)[],
    options?: { notifyWatchers?: boolean },
  ): Promise<void> {
    const shouldNotify = options?.notifyWatchers !== false;
    return graftPassThroughDocsPure(
      {
        localDb: this.localDb,
        trackSilentProblem: (name, ctx) => this.logging.trackSilentProblem(name, ctx),
        intercept: (err: unknown) => this.databaseRecovery.interceptDatabaseError(err),
        notifyDocId: shouldNotify ? (id) => this.notifyDocWatchers(id) : () => { /* silent graft */ },
        databaseId: this.internalDatabaseIdentifier,
        deviceLocalDocIds: DEVICE_LOCAL_DOC_IDS,
      },
      db,
      this.remoteDb,
      docs,
    );
  }

  // --- Pulled-doc listeners (#4249) ---
  //
  // A DocWatcher is the wrong tool for "tell me about every log doc that arrived
  // from another device". The watcher registry notifies only the watchers that
  // already track a given doc id, and broadcasts notifyNew to ALL watchers for
  // an untracked id — so a piggybacked watcher would either miss the docs it
  // cares about or be woken by every unrelated document in the database.
  //
  // This is the first-class alternative: register once for an id prefix, and
  // receive exactly the docs the sync handlers decided are real remote changes.
  private pulledDocListeners: Array<{ idPrefix: string; callback: (docs: any[]) => void }> = [];

  /**
   * Be told about documents that arrived from the remote database.
   *
   * The callback receives only docs whose id starts with `idPrefix`, and only
   * at the points where the sync handlers already concluded this is a genuine
   * remote change (a strictly newer revision, a document this device had never
   * cached, or a pulled deletion). Own-device revision echoes are excluded by
   * construction, because collection happens at those decision points and
   * nowhere else. Pulled DELETIONS are the exception: a tombstone carries no
   * device id, so this device's own deletions echoing back are delivered too.
   *
   * A deletion is delivered as the last known document body with `_deleted`
   * set, when this device still had that body cached. Otherwise only
   * `{_id, _deleted}` is available — the caller must cope with a body-less
   * tombstone.
   *
   * Returns a handle whose `destroy()` removes the listener.
   */
  registerPulledDocListener(idPrefix: string, callback: (docs: any[]) => void): { destroy: () => void } {
    const listener = { idPrefix, callback };
    this.pulledDocListeners.push(listener);
    return {
      destroy: () => {
        const index = this.pulledDocListeners.indexOf(listener);
        if (index >= 0) this.pulledDocListeners.splice(index, 1);
      },
    };
  }

  /**
   * Hand a batch of pulled docs to the matching listeners.
   *
   * Kept cheap on purpose: this runs inside the sync pull loop, the exact place
   * where per-document work froze cold starts (#4243). With no listeners
   * registered it costs one length check. A listener that throws must not break
   * replication, but must not vanish either — it is logged and reported.
   */
  private deliverPulledDocsToListeners(docs: any[]): void {
    if (this.pulledDocListeners.length === 0 || docs.length === 0) return;
    for (const listener of this.pulledDocListeners) {
      const matching = docs.filter(doc => doc != null && typeof doc._id === 'string' && doc._id.startsWith(listener.idPrefix));
      if (matching.length === 0) continue;
      try {
        listener.callback(matching);
      } catch (err) {
        // a swallowed failure here would mean silently stale calendar dots with
        // nothing to debug from — surface it (#4249 I6)
        // a listener that throws is OUR bug and must surface — but an
        // unavailable database during our OWN sign-out wipe is expected. The
        // console line moves with the report, since the E2E gate fails on any
        // console.error that is not allow-listed; it keeps the reason and the
        // listener prefix, which are the two fields the suppressed report
        // carried.
        if (isDatabaseTornDown(err)) {
          console.log(`Db:${this.internalDatabaseIdentifier}: pulled-doc listener skipped `
            + `— our own teardown (${(err as any).reason}, ${listener.idPrefix})`);
          continue;
        }
        console.error(`Db:${this.internalDatabaseIdentifier}: pulled-doc listener failed`, err);
        this.logging.trackSilentProblem('PulledDocListenerFailed', {
          idPrefix: listener.idPrefix,
          name: (err as any)?.name,
          message: (err as any)?.message,
        });
      }
    }
  }

  /**
   * Decide what a pull batch's tombstones really were (#4441), and repair a
   * document this device is still showing after the server deleted it (#4464).
   *
   * A tombstone carrying `db__conflictClose: true` usually means one losing
   * branch was closed and the document itself is still there. But once every
   * branch of a document is deleted, that same flagged tombstone can be the
   * winning revision, and then the document really is gone. The flag cannot tell
   * those apart; the local database can, so we ask it.
   *
   * The same answer settles a second question (#4464). The pull asks the server
   * for winning revisions only, and a deleted revision cannot win while a live
   * one exists — so a tombstone arriving on the pull means the document is gone
   * on the server. If it is still alive HERE, this device holds a branch the
   * server has already closed, and nothing will offer it the closing tombstone
   * again. `repairServerDeletedDocAsync` below brings it back into step.
   *
   * Runs after the pull loop rather than inside it — the loop is the path that
   * froze cold starts in #4243 and must stay synchronous. Nothing is lost by
   * waiting: the document watchers were already notified inside the loop.
   *
   * Never throws — a failure here must not break replication.
   */
  private async settlePulledDeletionsFromSyncAsync(tombstones: PulledDeletionToSettle[]): Promise<void> {
    // Resolve the handle per call rather than capturing it. A database
    // recreation swaps in a NEW PouchDB instance and closes the old one without
    // ever nulling the field, so a captured handle would keep reading a closed
    // database (same reason resolveConflictedDocsAsync does this).
    const readDb = (): DbHandle => {
      const current = this.localDb;
      if (!current) throw new Error('local database is gone');
      return current;
    };
    // Two different things can take the database away while this run is in
    // flight, and they must NOT be treated the same. This is the same split
    // isDatabaseTornDown documents (see database-unavailable-error.ts) — that
    // test itself cannot be reused, because this probe reads the raw handle
    // rather than going through the guarded read, so it never sees a
    // DatabaseUnavailableError.
    //
    // Tearing down: a sign-out wipe, a shutdown, or a purge/restore that has
    // deliberately detached the remote handle. All three are our own doing, so
    // they go quiet — and a console.error would fail the E2E
    // unexpected-browser-error gate. (recreateLocalDbAsync nulls localDb only
    // once a shutdown has started, so a null handle here really does mean a
    // teardown, not a recreation.)
    //
    // The third one is why remoteDetachedForOwnWork is here rather than in a
    // predicate of its own (df-ac72b56eef36). A purge nulls only the REMOTE
    // handle and only for its own duration: the local handle is alive and the
    // lock is not shutting down, so the first two tests both say no while every
    // repair throws `database handle is gone`. Those failures must go quiet,
    // AND the run must stop — the documents are about to be purged, so carrying
    // on costs one failed repair per remaining tombstone, which is why the
    // reported case showed three console errors rather than one. Widening this
    // predicate gives both, because isDatabaseUnreadable is built on it.
    const isDatabaseTearingDown = (): boolean =>
      !this.localDb || this.rwLock.isShuttingDown || this.remoteDetachedForOwnWork > 0;
    // Unreadable: the above, plus a database RECOVERY, which swaps the handle
    // out from under us. Either way a read would fail, so the run stops — but a
    // recovery is not our own teardown and does not go quiet. See
    // reportStoppedEarly.
    const isDatabaseUnreadable = (): boolean => isDatabaseTearingDown() || !!this.recreationGate;

    try {
      const result = await settlePulledDeletionsAsync({
        isDocumentAliveAsync: (docId: string) => isDocumentAliveInDbAsync(id => readDb().get(id), docId),
        repairServerDeletedDocAsync: (docId: string) => this.adoptRemoteRevisionsAsync(docId),
        isDatabaseUnreadable,
        evictFromCache: (docId: string) => { this.documentCache.delete(docId); },
        // See design-docs/sync-integrity.md, "Putting back an entry the loop
        // dropped too early", for why each refusal below is the way round it is.
        restoreCacheEntryIfAbsent: (docId: string, body: any): boolean => {
          // if an impersonation session started while this run was away at the
          // network, the cache was deliberately cleared for a DIFFERENT database
          // (setTemporaryDb) — putting a base-user object with a base-database
          // revision back into it is exactly what that clear prevents
          if (this.temporaryDb) return false;
          // if something has already registered an object for this document, it
          // is newer than the body captured before the eviction, so overwriting
          // it would be the #4669 loss. Asks `deref()` rather than `has()`: an
          // entry whose weak reference has been collected holds no object, and
          // standing down for one of those would leave the document without one.
          if (this.documentCache.get(docId)?.ref.deref() != null) return false;
          // re-register, and nothing more — no field of `body` is read or
          // written except its revision, so no stale content travels anywhere.
          // `lastKnownDbBodyFingerprint` stays unset because this body did not
          // come from a database read here, and absent is its honest value.
          this.documentCache.set(docId, { ref: new WeakRef(body as PouchDoc), expectedRev: body?._rev });
          return true;
        },
        notifyWatchers: (docId: string) => { this.notifyDocWatchers(docId); },
        publishDeletions: (docs: any[]) => { this.deliverPulledDocsToListeners(docs); },
        reportStoppedEarly: (unsettledCount: number) => {
          if (isDatabaseTearingDown()) {
            console.log(`Db:${this.internalDatabaseIdentifier}: pulled-deletion settle stopped `
              + `— our own teardown (${unsettledCount} tombstone(s) left unsettled)`);
            return;
          }
          // A recovery reached us instead. The abandoned tombstones do NOT come
          // back: the replication checkpoint has already moved past them, so
          // each one stays in exactly the state #4441 exists to fix — a real
          // deletion whose identity-map entry is never evicted and whose day is
          // never republished. That is unresolved, so it is a problem, not info.
          this.logging.trackSilentProblem('ConflictCloseSettleAbandoned', {
            db: this.internalDatabaseIdentifier,
            unsettledCount,
            reason: 'database-recovering',
          });
        },
        reportProbeFailure: (docId: string, err: unknown) => {
          // if the database was torn down between the check above and this read,
          // it is our own doing after all — say so in the log and file nothing.
          // console.log, not console.error, for the E2E gate. A recovery is
          // deliberately NOT covered here; it still reports.
          if (isDatabaseTearingDown()) {
            console.log(`Db:${this.internalDatabaseIdentifier}: pulled-deletion settle skipped `
              + `— our own teardown (${docId})`);
            return;
          }
          // the deletion was neither applied nor ruled out, so the condition is
          // still unresolved — problem tier, not info (#4382)
          console.error(`Db:${this.internalDatabaseIdentifier}: could not tell whether ${docId} `
            + `is still there after a pulled deletion — ${describeErrorForLog(err)}`);
          this.logging.trackSilentProblem('ConflictCloseAlivenessProbeFailed', {
            db: this.internalDatabaseIdentifier,
            docType: docTypeFromId(docId),
            name: (err as any)?.name,
            message: (err as any)?.message,
          });
        },
        reportRealDeletion: (docId: string, hadCachedBody: boolean) => {
          // the eviction has already run and the publish is queued for the end
          // of the batch, so nothing here is unresolved — info tier. It is here
          // to measure how often this rare window is reached (#4382), and
          // hadCachedBody is what separates a document this device really held
          // from one it never had — see PulledDeletionSettleResult for why a
          // local not-found cannot tell those two apart on its own.
          this.logging.trackSilentInfo('ConflictCloseTombstoneWasRealDeletion', {
            db: this.internalDatabaseIdentifier,
            docType: docTypeFromId(docId),
            hadCachedBody,
          });
        },
        reportRepairOutcome: (docId: string, outcome: PulledDeletionRepairOutcome, err?: unknown) => {
          // #4464. Four outcomes, and only ONE of them is still unresolved when
          // this runs — so only that one is a problem (#4382's tiers). The
          // `default` arm below is what makes a fifth outcome a compile error
          // rather than a silent no-report; a bare switch would not, because
          // every arm returns void and nothing would object to a missing case.
          switch (outcome) {
            case PulledDeletionRepairOutcome.Repaired:
              console.log(`Db:${this.internalDatabaseIdentifier}: ${docId} was still showing after the `
                + `server deleted it — adopted the server's revisions, it is gone now`);
              this.logging.trackSilentInfo('PulledDeletionDivergenceRepaired', {
                db: this.internalDatabaseIdentifier,
                docType: docTypeFromId(docId),
              });
              return;

            case PulledDeletionRepairOutcome.StillAliveNewerLocalRevision:
              // benign and self-healing: the server's tombstones came down and
              // the document is still alive, so this device holds a newer
              // revision it has not pushed yet. That revision keeps winning here
              // and the next push revives the document on the server.
              console.log(`Db:${this.internalDatabaseIdentifier}: ${docId} is still alive after adopting `
                + `the server's revisions — this device holds a newer unpushed revision`);
              this.logging.trackSilentInfo('PulledDeletionRepairFoundNewerLocalEdit', {
                db: this.internalDatabaseIdentifier,
                docType: docTypeFromId(docId),
              });
              return;

            case PulledDeletionRepairOutcome.ServerRevisionIsAliveAgain:
              // also benign, and a different cause worth telling apart: the
              // document is not deleted on the server any more, so there was
              // nothing out of step. The ordinary pull delivers the live revision.
              console.log(`Db:${this.internalDatabaseIdentifier}: ${docId} is alive on the server again `
                + `— nothing to repair`);
              this.logging.trackSilentInfo('PulledDeletionRepairFoundServerAliveAgain', {
                db: this.internalDatabaseIdentifier,
                docType: docTypeFromId(docId),
              });
              return;

            case PulledDeletionRepairOutcome.Failed:
              // Same teardown carve-out the probe's report takes, and for the
              // same reason: a console.error during our own sign-out wipe would
              // fail the E2E unexpected-browser-error gate.
              if (isDatabaseTearingDown()) {
                console.log(`Db:${this.internalDatabaseIdentifier}: repair of ${docId} skipped `
                  + `— our own teardown`);
                return;
              }
              // this one really is unresolved: the tombstone does not come back
              // (the replication checkpoint has moved past it), so the document
              // keeps showing on this device until something else deletes it.
              console.error(`Db:${this.internalDatabaseIdentifier}: could not bring ${docId} back into step `
                + `with the server — ${describeErrorForLog(err)}`);
              this.logging.trackSilentProblem('PulledDeletionRepairFailed', {
                db: this.internalDatabaseIdentifier,
                docType: docTypeFromId(docId),
                name: (err as any)?.name,
                message: (err as any)?.message,
              });
              return;

            default: {
              // An outcome nobody wrote an arm for. The `never` assignment is
              // what fails the BUILD when a fifth value is added, which is the
              // whole point — going quiet about a new outcome is how an
              // unresolved condition stops being visible. At runtime it can only
              // be reached by a value that skipped the compiler, so it reports
              // rather than assuming which tier it belongs to.
              const unhandled: never = outcome;
              console.error(`Db:${this.internalDatabaseIdentifier}: unhandled repair outcome `
                + `${String(unhandled)} for ${docId}`);
              this.logging.trackSilentProblem('PulledDeletionRepairOutcomeUnhandled', {
                db: this.internalDatabaseIdentifier,
                docType: docTypeFromId(docId),
                outcome: String(unhandled),
              });
              return;
            }
          }
        },
      }, tombstones);

      const didSomething = result.realDeletionCount > 0 || result.stillAliveCount > 0
        || result.unknownCount > 0 || result.stoppedEarly;
      if (didSomething) {
        console.log(`Db:${this.internalDatabaseIdentifier}: settled ${tombstones.length} pulled `
          + `tombstone(s) — ${result.realDeletionCount} real deletion(s), `
          + `${result.alreadyHandledCount} ordinary, ${result.stillAliveCount} out of step `
          + `(${result.repairedCount} repaired, ${result.stillAliveAfterRepairCount} newer locally, `
          + `${result.serverAliveAgainCount} alive on the server again, `
          + `${result.repairFailedCount} failed, ${result.cacheEntryRestoredCount} cache entr`
          + `${result.cacheEntryRestoredCount === 1 ? 'y' : 'ies'} put back), `
          + `${result.unknownCount} unknown`
          + `${result.stoppedEarly ? ', stopped early (database unreadable)' : ''}`);
      }
    } catch (err) {
      // same teardown carve-out the probe's report takes, for the same reason
      if (isDatabaseTearingDown()) {
        console.log(`Db:${this.internalDatabaseIdentifier}: pulled-deletion settle abandoned `
          + `— our own teardown`);
        return;
      }
      console.error(`Db:${this.internalDatabaseIdentifier}: settling pulled deletions failed`, err);
      this.logging.trackSilentProblem('ConflictCloseSettleFailed', {
        db: this.internalDatabaseIdentifier,
        count: tombstones.length,
        name: (err as any)?.name,
        message: (err as any)?.message,
      });
    }
  }

  /**
   * Copy every revision the SERVER holds for one document into the local
   * revision tree (#4464).
   *
   * Used when a pulled tombstone left the document still alive here, which means
   * this device is holding a branch the server has already closed and will never
   * be offered the tombstone that closes it.
   *
   * ADDING the server's revisions is the whole method, and that is deliberate:
   * it can never destroy anything. Deleting the local live revision instead
   * would look simpler and would be silent data loss, because that revision can
   * be an edit the user made offline and has not pushed yet. DO NOT "simplify"
   * this into a local delete.
   *
   * `design-docs/sync-integrity.md` (the #4464 part of the #4441 section) has the
   * rest: why `open_revs: 'all'` and `revs: true` are both load-bearing, why one
   * revision is written per call, and why running this twice is harmless.
   *
   * Throws when the document could not be brought back into step — the caller
   * reports that, because such a document stays out of step for good.
   */
  private async adoptRemoteRevisionsAsync(docId: string): Promise<AdoptedRemoteRevisions> {
    // Two DIFFERENT hazards, guarded by two different things — do not collapse
    // them, because only one of them is what the lock is for:
    //
    //  - a RECREATION swaps in a new PouchDB instance without nulling the
    //    field, and it announces itself through `recreationGate` rather than
    //    the lock (`recreateLocalDbAsync` never takes the write lock). So
    //    waiting out the gate is the only thing that helps here, and the read
    //    lock buys nothing against it.
    //  - a SHUTDOWN closes and nulls the handles, and it DOES take the write
    //    lock. That is what the per-write read lock below excludes.
    if (!await this.waitForRecreationGate()) throw new Error('database is being recreated');
    if (!this.remoteDb || !this.localDb) throw new Error('database handle is gone');

    // the decisions themselves live in the node-testable helper; this method
    // owns only the guards and the two real database calls
    return await adoptRemoteLeavesAsync({
      // pouchdb ships no typings for the open-revs form, which answers with
      // one entry per branch tip: `{ ok: <doc> }` for a revision it could
      // return, and `{ missing: <rev> }` for one it could not.
      //
      // Retried, and that is not belt-and-braces: a failed repair is
      // PERMANENT. The replication checkpoint has already moved past this
      // tombstone, so nothing will offer it again — which means one dropped
      // connection here would strand the document in exactly the state this
      // whole change exists to fix.
      //
      // Deliberately OUTSIDE the read lock. `shutdownAsync` takes the write
      // lock and waits for readers with no timeout, so holding it across a
      // retry loop — up to five attempts with backoff between them — would make
      // a sign-out on a flaky connection wait that whole time. The remote read
      // touches no local handle, so it does not need the lock at all.
      readRemoteLeavesAsync: id => this.retryRemoteCallOnTransientErrorAsync(
        `adopt remote revisions for ${id}`,
        () => (this.remoteDb as any).get(id, { open_revs: 'all', revs: true }),
      ),
      writeLocalRevisionAsync: async revision => {
        // The lock is taken per WRITE rather than around the whole graft, so a
        // sign-out never waits out the retry loop above. What it excludes is a
        // SHUTDOWN closing the handle underneath a write already in flight —
        // not a recreation, which does not take this lock at all.
        //
        // What makes the handle read safe is that it and the `bulkDocs` call
        // are one synchronous step, with no await between them. A recreation
        // landing BETWEEN writes is fine: the next write reads the new handle,
        // and a half-applied graft is settled by the aliveness re-probe in
        // `repairOutOfStepDocAsync`.
        if (!this.rwLock.acquireRead()) throw new Error('read lock refused');
        try {
          const local = this.localDb;
          if (!local) throw new Error('local database went away mid-graft');
          const [row] = await local.bulkDocs([revision], { new_edits: false }) as Array<any>;
          return row;
        } finally {
          this.rwLock.releaseRead();
        }
      },
    }, docId);
  }

  /** Called in the sync pull handler after canonical object is updated in-place. */
  private notifyDocWatchers(docId: string): void {
    const watchers = this.docWatcherRegistry.get(docId);
    if (watchers && watchers.size > 0) {
      // existing watched doc changed — notify specific watchers
      for (const watcher of watchers) {
        watcher.notify(docId);
      }
    } else {
      // untracked doc — broadcast as New to all active watchers
      for (const watcher of this.allActiveWatchers) {
        watcher.notifyNew(docId);
      }
    }
  }
}

@Injectable()
export class DatabaseWrapperProvider {
  public databaseStates: { [name: string]: DatabaseState } = {};
  public allDatabases: DatabaseWrapper[] = [];

  constructor(
    private pubSub: PubSubService,
    private auth: AuthService,
    public logging: LoggingService,
    public databaseRecovery: DatabaseRecoveryService,
    public appService: AppService,
    private onlineStateService: OnlineStateService,
    private debug: DebugService,
  ) {
    this.auth.registerDbProvider(this);
  }

  createDatabaseWrapper(): DatabaseWrapper {
    let db = new DatabaseWrapper(this.pubSub, this.auth, this, this.logging, this.databaseRecovery, this.onlineStateService, this.debug);
    this.allDatabases.push(db);
    return db;
  }

  /** The databases a sweep may still act on (#4506) — `allDatabases` minus any wrapper
   * whose shutdown has started. `allDatabases` itself stays complete on purpose; which
   * sites need which, and why, is in design-docs/pass-through-db.md, "Which databases a
   * sweep may act on". */
  get activeDatabases(): DatabaseWrapper[] {
    return selectActiveDatabases(this.allDatabases);
  }

  allDatabasesStatus() {
    let stateCounts: { [key: string]: number } =
      // key declared as string because of TypeScript issue #2491
      {};
    for (let s in this.databaseStates) {
      let state = this.databaseStates[s];
      if (stateCounts[state] == null) stateCounts[state] = 0;
      stateCounts[state]++;
    }

    // if any db is offline
    if (stateCounts[DatabaseState.Offline] > 0) {
      return DatabaseState.Offline;
    }

    // if any db is syncing
    if (stateCounts[DatabaseState.Syncing] > 0) {
      return DatabaseState.Syncing;
    }

    // otherwise, it's done
    return DatabaseState.Ready;
  }

  /** Mark every database Offline AND tell the rest of the app about it (#3991 F8b).
   *
   * This used to pre-write `databaseStates[s] = Offline` in a first loop before calling
   * `updateState`. That looked harmless but made the whole method SILENT: `updateState` compares
   * the new state against the CURRENT value in this very map to decide whether to publish, so by
   * pre-writing Offline we guaranteed `prev === state` and every call hit its early return. The
   * map changed and `CloudStateChangeEvent` never fired — so `ConnectionStateService` never
   * learned we were offline, and nothing that listens to it (the offline toast, the 2px line, the
   * standing "Offline" pill) ever showed. It also POISONED the map: a later, legitimate
   * `updateState(Offline)` from a sync error handler would then early-return too.
   *
   * The pre-write was also redundant — `updateState` assigns the map itself and
   * `databaseStates` is only ever written by `updateState`, so looping the wrappers alone
   * reaches every key that a live wrapper owns.
   *
   * It loops `activeDatabases`, not the complete list (#4506), so a superseded `user`
   * wrapper cannot overwrite the live one's `databaseStates` key. See
   * design-docs/pass-through-db.md, "Which databases a sweep may act on". */
  setAllDatabasesStatusOffline() {
    for (let db of this.activeDatabases) {
      db.updateState(DatabaseState.Offline);
    }
  }

  /** Ensure all database syncs are active. Used when app resumes from background on iOS
   * where Safari may have killed sync connections while the app was backgrounded. */
  ensureSyncsActive() {
    // #4506: a shut-down wrapper must not be restarted — see design-docs/pass-through-db.md,
    // "Which databases a sweep may act on".
    for (const db of this.activeDatabases) {
      db.ensureSyncActive();
    }
  }

  /** Attempt graceful PouchDB recreation on the live databases. Returns true if all succeeded.
   *
   * #4506: a shut-down wrapper is skipped, because recreating one BRINGS IT BACK — and the
   * loop's own residual race is a known gap. Both are in design-docs/pass-through-db.md,
   * "Which databases a sweep may act on". */
  async attemptGracefulRecoveryAsync(): Promise<boolean> {
    for (const db of this.activeDatabases) {
      if (!await db.attemptGracefulRecoveryAsync()) return false;
    }
    return true;
  }

  userDatabasesStatus() {
    return this.databaseStates['user'];
  }

  public createOrLoadDatabase(databaseId: string): any {
    return new PouchDB(databaseId, {
      auto_compaction: true,
      ...getLocalAdapterOpts(
        makeLocalCorruptionReporter(this.logging, databaseId),
        makeLocalAdapterFaultReporter(this.logging, databaseId),
      ),
    });
  }
}
